import {
  getOpenRetailerListApi,
  getOpenRetailerListApiSilent,
} from "@/utils/neturls";
import { MAP_REGION_CN, MAP_REGION_MY } from "@/utils/config";

const MY_COUNTRY_PATTERN = /malaysia|马来西亚/i;

const getDealerDistrictName = (item) =>
  item?.district?.localizedFullName ||
  item?.district?.fullName ||
  item?.district?.shortName ||
  item?.districtName ||
  "";

export const buildDealerFullAddress = (item) => {
  const countryName = String(item?.countryName || "").trim();
  const provinceName = String(item?.provinceName || "").trim();
  const cityName = String(item?.cityName || "").trim();
  const districtName = getDealerDistrictName(item);
  const address = String(item?.address || "").trim();

  if (isMalaysiaDealer(item)) {
    return [address, districtName, cityName, provinceName, countryName]
      .filter(Boolean)
      .join(", ");
  }

  return `${countryName}${provinceName}${cityName}${districtName}${address}`;
};

export const getDealerDisplayAddress = (item) => {
  if (!item) return "";
  if (item.fullAddress) return item.fullAddress;
  const built = buildDealerFullAddress(item);
  return built || String(item.address || "").trim();
};

const roundCoord6 = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? Number(num.toFixed(6)) : value;
};

// const MY_COUNTRY_IDS = new Set(["202509091935343870050000"]);

export const normalizeDealerItem = (item) => {
  const lat = item?.latitude ?? item?.lat;
  const lng = item?.longitude ?? item?.lon ?? item?.lng;
  item.latitude = lat == null ? lat : roundCoord6(lat);
  item.longitude = lng == null ? lng : roundCoord6(lng);
  item.countryName =
    item?.district?.city?.province?.country?.localizedFullName ||
    item?.district?.city?.province?.country?.fullName ||
    item?.district?.city?.province?.country?.shortName ||
    item?.countryName ||
    "";
  item.provinceName =
    item?.district?.city?.province?.localizedFullName ||
    item?.district?.city?.province?.fullName ||
    item?.district?.city?.province?.shortName ||
    item?.provinceName ||
    "";
  item.cityName =
    item?.district?.city?.localizedFullName ||
    item?.district?.city?.fullName ||
    item?.district?.city?.shortName ||
    "";
  item.districtName = getDealerDistrictName(item);
  item.fullAddress = buildDealerFullAddress(item);
  return item;
};

export const getDealerItemKey = (item, index = 0) =>
  item?.id ?? `${item?.fullName || "store"}-${index}`;

/** 重叠坐标门店弹框：优先展示当前已选中的那家 */
export const resolveDealerIndexInGroup = (grouped, preferredKey) => {
  if (!Array.isArray(grouped) || grouped.length === 0) return 0;
  if (preferredKey == null || preferredKey === "") return 0;
  const idx = grouped.findIndex((d) => d?.__listKey === preferredKey);
  return idx >= 0 ? idx : 0;
};

export const hasValidMapCoordinates = (item) => {
  const lat = Number(item?.latitude ?? item?.lat);
  const lng = Number(item?.longitude ?? item?.lon ?? item?.lng);
  return (
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    !(lat === 0 && lng === 0)
  );
};

const getDealerCountryId = (item) =>
  item?.district?.city?.province?.country?.id ??
  item?.countryId ??
  item?.district?.countryId ??
  null;

const isMalaysiaByCoordinates = (item) => {
  if (!hasValidMapCoordinates(item)) return false;
  const lat = Number(item.latitude ?? item.lat);
  const lng = Number(item.longitude ?? item.lng);
  return lat >= 0.85 && lat <= 7.5 && lng >= 99 && lng <= 119.5;
};

export const isMalaysiaDealer = (item) => {
  if (!item) return false;

  // const countryId = String(getDealerCountryId(item) || "");
  // if (MY_COUNTRY_IDS.has(countryId)) return true;

  const country = item?.district?.city?.province?.country;
  const countryName = String(
    item.countryName ||
      country?.localizedFullName ||
      country?.fullName ||
      country?.shortName ||
      ""
  );
  const countryCode = String(
    country?.code || country?.isoCode || country?.countryCode || ""
  ).toUpperCase();

  if (["MY", "MYS"].includes(countryCode)) return true;
  if (MY_COUNTRY_PATTERN.test(countryName)) return true;

  return isMalaysiaByCoordinates(item);
};

export const isDealerInRegion = (item, region) => {
  if (!item) return false;
  const isMalaysia = isMalaysiaDealer(item);
  if (region === MAP_REGION_MY) return isMalaysia;
  if (region === MAP_REGION_CN) return !isMalaysia;
  return false;
};

export const filterDealersByRegion = (dealers, region) =>
  (dealers || []).filter((item) => isDealerInRegion(item, region));

export const groupDealersByCountryProvince = (dealers) => {
  const countryMap = new Map();
  (dealers || []).forEach((raw, index) => {
    const item = normalizeDealerItem({ ...raw });
    const countryName = item.countryName || "—";
    const provinceName = item.provinceName || "—";
    if (!countryMap.has(countryName)) {
      countryMap.set(countryName, new Map());
    }
    const provinceMap = countryMap.get(countryName);
    if (!provinceMap.has(provinceName)) {
      provinceMap.set(provinceName, []);
    }
    provinceMap.get(provinceName).push({
      ...item,
      __listKey: getDealerItemKey(item, index),
    });
  });

  return [...countryMap.entries()].map(([countryName, provinceMap]) => ({
    countryName,
    provinces: [...provinceMap.entries()]
      .map(([provinceName, list]) => ({
        provinceName,
        dealers: list.sort((a, b) =>
          String(a.fullName || "").localeCompare(String(b.fullName || ""), "zh")
        ),
      }))
      .sort((a, b) =>
        String(a.provinceName).localeCompare(String(b.provinceName), "zh")
      ),
  }));
};

let cachedAllRetailers = null;
let fetchAllPromise = null;
const cachedRegionRetailers = new Map();

export const invalidateAllRetailersCache = () => {
  cachedAllRetailers = null;
  fetchAllPromise = null;
  cachedRegionRetailers.clear();
};

/** 全量门店已拉取过时，按国家 Tab 同步返回缓存列表 */
export const getCachedRetailersForRegion = (region) => {
  if (cachedRegionRetailers.has(region)) {
    return cachedRegionRetailers.get(region);
  }
  if (!cachedAllRetailers) return null;
  const filtered = filterDealersByRegion(cachedAllRetailers, region);
  cachedRegionRetailers.set(region, filtered);
  return filtered;
};

export const fetchAllRetailers = async ({ forceRefresh = false, silent = false } = {}) => {
  if (forceRefresh) {
    invalidateAllRetailersCache();
  }
  if (cachedAllRetailers) return cachedAllRetailers;
  if (fetchAllPromise) return fetchAllPromise;

  fetchAllPromise = (async () => {

    let param = {
      approvalStatusEqual: ['APPROVED'],
    };
    const query = [];
    Object.keys(param).forEach((key) => {
      let list = param[key];
      let innerquery;
      if (list && Array.isArray(list)) {
        innerquery = list
          .filter((item) => item)
          .map((item) => `${key}=${item}`)
          .join("&");
        if (innerquery) {
          query.push(innerquery);
        }
      }
    });
    const finalquery = query.join("&"); // 手动拼接查询字符串
    console.log('我进来调用了 getOpenRetailerListApi',finalquery)
    let res = silent
      ? await getOpenRetailerListApiSilent(finalquery, null)
      : await getOpenRetailerListApi(finalquery, null);
    let templist =res?.data?.list || []
    console.log('过滤前的数量',templist.length)
    templist = templist.filter(item=>item?.longitude!=0 && item?.latitude!=0)
    console.log('过滤后的数量',templist.length)
    // if(templist.some(item=>item?.longitude==0 && item?.latitude==0)){
    //   res ={
    //     "error": null,
    //     "data": {
    //       "list": [
    //         {
    //           "flagList": [],
    //           "id": "202509051425579558450000",
    //           "createTimestamp": 1757082358000,
    //           "updateTimestamp": 1757082358000,
    //           "directSuperiorDistributorId": "202509051422443662070000",
    //           "fullName": "上海广台乔子汽车服务店",
    //           "approvalStatus": "APPROVED",
    //           "approvalStatusDescription": "已审核",
    //           "districtId": "202509051418534975210000",
    //           "address": "延安西路1448弄3号",
    //           "longitude": 121.418492,
    //           "latitude": 31.210415,
    //           // "longitude": 121.41819,
    //           // "latitude": 31.2104,
    //           "businessLicenseType": "BUSINESS_LICENSE",
    //           "businessLicenseTypeDescription": "营业执照",
    //           "businessLicenseNumber": "91310118560192303A",
    //           "firstBusinessLicensePicFileId": null,
    //           "secondBusinessLicensePicFileId": null,
    //           "thirdBusinessLicensePicFileId": null,
    //           "directSuperiorDistributor": {
    //             "flagList": [],
    //             "id": "202509051422443662070000",
    //             "createTimestamp": 1757082164000,
    //             "updateTimestamp": 1757082165000,
    //             "type": "DISTRIBUTOR",
    //             "typeDescription": "分销商",
    //             "level": 1,
    //             "directSuperiorDistributorId": null,
    //             "fullName": "上海广台乔子实业发展有限公司（电商）",
    //             "approvalStatus": "APPROVED",
    //             "approvalStatusDescription": "已审核",
    //             "districtId": "202509051418534975210000",
    //             "address": "延安西路1448弄3号",
    //             "longitude": 121.418492,
    //             "latitude": 31.210415,
    //             // "longitude": 121.423027,
    //             // "latitude": 31.208457,
    //             "businessLicenseType": "BUSINESS_LICENSE",
    //             "businessLicenseTypeDescription": "营业执照",
    //             "businessLicenseNumber": "91310118560192303A",
    //             "firstBusinessLicensePicFileId": null,
    //             "secondBusinessLicensePicFileId": null,
    //             "thirdBusinessLicensePicFileId": null,
    //             "directSuperiorDistributor": null,
    //             "district": {
    //               "flagList": [],
    //               "id": "202509051418534975210000",
    //               "createTimestamp": 1757081933000,
    //               "updateTimestamp": 1757081933000,
    //               "cityId": "202509051418532875210000",
    //               "regionalismCode": "310105",
    //               "localizedFullName": "长宁区",
    //               "fullName": "长宁区",
    //               "fullNameZhCn": "长宁区",
    //               "city": {
    //                 "flagList": [],
    //                 "id": "202509051418532875210000",
    //                 "createTimestamp": 1757081933000,
    //                 "updateTimestamp": 1757081933000,
    //                 "provinceId": "202509051418532155210000",
    //                 "regionalismCode": "310000",
    //                 "localizedFullName": "上海市",
    //                 "fullName": "上海市",
    //                 "fullNameZhCn": "上海市",
    //                 "province": {
    //                   "flagList": [],
    //                   "id": "202509051418532155210000",
    //                   "createTimestamp": 1757081933000,
    //                   "updateTimestamp": 1757081933000,
    //                   "countryId": "202509051417566675210000",
    //                   "regionalismCode": "310000",
    //                   "localizedFullName": "上海市",
    //                   "fullName": "上海市",
    //                   "fullNameZhCn": "上海市",
    //                   "country": {
    //                     "flagList": [],
    //                     "id": "202509051417566675210000",
    //                     "createTimestamp": 1757081877000,
    //                     "updateTimestamp": 1757081877000,
    //                     "localizedFullName": "中华人民共和国",
    //                     "fullName": "中华人民共和国",
    //                     "fullNameZhCn": "中华人民共和国"
    //                   }
    //                 }
    //               }
    //             },
    //             "firstBusinessLicensePicFile": null,
    //             "secondBusinessLicensePicFile": null,
    //             "thirdBusinessLicensePicFile": null,
    //             "userPartyType": "DISTRIBUTOR",
    //             "userPartyTypeDescription": "分销商",
    //             "merchantPartyType": "DISTRIBUTOR",
    //             "merchantPartyTypeDescription": "分销商",
    //             "businessPartyTypeDescription": "分销商",
    //             "businessPartyType": "DISTRIBUTOR",
    //             "warrantyConfigRefTargetTypeDescription": "分销商",
    //             "warrantyConfigRefTargetType": "DISTRIBUTOR",
    //             "warehouseOwningPartyTypeDescription": "分销商",
    //             "warehouseOwningPartyType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceTypeDescription": "分销商",
    //             "warehouseInventoryOutboundTargetType": "DISTRIBUTOR",
    //             "warehouseInventoryOutboundTargetTypeDescription": "分销商",
    //             "productRefTargetTypeDescription": "分销商",
    //             "productRefTargetType": "DISTRIBUTOR",
    //             "sellerPartyTypeDescription": "分销商",
    //             "sellerPartyType": "DISTRIBUTOR",
    //             "buyerPartyTypeDescription": "分销商",
    //             "buyerPartyType": "DISTRIBUTOR"
    //           },
    //           "district": {
    //             "flagList": [],
    //             "id": "202509051418534975210000",
    //             "createTimestamp": 1757081933000,
    //             "updateTimestamp": 1757081933000,
    //             "cityId": "202509051418532875210000",
    //             "regionalismCode": "310105",
    //             "localizedFullName": "长宁区",
    //             "fullName": "长宁区",
    //             "fullNameZhCn": "长宁区",
    //             "city": {
    //               "flagList": [],
    //               "id": "202509051418532875210000",
    //               "createTimestamp": 1757081933000,
    //               "updateTimestamp": 1757081933000,
    //               "provinceId": "202509051418532155210000",
    //               "regionalismCode": "310000",
    //               "localizedFullName": "上海市",
    //               "fullName": "上海市",
    //               "fullNameZhCn": "上海市",
    //               "province": {
    //                 "flagList": [],
    //                 "id": "202509051418532155210000",
    //                 "createTimestamp": 1757081933000,
    //                 "updateTimestamp": 1757081933000,
    //                 "countryId": "202509051417566675210000",
    //                 "regionalismCode": "310000",
    //                 "localizedFullName": "上海市",
    //                 "fullName": "上海市",
    //                 "fullNameZhCn": "上海市",
    //                 "country": {
    //                   "flagList": [],
    //                   "id": "202509051417566675210000",
    //                   "createTimestamp": 1757081877000,
    //                   "updateTimestamp": 1757081877000,
    //                   "localizedFullName": "中华人民共和国",
    //                   "fullName": "中华人民共和国",
    //                   "fullNameZhCn": "中华人民共和国"
    //                 }
    //               }
    //             }
    //           },
    //           "firstBusinessLicensePicFile": null,
    //           "secondBusinessLicensePicFile": null,
    //           "thirdBusinessLicensePicFile": null,
    //           "userPartyType": "RETAILER",
    //           "userPartyTypeDescription": "零售商",
    //           "merchantPartyType": "RETAILER",
    //           "merchantPartyTypeDescription": "零售商",
    //           "businessPartyTypeDescription": "零售商",
    //           "businessPartyType": "RETAILER",
    //           "warrantyConfigRefTargetTypeDescription": "零售商",
    //           "warrantyConfigRefTargetType": "RETAILER",
    //           "warehouseOwningPartyTypeDescription": "零售商",
    //           "warehouseOwningPartyType": "RETAILER",
    //           "warehouseInventoryOutboundTargetType": "RETAILER",
    //           "warehouseInventoryOutboundTargetTypeDescription": "零售商",
    //           "productRefTargetTypeDescription": "零售商",
    //           "productRefTargetType": "RETAILER",
    //           "serviceStoreOwningPartyType": "RETAILER",
    //           "serviceStoreOwningPartyTypeDescription": "零售商",
    //           "serviceRefTargetTypeDescription": "零售商",
    //           "serviceRefTargetType": "RETAILER",
    //           "sellerPartyTypeDescription": "零售商",
    //           "sellerPartyType": "RETAILER",
    //           "buyerPartyTypeDescription": "零售商",
    //           "buyerPartyType": "RETAILER"
    //         },
    //         {
    //           "flagList": [],
    //           "id": "202509051426528716240000",
    //           "createTimestamp": 1757082413000,
    //           "updateTimestamp": 1757082413000,
    //           "directSuperiorDistributorId": "202509051422480002070000",
    //           "fullName": "TS TYRE AUTOCARE SDN BHD (SUNWAY PJS)",
    //           "approvalStatus": "APPROVED",
    //           "approvalStatusDescription": "已审核",
    //           "districtId": "202509091652388706240000",
    //           "address": "1, Jalan PJS 7/20b, Bandar Sunway, 47500 Subang Jaya, Selangor",
    //           "longitude": 101.6143233,
    //           "latitude": 3.0650258,
    //           "businessLicenseType": "BUSINESS_REGISTRATION_CERTIFICATE",
    //           "businessLicenseTypeDescription": "商业登记证",
    //           "businessLicenseNumber": "********-***-**-**-*",
    //           "firstBusinessLicensePicFileId": null,
    //           "secondBusinessLicensePicFileId": null,
    //           "thirdBusinessLicensePicFileId": null,
    //           "directSuperiorDistributor": {
    //             "flagList": [],
    //             "id": "202509051422480002070000",
    //             "createTimestamp": 1757082168000,
    //             "updateTimestamp": 1757082168000,
    //             "type": "DISTRIBUTOR",
    //             "typeDescription": "分销商",
    //             "level": 1,
    //             "directSuperiorDistributorId": null,
    //             "fullName": "GTQZ SDN BHD",
    //             "approvalStatus": "APPROVED",
    //             "approvalStatusDescription": "已审核",
    //             "districtId": "202509091652252096240000",
    //             "address": "30, Jalan Segambut Pusat, Segambut, 51200 Kuala Lumpur",
    //             "longitude": 121.423027,
    //             "latitude": 31.208457,
    //             "businessLicenseType": "BUSINESS_REGISTRATION_CERTIFICATE",
    //             "businessLicenseTypeDescription": "商业登记证",
    //             "businessLicenseNumber": "********-***-**-**-*",
    //             "firstBusinessLicensePicFileId": null,
    //             "secondBusinessLicensePicFileId": null,
    //             "thirdBusinessLicensePicFileId": null,
    //             "directSuperiorDistributor": null,
    //             "district": {
    //               "flagList": [],
    //               "id": "202509091652252096240000",
    //               "createTimestamp": 1757436745000,
    //               "updateTimestamp": 1757436745000,
    //               "cityId": "202509091652251526240000",
    //               "regionalismCode": null,
    //               "localizedFullName": "KualaLumpur",
    //               "fullName": "KualaLumpur",
    //               "fullNameZhCn": "KualaLumpur",
    //               "city": {
    //                 "flagList": [],
    //                 "id": "202509091652251526240000",
    //                 "createTimestamp": 1757436745000,
    //                 "updateTimestamp": 1757436745000,
    //                 "provinceId": "202509091652250946240000",
    //                 "regionalismCode": null,
    //                 "localizedFullName": "KualaLumpur",
    //                 "fullName": "KualaLumpur",
    //                 "fullNameZhCn": "KualaLumpur",
    //                 "province": {
    //                   "flagList": [],
    //                   "id": "202509091652250946240000",
    //                   "createTimestamp": 1757436745000,
    //                   "updateTimestamp": 1757436745000,
    //                   "countryId": "202509091652211346240000",
    //                   "regionalismCode": null,
    //                   "localizedFullName": "KualaLumpur",
    //                   "fullName": "KualaLumpur",
    //                   "fullNameZhCn": "KualaLumpur",
    //                   "country": {
    //                     "flagList": [],
    //                     "id": "202509091652211346240000",
    //                     "createTimestamp": 1757436741000,
    //                     "updateTimestamp": 1757436741000,
    //                     "localizedFullName": "Malaysia",
    //                     "fullName": "Malaysia",
    //                     "fullNameZhCn": "Malaysia"
    //                   }
    //                 }
    //               }
    //             },
    //             "firstBusinessLicensePicFile": null,
    //             "secondBusinessLicensePicFile": null,
    //             "thirdBusinessLicensePicFile": null,
    //             "userPartyType": "DISTRIBUTOR",
    //             "userPartyTypeDescription": "分销商",
    //             "merchantPartyType": "DISTRIBUTOR",
    //             "merchantPartyTypeDescription": "分销商",
    //             "businessPartyTypeDescription": "分销商",
    //             "businessPartyType": "DISTRIBUTOR",
    //             "warrantyConfigRefTargetTypeDescription": "分销商",
    //             "warrantyConfigRefTargetType": "DISTRIBUTOR",
    //             "warehouseOwningPartyTypeDescription": "分销商",
    //             "warehouseOwningPartyType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceTypeDescription": "分销商",
    //             "warehouseInventoryOutboundTargetType": "DISTRIBUTOR",
    //             "warehouseInventoryOutboundTargetTypeDescription": "分销商",
    //             "productRefTargetTypeDescription": "分销商",
    //             "productRefTargetType": "DISTRIBUTOR",
    //             "sellerPartyTypeDescription": "分销商",
    //             "sellerPartyType": "DISTRIBUTOR",
    //             "buyerPartyTypeDescription": "分销商",
    //             "buyerPartyType": "DISTRIBUTOR"
    //           },
    //           "district": {
    //             "flagList": [],
    //             "id": "202509091652388706240000",
    //             "createTimestamp": 1757436759000,
    //             "updateTimestamp": 1757436759000,
    //             "cityId": "202509091652388106240000",
    //             "regionalismCode": null,
    //             "localizedFullName": "Petaling",
    //             "fullName": "Petaling",
    //             "fullNameZhCn": "Petaling",
    //             "city": {
    //               "flagList": [],
    //               "id": "202509091652388106240000",
    //               "createTimestamp": 1757436759000,
    //               "updateTimestamp": 1757436759000,
    //               "provinceId": "202509091652380606240000",
    //               "regionalismCode": null,
    //               "localizedFullName": "Petaling",
    //               "fullName": "Petaling",
    //               "fullNameZhCn": "Petaling",
    //               "province": {
    //                 "flagList": [],
    //                 "id": "202509091652380606240000",
    //                 "createTimestamp": 1757436758000,
    //                 "updateTimestamp": 1757436758000,
    //                 "countryId": "202509091652211346240000",
    //                 "regionalismCode": null,
    //                 "localizedFullName": "Selangor",
    //                 "fullName": "Selangor",
    //                 "fullNameZhCn": "Selangor",
    //                 "country": {
    //                   "flagList": [],
    //                   "id": "202509091652211346240000",
    //                   "createTimestamp": 1757436741000,
    //                   "updateTimestamp": 1757436741000,
    //                   "localizedFullName": "Malaysia",
    //                   "fullName": "Malaysia",
    //                   "fullNameZhCn": "Malaysia"
    //                 }
    //               }
    //             }
    //           },
    //           "firstBusinessLicensePicFile": null,
    //           "secondBusinessLicensePicFile": null,
    //           "thirdBusinessLicensePicFile": null,
    //           "userPartyType": "RETAILER",
    //           "userPartyTypeDescription": "零售商",
    //           "merchantPartyType": "RETAILER",
    //           "merchantPartyTypeDescription": "零售商",
    //           "businessPartyTypeDescription": "零售商",
    //           "businessPartyType": "RETAILER",
    //           "warrantyConfigRefTargetTypeDescription": "零售商",
    //           "warrantyConfigRefTargetType": "RETAILER",
    //           "warehouseOwningPartyTypeDescription": "零售商",
    //           "warehouseOwningPartyType": "RETAILER",
    //           "warehouseInventoryOutboundTargetType": "RETAILER",
    //           "warehouseInventoryOutboundTargetTypeDescription": "零售商",
    //           "productRefTargetTypeDescription": "零售商",
    //           "productRefTargetType": "RETAILER",
    //           "serviceStoreOwningPartyType": "RETAILER",
    //           "serviceStoreOwningPartyTypeDescription": "零售商",
    //           "serviceRefTargetTypeDescription": "零售商",
    //           "serviceRefTargetType": "RETAILER",
    //           "sellerPartyTypeDescription": "零售商",
    //           "sellerPartyType": "RETAILER",
    //           "buyerPartyTypeDescription": "零售商",
    //           "buyerPartyType": "RETAILER"
    //         },
    //         {
    //           "flagList": [],
    //           "id": "202509150959149633630000",
    //           "createTimestamp": 1757930355000,
    //           "updateTimestamp": 1757930355000,
    //           "directSuperiorDistributorId": "202509051422443662070000",
    //           "fullName": "嘉实多汽车车膜旗舰店（天猫）",
    //           "approvalStatus": "APPROVED",
    //           "approvalStatusDescription": "已审核",
    //           "districtId": "202509051418534975210000",
    //           "address": "延安西路1448弄3号",
    //           "longitude": 121.418492,
    //           "latitude": 31.210415,
    //           // "longitude": 121.41819,
    //           // "latitude": 31.2104,
    //           "businessLicenseType": "BUSINESS_LICENSE",
    //           "businessLicenseTypeDescription": "营业执照",
    //           "businessLicenseNumber": "91310118560192303A",
    //           "firstBusinessLicensePicFileId": null,
    //           "secondBusinessLicensePicFileId": null,
    //           "thirdBusinessLicensePicFileId": null,
    //           "directSuperiorDistributor": {
    //             "flagList": [],
    //             "id": "202509051422443662070000",
    //             "createTimestamp": 1757082164000,
    //             "updateTimestamp": 1757082165000,
    //             "type": "DISTRIBUTOR",
    //             "typeDescription": "分销商",
    //             "level": 1,
    //             "directSuperiorDistributorId": null,
    //             "fullName": "上海广台乔子实业发展有限公司（电商）",
    //             "approvalStatus": "APPROVED",
    //             "approvalStatusDescription": "已审核",
    //             "districtId": "202509051418534975210000",
    //             "address": "延安西路1448弄3号",
    //             "longitude": 121.418492,
    //             "latitude": 31.210415,
    //             // "longitude": 121.423027,
    //             // "latitude": 31.208457,
    //             "businessLicenseType": "BUSINESS_LICENSE",
    //             "businessLicenseTypeDescription": "营业执照",
    //             "businessLicenseNumber": "91310118560192303A",
    //             "firstBusinessLicensePicFileId": null,
    //             "secondBusinessLicensePicFileId": null,
    //             "thirdBusinessLicensePicFileId": null,
    //             "directSuperiorDistributor": null,
    //             "district": {
    //               "flagList": [],
    //               "id": "202509051418534975210000",
    //               "createTimestamp": 1757081933000,
    //               "updateTimestamp": 1757081933000,
    //               "cityId": "202509051418532875210000",
    //               "regionalismCode": "310105",
    //               "localizedFullName": "长宁区",
    //               "fullName": "长宁区",
    //               "fullNameZhCn": "长宁区",
    //               "city": {
    //                 "flagList": [],
    //                 "id": "202509051418532875210000",
    //                 "createTimestamp": 1757081933000,
    //                 "updateTimestamp": 1757081933000,
    //                 "provinceId": "202509051418532155210000",
    //                 "regionalismCode": "310000",
    //                 "localizedFullName": "上海市",
    //                 "fullName": "上海市",
    //                 "fullNameZhCn": "上海市",
    //                 "province": {
    //                   "flagList": [],
    //                   "id": "202509051418532155210000",
    //                   "createTimestamp": 1757081933000,
    //                   "updateTimestamp": 1757081933000,
    //                   "countryId": "202509051417566675210000",
    //                   "regionalismCode": "310000",
    //                   "localizedFullName": "上海市",
    //                   "fullName": "上海市",
    //                   "fullNameZhCn": "上海市",
    //                   "country": {
    //                     "flagList": [],
    //                     "id": "202509051417566675210000",
    //                     "createTimestamp": 1757081877000,
    //                     "updateTimestamp": 1757081877000,
    //                     "localizedFullName": "中华人民共和国",
    //                     "fullName": "中华人民共和国",
    //                     "fullNameZhCn": "中华人民共和国"
    //                   }
    //                 }
    //               }
    //             },
    //             "firstBusinessLicensePicFile": null,
    //             "secondBusinessLicensePicFile": null,
    //             "thirdBusinessLicensePicFile": null,
    //             "userPartyType": "DISTRIBUTOR",
    //             "userPartyTypeDescription": "分销商",
    //             "merchantPartyType": "DISTRIBUTOR",
    //             "merchantPartyTypeDescription": "分销商",
    //             "businessPartyTypeDescription": "分销商",
    //             "businessPartyType": "DISTRIBUTOR",
    //             "warrantyConfigRefTargetTypeDescription": "分销商",
    //             "warrantyConfigRefTargetType": "DISTRIBUTOR",
    //             "warehouseOwningPartyTypeDescription": "分销商",
    //             "warehouseOwningPartyType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceTypeDescription": "分销商",
    //             "warehouseInventoryOutboundTargetType": "DISTRIBUTOR",
    //             "warehouseInventoryOutboundTargetTypeDescription": "分销商",
    //             "productRefTargetTypeDescription": "分销商",
    //             "productRefTargetType": "DISTRIBUTOR",
    //             "sellerPartyTypeDescription": "分销商",
    //             "sellerPartyType": "DISTRIBUTOR",
    //             "buyerPartyTypeDescription": "分销商",
    //             "buyerPartyType": "DISTRIBUTOR"
    //           },
    //           "district": {
    //             "flagList": [],
    //             "id": "202509051418534975210000",
    //             "createTimestamp": 1757081933000,
    //             "updateTimestamp": 1757081933000,
    //             "cityId": "202509051418532875210000",
    //             "regionalismCode": "310105",
    //             "localizedFullName": "长宁区",
    //             "fullName": "长宁区",
    //             "fullNameZhCn": "长宁区",
    //             "city": {
    //               "flagList": [],
    //               "id": "202509051418532875210000",
    //               "createTimestamp": 1757081933000,
    //               "updateTimestamp": 1757081933000,
    //               "provinceId": "202509051418532155210000",
    //               "regionalismCode": "310000",
    //               "localizedFullName": "上海市",
    //               "fullName": "上海市",
    //               "fullNameZhCn": "上海市",
    //               "province": {
    //                 "flagList": [],
    //                 "id": "202509051418532155210000",
    //                 "createTimestamp": 1757081933000,
    //                 "updateTimestamp": 1757081933000,
    //                 "countryId": "202509051417566675210000",
    //                 "regionalismCode": "310000",
    //                 "localizedFullName": "上海市",
    //                 "fullName": "上海市",
    //                 "fullNameZhCn": "上海市",
    //                 "country": {
    //                   "flagList": [],
    //                   "id": "202509051417566675210000",
    //                   "createTimestamp": 1757081877000,
    //                   "updateTimestamp": 1757081877000,
    //                   "localizedFullName": "中华人民共和国",
    //                   "fullName": "中华人民共和国",
    //                   "fullNameZhCn": "中华人民共和国"
    //                 }
    //               }
    //             }
    //           },
    //           "firstBusinessLicensePicFile": null,
    //           "secondBusinessLicensePicFile": null,
    //           "thirdBusinessLicensePicFile": null,
    //           "userPartyType": "RETAILER",
    //           "userPartyTypeDescription": "零售商",
    //           "merchantPartyType": "RETAILER",
    //           "merchantPartyTypeDescription": "零售商",
    //           "businessPartyTypeDescription": "零售商",
    //           "businessPartyType": "RETAILER",
    //           "warrantyConfigRefTargetTypeDescription": "零售商",
    //           "warrantyConfigRefTargetType": "RETAILER",
    //           "warehouseOwningPartyTypeDescription": "零售商",
    //           "warehouseOwningPartyType": "RETAILER",
    //           "warehouseInventoryOutboundTargetType": "RETAILER",
    //           "warehouseInventoryOutboundTargetTypeDescription": "零售商",
    //           "productRefTargetTypeDescription": "零售商",
    //           "productRefTargetType": "RETAILER",
    //           "serviceStoreOwningPartyType": "RETAILER",
    //           "serviceStoreOwningPartyTypeDescription": "零售商",
    //           "serviceRefTargetTypeDescription": "零售商",
    //           "serviceRefTargetType": "RETAILER",
    //           "sellerPartyTypeDescription": "零售商",
    //           "sellerPartyType": "RETAILER",
    //           "buyerPartyTypeDescription": "零售商",
    //           "buyerPartyType": "RETAILER"
    //         },
    //         {
    //           "flagList": [],
    //           "id": "202509220513359318640000",
    //           "createTimestamp": 1758518016000,
    //           "updateTimestamp": 1758518077000,
    //           "directSuperiorDistributorId": "202509220505318268640000",
    //           "fullName": "嘉实多汽车服务尊享店（上海嘉川路店）",
    //           "approvalStatus": "APPROVED",
    //           "approvalStatusDescription": "已审核",
    //           "districtId": "202509051418534255210000",
    //           "address": "上海市徐汇区嘉川路273号隔壁",
    //           "longitude": 121.42354,
    //           "latitude": 31.14064,
    //           "businessLicenseType": "BUSINESS_LICENSE",
    //           "businessLicenseTypeDescription": "营业执照",
    //           "businessLicenseNumber": "****",
    //           "firstBusinessLicensePicFileId": null,
    //           "secondBusinessLicensePicFileId": null,
    //           "thirdBusinessLicensePicFileId": null,
    //           "directSuperiorDistributor": {
    //             "flagList": [],
    //             "id": "202509220505318268640000",
    //             "createTimestamp": 1758517532000,
    //             "updateTimestamp": 1758517637000,
    //             "type": "DISTRIBUTOR",
    //             "typeDescription": "分销商",
    //             "level": 1,
    //             "directSuperiorDistributorId": null,
    //             "fullName": "嘉实多（上海）汽车技术服务有限公司",
    //             "approvalStatus": "APPROVED",
    //             "approvalStatusDescription": "已审核",
    //             "districtId": "202509051418534255210000",
    //             "address": "上海市徐汇区冠生园路393号4幢1层125室",
    //             "longitude": 0.0,
    //             "latitude": 0.0,
    //             "businessLicenseType": "BUSINESS_LICENSE",
    //             "businessLicenseTypeDescription": "营业执照",
    //             "businessLicenseNumber": "****",
    //             "firstBusinessLicensePicFileId": null,
    //             "secondBusinessLicensePicFileId": null,
    //             "thirdBusinessLicensePicFileId": null,
    //             "directSuperiorDistributor": null,
    //             "district": {
    //               "flagList": [],
    //               "id": "202509051418534255210000",
    //               "createTimestamp": 1757081933000,
    //               "updateTimestamp": 1757081933000,
    //               "cityId": "202509051418532875210000",
    //               "regionalismCode": "310104",
    //               "localizedFullName": "徐汇区",
    //               "fullName": "徐汇区",
    //               "fullNameZhCn": "徐汇区",
    //               "city": {
    //                 "flagList": [],
    //                 "id": "202509051418532875210000",
    //                 "createTimestamp": 1757081933000,
    //                 "updateTimestamp": 1757081933000,
    //                 "provinceId": "202509051418532155210000",
    //                 "regionalismCode": "310000",
    //                 "localizedFullName": "上海市",
    //                 "fullName": "上海市",
    //                 "fullNameZhCn": "上海市",
    //                 "province": {
    //                   "flagList": [],
    //                   "id": "202509051418532155210000",
    //                   "createTimestamp": 1757081933000,
    //                   "updateTimestamp": 1757081933000,
    //                   "countryId": "202509051417566675210000",
    //                   "regionalismCode": "310000",
    //                   "localizedFullName": "上海市",
    //                   "fullName": "上海市",
    //                   "fullNameZhCn": "上海市",
    //                   "country": {
    //                     "flagList": [],
    //                     "id": "202509051417566675210000",
    //                     "createTimestamp": 1757081877000,
    //                     "updateTimestamp": 1757081877000,
    //                     "localizedFullName": "中华人民共和国",
    //                     "fullName": "中华人民共和国",
    //                     "fullNameZhCn": "中华人民共和国"
    //                   }
    //                 }
    //               }
    //             },
    //             "firstBusinessLicensePicFile": null,
    //             "secondBusinessLicensePicFile": null,
    //             "thirdBusinessLicensePicFile": null,
    //             "userPartyType": "DISTRIBUTOR",
    //             "userPartyTypeDescription": "分销商",
    //             "merchantPartyType": "DISTRIBUTOR",
    //             "merchantPartyTypeDescription": "分销商",
    //             "businessPartyTypeDescription": "分销商",
    //             "businessPartyType": "DISTRIBUTOR",
    //             "warrantyConfigRefTargetTypeDescription": "分销商",
    //             "warrantyConfigRefTargetType": "DISTRIBUTOR",
    //             "warehouseOwningPartyTypeDescription": "分销商",
    //             "warehouseOwningPartyType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceTypeDescription": "分销商",
    //             "warehouseInventoryOutboundTargetType": "DISTRIBUTOR",
    //             "warehouseInventoryOutboundTargetTypeDescription": "分销商",
    //             "productRefTargetTypeDescription": "分销商",
    //             "productRefTargetType": "DISTRIBUTOR",
    //             "sellerPartyTypeDescription": "分销商",
    //             "sellerPartyType": "DISTRIBUTOR",
    //             "buyerPartyTypeDescription": "分销商",
    //             "buyerPartyType": "DISTRIBUTOR"
    //           },
    //           "district": {
    //             "flagList": [],
    //             "id": "202509051418534255210000",
    //             "createTimestamp": 1757081933000,
    //             "updateTimestamp": 1757081933000,
    //             "cityId": "202509051418532875210000",
    //             "regionalismCode": "310104",
    //             "localizedFullName": "徐汇区",
    //             "fullName": "徐汇区",
    //             "fullNameZhCn": "徐汇区",
    //             "city": {
    //               "flagList": [],
    //               "id": "202509051418532875210000",
    //               "createTimestamp": 1757081933000,
    //               "updateTimestamp": 1757081933000,
    //               "provinceId": "202509051418532155210000",
    //               "regionalismCode": "310000",
    //               "localizedFullName": "上海市",
    //               "fullName": "上海市",
    //               "fullNameZhCn": "上海市",
    //               "province": {
    //                 "flagList": [],
    //                 "id": "202509051418532155210000",
    //                 "createTimestamp": 1757081933000,
    //                 "updateTimestamp": 1757081933000,
    //                 "countryId": "202509051417566675210000",
    //                 "regionalismCode": "310000",
    //                 "localizedFullName": "上海市",
    //                 "fullName": "上海市",
    //                 "fullNameZhCn": "上海市",
    //                 "country": {
    //                   "flagList": [],
    //                   "id": "202509051417566675210000",
    //                   "createTimestamp": 1757081877000,
    //                   "updateTimestamp": 1757081877000,
    //                   "localizedFullName": "中华人民共和国",
    //                   "fullName": "中华人民共和国",
    //                   "fullNameZhCn": "中华人民共和国"
    //                 }
    //               }
    //             }
    //           },
    //           "firstBusinessLicensePicFile": null,
    //           "secondBusinessLicensePicFile": null,
    //           "thirdBusinessLicensePicFile": null,
    //           "userPartyType": "RETAILER",
    //           "userPartyTypeDescription": "零售商",
    //           "merchantPartyType": "RETAILER",
    //           "merchantPartyTypeDescription": "零售商",
    //           "businessPartyTypeDescription": "零售商",
    //           "businessPartyType": "RETAILER",
    //           "warrantyConfigRefTargetTypeDescription": "零售商",
    //           "warrantyConfigRefTargetType": "RETAILER",
    //           "warehouseOwningPartyTypeDescription": "零售商",
    //           "warehouseOwningPartyType": "RETAILER",
    //           "warehouseInventoryOutboundTargetType": "RETAILER",
    //           "warehouseInventoryOutboundTargetTypeDescription": "零售商",
    //           "productRefTargetTypeDescription": "零售商",
    //           "productRefTargetType": "RETAILER",
    //           "serviceStoreOwningPartyType": "RETAILER",
    //           "serviceStoreOwningPartyTypeDescription": "零售商",
    //           "serviceRefTargetTypeDescription": "零售商",
    //           "serviceRefTargetType": "RETAILER",
    //           "sellerPartyTypeDescription": "零售商",
    //           "sellerPartyType": "RETAILER",
    //           "buyerPartyTypeDescription": "零售商",
    //           "buyerPartyType": "RETAILER"
    //         },
    //         {
    //           "flagList": [],
    //           "id": "202509230820205966830000",
    //           "createTimestamp": 1758615621000,
    //           "updateTimestamp": 1758615657000,
    //           "directSuperiorDistributorId": "202509230730592586830000",
    //           "fullName": "昆明车酷艺多帮（科业路店）",
    //           "approvalStatus": "APPROVED",
    //           "approvalStatusDescription": "已审核",
    //           "districtId": "202509051421031525210000",
    //           "address": "科业路165号",
    //           "longitude": 102.67441,
    //           "latitude": 25.0612,
    //           "businessLicenseType": "BUSINESS_LICENSE",
    //           "businessLicenseTypeDescription": "营业执照",
    //           "businessLicenseNumber": "****",
    //           "firstBusinessLicensePicFileId": null,
    //           "secondBusinessLicensePicFileId": null,
    //           "thirdBusinessLicensePicFileId": null,
    //           "directSuperiorDistributor": {
    //             "flagList": [],
    //             "id": "202509230730592586830000",
    //             "createTimestamp": 1758612659000,
    //             "updateTimestamp": 1758612694000,
    //             "type": "DISTRIBUTOR",
    //             "typeDescription": "分销商",
    //             "level": 1,
    //             "directSuperiorDistributorId": null,
    //             "fullName": "驰捷车多管（云南）企业管理有限公司",
    //             "approvalStatus": "APPROVED",
    //             "approvalStatusDescription": "已审核",
    //             "districtId": "202509051421033585210000",
    //             "address": "云南省昆明市西山区西坝路639号",
    //             "longitude": 0.0,
    //             "latitude": 0.0,
    //             "businessLicenseType": "BUSINESS_LICENSE",
    //             "businessLicenseTypeDescription": "营业执照",
    //             "businessLicenseNumber": "91310118560192303A",
    //             "firstBusinessLicensePicFileId": null,
    //             "secondBusinessLicensePicFileId": null,
    //             "thirdBusinessLicensePicFileId": null,
    //             "directSuperiorDistributor": null,
    //             "district": {
    //               "flagList": [],
    //               "id": "202509051421033585210000",
    //               "createTimestamp": 1757082063000,
    //               "updateTimestamp": 1757082063000,
    //               "cityId": "202509051421030835210000",
    //               "regionalismCode": "530112",
    //               "localizedFullName": "西山区",
    //               "fullName": "西山区",
    //               "fullNameZhCn": "西山区",
    //               "city": {
    //                 "flagList": [],
    //                 "id": "202509051421030835210000",
    //                 "createTimestamp": 1757082063000,
    //                 "updateTimestamp": 1757082063000,
    //                 "provinceId": "202509051421030075210000",
    //                 "regionalismCode": "530100",
    //                 "localizedFullName": "昆明市",
    //                 "fullName": "昆明市",
    //                 "fullNameZhCn": "昆明市",
    //                 "province": {
    //                   "flagList": [],
    //                   "id": "202509051421030075210000",
    //                   "createTimestamp": 1757082063000,
    //                   "updateTimestamp": 1757082063000,
    //                   "countryId": "202509051417566675210000",
    //                   "regionalismCode": "530000",
    //                   "localizedFullName": "云南省",
    //                   "fullName": "云南省",
    //                   "fullNameZhCn": "云南省",
    //                   "country": {
    //                     "flagList": [],
    //                     "id": "202509051417566675210000",
    //                     "createTimestamp": 1757081877000,
    //                     "updateTimestamp": 1757081877000,
    //                     "localizedFullName": "中华人民共和国",
    //                     "fullName": "中华人民共和国",
    //                     "fullNameZhCn": "中华人民共和国"
    //                   }
    //                 }
    //               }
    //             },
    //             "firstBusinessLicensePicFile": null,
    //             "secondBusinessLicensePicFile": null,
    //             "thirdBusinessLicensePicFile": null,
    //             "userPartyType": "DISTRIBUTOR",
    //             "userPartyTypeDescription": "分销商",
    //             "merchantPartyType": "DISTRIBUTOR",
    //             "merchantPartyTypeDescription": "分销商",
    //             "businessPartyTypeDescription": "分销商",
    //             "businessPartyType": "DISTRIBUTOR",
    //             "warrantyConfigRefTargetTypeDescription": "分销商",
    //             "warrantyConfigRefTargetType": "DISTRIBUTOR",
    //             "warehouseOwningPartyTypeDescription": "分销商",
    //             "warehouseOwningPartyType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceTypeDescription": "分销商",
    //             "warehouseInventoryOutboundTargetType": "DISTRIBUTOR",
    //             "warehouseInventoryOutboundTargetTypeDescription": "分销商",
    //             "productRefTargetTypeDescription": "分销商",
    //             "productRefTargetType": "DISTRIBUTOR",
    //             "sellerPartyTypeDescription": "分销商",
    //             "sellerPartyType": "DISTRIBUTOR",
    //             "buyerPartyTypeDescription": "分销商",
    //             "buyerPartyType": "DISTRIBUTOR"
    //           },
    //           "district": {
    //             "flagList": [],
    //             "id": "202509051421031525210000",
    //             "createTimestamp": 1757082063000,
    //             "updateTimestamp": 1757082063000,
    //             "cityId": "202509051421030835210000",
    //             "regionalismCode": "530102",
    //             "localizedFullName": "五华区",
    //             "fullName": "五华区",
    //             "fullNameZhCn": "五华区",
    //             "city": {
    //               "flagList": [],
    //               "id": "202509051421030835210000",
    //               "createTimestamp": 1757082063000,
    //               "updateTimestamp": 1757082063000,
    //               "provinceId": "202509051421030075210000",
    //               "regionalismCode": "530100",
    //               "localizedFullName": "昆明市",
    //               "fullName": "昆明市",
    //               "fullNameZhCn": "昆明市",
    //               "province": {
    //                 "flagList": [],
    //                 "id": "202509051421030075210000",
    //                 "createTimestamp": 1757082063000,
    //                 "updateTimestamp": 1757082063000,
    //                 "countryId": "202509051417566675210000",
    //                 "regionalismCode": "530000",
    //                 "localizedFullName": "云南省",
    //                 "fullName": "云南省",
    //                 "fullNameZhCn": "云南省",
    //                 "country": {
    //                   "flagList": [],
    //                   "id": "202509051417566675210000",
    //                   "createTimestamp": 1757081877000,
    //                   "updateTimestamp": 1757081877000,
    //                   "localizedFullName": "中华人民共和国",
    //                   "fullName": "中华人民共和国",
    //                   "fullNameZhCn": "中华人民共和国"
    //                 }
    //               }
    //             }
    //           },
    //           "firstBusinessLicensePicFile": null,
    //           "secondBusinessLicensePicFile": null,
    //           "thirdBusinessLicensePicFile": null,
    //           "userPartyType": "RETAILER",
    //           "userPartyTypeDescription": "零售商",
    //           "merchantPartyType": "RETAILER",
    //           "merchantPartyTypeDescription": "零售商",
    //           "businessPartyTypeDescription": "零售商",
    //           "businessPartyType": "RETAILER",
    //           "warrantyConfigRefTargetTypeDescription": "零售商",
    //           "warrantyConfigRefTargetType": "RETAILER",
    //           "warehouseOwningPartyTypeDescription": "零售商",
    //           "warehouseOwningPartyType": "RETAILER",
    //           "warehouseInventoryOutboundTargetType": "RETAILER",
    //           "warehouseInventoryOutboundTargetTypeDescription": "零售商",
    //           "productRefTargetTypeDescription": "零售商",
    //           "productRefTargetType": "RETAILER",
    //           "serviceStoreOwningPartyType": "RETAILER",
    //           "serviceStoreOwningPartyTypeDescription": "零售商",
    //           "serviceRefTargetTypeDescription": "零售商",
    //           "serviceRefTargetType": "RETAILER",
    //           "sellerPartyTypeDescription": "零售商",
    //           "sellerPartyType": "RETAILER",
    //           "buyerPartyTypeDescription": "零售商",
    //           "buyerPartyType": "RETAILER"
    //         },
    //         {
    //           "flagList": [],
    //           "id": "202510090607336926830000",
    //           "createTimestamp": 1759990054000,
    //           "updateTimestamp": 1761119271000,
    //           "directSuperiorDistributorId": "202509051422480002070000",
    //           "fullName": "Action Auto Agency (M) Sdn Bhd",
    //           "approvalStatus": "APPROVED",
    //           "approvalStatusDescription": "已审核",
    //           "districtId": "202509091652252096240000",
    //           "address": "30, Jalan Segambut Pusat, Segambut, 51200 Kuala Lumpur, Wilayah Persekutuan Kuala Lumpur, Malaysia",
    //           "longitude": 101.67759,
    //           "latitude": 3.18645,
    //           "businessLicenseType": "BUSINESS_REGISTRATION_CERTIFICATE",
    //           "businessLicenseTypeDescription": "商业登记证",
    //           "businessLicenseNumber": "********-***-**-**-*",
    //           "firstBusinessLicensePicFileId": null,
    //           "secondBusinessLicensePicFileId": null,
    //           "thirdBusinessLicensePicFileId": null,
    //           "directSuperiorDistributor": {
    //             "flagList": [],
    //             "id": "202509051422480002070000",
    //             "createTimestamp": 1757082168000,
    //             "updateTimestamp": 1757082168000,
    //             "type": "DISTRIBUTOR",
    //             "typeDescription": "分销商",
    //             "level": 1,
    //             "directSuperiorDistributorId": null,
    //             "fullName": "GTQZ SDN BHD",
    //             "approvalStatus": "APPROVED",
    //             "approvalStatusDescription": "已审核",
    //             "districtId": "202509091652252096240000",
    //             "address": "30, Jalan Segambut Pusat, Segambut, 51200 Kuala Lumpur",
    //             "longitude": 121.423027,
    //             "latitude": 31.208457,
    //             "businessLicenseType": "BUSINESS_REGISTRATION_CERTIFICATE",
    //             "businessLicenseTypeDescription": "商业登记证",
    //             "businessLicenseNumber": "********-***-**-**-*",
    //             "firstBusinessLicensePicFileId": null,
    //             "secondBusinessLicensePicFileId": null,
    //             "thirdBusinessLicensePicFileId": null,
    //             "directSuperiorDistributor": null,
    //             "district": {
    //               "flagList": [],
    //               "id": "202509091652252096240000",
    //               "createTimestamp": 1757436745000,
    //               "updateTimestamp": 1757436745000,
    //               "cityId": "202509091652251526240000",
    //               "regionalismCode": null,
    //               "localizedFullName": "KualaLumpur",
    //               "fullName": "KualaLumpur",
    //               "fullNameZhCn": "KualaLumpur",
    //               "city": {
    //                 "flagList": [],
    //                 "id": "202509091652251526240000",
    //                 "createTimestamp": 1757436745000,
    //                 "updateTimestamp": 1757436745000,
    //                 "provinceId": "202509091652250946240000",
    //                 "regionalismCode": null,
    //                 "localizedFullName": "KualaLumpur",
    //                 "fullName": "KualaLumpur",
    //                 "fullNameZhCn": "KualaLumpur",
    //                 "province": {
    //                   "flagList": [],
    //                   "id": "202509091652250946240000",
    //                   "createTimestamp": 1757436745000,
    //                   "updateTimestamp": 1757436745000,
    //                   "countryId": "202509091652211346240000",
    //                   "regionalismCode": null,
    //                   "localizedFullName": "KualaLumpur",
    //                   "fullName": "KualaLumpur",
    //                   "fullNameZhCn": "KualaLumpur",
    //                   "country": {
    //                     "flagList": [],
    //                     "id": "202509091652211346240000",
    //                     "createTimestamp": 1757436741000,
    //                     "updateTimestamp": 1757436741000,
    //                     "localizedFullName": "Malaysia",
    //                     "fullName": "Malaysia",
    //                     "fullNameZhCn": "Malaysia"
    //                   }
    //                 }
    //               }
    //             },
    //             "firstBusinessLicensePicFile": null,
    //             "secondBusinessLicensePicFile": null,
    //             "thirdBusinessLicensePicFile": null,
    //             "userPartyType": "DISTRIBUTOR",
    //             "userPartyTypeDescription": "分销商",
    //             "merchantPartyType": "DISTRIBUTOR",
    //             "merchantPartyTypeDescription": "分销商",
    //             "businessPartyTypeDescription": "分销商",
    //             "businessPartyType": "DISTRIBUTOR",
    //             "warrantyConfigRefTargetTypeDescription": "分销商",
    //             "warrantyConfigRefTargetType": "DISTRIBUTOR",
    //             "warehouseOwningPartyTypeDescription": "分销商",
    //             "warehouseOwningPartyType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceTypeDescription": "分销商",
    //             "warehouseInventoryOutboundTargetType": "DISTRIBUTOR",
    //             "warehouseInventoryOutboundTargetTypeDescription": "分销商",
    //             "productRefTargetTypeDescription": "分销商",
    //             "productRefTargetType": "DISTRIBUTOR",
    //             "sellerPartyTypeDescription": "分销商",
    //             "sellerPartyType": "DISTRIBUTOR",
    //             "buyerPartyTypeDescription": "分销商",
    //             "buyerPartyType": "DISTRIBUTOR"
    //           },
    //           "district": {
    //             "flagList": [],
    //             "id": "202509091652252096240000",
    //             "createTimestamp": 1757436745000,
    //             "updateTimestamp": 1757436745000,
    //             "cityId": "202509091652251526240000",
    //             "regionalismCode": null,
    //             "localizedFullName": "KualaLumpur",
    //             "fullName": "KualaLumpur",
    //             "fullNameZhCn": "KualaLumpur",
    //             "city": {
    //               "flagList": [],
    //               "id": "202509091652251526240000",
    //               "createTimestamp": 1757436745000,
    //               "updateTimestamp": 1757436745000,
    //               "provinceId": "202509091652250946240000",
    //               "regionalismCode": null,
    //               "localizedFullName": "KualaLumpur",
    //               "fullName": "KualaLumpur",
    //               "fullNameZhCn": "KualaLumpur",
    //               "province": {
    //                 "flagList": [],
    //                 "id": "202509091652250946240000",
    //                 "createTimestamp": 1757436745000,
    //                 "updateTimestamp": 1757436745000,
    //                 "countryId": "202509091652211346240000",
    //                 "regionalismCode": null,
    //                 "localizedFullName": "KualaLumpur",
    //                 "fullName": "KualaLumpur",
    //                 "fullNameZhCn": "KualaLumpur",
    //                 "country": {
    //                   "flagList": [],
    //                   "id": "202509091652211346240000",
    //                   "createTimestamp": 1757436741000,
    //                   "updateTimestamp": 1757436741000,
    //                   "localizedFullName": "Malaysia",
    //                   "fullName": "Malaysia",
    //                   "fullNameZhCn": "Malaysia"
    //                 }
    //               }
    //             }
    //           },
    //           "firstBusinessLicensePicFile": null,
    //           "secondBusinessLicensePicFile": null,
    //           "thirdBusinessLicensePicFile": null,
    //           "userPartyType": "RETAILER",
    //           "userPartyTypeDescription": "零售商",
    //           "merchantPartyType": "RETAILER",
    //           "merchantPartyTypeDescription": "零售商",
    //           "businessPartyTypeDescription": "零售商",
    //           "businessPartyType": "RETAILER",
    //           "warrantyConfigRefTargetTypeDescription": "零售商",
    //           "warrantyConfigRefTargetType": "RETAILER",
    //           "warehouseOwningPartyTypeDescription": "零售商",
    //           "warehouseOwningPartyType": "RETAILER",
    //           "warehouseInventoryOutboundTargetType": "RETAILER",
    //           "warehouseInventoryOutboundTargetTypeDescription": "零售商",
    //           "productRefTargetTypeDescription": "零售商",
    //           "productRefTargetType": "RETAILER",
    //           "serviceStoreOwningPartyType": "RETAILER",
    //           "serviceStoreOwningPartyTypeDescription": "零售商",
    //           "serviceRefTargetTypeDescription": "零售商",
    //           "serviceRefTargetType": "RETAILER",
    //           "sellerPartyTypeDescription": "零售商",
    //           "sellerPartyType": "RETAILER",
    //           "buyerPartyTypeDescription": "零售商",
    //           "buyerPartyType": "RETAILER"
    //         },
    //         {
    //           "flagList": [],
    //           "id": "202510110527213796830000",
    //           "createTimestamp": 1760160441000,
    //           "updateTimestamp": 1760160956000,
    //           "directSuperiorDistributorId": "202509230730592586830000",
    //           "fullName": "昆明车酷艺多帮（莲花池店）",
    //           "approvalStatus": "APPROVED",
    //           "approvalStatusDescription": "已审核",
    //           "districtId": "202509051421031525210000",
    //           "address": "远建商务大厦负2层停车场",
    //           "longitude": 102.70112,
    //           "latitude": 25.06386,
    //           "businessLicenseType": "BUSINESS_LICENSE",
    //           "businessLicenseTypeDescription": "营业执照",
    //           "businessLicenseNumber": "91530102MADA40M79A",
    //           "firstBusinessLicensePicFileId": null,
    //           "secondBusinessLicensePicFileId": null,
    //           "thirdBusinessLicensePicFileId": null,
    //           "directSuperiorDistributor": {
    //             "flagList": [],
    //             "id": "202509230730592586830000",
    //             "createTimestamp": 1758612659000,
    //             "updateTimestamp": 1758612694000,
    //             "type": "DISTRIBUTOR",
    //             "typeDescription": "分销商",
    //             "level": 1,
    //             "directSuperiorDistributorId": null,
    //             "fullName": "驰捷车多管（云南）企业管理有限公司",
    //             "approvalStatus": "APPROVED",
    //             "approvalStatusDescription": "已审核",
    //             "districtId": "202509051421033585210000",
    //             "address": "云南省昆明市西山区西坝路639号",
    //             "longitude": 0.0,
    //             "latitude": 0.0,
    //             "businessLicenseType": "BUSINESS_LICENSE",
    //             "businessLicenseTypeDescription": "营业执照",
    //             "businessLicenseNumber": "91310118560192303A",
    //             "firstBusinessLicensePicFileId": null,
    //             "secondBusinessLicensePicFileId": null,
    //             "thirdBusinessLicensePicFileId": null,
    //             "directSuperiorDistributor": null,
    //             "district": {
    //               "flagList": [],
    //               "id": "202509051421033585210000",
    //               "createTimestamp": 1757082063000,
    //               "updateTimestamp": 1757082063000,
    //               "cityId": "202509051421030835210000",
    //               "regionalismCode": "530112",
    //               "localizedFullName": "西山区",
    //               "fullName": "西山区",
    //               "fullNameZhCn": "西山区",
    //               "city": {
    //                 "flagList": [],
    //                 "id": "202509051421030835210000",
    //                 "createTimestamp": 1757082063000,
    //                 "updateTimestamp": 1757082063000,
    //                 "provinceId": "202509051421030075210000",
    //                 "regionalismCode": "530100",
    //                 "localizedFullName": "昆明市",
    //                 "fullName": "昆明市",
    //                 "fullNameZhCn": "昆明市",
    //                 "province": {
    //                   "flagList": [],
    //                   "id": "202509051421030075210000",
    //                   "createTimestamp": 1757082063000,
    //                   "updateTimestamp": 1757082063000,
    //                   "countryId": "202509051417566675210000",
    //                   "regionalismCode": "530000",
    //                   "localizedFullName": "云南省",
    //                   "fullName": "云南省",
    //                   "fullNameZhCn": "云南省",
    //                   "country": {
    //                     "flagList": [],
    //                     "id": "202509051417566675210000",
    //                     "createTimestamp": 1757081877000,
    //                     "updateTimestamp": 1757081877000,
    //                     "localizedFullName": "中华人民共和国",
    //                     "fullName": "中华人民共和国",
    //                     "fullNameZhCn": "中华人民共和国"
    //                   }
    //                 }
    //               }
    //             },
    //             "firstBusinessLicensePicFile": null,
    //             "secondBusinessLicensePicFile": null,
    //             "thirdBusinessLicensePicFile": null,
    //             "userPartyType": "DISTRIBUTOR",
    //             "userPartyTypeDescription": "分销商",
    //             "merchantPartyType": "DISTRIBUTOR",
    //             "merchantPartyTypeDescription": "分销商",
    //             "businessPartyTypeDescription": "分销商",
    //             "businessPartyType": "DISTRIBUTOR",
    //             "warrantyConfigRefTargetTypeDescription": "分销商",
    //             "warrantyConfigRefTargetType": "DISTRIBUTOR",
    //             "warehouseOwningPartyTypeDescription": "分销商",
    //             "warehouseOwningPartyType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceTypeDescription": "分销商",
    //             "warehouseInventoryOutboundTargetType": "DISTRIBUTOR",
    //             "warehouseInventoryOutboundTargetTypeDescription": "分销商",
    //             "productRefTargetTypeDescription": "分销商",
    //             "productRefTargetType": "DISTRIBUTOR",
    //             "sellerPartyTypeDescription": "分销商",
    //             "sellerPartyType": "DISTRIBUTOR",
    //             "buyerPartyTypeDescription": "分销商",
    //             "buyerPartyType": "DISTRIBUTOR"
    //           },
    //           "district": {
    //             "flagList": [],
    //             "id": "202509051421031525210000",
    //             "createTimestamp": 1757082063000,
    //             "updateTimestamp": 1757082063000,
    //             "cityId": "202509051421030835210000",
    //             "regionalismCode": "530102",
    //             "localizedFullName": "五华区",
    //             "fullName": "五华区",
    //             "fullNameZhCn": "五华区",
    //             "city": {
    //               "flagList": [],
    //               "id": "202509051421030835210000",
    //               "createTimestamp": 1757082063000,
    //               "updateTimestamp": 1757082063000,
    //               "provinceId": "202509051421030075210000",
    //               "regionalismCode": "530100",
    //               "localizedFullName": "昆明市",
    //               "fullName": "昆明市",
    //               "fullNameZhCn": "昆明市",
    //               "province": {
    //                 "flagList": [],
    //                 "id": "202509051421030075210000",
    //                 "createTimestamp": 1757082063000,
    //                 "updateTimestamp": 1757082063000,
    //                 "countryId": "202509051417566675210000",
    //                 "regionalismCode": "530000",
    //                 "localizedFullName": "云南省",
    //                 "fullName": "云南省",
    //                 "fullNameZhCn": "云南省",
    //                 "country": {
    //                   "flagList": [],
    //                   "id": "202509051417566675210000",
    //                   "createTimestamp": 1757081877000,
    //                   "updateTimestamp": 1757081877000,
    //                   "localizedFullName": "中华人民共和国",
    //                   "fullName": "中华人民共和国",
    //                   "fullNameZhCn": "中华人民共和国"
    //                 }
    //               }
    //             }
    //           },
    //           "firstBusinessLicensePicFile": null,
    //           "secondBusinessLicensePicFile": null,
    //           "thirdBusinessLicensePicFile": null,
    //           "userPartyType": "RETAILER",
    //           "userPartyTypeDescription": "零售商",
    //           "merchantPartyType": "RETAILER",
    //           "merchantPartyTypeDescription": "零售商",
    //           "businessPartyTypeDescription": "零售商",
    //           "businessPartyType": "RETAILER",
    //           "warrantyConfigRefTargetTypeDescription": "零售商",
    //           "warrantyConfigRefTargetType": "RETAILER",
    //           "warehouseOwningPartyTypeDescription": "零售商",
    //           "warehouseOwningPartyType": "RETAILER",
    //           "warehouseInventoryOutboundTargetType": "RETAILER",
    //           "warehouseInventoryOutboundTargetTypeDescription": "零售商",
    //           "productRefTargetTypeDescription": "零售商",
    //           "productRefTargetType": "RETAILER",
    //           "serviceStoreOwningPartyType": "RETAILER",
    //           "serviceStoreOwningPartyTypeDescription": "零售商",
    //           "serviceRefTargetTypeDescription": "零售商",
    //           "serviceRefTargetType": "RETAILER",
    //           "sellerPartyTypeDescription": "零售商",
    //           "sellerPartyType": "RETAILER",
    //           "buyerPartyTypeDescription": "零售商",
    //           "buyerPartyType": "RETAILER"
    //         },
    //         {
    //           "flagList": [],
    //           "id": "202510210921020406830000",
    //           "createTimestamp": 1761038462000,
    //           "updateTimestamp": 1761107174000,
    //           "directSuperiorDistributorId": "202509051422480002070000",
    //           "fullName": "CAF Flagship Store",
    //           "approvalStatus": "APPROVED",
    //           "approvalStatusDescription": "已审核",
    //           "districtId": "202509091652388706240000",
    //           "address": "12, Jalan PJU 3/43, Sunway Damansara Technology Park",
    //           "longitude": 101.6099,
    //           "latitude": 3.1593,
    //           "businessLicenseType": "BUSINESS_REGISTRATION_CERTIFICATE",
    //           "businessLicenseTypeDescription": "商业登记证",
    //           "businessLicenseNumber": "202501006359",
    //           "firstBusinessLicensePicFileId": null,
    //           "secondBusinessLicensePicFileId": null,
    //           "thirdBusinessLicensePicFileId": null,
    //           "directSuperiorDistributor": {
    //             "flagList": [],
    //             "id": "202509051422480002070000",
    //             "createTimestamp": 1757082168000,
    //             "updateTimestamp": 1757082168000,
    //             "type": "DISTRIBUTOR",
    //             "typeDescription": "分销商",
    //             "level": 1,
    //             "directSuperiorDistributorId": null,
    //             "fullName": "GTQZ SDN BHD",
    //             "approvalStatus": "APPROVED",
    //             "approvalStatusDescription": "已审核",
    //             "districtId": "202509091652252096240000",
    //             "address": "30, Jalan Segambut Pusat, Segambut, 51200 Kuala Lumpur",
    //             "longitude": 121.423027,
    //             "latitude": 31.208457,
    //             "businessLicenseType": "BUSINESS_REGISTRATION_CERTIFICATE",
    //             "businessLicenseTypeDescription": "商业登记证",
    //             "businessLicenseNumber": "********-***-**-**-*",
    //             "firstBusinessLicensePicFileId": null,
    //             "secondBusinessLicensePicFileId": null,
    //             "thirdBusinessLicensePicFileId": null,
    //             "directSuperiorDistributor": null,
    //             "district": {
    //               "flagList": [],
    //               "id": "202509091652252096240000",
    //               "createTimestamp": 1757436745000,
    //               "updateTimestamp": 1757436745000,
    //               "cityId": "202509091652251526240000",
    //               "regionalismCode": null,
    //               "localizedFullName": "KualaLumpur",
    //               "fullName": "KualaLumpur",
    //               "fullNameZhCn": "KualaLumpur",
    //               "city": {
    //                 "flagList": [],
    //                 "id": "202509091652251526240000",
    //                 "createTimestamp": 1757436745000,
    //                 "updateTimestamp": 1757436745000,
    //                 "provinceId": "202509091652250946240000",
    //                 "regionalismCode": null,
    //                 "localizedFullName": "KualaLumpur",
    //                 "fullName": "KualaLumpur",
    //                 "fullNameZhCn": "KualaLumpur",
    //                 "province": {
    //                   "flagList": [],
    //                   "id": "202509091652250946240000",
    //                   "createTimestamp": 1757436745000,
    //                   "updateTimestamp": 1757436745000,
    //                   "countryId": "202509091652211346240000",
    //                   "regionalismCode": null,
    //                   "localizedFullName": "KualaLumpur",
    //                   "fullName": "KualaLumpur",
    //                   "fullNameZhCn": "KualaLumpur",
    //                   "country": {
    //                     "flagList": [],
    //                     "id": "202509091652211346240000",
    //                     "createTimestamp": 1757436741000,
    //                     "updateTimestamp": 1757436741000,
    //                     "localizedFullName": "Malaysia",
    //                     "fullName": "Malaysia",
    //                     "fullNameZhCn": "Malaysia"
    //                   }
    //                 }
    //               }
    //             },
    //             "firstBusinessLicensePicFile": null,
    //             "secondBusinessLicensePicFile": null,
    //             "thirdBusinessLicensePicFile": null,
    //             "userPartyType": "DISTRIBUTOR",
    //             "userPartyTypeDescription": "分销商",
    //             "merchantPartyType": "DISTRIBUTOR",
    //             "merchantPartyTypeDescription": "分销商",
    //             "businessPartyTypeDescription": "分销商",
    //             "businessPartyType": "DISTRIBUTOR",
    //             "warrantyConfigRefTargetTypeDescription": "分销商",
    //             "warrantyConfigRefTargetType": "DISTRIBUTOR",
    //             "warehouseOwningPartyTypeDescription": "分销商",
    //             "warehouseOwningPartyType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceTypeDescription": "分销商",
    //             "warehouseInventoryOutboundTargetType": "DISTRIBUTOR",
    //             "warehouseInventoryOutboundTargetTypeDescription": "分销商",
    //             "productRefTargetTypeDescription": "分销商",
    //             "productRefTargetType": "DISTRIBUTOR",
    //             "sellerPartyTypeDescription": "分销商",
    //             "sellerPartyType": "DISTRIBUTOR",
    //             "buyerPartyTypeDescription": "分销商",
    //             "buyerPartyType": "DISTRIBUTOR"
    //           },
    //           "district": {
    //             "flagList": [],
    //             "id": "202509091652388706240000",
    //             "createTimestamp": 1757436759000,
    //             "updateTimestamp": 1757436759000,
    //             "cityId": "202509091652388106240000",
    //             "regionalismCode": null,
    //             "localizedFullName": "Petaling",
    //             "fullName": "Petaling",
    //             "fullNameZhCn": "Petaling",
    //             "city": {
    //               "flagList": [],
    //               "id": "202509091652388106240000",
    //               "createTimestamp": 1757436759000,
    //               "updateTimestamp": 1757436759000,
    //               "provinceId": "202509091652380606240000",
    //               "regionalismCode": null,
    //               "localizedFullName": "Petaling",
    //               "fullName": "Petaling",
    //               "fullNameZhCn": "Petaling",
    //               "province": {
    //                 "flagList": [],
    //                 "id": "202509091652380606240000",
    //                 "createTimestamp": 1757436758000,
    //                 "updateTimestamp": 1757436758000,
    //                 "countryId": "202509091652211346240000",
    //                 "regionalismCode": null,
    //                 "localizedFullName": "Selangor",
    //                 "fullName": "Selangor",
    //                 "fullNameZhCn": "Selangor",
    //                 "country": {
    //                   "flagList": [],
    //                   "id": "202509091652211346240000",
    //                   "createTimestamp": 1757436741000,
    //                   "updateTimestamp": 1757436741000,
    //                   "localizedFullName": "Malaysia",
    //                   "fullName": "Malaysia",
    //                   "fullNameZhCn": "Malaysia"
    //                 }
    //               }
    //             }
    //           },
    //           "firstBusinessLicensePicFile": null,
    //           "secondBusinessLicensePicFile": null,
    //           "thirdBusinessLicensePicFile": null,
    //           "userPartyType": "RETAILER",
    //           "userPartyTypeDescription": "零售商",
    //           "merchantPartyType": "RETAILER",
    //           "merchantPartyTypeDescription": "零售商",
    //           "businessPartyTypeDescription": "零售商",
    //           "businessPartyType": "RETAILER",
    //           "warrantyConfigRefTargetTypeDescription": "零售商",
    //           "warrantyConfigRefTargetType": "RETAILER",
    //           "warehouseOwningPartyTypeDescription": "零售商",
    //           "warehouseOwningPartyType": "RETAILER",
    //           "warehouseInventoryOutboundTargetType": "RETAILER",
    //           "warehouseInventoryOutboundTargetTypeDescription": "零售商",
    //           "productRefTargetTypeDescription": "零售商",
    //           "productRefTargetType": "RETAILER",
    //           "serviceStoreOwningPartyType": "RETAILER",
    //           "serviceStoreOwningPartyTypeDescription": "零售商",
    //           "serviceRefTargetTypeDescription": "零售商",
    //           "serviceRefTargetType": "RETAILER",
    //           "sellerPartyTypeDescription": "零售商",
    //           "sellerPartyType": "RETAILER",
    //           "buyerPartyTypeDescription": "零售商",
    //           "buyerPartyType": "RETAILER"
    //         },
    //         {
    //           "flagList": [],
    //           "id": "202510220742372816830000",
    //           "createTimestamp": 1761118957000,
    //           "updateTimestamp": 1761119296000,
    //           "directSuperiorDistributorId": "202509051422480002070000",
    //           "fullName": "Deuts Auto Service Sdn Bhd",
    //           "approvalStatus": "APPROVED",
    //           "approvalStatusDescription": "已审核",
    //           "districtId": "202509091652388706240000",
    //           "address": "22, Jalan Penyelenggara U1/77, Taman Perindustrian Batu Tiga, Hicom-glenmarie Industrial Park, 40150 Shah Alam, Selangor",
    //           "longitude": 101.5557298,
    //           "latitude": 3.0814173,
    //           "businessLicenseType": "BUSINESS_REGISTRATION_CERTIFICATE",
    //           "businessLicenseTypeDescription": "商业登记证",
    //           "businessLicenseNumber": "202201018598",
    //           "firstBusinessLicensePicFileId": null,
    //           "secondBusinessLicensePicFileId": null,
    //           "thirdBusinessLicensePicFileId": null,
    //           "directSuperiorDistributor": {
    //             "flagList": [],
    //             "id": "202509051422480002070000",
    //             "createTimestamp": 1757082168000,
    //             "updateTimestamp": 1757082168000,
    //             "type": "DISTRIBUTOR",
    //             "typeDescription": "分销商",
    //             "level": 1,
    //             "directSuperiorDistributorId": null,
    //             "fullName": "GTQZ SDN BHD",
    //             "approvalStatus": "APPROVED",
    //             "approvalStatusDescription": "已审核",
    //             "districtId": "202509091652252096240000",
    //             "address": "30, Jalan Segambut Pusat, Segambut, 51200 Kuala Lumpur",
    //             "longitude": 121.423027,
    //             "latitude": 31.208457,
    //             "businessLicenseType": "BUSINESS_REGISTRATION_CERTIFICATE",
    //             "businessLicenseTypeDescription": "商业登记证",
    //             "businessLicenseNumber": "********-***-**-**-*",
    //             "firstBusinessLicensePicFileId": null,
    //             "secondBusinessLicensePicFileId": null,
    //             "thirdBusinessLicensePicFileId": null,
    //             "directSuperiorDistributor": null,
    //             "district": {
    //               "flagList": [],
    //               "id": "202509091652252096240000",
    //               "createTimestamp": 1757436745000,
    //               "updateTimestamp": 1757436745000,
    //               "cityId": "202509091652251526240000",
    //               "regionalismCode": null,
    //               "localizedFullName": "KualaLumpur",
    //               "fullName": "KualaLumpur",
    //               "fullNameZhCn": "KualaLumpur",
    //               "city": {
    //                 "flagList": [],
    //                 "id": "202509091652251526240000",
    //                 "createTimestamp": 1757436745000,
    //                 "updateTimestamp": 1757436745000,
    //                 "provinceId": "202509091652250946240000",
    //                 "regionalismCode": null,
    //                 "localizedFullName": "KualaLumpur",
    //                 "fullName": "KualaLumpur",
    //                 "fullNameZhCn": "KualaLumpur",
    //                 "province": {
    //                   "flagList": [],
    //                   "id": "202509091652250946240000",
    //                   "createTimestamp": 1757436745000,
    //                   "updateTimestamp": 1757436745000,
    //                   "countryId": "202509091652211346240000",
    //                   "regionalismCode": null,
    //                   "localizedFullName": "KualaLumpur",
    //                   "fullName": "KualaLumpur",
    //                   "fullNameZhCn": "KualaLumpur",
    //                   "country": {
    //                     "flagList": [],
    //                     "id": "202509091652211346240000",
    //                     "createTimestamp": 1757436741000,
    //                     "updateTimestamp": 1757436741000,
    //                     "localizedFullName": "Malaysia",
    //                     "fullName": "Malaysia",
    //                     "fullNameZhCn": "Malaysia"
    //                   }
    //                 }
    //               }
    //             },
    //             "firstBusinessLicensePicFile": null,
    //             "secondBusinessLicensePicFile": null,
    //             "thirdBusinessLicensePicFile": null,
    //             "userPartyType": "DISTRIBUTOR",
    //             "userPartyTypeDescription": "分销商",
    //             "merchantPartyType": "DISTRIBUTOR",
    //             "merchantPartyTypeDescription": "分销商",
    //             "businessPartyTypeDescription": "分销商",
    //             "businessPartyType": "DISTRIBUTOR",
    //             "warrantyConfigRefTargetTypeDescription": "分销商",
    //             "warrantyConfigRefTargetType": "DISTRIBUTOR",
    //             "warehouseOwningPartyTypeDescription": "分销商",
    //             "warehouseOwningPartyType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceTypeDescription": "分销商",
    //             "warehouseInventoryOutboundTargetType": "DISTRIBUTOR",
    //             "warehouseInventoryOutboundTargetTypeDescription": "分销商",
    //             "productRefTargetTypeDescription": "分销商",
    //             "productRefTargetType": "DISTRIBUTOR",
    //             "sellerPartyTypeDescription": "分销商",
    //             "sellerPartyType": "DISTRIBUTOR",
    //             "buyerPartyTypeDescription": "分销商",
    //             "buyerPartyType": "DISTRIBUTOR"
    //           },
    //           "district": {
    //             "flagList": [],
    //             "id": "202509091652388706240000",
    //             "createTimestamp": 1757436759000,
    //             "updateTimestamp": 1757436759000,
    //             "cityId": "202509091652388106240000",
    //             "regionalismCode": null,
    //             "localizedFullName": "Petaling",
    //             "fullName": "Petaling",
    //             "fullNameZhCn": "Petaling",
    //             "city": {
    //               "flagList": [],
    //               "id": "202509091652388106240000",
    //               "createTimestamp": 1757436759000,
    //               "updateTimestamp": 1757436759000,
    //               "provinceId": "202509091652380606240000",
    //               "regionalismCode": null,
    //               "localizedFullName": "Petaling",
    //               "fullName": "Petaling",
    //               "fullNameZhCn": "Petaling",
    //               "province": {
    //                 "flagList": [],
    //                 "id": "202509091652380606240000",
    //                 "createTimestamp": 1757436758000,
    //                 "updateTimestamp": 1757436758000,
    //                 "countryId": "202509091652211346240000",
    //                 "regionalismCode": null,
    //                 "localizedFullName": "Selangor",
    //                 "fullName": "Selangor",
    //                 "fullNameZhCn": "Selangor",
    //                 "country": {
    //                   "flagList": [],
    //                   "id": "202509091652211346240000",
    //                   "createTimestamp": 1757436741000,
    //                   "updateTimestamp": 1757436741000,
    //                   "localizedFullName": "Malaysia",
    //                   "fullName": "Malaysia",
    //                   "fullNameZhCn": "Malaysia"
    //                 }
    //               }
    //             }
    //           },
    //           "firstBusinessLicensePicFile": null,
    //           "secondBusinessLicensePicFile": null,
    //           "thirdBusinessLicensePicFile": null,
    //           "userPartyType": "RETAILER",
    //           "userPartyTypeDescription": "零售商",
    //           "merchantPartyType": "RETAILER",
    //           "merchantPartyTypeDescription": "零售商",
    //           "businessPartyTypeDescription": "零售商",
    //           "businessPartyType": "RETAILER",
    //           "warrantyConfigRefTargetTypeDescription": "零售商",
    //           "warrantyConfigRefTargetType": "RETAILER",
    //           "warehouseOwningPartyTypeDescription": "零售商",
    //           "warehouseOwningPartyType": "RETAILER",
    //           "warehouseInventoryOutboundTargetType": "RETAILER",
    //           "warehouseInventoryOutboundTargetTypeDescription": "零售商",
    //           "productRefTargetTypeDescription": "零售商",
    //           "productRefTargetType": "RETAILER",
    //           "serviceStoreOwningPartyType": "RETAILER",
    //           "serviceStoreOwningPartyTypeDescription": "零售商",
    //           "serviceRefTargetTypeDescription": "零售商",
    //           "serviceRefTargetType": "RETAILER",
    //           "sellerPartyTypeDescription": "零售商",
    //           "sellerPartyType": "RETAILER",
    //           "buyerPartyTypeDescription": "零售商",
    //           "buyerPartyType": "RETAILER"
    //         },
    //         {
    //           "flagList": [],
    //           "id": "202510231104257106830000",
    //           "createTimestamp": 1761217466000,
    //           "updateTimestamp": 1761217635000,
    //           "directSuperiorDistributorId": "202510220430248576830000",
    //           "fullName": "上海斐骏汽车服务有限公司",
    //           "approvalStatus": "APPROVED",
    //           "approvalStatusDescription": "已审核",
    //           "districtId": "202509051418538535210000",
    //           "address": "上海市闵行区双柏路800号5幢103室",
    //           "longitude": 121.43968,
    //           "latitude": 31.09167,
    //           "businessLicenseType": "BUSINESS_LICENSE",
    //           "businessLicenseTypeDescription": "营业执照",
    //           "businessLicenseNumber": "91310120MAD8G0JB68",
    //           "firstBusinessLicensePicFileId": null,
    //           "secondBusinessLicensePicFileId": null,
    //           "thirdBusinessLicensePicFileId": null,
    //           "directSuperiorDistributor": {
    //             "flagList": [],
    //             "id": "202510220430248576830000",
    //             "createTimestamp": 1761107425000,
    //             "updateTimestamp": 1761107628000,
    //             "type": "DISTRIBUTOR",
    //             "typeDescription": "分销商",
    //             "level": 1,
    //             "directSuperiorDistributorId": null,
    //             "fullName": "上海添鼎实业有限公司",
    //             "approvalStatus": "APPROVED",
    //             "approvalStatusDescription": "已审核",
    //             "districtId": "202509051418537135210000",
    //             "address": "上海虹口区上海星荟中心星荟中心1座",
    //             "longitude": 0.0,
    //             "latitude": 0.0,
    //             "businessLicenseType": "BUSINESS_LICENSE",
    //             "businessLicenseTypeDescription": "营业执照",
    //             "businessLicenseNumber": "91310118692938883E",
    //             "firstBusinessLicensePicFileId": null,
    //             "secondBusinessLicensePicFileId": null,
    //             "thirdBusinessLicensePicFileId": null,
    //             "directSuperiorDistributor": null,
    //             "district": {
    //               "flagList": [],
    //               "id": "202509051418537135210000",
    //               "createTimestamp": 1757081934000,
    //               "updateTimestamp": 1757081934000,
    //               "cityId": "202509051418532875210000",
    //               "regionalismCode": "310109",
    //               "localizedFullName": "虹口区",
    //               "fullName": "虹口区",
    //               "fullNameZhCn": "虹口区",
    //               "city": {
    //                 "flagList": [],
    //                 "id": "202509051418532875210000",
    //                 "createTimestamp": 1757081933000,
    //                 "updateTimestamp": 1757081933000,
    //                 "provinceId": "202509051418532155210000",
    //                 "regionalismCode": "310000",
    //                 "localizedFullName": "上海市",
    //                 "fullName": "上海市",
    //                 "fullNameZhCn": "上海市",
    //                 "province": {
    //                   "flagList": [],
    //                   "id": "202509051418532155210000",
    //                   "createTimestamp": 1757081933000,
    //                   "updateTimestamp": 1757081933000,
    //                   "countryId": "202509051417566675210000",
    //                   "regionalismCode": "310000",
    //                   "localizedFullName": "上海市",
    //                   "fullName": "上海市",
    //                   "fullNameZhCn": "上海市",
    //                   "country": {
    //                     "flagList": [],
    //                     "id": "202509051417566675210000",
    //                     "createTimestamp": 1757081877000,
    //                     "updateTimestamp": 1757081877000,
    //                     "localizedFullName": "中华人民共和国",
    //                     "fullName": "中华人民共和国",
    //                     "fullNameZhCn": "中华人民共和国"
    //                   }
    //                 }
    //               }
    //             },
    //             "firstBusinessLicensePicFile": null,
    //             "secondBusinessLicensePicFile": null,
    //             "thirdBusinessLicensePicFile": null,
    //             "userPartyType": "DISTRIBUTOR",
    //             "userPartyTypeDescription": "分销商",
    //             "merchantPartyType": "DISTRIBUTOR",
    //             "merchantPartyTypeDescription": "分销商",
    //             "businessPartyTypeDescription": "分销商",
    //             "businessPartyType": "DISTRIBUTOR",
    //             "warrantyConfigRefTargetTypeDescription": "分销商",
    //             "warrantyConfigRefTargetType": "DISTRIBUTOR",
    //             "warehouseOwningPartyTypeDescription": "分销商",
    //             "warehouseOwningPartyType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceTypeDescription": "分销商",
    //             "warehouseInventoryOutboundTargetType": "DISTRIBUTOR",
    //             "warehouseInventoryOutboundTargetTypeDescription": "分销商",
    //             "productRefTargetTypeDescription": "分销商",
    //             "productRefTargetType": "DISTRIBUTOR",
    //             "sellerPartyTypeDescription": "分销商",
    //             "sellerPartyType": "DISTRIBUTOR",
    //             "buyerPartyTypeDescription": "分销商",
    //             "buyerPartyType": "DISTRIBUTOR"
    //           },
    //           "district": {
    //             "flagList": [],
    //             "id": "202509051418538535210000",
    //             "createTimestamp": 1757081934000,
    //             "updateTimestamp": 1757081934000,
    //             "cityId": "202509051418532875210000",
    //             "regionalismCode": "310112",
    //             "localizedFullName": "闵行区",
    //             "fullName": "闵行区",
    //             "fullNameZhCn": "闵行区",
    //             "city": {
    //               "flagList": [],
    //               "id": "202509051418532875210000",
    //               "createTimestamp": 1757081933000,
    //               "updateTimestamp": 1757081933000,
    //               "provinceId": "202509051418532155210000",
    //               "regionalismCode": "310000",
    //               "localizedFullName": "上海市",
    //               "fullName": "上海市",
    //               "fullNameZhCn": "上海市",
    //               "province": {
    //                 "flagList": [],
    //                 "id": "202509051418532155210000",
    //                 "createTimestamp": 1757081933000,
    //                 "updateTimestamp": 1757081933000,
    //                 "countryId": "202509051417566675210000",
    //                 "regionalismCode": "310000",
    //                 "localizedFullName": "上海市",
    //                 "fullName": "上海市",
    //                 "fullNameZhCn": "上海市",
    //                 "country": {
    //                   "flagList": [],
    //                   "id": "202509051417566675210000",
    //                   "createTimestamp": 1757081877000,
    //                   "updateTimestamp": 1757081877000,
    //                   "localizedFullName": "中华人民共和国",
    //                   "fullName": "中华人民共和国",
    //                   "fullNameZhCn": "中华人民共和国"
    //                 }
    //               }
    //             }
    //           },
    //           "firstBusinessLicensePicFile": null,
    //           "secondBusinessLicensePicFile": null,
    //           "thirdBusinessLicensePicFile": null,
    //           "userPartyType": "RETAILER",
    //           "userPartyTypeDescription": "零售商",
    //           "merchantPartyType": "RETAILER",
    //           "merchantPartyTypeDescription": "零售商",
    //           "businessPartyTypeDescription": "零售商",
    //           "businessPartyType": "RETAILER",
    //           "warrantyConfigRefTargetTypeDescription": "零售商",
    //           "warrantyConfigRefTargetType": "RETAILER",
    //           "warehouseOwningPartyTypeDescription": "零售商",
    //           "warehouseOwningPartyType": "RETAILER",
    //           "warehouseInventoryOutboundTargetType": "RETAILER",
    //           "warehouseInventoryOutboundTargetTypeDescription": "零售商",
    //           "productRefTargetTypeDescription": "零售商",
    //           "productRefTargetType": "RETAILER",
    //           "serviceStoreOwningPartyType": "RETAILER",
    //           "serviceStoreOwningPartyTypeDescription": "零售商",
    //           "serviceRefTargetTypeDescription": "零售商",
    //           "serviceRefTargetType": "RETAILER",
    //           "sellerPartyTypeDescription": "零售商",
    //           "sellerPartyType": "RETAILER",
    //           "buyerPartyTypeDescription": "零售商",
    //           "buyerPartyType": "RETAILER"
    //         },
    //         {
    //           "flagList": [],
    //           "id": "202511030755123777490000",
    //           "createTimestamp": 1762156512000,
    //           "updateTimestamp": 1762156789000,
    //           "directSuperiorDistributorId": "202511030753304617490000",
    //           "fullName": "吉悦铂雅名车养护中心",
    //           "approvalStatus": "APPROVED",
    //           "approvalStatusDescription": "已审核",
    //           "districtId": "202509051418395665210000",
    //           "address": "吉林省吉林市昌邑区维昌街花溪雅苑1号楼",
    //           "longitude": 126.585807,
    //           "latitude": 43.851814,
    //           "businessLicenseType": "BUSINESS_LICENSE",
    //           "businessLicenseTypeDescription": "营业执照",
    //           "businessLicenseNumber": "暂无",
    //           "firstBusinessLicensePicFileId": null,
    //           "secondBusinessLicensePicFileId": null,
    //           "thirdBusinessLicensePicFileId": null,
    //           "directSuperiorDistributor": {
    //             "flagList": [],
    //             "id": "202511030753304617490000",
    //             "createTimestamp": 1762156410000,
    //             "updateTimestamp": 1762156744000,
    //             "type": "DISTRIBUTOR",
    //             "typeDescription": "分销商",
    //             "level": 1,
    //             "directSuperiorDistributorId": null,
    //             "fullName": "吉悦铂雅名车（吉林省）汽车保养有限公司",
    //             "approvalStatus": "APPROVED",
    //             "approvalStatusDescription": "已审核",
    //             "districtId": "202509051418395665210000",
    //             "address": "吉林市昌邑区维昌路锦江花园1幢1-2层3号房、4号房",
    //             "longitude": 0.0,
    //             "latitude": 0.0,
    //             "businessLicenseType": "BUSINESS_LICENSE",
    //             "businessLicenseTypeDescription": "营业执照",
    //             "businessLicenseNumber": "91220202MAELNGPL16",
    //             "firstBusinessLicensePicFileId": null,
    //             "secondBusinessLicensePicFileId": null,
    //             "thirdBusinessLicensePicFileId": null,
    //             "directSuperiorDistributor": null,
    //             "district": {
    //               "flagList": [],
    //               "id": "202509051418395665210000",
    //               "createTimestamp": 1757081920000,
    //               "updateTimestamp": 1757081920000,
    //               "cityId": "202509051418394985210000",
    //               "regionalismCode": "220202",
    //               "localizedFullName": "昌邑区",
    //               "fullName": "昌邑区",
    //               "fullNameZhCn": "昌邑区",
    //               "city": {
    //                 "flagList": [],
    //                 "id": "202509051418394985210000",
    //                 "createTimestamp": 1757081919000,
    //                 "updateTimestamp": 1757081919000,
    //                 "provinceId": "202509051418385725210000",
    //                 "regionalismCode": "220200",
    //                 "localizedFullName": "吉林市",
    //                 "fullName": "吉林市",
    //                 "fullNameZhCn": "吉林市",
    //                 "province": {
    //                   "flagList": [],
    //                   "id": "202509051418385725210000",
    //                   "createTimestamp": 1757081919000,
    //                   "updateTimestamp": 1757081919000,
    //                   "countryId": "202509051417566675210000",
    //                   "regionalismCode": "220000",
    //                   "localizedFullName": "吉林省",
    //                   "fullName": "吉林省",
    //                   "fullNameZhCn": "吉林省",
    //                   "country": {
    //                     "flagList": [],
    //                     "id": "202509051417566675210000",
    //                     "createTimestamp": 1757081877000,
    //                     "updateTimestamp": 1757081877000,
    //                     "localizedFullName": "中华人民共和国",
    //                     "fullName": "中华人民共和国",
    //                     "fullNameZhCn": "中华人民共和国"
    //                   }
    //                 }
    //               }
    //             },
    //             "firstBusinessLicensePicFile": null,
    //             "secondBusinessLicensePicFile": null,
    //             "thirdBusinessLicensePicFile": null,
    //             "userPartyType": "DISTRIBUTOR",
    //             "userPartyTypeDescription": "分销商",
    //             "merchantPartyType": "DISTRIBUTOR",
    //             "merchantPartyTypeDescription": "分销商",
    //             "businessPartyTypeDescription": "分销商",
    //             "businessPartyType": "DISTRIBUTOR",
    //             "warrantyConfigRefTargetTypeDescription": "分销商",
    //             "warrantyConfigRefTargetType": "DISTRIBUTOR",
    //             "warehouseOwningPartyTypeDescription": "分销商",
    //             "warehouseOwningPartyType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceTypeDescription": "分销商",
    //             "warehouseInventoryOutboundTargetType": "DISTRIBUTOR",
    //             "warehouseInventoryOutboundTargetTypeDescription": "分销商",
    //             "productRefTargetTypeDescription": "分销商",
    //             "productRefTargetType": "DISTRIBUTOR",
    //             "sellerPartyTypeDescription": "分销商",
    //             "sellerPartyType": "DISTRIBUTOR",
    //             "buyerPartyTypeDescription": "分销商",
    //             "buyerPartyType": "DISTRIBUTOR"
    //           },
    //           "district": {
    //             "flagList": [],
    //             "id": "202509051418395665210000",
    //             "createTimestamp": 1757081920000,
    //             "updateTimestamp": 1757081920000,
    //             "cityId": "202509051418394985210000",
    //             "regionalismCode": "220202",
    //             "localizedFullName": "昌邑区",
    //             "fullName": "昌邑区",
    //             "fullNameZhCn": "昌邑区",
    //             "city": {
    //               "flagList": [],
    //               "id": "202509051418394985210000",
    //               "createTimestamp": 1757081919000,
    //               "updateTimestamp": 1757081919000,
    //               "provinceId": "202509051418385725210000",
    //               "regionalismCode": "220200",
    //               "localizedFullName": "吉林市",
    //               "fullName": "吉林市",
    //               "fullNameZhCn": "吉林市",
    //               "province": {
    //                 "flagList": [],
    //                 "id": "202509051418385725210000",
    //                 "createTimestamp": 1757081919000,
    //                 "updateTimestamp": 1757081919000,
    //                 "countryId": "202509051417566675210000",
    //                 "regionalismCode": "220000",
    //                 "localizedFullName": "吉林省",
    //                 "fullName": "吉林省",
    //                 "fullNameZhCn": "吉林省",
    //                 "country": {
    //                   "flagList": [],
    //                   "id": "202509051417566675210000",
    //                   "createTimestamp": 1757081877000,
    //                   "updateTimestamp": 1757081877000,
    //                   "localizedFullName": "中华人民共和国",
    //                   "fullName": "中华人民共和国",
    //                   "fullNameZhCn": "中华人民共和国"
    //                 }
    //               }
    //             }
    //           },
    //           "firstBusinessLicensePicFile": null,
    //           "secondBusinessLicensePicFile": null,
    //           "thirdBusinessLicensePicFile": null,
    //           "userPartyType": "RETAILER",
    //           "userPartyTypeDescription": "零售商",
    //           "merchantPartyType": "RETAILER",
    //           "merchantPartyTypeDescription": "零售商",
    //           "businessPartyTypeDescription": "零售商",
    //           "businessPartyType": "RETAILER",
    //           "warrantyConfigRefTargetTypeDescription": "零售商",
    //           "warrantyConfigRefTargetType": "RETAILER",
    //           "warehouseOwningPartyTypeDescription": "零售商",
    //           "warehouseOwningPartyType": "RETAILER",
    //           "warehouseInventoryOutboundTargetType": "RETAILER",
    //           "warehouseInventoryOutboundTargetTypeDescription": "零售商",
    //           "productRefTargetTypeDescription": "零售商",
    //           "productRefTargetType": "RETAILER",
    //           "serviceStoreOwningPartyType": "RETAILER",
    //           "serviceStoreOwningPartyTypeDescription": "零售商",
    //           "serviceRefTargetTypeDescription": "零售商",
    //           "serviceRefTargetType": "RETAILER",
    //           "sellerPartyTypeDescription": "零售商",
    //           "sellerPartyType": "RETAILER",
    //           "buyerPartyTypeDescription": "零售商",
    //           "buyerPartyType": "RETAILER"
    //         },
    //         {
    //           "flagList": [],
    //           "id": "202602040859527010520000",
    //           "createTimestamp": 1770195593000,
    //           "updateTimestamp": 1770195845000,
    //           "directSuperiorDistributorId": "202602040857330100520000",
    //           "fullName": "义乌唐龙汽车用品公司",
    //           "approvalStatus": "APPROVED",
    //           "approvalStatusDescription": "已审核",
    //           "districtId": "202509051419070425210000",
    //           "address": "稠城街道大都公寓1幢2单元502室",
    //           "longitude": 120.076375,
    //           "latitude": 29.318824,
    //           "businessLicenseType": "BUSINESS_LICENSE",
    //           "businessLicenseTypeDescription": "营业执照",
    //           "businessLicenseNumber": "91330782058301658U",
    //           "firstBusinessLicensePicFileId": "202602040859301710520000",
    //           "secondBusinessLicensePicFileId": null,
    //           "thirdBusinessLicensePicFileId": null,
    //           "directSuperiorDistributor": {
    //             "flagList": [],
    //             "id": "202602040857330100520000",
    //             "createTimestamp": 1770195453000,
    //             "updateTimestamp": 1770195845000,
    //             "type": "DISTRIBUTOR",
    //             "typeDescription": "分销商",
    //             "level": 1,
    //             "directSuperiorDistributorId": null,
    //             "fullName": "义乌市唐龙汽车用品有限公司",
    //             "approvalStatus": "APPROVED",
    //             "approvalStatusDescription": "已审核",
    //             "districtId": "202509051419070425210000",
    //             "address": "稠城街道大都公寓1幢2单元502室",
    //             "longitude": 0.0,
    //             "latitude": 0.0,
    //             "businessLicenseType": "BUSINESS_LICENSE",
    //             "businessLicenseTypeDescription": "营业执照",
    //             "businessLicenseNumber": "91330782058301658U",
    //             "firstBusinessLicensePicFileId": "202602040857091350520000",
    //             "secondBusinessLicensePicFileId": null,
    //             "thirdBusinessLicensePicFileId": null,
    //             "directSuperiorDistributor": null,
    //             "district": {
    //               "flagList": [],
    //               "id": "202509051419070425210000",
    //               "createTimestamp": 1757081947000,
    //               "updateTimestamp": 1757081947000,
    //               "cityId": "202509051419065465210000",
    //               "regionalismCode": "330782",
    //               "localizedFullName": "义乌市",
    //               "fullName": "义乌市",
    //               "fullNameZhCn": "义乌市",
    //               "city": {
    //                 "flagList": [],
    //                 "id": "202509051419065465210000",
    //                 "createTimestamp": 1757081947000,
    //                 "updateTimestamp": 1757081947000,
    //                 "provinceId": "202509051419022725210000",
    //                 "regionalismCode": "330700",
    //                 "localizedFullName": "金华市",
    //                 "fullName": "金华市",
    //                 "fullNameZhCn": "金华市",
    //                 "province": {
    //                   "flagList": [],
    //                   "id": "202509051419022725210000",
    //                   "createTimestamp": 1757081942000,
    //                   "updateTimestamp": 1757081942000,
    //                   "countryId": "202509051417566675210000",
    //                   "regionalismCode": "330000",
    //                   "localizedFullName": "浙江省",
    //                   "fullName": "浙江省",
    //                   "fullNameZhCn": "浙江省",
    //                   "country": {
    //                     "flagList": [],
    //                     "id": "202509051417566675210000",
    //                     "createTimestamp": 1757081877000,
    //                     "updateTimestamp": 1757081877000,
    //                     "localizedFullName": "中华人民共和国",
    //                     "fullName": "中华人民共和国",
    //                     "fullNameZhCn": "中华人民共和国"
    //                   }
    //                 }
    //               }
    //             },
    //             "firstBusinessLicensePicFile": {
    //               "flagList": [],
    //               "id": "202602040857091350520000",
    //               "createTimestamp": 1770195429000,
    //               "updateTimestamp": 1770195429000,
    //               "format": "JPEG",
    //               "formatDescription": "JPEG",
    //               "width": 1706,
    //               "height": 1279,
    //               "ossBucket": "goldenkiwiprod",
    //               "ossPath": "nbiz/distributor/2026/02/04/08/1706_1279_df13678408e76e712cf9eddf5eb765fb.jpeg",
    //               "url": "https://img.youbanban.com/oss/download/nbiz/distributor/2026/02/04/08/1706_1279_df13678408e76e712cf9eddf5eb765fb.jpeg",
    //               "fileTypeDescription": "图片",
    //               "fileType": "PIC"
    //             },
    //             "secondBusinessLicensePicFile": null,
    //             "thirdBusinessLicensePicFile": null,
    //             "userPartyType": "DISTRIBUTOR",
    //             "userPartyTypeDescription": "分销商",
    //             "merchantPartyType": "DISTRIBUTOR",
    //             "merchantPartyTypeDescription": "分销商",
    //             "businessPartyTypeDescription": "分销商",
    //             "businessPartyType": "DISTRIBUTOR",
    //             "warrantyConfigRefTargetTypeDescription": "分销商",
    //             "warrantyConfigRefTargetType": "DISTRIBUTOR",
    //             "warehouseOwningPartyTypeDescription": "分销商",
    //             "warehouseOwningPartyType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceTypeDescription": "分销商",
    //             "warehouseInventoryOutboundTargetType": "DISTRIBUTOR",
    //             "warehouseInventoryOutboundTargetTypeDescription": "分销商",
    //             "productRefTargetTypeDescription": "分销商",
    //             "productRefTargetType": "DISTRIBUTOR",
    //             "sellerPartyTypeDescription": "分销商",
    //             "sellerPartyType": "DISTRIBUTOR",
    //             "buyerPartyTypeDescription": "分销商",
    //             "buyerPartyType": "DISTRIBUTOR"
    //           },
    //           "district": {
    //             "flagList": [],
    //             "id": "202509051419070425210000",
    //             "createTimestamp": 1757081947000,
    //             "updateTimestamp": 1757081947000,
    //             "cityId": "202509051419065465210000",
    //             "regionalismCode": "330782",
    //             "localizedFullName": "义乌市",
    //             "fullName": "义乌市",
    //             "fullNameZhCn": "义乌市",
    //             "city": {
    //               "flagList": [],
    //               "id": "202509051419065465210000",
    //               "createTimestamp": 1757081947000,
    //               "updateTimestamp": 1757081947000,
    //               "provinceId": "202509051419022725210000",
    //               "regionalismCode": "330700",
    //               "localizedFullName": "金华市",
    //               "fullName": "金华市",
    //               "fullNameZhCn": "金华市",
    //               "province": {
    //                 "flagList": [],
    //                 "id": "202509051419022725210000",
    //                 "createTimestamp": 1757081942000,
    //                 "updateTimestamp": 1757081942000,
    //                 "countryId": "202509051417566675210000",
    //                 "regionalismCode": "330000",
    //                 "localizedFullName": "浙江省",
    //                 "fullName": "浙江省",
    //                 "fullNameZhCn": "浙江省",
    //                 "country": {
    //                   "flagList": [],
    //                   "id": "202509051417566675210000",
    //                   "createTimestamp": 1757081877000,
    //                   "updateTimestamp": 1757081877000,
    //                   "localizedFullName": "中华人民共和国",
    //                   "fullName": "中华人民共和国",
    //                   "fullNameZhCn": "中华人民共和国"
    //                 }
    //               }
    //             }
    //           },
    //           "firstBusinessLicensePicFile": {
    //             "flagList": [],
    //             "id": "202602040859301710520000",
    //             "createTimestamp": 1770195570000,
    //             "updateTimestamp": 1770195570000,
    //             "format": "JPEG",
    //             "formatDescription": "JPEG",
    //             "width": 1706,
    //             "height": 1279,
    //             "ossBucket": "goldenkiwiprod",
    //             "ossPath": "nbiz/retailer/2026/02/04/08/1706_1279_df13678408e76e712cf9eddf5eb765fb.jpeg",
    //             "url": "https://img.youbanban.com/oss/download/nbiz/retailer/2026/02/04/08/1706_1279_df13678408e76e712cf9eddf5eb765fb.jpeg",
    //             "fileTypeDescription": "图片",
    //             "fileType": "PIC"
    //           },
    //           "secondBusinessLicensePicFile": null,
    //           "thirdBusinessLicensePicFile": null,
    //           "userPartyType": "RETAILER",
    //           "userPartyTypeDescription": "零售商",
    //           "merchantPartyType": "RETAILER",
    //           "merchantPartyTypeDescription": "零售商",
    //           "businessPartyTypeDescription": "零售商",
    //           "businessPartyType": "RETAILER",
    //           "warrantyConfigRefTargetTypeDescription": "零售商",
    //           "warrantyConfigRefTargetType": "RETAILER",
    //           "warehouseOwningPartyTypeDescription": "零售商",
    //           "warehouseOwningPartyType": "RETAILER",
    //           "warehouseInventoryOutboundTargetType": "RETAILER",
    //           "warehouseInventoryOutboundTargetTypeDescription": "零售商",
    //           "productRefTargetTypeDescription": "零售商",
    //           "productRefTargetType": "RETAILER",
    //           "serviceStoreOwningPartyType": "RETAILER",
    //           "serviceStoreOwningPartyTypeDescription": "零售商",
    //           "serviceRefTargetTypeDescription": "零售商",
    //           "serviceRefTargetType": "RETAILER",
    //           "sellerPartyTypeDescription": "零售商",
    //           "sellerPartyType": "RETAILER",
    //           "buyerPartyTypeDescription": "零售商",
    //           "buyerPartyType": "RETAILER"
    //         },
    //         {
    //           "flagList": [],
    //           "id": "202603040614588690520000",
    //           "createTimestamp": 1772604899000,
    //           "updateTimestamp": 1773664968000,
    //           "directSuperiorDistributorId": "202509051422443662070000",
    //           "fullName": "嘉实多汽车贴膜京东自营旗舰店",
    //           "approvalStatus": "APPROVED",
    //           "approvalStatusDescription": "已审核",
    //           "districtId": "202509051418534975210000",
    //           "address": "延安西路1448弄3号",
    //           "longitude": 121.418492,
    //           "latitude": 31.210415,
    //           // "longitude": 121.41819,
    //           // "latitude": 31.2104,
    //           "businessLicenseType": "BUSINESS_LICENSE",
    //           "businessLicenseTypeDescription": "营业执照",
    //           "businessLicenseNumber": "91310118560192303A",
    //           "firstBusinessLicensePicFileId": null,
    //           "secondBusinessLicensePicFileId": null,
    //           "thirdBusinessLicensePicFileId": null,
    //           "directSuperiorDistributor": {
    //             "flagList": [],
    //             "id": "202509051422443662070000",
    //             "createTimestamp": 1757082164000,
    //             "updateTimestamp": 1757082165000,
    //             "type": "DISTRIBUTOR",
    //             "typeDescription": "分销商",
    //             "level": 1,
    //             "directSuperiorDistributorId": null,
    //             "fullName": "上海广台乔子实业发展有限公司（电商）",
    //             "approvalStatus": "APPROVED",
    //             "approvalStatusDescription": "已审核",
    //             "districtId": "202509051418534975210000",
    //             "address": "延安西路1448弄3号",
    //             "longitude": 121.418492,
    //             "latitude": 31.210415,
    //             // "longitude": 121.423027,
    //             // "latitude": 31.208457,
    //             "businessLicenseType": "BUSINESS_LICENSE",
    //             "businessLicenseTypeDescription": "营业执照",
    //             "businessLicenseNumber": "91310118560192303A",
    //             "firstBusinessLicensePicFileId": null,
    //             "secondBusinessLicensePicFileId": null,
    //             "thirdBusinessLicensePicFileId": null,
    //             "directSuperiorDistributor": null,
    //             "district": {
    //               "flagList": [],
    //               "id": "202509051418534975210000",
    //               "createTimestamp": 1757081933000,
    //               "updateTimestamp": 1757081933000,
    //               "cityId": "202509051418532875210000",
    //               "regionalismCode": "310105",
    //               "localizedFullName": "长宁区",
    //               "fullName": "长宁区",
    //               "fullNameZhCn": "长宁区",
    //               "city": {
    //                 "flagList": [],
    //                 "id": "202509051418532875210000",
    //                 "createTimestamp": 1757081933000,
    //                 "updateTimestamp": 1757081933000,
    //                 "provinceId": "202509051418532155210000",
    //                 "regionalismCode": "310000",
    //                 "localizedFullName": "上海市",
    //                 "fullName": "上海市",
    //                 "fullNameZhCn": "上海市",
    //                 "province": {
    //                   "flagList": [],
    //                   "id": "202509051418532155210000",
    //                   "createTimestamp": 1757081933000,
    //                   "updateTimestamp": 1757081933000,
    //                   "countryId": "202509051417566675210000",
    //                   "regionalismCode": "310000",
    //                   "localizedFullName": "上海市",
    //                   "fullName": "上海市",
    //                   "fullNameZhCn": "上海市",
    //                   "country": {
    //                     "flagList": [],
    //                     "id": "202509051417566675210000",
    //                     "createTimestamp": 1757081877000,
    //                     "updateTimestamp": 1757081877000,
    //                     "localizedFullName": "中华人民共和国",
    //                     "fullName": "中华人民共和国",
    //                     "fullNameZhCn": "中华人民共和国"
    //                   }
    //                 }
    //               }
    //             },
    //             "firstBusinessLicensePicFile": null,
    //             "secondBusinessLicensePicFile": null,
    //             "thirdBusinessLicensePicFile": null,
    //             "userPartyType": "DISTRIBUTOR",
    //             "userPartyTypeDescription": "分销商",
    //             "merchantPartyType": "DISTRIBUTOR",
    //             "merchantPartyTypeDescription": "分销商",
    //             "businessPartyTypeDescription": "分销商",
    //             "businessPartyType": "DISTRIBUTOR",
    //             "warrantyConfigRefTargetTypeDescription": "分销商",
    //             "warrantyConfigRefTargetType": "DISTRIBUTOR",
    //             "warehouseOwningPartyTypeDescription": "分销商",
    //             "warehouseOwningPartyType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceTypeDescription": "分销商",
    //             "warehouseInventoryOutboundTargetType": "DISTRIBUTOR",
    //             "warehouseInventoryOutboundTargetTypeDescription": "分销商",
    //             "productRefTargetTypeDescription": "分销商",
    //             "productRefTargetType": "DISTRIBUTOR",
    //             "sellerPartyTypeDescription": "分销商",
    //             "sellerPartyType": "DISTRIBUTOR",
    //             "buyerPartyTypeDescription": "分销商",
    //             "buyerPartyType": "DISTRIBUTOR"
    //           },
    //           "district": {
    //             "flagList": [],
    //             "id": "202509051418534975210000",
    //             "createTimestamp": 1757081933000,
    //             "updateTimestamp": 1757081933000,
    //             "cityId": "202509051418532875210000",
    //             "regionalismCode": "310105",
    //             "localizedFullName": "长宁区",
    //             "fullName": "长宁区",
    //             "fullNameZhCn": "长宁区",
    //             "city": {
    //               "flagList": [],
    //               "id": "202509051418532875210000",
    //               "createTimestamp": 1757081933000,
    //               "updateTimestamp": 1757081933000,
    //               "provinceId": "202509051418532155210000",
    //               "regionalismCode": "310000",
    //               "localizedFullName": "上海市",
    //               "fullName": "上海市",
    //               "fullNameZhCn": "上海市",
    //               "province": {
    //                 "flagList": [],
    //                 "id": "202509051418532155210000",
    //                 "createTimestamp": 1757081933000,
    //                 "updateTimestamp": 1757081933000,
    //                 "countryId": "202509051417566675210000",
    //                 "regionalismCode": "310000",
    //                 "localizedFullName": "上海市",
    //                 "fullName": "上海市",
    //                 "fullNameZhCn": "上海市",
    //                 "country": {
    //                   "flagList": [],
    //                   "id": "202509051417566675210000",
    //                   "createTimestamp": 1757081877000,
    //                   "updateTimestamp": 1757081877000,
    //                   "localizedFullName": "中华人民共和国",
    //                   "fullName": "中华人民共和国",
    //                   "fullNameZhCn": "中华人民共和国"
    //                 }
    //               }
    //             }
    //           },
    //           "firstBusinessLicensePicFile": null,
    //           "secondBusinessLicensePicFile": null,
    //           "thirdBusinessLicensePicFile": null,
    //           "userPartyType": "RETAILER",
    //           "userPartyTypeDescription": "零售商",
    //           "merchantPartyType": "RETAILER",
    //           "merchantPartyTypeDescription": "零售商",
    //           "businessPartyTypeDescription": "零售商",
    //           "businessPartyType": "RETAILER",
    //           "warrantyConfigRefTargetTypeDescription": "零售商",
    //           "warrantyConfigRefTargetType": "RETAILER",
    //           "warehouseOwningPartyTypeDescription": "零售商",
    //           "warehouseOwningPartyType": "RETAILER",
    //           "warehouseInventoryOutboundTargetType": "RETAILER",
    //           "warehouseInventoryOutboundTargetTypeDescription": "零售商",
    //           "productRefTargetTypeDescription": "零售商",
    //           "productRefTargetType": "RETAILER",
    //           "serviceStoreOwningPartyType": "RETAILER",
    //           "serviceStoreOwningPartyTypeDescription": "零售商",
    //           "serviceRefTargetTypeDescription": "零售商",
    //           "serviceRefTargetType": "RETAILER",
    //           "sellerPartyTypeDescription": "零售商",
    //           "sellerPartyType": "RETAILER",
    //           "buyerPartyTypeDescription": "零售商",
    //           "buyerPartyType": "RETAILER"
    //         },
    //         {
    //           "flagList": [],
    //           "id": "202603161232144536220000",
    //           "createTimestamp": 1773664334000,
    //           "updateTimestamp": 1773664968000,
    //           "directSuperiorDistributorId": "202509230730592586830000",
    //           "fullName": "昆明伍尔特汽车服务有限公司",
    //           "approvalStatus": "APPROVED",
    //           "approvalStatusDescription": "已审核",
    //           "districtId": "202509051421033585210000",
    //           "address": "富城车世界21栋155-162号",
    //           "longitude": 102.688,
    //           "latitude": 25.00381,
    //           "businessLicenseType": "BUSINESS_LICENSE",
    //           "businessLicenseTypeDescription": "营业执照",
    //           "businessLicenseNumber": "****",
    //           "firstBusinessLicensePicFileId": null,
    //           "secondBusinessLicensePicFileId": null,
    //           "thirdBusinessLicensePicFileId": null,
    //           "directSuperiorDistributor": {
    //             "flagList": [],
    //             "id": "202509230730592586830000",
    //             "createTimestamp": 1758612659000,
    //             "updateTimestamp": 1758612694000,
    //             "type": "DISTRIBUTOR",
    //             "typeDescription": "分销商",
    //             "level": 1,
    //             "directSuperiorDistributorId": null,
    //             "fullName": "驰捷车多管（云南）企业管理有限公司",
    //             "approvalStatus": "APPROVED",
    //             "approvalStatusDescription": "已审核",
    //             "districtId": "202509051421033585210000",
    //             "address": "云南省昆明市西山区西坝路639号",
    //             "longitude": 0.0,
    //             "latitude": 0.0,
    //             "businessLicenseType": "BUSINESS_LICENSE",
    //             "businessLicenseTypeDescription": "营业执照",
    //             "businessLicenseNumber": "91310118560192303A",
    //             "firstBusinessLicensePicFileId": null,
    //             "secondBusinessLicensePicFileId": null,
    //             "thirdBusinessLicensePicFileId": null,
    //             "directSuperiorDistributor": null,
    //             "district": {
    //               "flagList": [],
    //               "id": "202509051421033585210000",
    //               "createTimestamp": 1757082063000,
    //               "updateTimestamp": 1757082063000,
    //               "cityId": "202509051421030835210000",
    //               "regionalismCode": "530112",
    //               "localizedFullName": "西山区",
    //               "fullName": "西山区",
    //               "fullNameZhCn": "西山区",
    //               "city": {
    //                 "flagList": [],
    //                 "id": "202509051421030835210000",
    //                 "createTimestamp": 1757082063000,
    //                 "updateTimestamp": 1757082063000,
    //                 "provinceId": "202509051421030075210000",
    //                 "regionalismCode": "530100",
    //                 "localizedFullName": "昆明市",
    //                 "fullName": "昆明市",
    //                 "fullNameZhCn": "昆明市",
    //                 "province": {
    //                   "flagList": [],
    //                   "id": "202509051421030075210000",
    //                   "createTimestamp": 1757082063000,
    //                   "updateTimestamp": 1757082063000,
    //                   "countryId": "202509051417566675210000",
    //                   "regionalismCode": "530000",
    //                   "localizedFullName": "云南省",
    //                   "fullName": "云南省",
    //                   "fullNameZhCn": "云南省",
    //                   "country": {
    //                     "flagList": [],
    //                     "id": "202509051417566675210000",
    //                     "createTimestamp": 1757081877000,
    //                     "updateTimestamp": 1757081877000,
    //                     "localizedFullName": "中华人民共和国",
    //                     "fullName": "中华人民共和国",
    //                     "fullNameZhCn": "中华人民共和国"
    //                   }
    //                 }
    //               }
    //             },
    //             "firstBusinessLicensePicFile": null,
    //             "secondBusinessLicensePicFile": null,
    //             "thirdBusinessLicensePicFile": null,
    //             "userPartyType": "DISTRIBUTOR",
    //             "userPartyTypeDescription": "分销商",
    //             "merchantPartyType": "DISTRIBUTOR",
    //             "merchantPartyTypeDescription": "分销商",
    //             "businessPartyTypeDescription": "分销商",
    //             "businessPartyType": "DISTRIBUTOR",
    //             "warrantyConfigRefTargetTypeDescription": "分销商",
    //             "warrantyConfigRefTargetType": "DISTRIBUTOR",
    //             "warehouseOwningPartyTypeDescription": "分销商",
    //             "warehouseOwningPartyType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceTypeDescription": "分销商",
    //             "warehouseInventoryOutboundTargetType": "DISTRIBUTOR",
    //             "warehouseInventoryOutboundTargetTypeDescription": "分销商",
    //             "productRefTargetTypeDescription": "分销商",
    //             "productRefTargetType": "DISTRIBUTOR",
    //             "sellerPartyTypeDescription": "分销商",
    //             "sellerPartyType": "DISTRIBUTOR",
    //             "buyerPartyTypeDescription": "分销商",
    //             "buyerPartyType": "DISTRIBUTOR"
    //           },
    //           "district": {
    //             "flagList": [],
    //             "id": "202509051421033585210000",
    //             "createTimestamp": 1757082063000,
    //             "updateTimestamp": 1757082063000,
    //             "cityId": "202509051421030835210000",
    //             "regionalismCode": "530112",
    //             "localizedFullName": "西山区",
    //             "fullName": "西山区",
    //             "fullNameZhCn": "西山区",
    //             "city": {
    //               "flagList": [],
    //               "id": "202509051421030835210000",
    //               "createTimestamp": 1757082063000,
    //               "updateTimestamp": 1757082063000,
    //               "provinceId": "202509051421030075210000",
    //               "regionalismCode": "530100",
    //               "localizedFullName": "昆明市",
    //               "fullName": "昆明市",
    //               "fullNameZhCn": "昆明市",
    //               "province": {
    //                 "flagList": [],
    //                 "id": "202509051421030075210000",
    //                 "createTimestamp": 1757082063000,
    //                 "updateTimestamp": 1757082063000,
    //                 "countryId": "202509051417566675210000",
    //                 "regionalismCode": "530000",
    //                 "localizedFullName": "云南省",
    //                 "fullName": "云南省",
    //                 "fullNameZhCn": "云南省",
    //                 "country": {
    //                   "flagList": [],
    //                   "id": "202509051417566675210000",
    //                   "createTimestamp": 1757081877000,
    //                   "updateTimestamp": 1757081877000,
    //                   "localizedFullName": "中华人民共和国",
    //                   "fullName": "中华人民共和国",
    //                   "fullNameZhCn": "中华人民共和国"
    //                 }
    //               }
    //             }
    //           },
    //           "firstBusinessLicensePicFile": null,
    //           "secondBusinessLicensePicFile": null,
    //           "thirdBusinessLicensePicFile": null,
    //           "userPartyType": "RETAILER",
    //           "userPartyTypeDescription": "零售商",
    //           "merchantPartyType": "RETAILER",
    //           "merchantPartyTypeDescription": "零售商",
    //           "businessPartyTypeDescription": "零售商",
    //           "businessPartyType": "RETAILER",
    //           "warrantyConfigRefTargetTypeDescription": "零售商",
    //           "warrantyConfigRefTargetType": "RETAILER",
    //           "warehouseOwningPartyTypeDescription": "零售商",
    //           "warehouseOwningPartyType": "RETAILER",
    //           "warehouseInventoryOutboundTargetType": "RETAILER",
    //           "warehouseInventoryOutboundTargetTypeDescription": "零售商",
    //           "productRefTargetTypeDescription": "零售商",
    //           "productRefTargetType": "RETAILER",
    //           "serviceStoreOwningPartyType": "RETAILER",
    //           "serviceStoreOwningPartyTypeDescription": "零售商",
    //           "serviceRefTargetTypeDescription": "零售商",
    //           "serviceRefTargetType": "RETAILER",
    //           "sellerPartyTypeDescription": "零售商",
    //           "sellerPartyType": "RETAILER",
    //           "buyerPartyTypeDescription": "零售商",
    //           "buyerPartyType": "RETAILER"
    //         },
    //         {
    //           "flagList": [],
    //           "id": "202603161238091906220000",
    //           "createTimestamp": 1773664689000,
    //           "updateTimestamp": 1773664968000,
    //           "directSuperiorDistributorId": "202509230730592586830000",
    //           "fullName": "建水车匠坊维修中心",
    //           "approvalStatus": "APPROVED",
    //           "approvalStatusDescription": "已审核",
    //           "districtId": "202509051421098905210000",
    //           "address": "太和临安1号门对面车匠坊维修中心",
    //           "longitude": 102.819479,
    //           "latitude": 23.643919,
    //           "businessLicenseType": "BUSINESS_LICENSE",
    //           "businessLicenseTypeDescription": "营业执照",
    //           "businessLicenseNumber": "****",
    //           "firstBusinessLicensePicFileId": null,
    //           "secondBusinessLicensePicFileId": null,
    //           "thirdBusinessLicensePicFileId": null,
    //           "directSuperiorDistributor": {
    //             "flagList": [],
    //             "id": "202509230730592586830000",
    //             "createTimestamp": 1758612659000,
    //             "updateTimestamp": 1758612694000,
    //             "type": "DISTRIBUTOR",
    //             "typeDescription": "分销商",
    //             "level": 1,
    //             "directSuperiorDistributorId": null,
    //             "fullName": "驰捷车多管（云南）企业管理有限公司",
    //             "approvalStatus": "APPROVED",
    //             "approvalStatusDescription": "已审核",
    //             "districtId": "202509051421033585210000",
    //             "address": "云南省昆明市西山区西坝路639号",
    //             "longitude": 0.0,
    //             "latitude": 0.0,
    //             "businessLicenseType": "BUSINESS_LICENSE",
    //             "businessLicenseTypeDescription": "营业执照",
    //             "businessLicenseNumber": "91310118560192303A",
    //             "firstBusinessLicensePicFileId": null,
    //             "secondBusinessLicensePicFileId": null,
    //             "thirdBusinessLicensePicFileId": null,
    //             "directSuperiorDistributor": null,
    //             "district": {
    //               "flagList": [],
    //               "id": "202509051421033585210000",
    //               "createTimestamp": 1757082063000,
    //               "updateTimestamp": 1757082063000,
    //               "cityId": "202509051421030835210000",
    //               "regionalismCode": "530112",
    //               "localizedFullName": "西山区",
    //               "fullName": "西山区",
    //               "fullNameZhCn": "西山区",
    //               "city": {
    //                 "flagList": [],
    //                 "id": "202509051421030835210000",
    //                 "createTimestamp": 1757082063000,
    //                 "updateTimestamp": 1757082063000,
    //                 "provinceId": "202509051421030075210000",
    //                 "regionalismCode": "530100",
    //                 "localizedFullName": "昆明市",
    //                 "fullName": "昆明市",
    //                 "fullNameZhCn": "昆明市",
    //                 "province": {
    //                   "flagList": [],
    //                   "id": "202509051421030075210000",
    //                   "createTimestamp": 1757082063000,
    //                   "updateTimestamp": 1757082063000,
    //                   "countryId": "202509051417566675210000",
    //                   "regionalismCode": "530000",
    //                   "localizedFullName": "云南省",
    //                   "fullName": "云南省",
    //                   "fullNameZhCn": "云南省",
    //                   "country": {
    //                     "flagList": [],
    //                     "id": "202509051417566675210000",
    //                     "createTimestamp": 1757081877000,
    //                     "updateTimestamp": 1757081877000,
    //                     "localizedFullName": "中华人民共和国",
    //                     "fullName": "中华人民共和国",
    //                     "fullNameZhCn": "中华人民共和国"
    //                   }
    //                 }
    //               }
    //             },
    //             "firstBusinessLicensePicFile": null,
    //             "secondBusinessLicensePicFile": null,
    //             "thirdBusinessLicensePicFile": null,
    //             "userPartyType": "DISTRIBUTOR",
    //             "userPartyTypeDescription": "分销商",
    //             "merchantPartyType": "DISTRIBUTOR",
    //             "merchantPartyTypeDescription": "分销商",
    //             "businessPartyTypeDescription": "分销商",
    //             "businessPartyType": "DISTRIBUTOR",
    //             "warrantyConfigRefTargetTypeDescription": "分销商",
    //             "warrantyConfigRefTargetType": "DISTRIBUTOR",
    //             "warehouseOwningPartyTypeDescription": "分销商",
    //             "warehouseOwningPartyType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceTypeDescription": "分销商",
    //             "warehouseInventoryOutboundTargetType": "DISTRIBUTOR",
    //             "warehouseInventoryOutboundTargetTypeDescription": "分销商",
    //             "productRefTargetTypeDescription": "分销商",
    //             "productRefTargetType": "DISTRIBUTOR",
    //             "sellerPartyTypeDescription": "分销商",
    //             "sellerPartyType": "DISTRIBUTOR",
    //             "buyerPartyTypeDescription": "分销商",
    //             "buyerPartyType": "DISTRIBUTOR"
    //           },
    //           "district": {
    //             "flagList": [],
    //             "id": "202509051421098905210000",
    //             "createTimestamp": 1757082070000,
    //             "updateTimestamp": 1757082070000,
    //             "cityId": "202509051421094685210000",
    //             "regionalismCode": "532524",
    //             "localizedFullName": "建水县",
    //             "fullName": "建水县",
    //             "fullNameZhCn": "建水县",
    //             "city": {
    //               "flagList": [],
    //               "id": "202509051421094685210000",
    //               "createTimestamp": 1757082069000,
    //               "updateTimestamp": 1757082069000,
    //               "provinceId": "202509051421030075210000",
    //               "regionalismCode": "532500",
    //               "localizedFullName": "红河哈尼族彝族自治州",
    //               "fullName": "红河哈尼族彝族自治州",
    //               "fullNameZhCn": "红河哈尼族彝族自治州",
    //               "province": {
    //                 "flagList": [],
    //                 "id": "202509051421030075210000",
    //                 "createTimestamp": 1757082063000,
    //                 "updateTimestamp": 1757082063000,
    //                 "countryId": "202509051417566675210000",
    //                 "regionalismCode": "530000",
    //                 "localizedFullName": "云南省",
    //                 "fullName": "云南省",
    //                 "fullNameZhCn": "云南省",
    //                 "country": {
    //                   "flagList": [],
    //                   "id": "202509051417566675210000",
    //                   "createTimestamp": 1757081877000,
    //                   "updateTimestamp": 1757081877000,
    //                   "localizedFullName": "中华人民共和国",
    //                   "fullName": "中华人民共和国",
    //                   "fullNameZhCn": "中华人民共和国"
    //                 }
    //               }
    //             }
    //           },
    //           "firstBusinessLicensePicFile": null,
    //           "secondBusinessLicensePicFile": null,
    //           "thirdBusinessLicensePicFile": null,
    //           "userPartyType": "RETAILER",
    //           "userPartyTypeDescription": "零售商",
    //           "merchantPartyType": "RETAILER",
    //           "merchantPartyTypeDescription": "零售商",
    //           "businessPartyTypeDescription": "零售商",
    //           "businessPartyType": "RETAILER",
    //           "warrantyConfigRefTargetTypeDescription": "零售商",
    //           "warrantyConfigRefTargetType": "RETAILER",
    //           "warehouseOwningPartyTypeDescription": "零售商",
    //           "warehouseOwningPartyType": "RETAILER",
    //           "warehouseInventoryOutboundTargetType": "RETAILER",
    //           "warehouseInventoryOutboundTargetTypeDescription": "零售商",
    //           "productRefTargetTypeDescription": "零售商",
    //           "productRefTargetType": "RETAILER",
    //           "serviceStoreOwningPartyType": "RETAILER",
    //           "serviceStoreOwningPartyTypeDescription": "零售商",
    //           "serviceRefTargetTypeDescription": "零售商",
    //           "serviceRefTargetType": "RETAILER",
    //           "sellerPartyTypeDescription": "零售商",
    //           "sellerPartyType": "RETAILER",
    //           "buyerPartyTypeDescription": "零售商",
    //           "buyerPartyType": "RETAILER"
    //         },
    //         {
    //           "flagList": [],
    //           "id": "202603310410532580530000",
    //           "createTimestamp": 1774930253000,
    //           "updateTimestamp": 1775642671000,
    //           "directSuperiorDistributorId": "202603230837030730530000",
    //           "fullName": "北京中升宝辉汽车销售服务有限公司",
    //           "approvalStatus": "APPROVED",
    //           "approvalStatusDescription": "已审核",
    //           "districtId": "202509051417571115210000",
    //           "address": "北京市朝阳区德胜门外花虎沟18号",
    //           "longitude": 116.35221,
    //           "latitude": 40.01724,
    //           "businessLicenseType": "BUSINESS_LICENSE",
    //           "businessLicenseTypeDescription": "营业执照",
    //           "businessLicenseNumber": "91110105746715360X",
    //           "firstBusinessLicensePicFileId": null,
    //           "secondBusinessLicensePicFileId": null,
    //           "thirdBusinessLicensePicFileId": null,
    //           "directSuperiorDistributor": {
    //             "flagList": [],
    //             "id": "202603230837030730530000",
    //             "createTimestamp": 1774255023000,
    //             "updateTimestamp": 1774255731000,
    //             "type": "DISTRIBUTOR",
    //             "typeDescription": "分销商",
    //             "level": 1,
    //             "directSuperiorDistributorId": null,
    //             "fullName": "兰考县翰峰信息科技有限公司",
    //             "approvalStatus": "APPROVED",
    //             "approvalStatusDescription": "已审核",
    //             "districtId": "202509051419452105210000",
    //             "address": "经济技术开发区兰考县科技路南50米兰考同乐居（跨境）电商家居产业园469号",
    //             "longitude": 0.0,
    //             "latitude": 0.0,
    //             "businessLicenseType": "BUSINESS_LICENSE",
    //             "businessLicenseTypeDescription": "营业执照",
    //             "businessLicenseNumber": "91410225MA9LHPBQ66",
    //             "firstBusinessLicensePicFileId": null,
    //             "secondBusinessLicensePicFileId": null,
    //             "thirdBusinessLicensePicFileId": null,
    //             "directSuperiorDistributor": null,
    //             "district": {
    //               "flagList": [],
    //               "id": "202509051419452105210000",
    //               "createTimestamp": 1757081985000,
    //               "updateTimestamp": 1757081985000,
    //               "cityId": "202509051419445765210000",
    //               "regionalismCode": "410225",
    //               "localizedFullName": "兰考县",
    //               "fullName": "兰考县",
    //               "fullNameZhCn": "兰考县",
    //               "city": {
    //                 "flagList": [],
    //                 "id": "202509051419445765210000",
    //                 "createTimestamp": 1757081985000,
    //                 "updateTimestamp": 1757081985000,
    //                 "provinceId": "202509051419435825210000",
    //                 "regionalismCode": "410200",
    //                 "localizedFullName": "开封市",
    //                 "fullName": "开封市",
    //                 "fullNameZhCn": "开封市",
    //                 "province": {
    //                   "flagList": [],
    //                   "id": "202509051419435825210000",
    //                   "createTimestamp": 1757081984000,
    //                   "updateTimestamp": 1757081984000,
    //                   "countryId": "202509051417566675210000",
    //                   "regionalismCode": "410000",
    //                   "localizedFullName": "河南省",
    //                   "fullName": "河南省",
    //                   "fullNameZhCn": "河南省",
    //                   "country": {
    //                     "flagList": [],
    //                     "id": "202509051417566675210000",
    //                     "createTimestamp": 1757081877000,
    //                     "updateTimestamp": 1757081877000,
    //                     "localizedFullName": "中华人民共和国",
    //                     "fullName": "中华人民共和国",
    //                     "fullNameZhCn": "中华人民共和国"
    //                   }
    //                 }
    //               }
    //             },
    //             "firstBusinessLicensePicFile": null,
    //             "secondBusinessLicensePicFile": null,
    //             "thirdBusinessLicensePicFile": null,
    //             "userPartyType": "DISTRIBUTOR",
    //             "userPartyTypeDescription": "分销商",
    //             "merchantPartyType": "DISTRIBUTOR",
    //             "merchantPartyTypeDescription": "分销商",
    //             "businessPartyTypeDescription": "分销商",
    //             "businessPartyType": "DISTRIBUTOR",
    //             "warrantyConfigRefTargetTypeDescription": "分销商",
    //             "warrantyConfigRefTargetType": "DISTRIBUTOR",
    //             "warehouseOwningPartyTypeDescription": "分销商",
    //             "warehouseOwningPartyType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceTypeDescription": "分销商",
    //             "warehouseInventoryOutboundTargetType": "DISTRIBUTOR",
    //             "warehouseInventoryOutboundTargetTypeDescription": "分销商",
    //             "productRefTargetTypeDescription": "分销商",
    //             "productRefTargetType": "DISTRIBUTOR",
    //             "sellerPartyTypeDescription": "分销商",
    //             "sellerPartyType": "DISTRIBUTOR",
    //             "buyerPartyTypeDescription": "分销商",
    //             "buyerPartyType": "DISTRIBUTOR"
    //           },
    //           "district": {
    //             "flagList": [],
    //             "id": "202509051417571115210000",
    //             "createTimestamp": 1757081877000,
    //             "updateTimestamp": 1757081877000,
    //             "cityId": "202509051417568825210000",
    //             "regionalismCode": "110105",
    //             "localizedFullName": "朝阳区",
    //             "fullName": "朝阳区",
    //             "fullNameZhCn": "朝阳区",
    //             "city": {
    //               "flagList": [],
    //               "id": "202509051417568825210000",
    //               "createTimestamp": 1757081877000,
    //               "updateTimestamp": 1757081877000,
    //               "provinceId": "202509051417568035210000",
    //               "regionalismCode": "110000",
    //               "localizedFullName": "北京市",
    //               "fullName": "北京市",
    //               "fullNameZhCn": "北京市",
    //               "province": {
    //                 "flagList": [],
    //                 "id": "202509051417568035210000",
    //                 "createTimestamp": 1757081877000,
    //                 "updateTimestamp": 1757081877000,
    //                 "countryId": "202509051417566675210000",
    //                 "regionalismCode": "110000",
    //                 "localizedFullName": "北京市",
    //                 "fullName": "北京市",
    //                 "fullNameZhCn": "北京市",
    //                 "country": {
    //                   "flagList": [],
    //                   "id": "202509051417566675210000",
    //                   "createTimestamp": 1757081877000,
    //                   "updateTimestamp": 1757081877000,
    //                   "localizedFullName": "中华人民共和国",
    //                   "fullName": "中华人民共和国",
    //                   "fullNameZhCn": "中华人民共和国"
    //                 }
    //               }
    //             }
    //           },
    //           "firstBusinessLicensePicFile": null,
    //           "secondBusinessLicensePicFile": null,
    //           "thirdBusinessLicensePicFile": null,
    //           "userPartyType": "RETAILER",
    //           "userPartyTypeDescription": "零售商",
    //           "merchantPartyType": "RETAILER",
    //           "merchantPartyTypeDescription": "零售商",
    //           "businessPartyTypeDescription": "零售商",
    //           "businessPartyType": "RETAILER",
    //           "warrantyConfigRefTargetTypeDescription": "零售商",
    //           "warrantyConfigRefTargetType": "RETAILER",
    //           "warehouseOwningPartyTypeDescription": "零售商",
    //           "warehouseOwningPartyType": "RETAILER",
    //           "warehouseInventoryOutboundTargetType": "RETAILER",
    //           "warehouseInventoryOutboundTargetTypeDescription": "零售商",
    //           "productRefTargetTypeDescription": "零售商",
    //           "productRefTargetType": "RETAILER",
    //           "serviceStoreOwningPartyType": "RETAILER",
    //           "serviceStoreOwningPartyTypeDescription": "零售商",
    //           "serviceRefTargetTypeDescription": "零售商",
    //           "serviceRefTargetType": "RETAILER",
    //           "sellerPartyTypeDescription": "零售商",
    //           "sellerPartyType": "RETAILER",
    //           "buyerPartyTypeDescription": "零售商",
    //           "buyerPartyType": "RETAILER"
    //         },
    //         {
    //           "flagList": [],
    //           "id": "202604070407303929230000",
    //           "createTimestamp": 1775534850000,
    //           "updateTimestamp": 1775642672000,
    //           "directSuperiorDistributorId": "202603230837030730530000",
    //           "fullName": "西安中升之宝汽车销售服务有限公司",
    //           "approvalStatus": "APPROVED",
    //           "approvalStatusDescription": "已审核",
    //           "districtId": "202509051421196835210000",
    //           "address": "穆将王立交东辅道",
    //           "longitude": 109.04624,
    //           "latitude": 34.26492,
    //           "businessLicenseType": "BUSINESS_LICENSE",
    //           "businessLicenseTypeDescription": "营业执照",
    //           "businessLicenseNumber": "91610111688958291Q",
    //           "firstBusinessLicensePicFileId": "202604070406152049230000",
    //           "secondBusinessLicensePicFileId": null,
    //           "thirdBusinessLicensePicFileId": null,
    //           "directSuperiorDistributor": {
    //             "flagList": [],
    //             "id": "202603230837030730530000",
    //             "createTimestamp": 1774255023000,
    //             "updateTimestamp": 1774255731000,
    //             "type": "DISTRIBUTOR",
    //             "typeDescription": "分销商",
    //             "level": 1,
    //             "directSuperiorDistributorId": null,
    //             "fullName": "兰考县翰峰信息科技有限公司",
    //             "approvalStatus": "APPROVED",
    //             "approvalStatusDescription": "已审核",
    //             "districtId": "202509051419452105210000",
    //             "address": "经济技术开发区兰考县科技路南50米兰考同乐居（跨境）电商家居产业园469号",
    //             "longitude": 0.0,
    //             "latitude": 0.0,
    //             "businessLicenseType": "BUSINESS_LICENSE",
    //             "businessLicenseTypeDescription": "营业执照",
    //             "businessLicenseNumber": "91410225MA9LHPBQ66",
    //             "firstBusinessLicensePicFileId": null,
    //             "secondBusinessLicensePicFileId": null,
    //             "thirdBusinessLicensePicFileId": null,
    //             "directSuperiorDistributor": null,
    //             "district": {
    //               "flagList": [],
    //               "id": "202509051419452105210000",
    //               "createTimestamp": 1757081985000,
    //               "updateTimestamp": 1757081985000,
    //               "cityId": "202509051419445765210000",
    //               "regionalismCode": "410225",
    //               "localizedFullName": "兰考县",
    //               "fullName": "兰考县",
    //               "fullNameZhCn": "兰考县",
    //               "city": {
    //                 "flagList": [],
    //                 "id": "202509051419445765210000",
    //                 "createTimestamp": 1757081985000,
    //                 "updateTimestamp": 1757081985000,
    //                 "provinceId": "202509051419435825210000",
    //                 "regionalismCode": "410200",
    //                 "localizedFullName": "开封市",
    //                 "fullName": "开封市",
    //                 "fullNameZhCn": "开封市",
    //                 "province": {
    //                   "flagList": [],
    //                   "id": "202509051419435825210000",
    //                   "createTimestamp": 1757081984000,
    //                   "updateTimestamp": 1757081984000,
    //                   "countryId": "202509051417566675210000",
    //                   "regionalismCode": "410000",
    //                   "localizedFullName": "河南省",
    //                   "fullName": "河南省",
    //                   "fullNameZhCn": "河南省",
    //                   "country": {
    //                     "flagList": [],
    //                     "id": "202509051417566675210000",
    //                     "createTimestamp": 1757081877000,
    //                     "updateTimestamp": 1757081877000,
    //                     "localizedFullName": "中华人民共和国",
    //                     "fullName": "中华人民共和国",
    //                     "fullNameZhCn": "中华人民共和国"
    //                   }
    //                 }
    //               }
    //             },
    //             "firstBusinessLicensePicFile": null,
    //             "secondBusinessLicensePicFile": null,
    //             "thirdBusinessLicensePicFile": null,
    //             "userPartyType": "DISTRIBUTOR",
    //             "userPartyTypeDescription": "分销商",
    //             "merchantPartyType": "DISTRIBUTOR",
    //             "merchantPartyTypeDescription": "分销商",
    //             "businessPartyTypeDescription": "分销商",
    //             "businessPartyType": "DISTRIBUTOR",
    //             "warrantyConfigRefTargetTypeDescription": "分销商",
    //             "warrantyConfigRefTargetType": "DISTRIBUTOR",
    //             "warehouseOwningPartyTypeDescription": "分销商",
    //             "warehouseOwningPartyType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceTypeDescription": "分销商",
    //             "warehouseInventoryOutboundTargetType": "DISTRIBUTOR",
    //             "warehouseInventoryOutboundTargetTypeDescription": "分销商",
    //             "productRefTargetTypeDescription": "分销商",
    //             "productRefTargetType": "DISTRIBUTOR",
    //             "sellerPartyTypeDescription": "分销商",
    //             "sellerPartyType": "DISTRIBUTOR",
    //             "buyerPartyTypeDescription": "分销商",
    //             "buyerPartyType": "DISTRIBUTOR"
    //           },
    //           "district": {
    //             "flagList": [],
    //             "id": "202509051421196835210000",
    //             "createTimestamp": 1757082080000,
    //             "updateTimestamp": 1757082080000,
    //             "cityId": "202509051421193935210000",
    //             "regionalismCode": "610111",
    //             "localizedFullName": "灞桥区",
    //             "fullName": "灞桥区",
    //             "fullNameZhCn": "灞桥区",
    //             "city": {
    //               "flagList": [],
    //               "id": "202509051421193935210000",
    //               "createTimestamp": 1757082079000,
    //               "updateTimestamp": 1757082079000,
    //               "provinceId": "202509051421193165210000",
    //               "regionalismCode": "610100",
    //               "localizedFullName": "西安市",
    //               "fullName": "西安市",
    //               "fullNameZhCn": "西安市",
    //               "province": {
    //                 "flagList": [],
    //                 "id": "202509051421193165210000",
    //                 "createTimestamp": 1757082079000,
    //                 "updateTimestamp": 1757082079000,
    //                 "countryId": "202509051417566675210000",
    //                 "regionalismCode": "610000",
    //                 "localizedFullName": "陕西省",
    //                 "fullName": "陕西省",
    //                 "fullNameZhCn": "陕西省",
    //                 "country": {
    //                   "flagList": [],
    //                   "id": "202509051417566675210000",
    //                   "createTimestamp": 1757081877000,
    //                   "updateTimestamp": 1757081877000,
    //                   "localizedFullName": "中华人民共和国",
    //                   "fullName": "中华人民共和国",
    //                   "fullNameZhCn": "中华人民共和国"
    //                 }
    //               }
    //             }
    //           },
    //           "firstBusinessLicensePicFile": {
    //             "flagList": [],
    //             "id": "202604070406152049230000",
    //             "createTimestamp": 1775534775000,
    //             "updateTimestamp": 1775534775000,
    //             "format": "JPEG",
    //             "formatDescription": "JPEG",
    //             "width": 2480,
    //             "height": 1732,
    //             "ossBucket": "goldenkiwiprod",
    //             "ossPath": "nbiz/retailer/2026/04/07/04/2480_1732_850ace3d98166ce5b265fe1bb14cb49d.jpeg",
    //             "url": "https://img.youbanban.com/oss/download/nbiz/retailer/2026/04/07/04/2480_1732_850ace3d98166ce5b265fe1bb14cb49d.jpeg",
    //             "fileTypeDescription": "图片",
    //             "fileType": "PIC"
    //           },
    //           "secondBusinessLicensePicFile": null,
    //           "thirdBusinessLicensePicFile": null,
    //           "userPartyType": "RETAILER",
    //           "userPartyTypeDescription": "零售商",
    //           "merchantPartyType": "RETAILER",
    //           "merchantPartyTypeDescription": "零售商",
    //           "businessPartyTypeDescription": "零售商",
    //           "businessPartyType": "RETAILER",
    //           "warrantyConfigRefTargetTypeDescription": "零售商",
    //           "warrantyConfigRefTargetType": "RETAILER",
    //           "warehouseOwningPartyTypeDescription": "零售商",
    //           "warehouseOwningPartyType": "RETAILER",
    //           "warehouseInventoryOutboundTargetType": "RETAILER",
    //           "warehouseInventoryOutboundTargetTypeDescription": "零售商",
    //           "productRefTargetTypeDescription": "零售商",
    //           "productRefTargetType": "RETAILER",
    //           "serviceStoreOwningPartyType": "RETAILER",
    //           "serviceStoreOwningPartyTypeDescription": "零售商",
    //           "serviceRefTargetTypeDescription": "零售商",
    //           "serviceRefTargetType": "RETAILER",
    //           "sellerPartyTypeDescription": "零售商",
    //           "sellerPartyType": "RETAILER",
    //           "buyerPartyTypeDescription": "零售商",
    //           "buyerPartyType": "RETAILER"
    //         },
    //         {
    //           "flagList": [],
    //           "id": "202604080959152349230000",
    //           "createTimestamp": 1775642355000,
    //           "updateTimestamp": 1775642673000,
    //           "directSuperiorDistributorId": "202509230730592586830000",
    //           "fullName": "玉溪通力汽修",
    //           "approvalStatus": "APPROVED",
    //           "approvalStatusDescription": "已审核",
    //           "districtId": "202509051421049115210000",
    //           "address": "玉兴路69号",
    //           "longitude": 102.554012,
    //           "latitude": 24.353923,
    //           "businessLicenseType": "BUSINESS_LICENSE",
    //           "businessLicenseTypeDescription": "营业执照",
    //           "businessLicenseNumber": "****",
    //           "firstBusinessLicensePicFileId": null,
    //           "secondBusinessLicensePicFileId": null,
    //           "thirdBusinessLicensePicFileId": null,
    //           "directSuperiorDistributor": {
    //             "flagList": [],
    //             "id": "202509230730592586830000",
    //             "createTimestamp": 1758612659000,
    //             "updateTimestamp": 1758612694000,
    //             "type": "DISTRIBUTOR",
    //             "typeDescription": "分销商",
    //             "level": 1,
    //             "directSuperiorDistributorId": null,
    //             "fullName": "驰捷车多管（云南）企业管理有限公司",
    //             "approvalStatus": "APPROVED",
    //             "approvalStatusDescription": "已审核",
    //             "districtId": "202509051421033585210000",
    //             "address": "云南省昆明市西山区西坝路639号",
    //             "longitude": 0.0,
    //             "latitude": 0.0,
    //             "businessLicenseType": "BUSINESS_LICENSE",
    //             "businessLicenseTypeDescription": "营业执照",
    //             "businessLicenseNumber": "91310118560192303A",
    //             "firstBusinessLicensePicFileId": null,
    //             "secondBusinessLicensePicFileId": null,
    //             "thirdBusinessLicensePicFileId": null,
    //             "directSuperiorDistributor": null,
    //             "district": {
    //               "flagList": [],
    //               "id": "202509051421033585210000",
    //               "createTimestamp": 1757082063000,
    //               "updateTimestamp": 1757082063000,
    //               "cityId": "202509051421030835210000",
    //               "regionalismCode": "530112",
    //               "localizedFullName": "西山区",
    //               "fullName": "西山区",
    //               "fullNameZhCn": "西山区",
    //               "city": {
    //                 "flagList": [],
    //                 "id": "202509051421030835210000",
    //                 "createTimestamp": 1757082063000,
    //                 "updateTimestamp": 1757082063000,
    //                 "provinceId": "202509051421030075210000",
    //                 "regionalismCode": "530100",
    //                 "localizedFullName": "昆明市",
    //                 "fullName": "昆明市",
    //                 "fullNameZhCn": "昆明市",
    //                 "province": {
    //                   "flagList": [],
    //                   "id": "202509051421030075210000",
    //                   "createTimestamp": 1757082063000,
    //                   "updateTimestamp": 1757082063000,
    //                   "countryId": "202509051417566675210000",
    //                   "regionalismCode": "530000",
    //                   "localizedFullName": "云南省",
    //                   "fullName": "云南省",
    //                   "fullNameZhCn": "云南省",
    //                   "country": {
    //                     "flagList": [],
    //                     "id": "202509051417566675210000",
    //                     "createTimestamp": 1757081877000,
    //                     "updateTimestamp": 1757081877000,
    //                     "localizedFullName": "中华人民共和国",
    //                     "fullName": "中华人民共和国",
    //                     "fullNameZhCn": "中华人民共和国"
    //                   }
    //                 }
    //               }
    //             },
    //             "firstBusinessLicensePicFile": null,
    //             "secondBusinessLicensePicFile": null,
    //             "thirdBusinessLicensePicFile": null,
    //             "userPartyType": "DISTRIBUTOR",
    //             "userPartyTypeDescription": "分销商",
    //             "merchantPartyType": "DISTRIBUTOR",
    //             "merchantPartyTypeDescription": "分销商",
    //             "businessPartyTypeDescription": "分销商",
    //             "businessPartyType": "DISTRIBUTOR",
    //             "warrantyConfigRefTargetTypeDescription": "分销商",
    //             "warrantyConfigRefTargetType": "DISTRIBUTOR",
    //             "warehouseOwningPartyTypeDescription": "分销商",
    //             "warehouseOwningPartyType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceTypeDescription": "分销商",
    //             "warehouseInventoryOutboundTargetType": "DISTRIBUTOR",
    //             "warehouseInventoryOutboundTargetTypeDescription": "分销商",
    //             "productRefTargetTypeDescription": "分销商",
    //             "productRefTargetType": "DISTRIBUTOR",
    //             "sellerPartyTypeDescription": "分销商",
    //             "sellerPartyType": "DISTRIBUTOR",
    //             "buyerPartyTypeDescription": "分销商",
    //             "buyerPartyType": "DISTRIBUTOR"
    //           },
    //           "district": {
    //             "flagList": [],
    //             "id": "202509051421049115210000",
    //             "createTimestamp": 1757082065000,
    //             "updateTimestamp": 1757082065000,
    //             "cityId": "202509051421048425210000",
    //             "regionalismCode": "530402",
    //             "localizedFullName": "红塔区",
    //             "fullName": "红塔区",
    //             "fullNameZhCn": "红塔区",
    //             "city": {
    //               "flagList": [],
    //               "id": "202509051421048425210000",
    //               "createTimestamp": 1757082065000,
    //               "updateTimestamp": 1757082065000,
    //               "provinceId": "202509051421030075210000",
    //               "regionalismCode": "530400",
    //               "localizedFullName": "玉溪市",
    //               "fullName": "玉溪市",
    //               "fullNameZhCn": "玉溪市",
    //               "province": {
    //                 "flagList": [],
    //                 "id": "202509051421030075210000",
    //                 "createTimestamp": 1757082063000,
    //                 "updateTimestamp": 1757082063000,
    //                 "countryId": "202509051417566675210000",
    //                 "regionalismCode": "530000",
    //                 "localizedFullName": "云南省",
    //                 "fullName": "云南省",
    //                 "fullNameZhCn": "云南省",
    //                 "country": {
    //                   "flagList": [],
    //                   "id": "202509051417566675210000",
    //                   "createTimestamp": 1757081877000,
    //                   "updateTimestamp": 1757081877000,
    //                   "localizedFullName": "中华人民共和国",
    //                   "fullName": "中华人民共和国",
    //                   "fullNameZhCn": "中华人民共和国"
    //                 }
    //               }
    //             }
    //           },
    //           "firstBusinessLicensePicFile": null,
    //           "secondBusinessLicensePicFile": null,
    //           "thirdBusinessLicensePicFile": null,
    //           "userPartyType": "RETAILER",
    //           "userPartyTypeDescription": "零售商",
    //           "merchantPartyType": "RETAILER",
    //           "merchantPartyTypeDescription": "零售商",
    //           "businessPartyTypeDescription": "零售商",
    //           "businessPartyType": "RETAILER",
    //           "warrantyConfigRefTargetTypeDescription": "零售商",
    //           "warrantyConfigRefTargetType": "RETAILER",
    //           "warehouseOwningPartyTypeDescription": "零售商",
    //           "warehouseOwningPartyType": "RETAILER",
    //           "warehouseInventoryOutboundTargetType": "RETAILER",
    //           "warehouseInventoryOutboundTargetTypeDescription": "零售商",
    //           "productRefTargetTypeDescription": "零售商",
    //           "productRefTargetType": "RETAILER",
    //           "serviceStoreOwningPartyType": "RETAILER",
    //           "serviceStoreOwningPartyTypeDescription": "零售商",
    //           "serviceRefTargetTypeDescription": "零售商",
    //           "serviceRefTargetType": "RETAILER",
    //           "sellerPartyTypeDescription": "零售商",
    //           "sellerPartyType": "RETAILER",
    //           "buyerPartyTypeDescription": "零售商",
    //           "buyerPartyType": "RETAILER"
    //         },
    //         {
    //           "flagList": [],
    //           "id": "202604081000013089230000",
    //           "createTimestamp": 1775642401000,
    //           "updateTimestamp": 1775642673000,
    //           "directSuperiorDistributorId": "202509230730592586830000",
    //           "fullName": "玉溪荣海汽车服务有限公司",
    //           "approvalStatus": "APPROVED",
    //           "approvalStatusDescription": "已审核",
    //           "districtId": "202509051421049115210000",
    //           "address": "金苑小区9幢1号",
    //           "longitude": 102.53958,
    //           "latitude": 24.37772,
    //           "businessLicenseType": "BUSINESS_LICENSE",
    //           "businessLicenseTypeDescription": "营业执照",
    //           "businessLicenseNumber": "****",
    //           "firstBusinessLicensePicFileId": null,
    //           "secondBusinessLicensePicFileId": null,
    //           "thirdBusinessLicensePicFileId": null,
    //           "directSuperiorDistributor": {
    //             "flagList": [],
    //             "id": "202509230730592586830000",
    //             "createTimestamp": 1758612659000,
    //             "updateTimestamp": 1758612694000,
    //             "type": "DISTRIBUTOR",
    //             "typeDescription": "分销商",
    //             "level": 1,
    //             "directSuperiorDistributorId": null,
    //             "fullName": "驰捷车多管（云南）企业管理有限公司",
    //             "approvalStatus": "APPROVED",
    //             "approvalStatusDescription": "已审核",
    //             "districtId": "202509051421033585210000",
    //             "address": "云南省昆明市西山区西坝路639号",
    //             "longitude": 0.0,
    //             "latitude": 0.0,
    //             "businessLicenseType": "BUSINESS_LICENSE",
    //             "businessLicenseTypeDescription": "营业执照",
    //             "businessLicenseNumber": "91310118560192303A",
    //             "firstBusinessLicensePicFileId": null,
    //             "secondBusinessLicensePicFileId": null,
    //             "thirdBusinessLicensePicFileId": null,
    //             "directSuperiorDistributor": null,
    //             "district": {
    //               "flagList": [],
    //               "id": "202509051421033585210000",
    //               "createTimestamp": 1757082063000,
    //               "updateTimestamp": 1757082063000,
    //               "cityId": "202509051421030835210000",
    //               "regionalismCode": "530112",
    //               "localizedFullName": "西山区",
    //               "fullName": "西山区",
    //               "fullNameZhCn": "西山区",
    //               "city": {
    //                 "flagList": [],
    //                 "id": "202509051421030835210000",
    //                 "createTimestamp": 1757082063000,
    //                 "updateTimestamp": 1757082063000,
    //                 "provinceId": "202509051421030075210000",
    //                 "regionalismCode": "530100",
    //                 "localizedFullName": "昆明市",
    //                 "fullName": "昆明市",
    //                 "fullNameZhCn": "昆明市",
    //                 "province": {
    //                   "flagList": [],
    //                   "id": "202509051421030075210000",
    //                   "createTimestamp": 1757082063000,
    //                   "updateTimestamp": 1757082063000,
    //                   "countryId": "202509051417566675210000",
    //                   "regionalismCode": "530000",
    //                   "localizedFullName": "云南省",
    //                   "fullName": "云南省",
    //                   "fullNameZhCn": "云南省",
    //                   "country": {
    //                     "flagList": [],
    //                     "id": "202509051417566675210000",
    //                     "createTimestamp": 1757081877000,
    //                     "updateTimestamp": 1757081877000,
    //                     "localizedFullName": "中华人民共和国",
    //                     "fullName": "中华人民共和国",
    //                     "fullNameZhCn": "中华人民共和国"
    //                   }
    //                 }
    //               }
    //             },
    //             "firstBusinessLicensePicFile": null,
    //             "secondBusinessLicensePicFile": null,
    //             "thirdBusinessLicensePicFile": null,
    //             "userPartyType": "DISTRIBUTOR",
    //             "userPartyTypeDescription": "分销商",
    //             "merchantPartyType": "DISTRIBUTOR",
    //             "merchantPartyTypeDescription": "分销商",
    //             "businessPartyTypeDescription": "分销商",
    //             "businessPartyType": "DISTRIBUTOR",
    //             "warrantyConfigRefTargetTypeDescription": "分销商",
    //             "warrantyConfigRefTargetType": "DISTRIBUTOR",
    //             "warehouseOwningPartyTypeDescription": "分销商",
    //             "warehouseOwningPartyType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceTypeDescription": "分销商",
    //             "warehouseInventoryOutboundTargetType": "DISTRIBUTOR",
    //             "warehouseInventoryOutboundTargetTypeDescription": "分销商",
    //             "productRefTargetTypeDescription": "分销商",
    //             "productRefTargetType": "DISTRIBUTOR",
    //             "sellerPartyTypeDescription": "分销商",
    //             "sellerPartyType": "DISTRIBUTOR",
    //             "buyerPartyTypeDescription": "分销商",
    //             "buyerPartyType": "DISTRIBUTOR"
    //           },
    //           "district": {
    //             "flagList": [],
    //             "id": "202509051421049115210000",
    //             "createTimestamp": 1757082065000,
    //             "updateTimestamp": 1757082065000,
    //             "cityId": "202509051421048425210000",
    //             "regionalismCode": "530402",
    //             "localizedFullName": "红塔区",
    //             "fullName": "红塔区",
    //             "fullNameZhCn": "红塔区",
    //             "city": {
    //               "flagList": [],
    //               "id": "202509051421048425210000",
    //               "createTimestamp": 1757082065000,
    //               "updateTimestamp": 1757082065000,
    //               "provinceId": "202509051421030075210000",
    //               "regionalismCode": "530400",
    //               "localizedFullName": "玉溪市",
    //               "fullName": "玉溪市",
    //               "fullNameZhCn": "玉溪市",
    //               "province": {
    //                 "flagList": [],
    //                 "id": "202509051421030075210000",
    //                 "createTimestamp": 1757082063000,
    //                 "updateTimestamp": 1757082063000,
    //                 "countryId": "202509051417566675210000",
    //                 "regionalismCode": "530000",
    //                 "localizedFullName": "云南省",
    //                 "fullName": "云南省",
    //                 "fullNameZhCn": "云南省",
    //                 "country": {
    //                   "flagList": [],
    //                   "id": "202509051417566675210000",
    //                   "createTimestamp": 1757081877000,
    //                   "updateTimestamp": 1757081877000,
    //                   "localizedFullName": "中华人民共和国",
    //                   "fullName": "中华人民共和国",
    //                   "fullNameZhCn": "中华人民共和国"
    //                 }
    //               }
    //             }
    //           },
    //           "firstBusinessLicensePicFile": null,
    //           "secondBusinessLicensePicFile": null,
    //           "thirdBusinessLicensePicFile": null,
    //           "userPartyType": "RETAILER",
    //           "userPartyTypeDescription": "零售商",
    //           "merchantPartyType": "RETAILER",
    //           "merchantPartyTypeDescription": "零售商",
    //           "businessPartyTypeDescription": "零售商",
    //           "businessPartyType": "RETAILER",
    //           "warrantyConfigRefTargetTypeDescription": "零售商",
    //           "warrantyConfigRefTargetType": "RETAILER",
    //           "warehouseOwningPartyTypeDescription": "零售商",
    //           "warehouseOwningPartyType": "RETAILER",
    //           "warehouseInventoryOutboundTargetType": "RETAILER",
    //           "warehouseInventoryOutboundTargetTypeDescription": "零售商",
    //           "productRefTargetTypeDescription": "零售商",
    //           "productRefTargetType": "RETAILER",
    //           "serviceStoreOwningPartyType": "RETAILER",
    //           "serviceStoreOwningPartyTypeDescription": "零售商",
    //           "serviceRefTargetTypeDescription": "零售商",
    //           "serviceRefTargetType": "RETAILER",
    //           "sellerPartyTypeDescription": "零售商",
    //           "sellerPartyType": "RETAILER",
    //           "buyerPartyTypeDescription": "零售商",
    //           "buyerPartyType": "RETAILER"
    //         },
    //         {
    //           "flagList": [],
    //           "id": "202604081016531679230000",
    //           "createTimestamp": 1775643413000,
    //           "updateTimestamp": 1775643466000,
    //           "directSuperiorDistributorId": "202509230730592586830000",
    //           "fullName": "鹤庆县金港名车汇",
    //           "approvalStatus": "APPROVED",
    //           "approvalStatusDescription": "已审核",
    //           "districtId": "202509051421122485210000",
    //           "address": "云鹤镇钟鼓楼西50米金港商业城地下金港名车",
    //           "longitude": 100.17422,
    //           "latitude": 26.562504,
    //           "businessLicenseType": "BUSINESS_LICENSE",
    //           "businessLicenseTypeDescription": "营业执照",
    //           "businessLicenseNumber": "****",
    //           "firstBusinessLicensePicFileId": null,
    //           "secondBusinessLicensePicFileId": null,
    //           "thirdBusinessLicensePicFileId": null,
    //           "directSuperiorDistributor": {
    //             "flagList": [],
    //             "id": "202509230730592586830000",
    //             "createTimestamp": 1758612659000,
    //             "updateTimestamp": 1758612694000,
    //             "type": "DISTRIBUTOR",
    //             "typeDescription": "分销商",
    //             "level": 1,
    //             "directSuperiorDistributorId": null,
    //             "fullName": "驰捷车多管（云南）企业管理有限公司",
    //             "approvalStatus": "APPROVED",
    //             "approvalStatusDescription": "已审核",
    //             "districtId": "202509051421033585210000",
    //             "address": "云南省昆明市西山区西坝路639号",
    //             "longitude": 0.0,
    //             "latitude": 0.0,
    //             "businessLicenseType": "BUSINESS_LICENSE",
    //             "businessLicenseTypeDescription": "营业执照",
    //             "businessLicenseNumber": "91310118560192303A",
    //             "firstBusinessLicensePicFileId": null,
    //             "secondBusinessLicensePicFileId": null,
    //             "thirdBusinessLicensePicFileId": null,
    //             "directSuperiorDistributor": null,
    //             "district": {
    //               "flagList": [],
    //               "id": "202509051421033585210000",
    //               "createTimestamp": 1757082063000,
    //               "updateTimestamp": 1757082063000,
    //               "cityId": "202509051421030835210000",
    //               "regionalismCode": "530112",
    //               "localizedFullName": "西山区",
    //               "fullName": "西山区",
    //               "fullNameZhCn": "西山区",
    //               "city": {
    //                 "flagList": [],
    //                 "id": "202509051421030835210000",
    //                 "createTimestamp": 1757082063000,
    //                 "updateTimestamp": 1757082063000,
    //                 "provinceId": "202509051421030075210000",
    //                 "regionalismCode": "530100",
    //                 "localizedFullName": "昆明市",
    //                 "fullName": "昆明市",
    //                 "fullNameZhCn": "昆明市",
    //                 "province": {
    //                   "flagList": [],
    //                   "id": "202509051421030075210000",
    //                   "createTimestamp": 1757082063000,
    //                   "updateTimestamp": 1757082063000,
    //                   "countryId": "202509051417566675210000",
    //                   "regionalismCode": "530000",
    //                   "localizedFullName": "云南省",
    //                   "fullName": "云南省",
    //                   "fullNameZhCn": "云南省",
    //                   "country": {
    //                     "flagList": [],
    //                     "id": "202509051417566675210000",
    //                     "createTimestamp": 1757081877000,
    //                     "updateTimestamp": 1757081877000,
    //                     "localizedFullName": "中华人民共和国",
    //                     "fullName": "中华人民共和国",
    //                     "fullNameZhCn": "中华人民共和国"
    //                   }
    //                 }
    //               }
    //             },
    //             "firstBusinessLicensePicFile": null,
    //             "secondBusinessLicensePicFile": null,
    //             "thirdBusinessLicensePicFile": null,
    //             "userPartyType": "DISTRIBUTOR",
    //             "userPartyTypeDescription": "分销商",
    //             "merchantPartyType": "DISTRIBUTOR",
    //             "merchantPartyTypeDescription": "分销商",
    //             "businessPartyTypeDescription": "分销商",
    //             "businessPartyType": "DISTRIBUTOR",
    //             "warrantyConfigRefTargetTypeDescription": "分销商",
    //             "warrantyConfigRefTargetType": "DISTRIBUTOR",
    //             "warehouseOwningPartyTypeDescription": "分销商",
    //             "warehouseOwningPartyType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceTypeDescription": "分销商",
    //             "warehouseInventoryOutboundTargetType": "DISTRIBUTOR",
    //             "warehouseInventoryOutboundTargetTypeDescription": "分销商",
    //             "productRefTargetTypeDescription": "分销商",
    //             "productRefTargetType": "DISTRIBUTOR",
    //             "sellerPartyTypeDescription": "分销商",
    //             "sellerPartyType": "DISTRIBUTOR",
    //             "buyerPartyTypeDescription": "分销商",
    //             "buyerPartyType": "DISTRIBUTOR"
    //           },
    //           "district": {
    //             "flagList": [],
    //             "id": "202509051421122485210000",
    //             "createTimestamp": 1757082072000,
    //             "updateTimestamp": 1757082072000,
    //             "cityId": "202509051421113855210000",
    //             "regionalismCode": "532932",
    //             "localizedFullName": "鹤庆县",
    //             "fullName": "鹤庆县",
    //             "fullNameZhCn": "鹤庆县",
    //             "city": {
    //               "flagList": [],
    //               "id": "202509051421113855210000",
    //               "createTimestamp": 1757082071000,
    //               "updateTimestamp": 1757082071000,
    //               "provinceId": "202509051421030075210000",
    //               "regionalismCode": "532900",
    //               "localizedFullName": "大理白族自治州",
    //               "fullName": "大理白族自治州",
    //               "fullNameZhCn": "大理白族自治州",
    //               "province": {
    //                 "flagList": [],
    //                 "id": "202509051421030075210000",
    //                 "createTimestamp": 1757082063000,
    //                 "updateTimestamp": 1757082063000,
    //                 "countryId": "202509051417566675210000",
    //                 "regionalismCode": "530000",
    //                 "localizedFullName": "云南省",
    //                 "fullName": "云南省",
    //                 "fullNameZhCn": "云南省",
    //                 "country": {
    //                   "flagList": [],
    //                   "id": "202509051417566675210000",
    //                   "createTimestamp": 1757081877000,
    //                   "updateTimestamp": 1757081877000,
    //                   "localizedFullName": "中华人民共和国",
    //                   "fullName": "中华人民共和国",
    //                   "fullNameZhCn": "中华人民共和国"
    //                 }
    //               }
    //             }
    //           },
    //           "firstBusinessLicensePicFile": null,
    //           "secondBusinessLicensePicFile": null,
    //           "thirdBusinessLicensePicFile": null,
    //           "userPartyType": "RETAILER",
    //           "userPartyTypeDescription": "零售商",
    //           "merchantPartyType": "RETAILER",
    //           "merchantPartyTypeDescription": "零售商",
    //           "businessPartyTypeDescription": "零售商",
    //           "businessPartyType": "RETAILER",
    //           "warrantyConfigRefTargetTypeDescription": "零售商",
    //           "warrantyConfigRefTargetType": "RETAILER",
    //           "warehouseOwningPartyTypeDescription": "零售商",
    //           "warehouseOwningPartyType": "RETAILER",
    //           "warehouseInventoryOutboundTargetType": "RETAILER",
    //           "warehouseInventoryOutboundTargetTypeDescription": "零售商",
    //           "productRefTargetTypeDescription": "零售商",
    //           "productRefTargetType": "RETAILER",
    //           "serviceStoreOwningPartyType": "RETAILER",
    //           "serviceStoreOwningPartyTypeDescription": "零售商",
    //           "serviceRefTargetTypeDescription": "零售商",
    //           "serviceRefTargetType": "RETAILER",
    //           "sellerPartyTypeDescription": "零售商",
    //           "sellerPartyType": "RETAILER",
    //           "buyerPartyTypeDescription": "零售商",
    //           "buyerPartyType": "RETAILER"
    //         },
    //         {
    //           "flagList": [],
    //           "id": "202604100553567489230000",
    //           "createTimestamp": 1775800437000,
    //           "updateTimestamp": 1776156793000,
    //           "directSuperiorDistributorId": "202603230837030730530000",
    //           "fullName": "南京中升沃茂汽车销售服务有限公司",
    //           "approvalStatus": "APPROVED",
    //           "approvalStatusDescription": "已审核",
    //           "districtId": "202509051418551415210000",
    //           "address": "上高路168号江苏世贸泰信东盛汽车贸易有限公司南京中升沃茂汽车销售服务有限公司",
    //           "longitude": 118.86827,
    //           "latitude": 31.98012,
    //           "businessLicenseType": "BUSINESS_LICENSE",
    //           "businessLicenseTypeDescription": "营业执照",
    //           "businessLicenseNumber": "91320115MA1YLQ8C42",
    //           "firstBusinessLicensePicFileId": "202604100519002639230000",
    //           "secondBusinessLicensePicFileId": null,
    //           "thirdBusinessLicensePicFileId": null,
    //           "directSuperiorDistributor": {
    //             "flagList": [],
    //             "id": "202603230837030730530000",
    //             "createTimestamp": 1774255023000,
    //             "updateTimestamp": 1774255731000,
    //             "type": "DISTRIBUTOR",
    //             "typeDescription": "分销商",
    //             "level": 1,
    //             "directSuperiorDistributorId": null,
    //             "fullName": "兰考县翰峰信息科技有限公司",
    //             "approvalStatus": "APPROVED",
    //             "approvalStatusDescription": "已审核",
    //             "districtId": "202509051419452105210000",
    //             "address": "经济技术开发区兰考县科技路南50米兰考同乐居（跨境）电商家居产业园469号",
    //             "longitude": 0.0,
    //             "latitude": 0.0,
    //             "businessLicenseType": "BUSINESS_LICENSE",
    //             "businessLicenseTypeDescription": "营业执照",
    //             "businessLicenseNumber": "91410225MA9LHPBQ66",
    //             "firstBusinessLicensePicFileId": null,
    //             "secondBusinessLicensePicFileId": null,
    //             "thirdBusinessLicensePicFileId": null,
    //             "directSuperiorDistributor": null,
    //             "district": {
    //               "flagList": [],
    //               "id": "202509051419452105210000",
    //               "createTimestamp": 1757081985000,
    //               "updateTimestamp": 1757081985000,
    //               "cityId": "202509051419445765210000",
    //               "regionalismCode": "410225",
    //               "localizedFullName": "兰考县",
    //               "fullName": "兰考县",
    //               "fullNameZhCn": "兰考县",
    //               "city": {
    //                 "flagList": [],
    //                 "id": "202509051419445765210000",
    //                 "createTimestamp": 1757081985000,
    //                 "updateTimestamp": 1757081985000,
    //                 "provinceId": "202509051419435825210000",
    //                 "regionalismCode": "410200",
    //                 "localizedFullName": "开封市",
    //                 "fullName": "开封市",
    //                 "fullNameZhCn": "开封市",
    //                 "province": {
    //                   "flagList": [],
    //                   "id": "202509051419435825210000",
    //                   "createTimestamp": 1757081984000,
    //                   "updateTimestamp": 1757081984000,
    //                   "countryId": "202509051417566675210000",
    //                   "regionalismCode": "410000",
    //                   "localizedFullName": "河南省",
    //                   "fullName": "河南省",
    //                   "fullNameZhCn": "河南省",
    //                   "country": {
    //                     "flagList": [],
    //                     "id": "202509051417566675210000",
    //                     "createTimestamp": 1757081877000,
    //                     "updateTimestamp": 1757081877000,
    //                     "localizedFullName": "中华人民共和国",
    //                     "fullName": "中华人民共和国",
    //                     "fullNameZhCn": "中华人民共和国"
    //                   }
    //                 }
    //               }
    //             },
    //             "firstBusinessLicensePicFile": null,
    //             "secondBusinessLicensePicFile": null,
    //             "thirdBusinessLicensePicFile": null,
    //             "userPartyType": "DISTRIBUTOR",
    //             "userPartyTypeDescription": "分销商",
    //             "merchantPartyType": "DISTRIBUTOR",
    //             "merchantPartyTypeDescription": "分销商",
    //             "businessPartyTypeDescription": "分销商",
    //             "businessPartyType": "DISTRIBUTOR",
    //             "warrantyConfigRefTargetTypeDescription": "分销商",
    //             "warrantyConfigRefTargetType": "DISTRIBUTOR",
    //             "warehouseOwningPartyTypeDescription": "分销商",
    //             "warehouseOwningPartyType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceTypeDescription": "分销商",
    //             "warehouseInventoryOutboundTargetType": "DISTRIBUTOR",
    //             "warehouseInventoryOutboundTargetTypeDescription": "分销商",
    //             "productRefTargetTypeDescription": "分销商",
    //             "productRefTargetType": "DISTRIBUTOR",
    //             "sellerPartyTypeDescription": "分销商",
    //             "sellerPartyType": "DISTRIBUTOR",
    //             "buyerPartyTypeDescription": "分销商",
    //             "buyerPartyType": "DISTRIBUTOR"
    //           },
    //           "district": {
    //             "flagList": [],
    //             "id": "202509051418551415210000",
    //             "createTimestamp": 1757081935000,
    //             "updateTimestamp": 1757081935000,
    //             "cityId": "202509051418545735210000",
    //             "regionalismCode": "320115",
    //             "localizedFullName": "江宁区",
    //             "fullName": "江宁区",
    //             "fullNameZhCn": "江宁区",
    //             "city": {
    //               "flagList": [],
    //               "id": "202509051418545735210000",
    //               "createTimestamp": 1757081935000,
    //               "updateTimestamp": 1757081935000,
    //               "provinceId": "202509051418545015210000",
    //               "regionalismCode": "320100",
    //               "localizedFullName": "南京市",
    //               "fullName": "南京市",
    //               "fullNameZhCn": "南京市",
    //               "province": {
    //                 "flagList": [],
    //                 "id": "202509051418545015210000",
    //                 "createTimestamp": 1757081935000,
    //                 "updateTimestamp": 1757081935000,
    //                 "countryId": "202509051417566675210000",
    //                 "regionalismCode": "320000",
    //                 "localizedFullName": "江苏省",
    //                 "fullName": "江苏省",
    //                 "fullNameZhCn": "江苏省",
    //                 "country": {
    //                   "flagList": [],
    //                   "id": "202509051417566675210000",
    //                   "createTimestamp": 1757081877000,
    //                   "updateTimestamp": 1757081877000,
    //                   "localizedFullName": "中华人民共和国",
    //                   "fullName": "中华人民共和国",
    //                   "fullNameZhCn": "中华人民共和国"
    //                 }
    //               }
    //             }
    //           },
    //           "firstBusinessLicensePicFile": {
    //             "flagList": [],
    //             "id": "202604100519002639230000",
    //             "createTimestamp": 1775798340000,
    //             "updateTimestamp": 1775798340000,
    //             "format": "JPEG",
    //             "formatDescription": "JPEG",
    //             "width": 1280,
    //             "height": 1707,
    //             "ossBucket": "goldenkiwiprod",
    //             "ossPath": "nbiz/retailer/2026/04/10/05/1280_1707_5a68b810df77f74ab58490cebf2f1cb3.jpeg",
    //             "url": "https://img.youbanban.com/oss/download/nbiz/retailer/2026/04/10/05/1280_1707_5a68b810df77f74ab58490cebf2f1cb3.jpeg",
    //             "fileTypeDescription": "图片",
    //             "fileType": "PIC"
    //           },
    //           "secondBusinessLicensePicFile": null,
    //           "thirdBusinessLicensePicFile": null,
    //           "userPartyType": "RETAILER",
    //           "userPartyTypeDescription": "零售商",
    //           "merchantPartyType": "RETAILER",
    //           "merchantPartyTypeDescription": "零售商",
    //           "businessPartyTypeDescription": "零售商",
    //           "businessPartyType": "RETAILER",
    //           "warrantyConfigRefTargetTypeDescription": "零售商",
    //           "warrantyConfigRefTargetType": "RETAILER",
    //           "warehouseOwningPartyTypeDescription": "零售商",
    //           "warehouseOwningPartyType": "RETAILER",
    //           "warehouseInventoryOutboundTargetType": "RETAILER",
    //           "warehouseInventoryOutboundTargetTypeDescription": "零售商",
    //           "productRefTargetTypeDescription": "零售商",
    //           "productRefTargetType": "RETAILER",
    //           "serviceStoreOwningPartyType": "RETAILER",
    //           "serviceStoreOwningPartyTypeDescription": "零售商",
    //           "serviceRefTargetTypeDescription": "零售商",
    //           "serviceRefTargetType": "RETAILER",
    //           "sellerPartyTypeDescription": "零售商",
    //           "sellerPartyType": "RETAILER",
    //           "buyerPartyTypeDescription": "零售商",
    //           "buyerPartyType": "RETAILER"
    //         },
    //         {
    //           "flagList": [],
    //           "id": "202604120706033429230000",
    //           "createTimestamp": 1775977563000,
    //           "updateTimestamp": 1776156793000,
    //           "directSuperiorDistributorId": "202603230837030730530000",
    //           "fullName": "北京中升之星汽车销售服务有限公司",
    //           "approvalStatus": "APPROVED",
    //           "approvalStatusDescription": "已审核",
    //           "districtId": "202509051417571115210000",
    //           "address": "北京市朝阳区王四营马房寺路333号",
    //           "longitude": 116.529288,
    //           "latitude": 39.868986,
    //           "businessLicenseType": "BUSINESS_LICENSE",
    //           "businessLicenseTypeDescription": "营业执照",
    //           "businessLicenseNumber": "91110105668436461T",
    //           "firstBusinessLicensePicFileId": null,
    //           "secondBusinessLicensePicFileId": null,
    //           "thirdBusinessLicensePicFileId": null,
    //           "directSuperiorDistributor": {
    //             "flagList": [],
    //             "id": "202603230837030730530000",
    //             "createTimestamp": 1774255023000,
    //             "updateTimestamp": 1774255731000,
    //             "type": "DISTRIBUTOR",
    //             "typeDescription": "分销商",
    //             "level": 1,
    //             "directSuperiorDistributorId": null,
    //             "fullName": "兰考县翰峰信息科技有限公司",
    //             "approvalStatus": "APPROVED",
    //             "approvalStatusDescription": "已审核",
    //             "districtId": "202509051419452105210000",
    //             "address": "经济技术开发区兰考县科技路南50米兰考同乐居（跨境）电商家居产业园469号",
    //             "longitude": 0.0,
    //             "latitude": 0.0,
    //             "businessLicenseType": "BUSINESS_LICENSE",
    //             "businessLicenseTypeDescription": "营业执照",
    //             "businessLicenseNumber": "91410225MA9LHPBQ66",
    //             "firstBusinessLicensePicFileId": null,
    //             "secondBusinessLicensePicFileId": null,
    //             "thirdBusinessLicensePicFileId": null,
    //             "directSuperiorDistributor": null,
    //             "district": {
    //               "flagList": [],
    //               "id": "202509051419452105210000",
    //               "createTimestamp": 1757081985000,
    //               "updateTimestamp": 1757081985000,
    //               "cityId": "202509051419445765210000",
    //               "regionalismCode": "410225",
    //               "localizedFullName": "兰考县",
    //               "fullName": "兰考县",
    //               "fullNameZhCn": "兰考县",
    //               "city": {
    //                 "flagList": [],
    //                 "id": "202509051419445765210000",
    //                 "createTimestamp": 1757081985000,
    //                 "updateTimestamp": 1757081985000,
    //                 "provinceId": "202509051419435825210000",
    //                 "regionalismCode": "410200",
    //                 "localizedFullName": "开封市",
    //                 "fullName": "开封市",
    //                 "fullNameZhCn": "开封市",
    //                 "province": {
    //                   "flagList": [],
    //                   "id": "202509051419435825210000",
    //                   "createTimestamp": 1757081984000,
    //                   "updateTimestamp": 1757081984000,
    //                   "countryId": "202509051417566675210000",
    //                   "regionalismCode": "410000",
    //                   "localizedFullName": "河南省",
    //                   "fullName": "河南省",
    //                   "fullNameZhCn": "河南省",
    //                   "country": {
    //                     "flagList": [],
    //                     "id": "202509051417566675210000",
    //                     "createTimestamp": 1757081877000,
    //                     "updateTimestamp": 1757081877000,
    //                     "localizedFullName": "中华人民共和国",
    //                     "fullName": "中华人民共和国",
    //                     "fullNameZhCn": "中华人民共和国"
    //                   }
    //                 }
    //               }
    //             },
    //             "firstBusinessLicensePicFile": null,
    //             "secondBusinessLicensePicFile": null,
    //             "thirdBusinessLicensePicFile": null,
    //             "userPartyType": "DISTRIBUTOR",
    //             "userPartyTypeDescription": "分销商",
    //             "merchantPartyType": "DISTRIBUTOR",
    //             "merchantPartyTypeDescription": "分销商",
    //             "businessPartyTypeDescription": "分销商",
    //             "businessPartyType": "DISTRIBUTOR",
    //             "warrantyConfigRefTargetTypeDescription": "分销商",
    //             "warrantyConfigRefTargetType": "DISTRIBUTOR",
    //             "warehouseOwningPartyTypeDescription": "分销商",
    //             "warehouseOwningPartyType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceTypeDescription": "分销商",
    //             "warehouseInventoryOutboundTargetType": "DISTRIBUTOR",
    //             "warehouseInventoryOutboundTargetTypeDescription": "分销商",
    //             "productRefTargetTypeDescription": "分销商",
    //             "productRefTargetType": "DISTRIBUTOR",
    //             "sellerPartyTypeDescription": "分销商",
    //             "sellerPartyType": "DISTRIBUTOR",
    //             "buyerPartyTypeDescription": "分销商",
    //             "buyerPartyType": "DISTRIBUTOR"
    //           },
    //           "district": {
    //             "flagList": [],
    //             "id": "202509051417571115210000",
    //             "createTimestamp": 1757081877000,
    //             "updateTimestamp": 1757081877000,
    //             "cityId": "202509051417568825210000",
    //             "regionalismCode": "110105",
    //             "localizedFullName": "朝阳区",
    //             "fullName": "朝阳区",
    //             "fullNameZhCn": "朝阳区",
    //             "city": {
    //               "flagList": [],
    //               "id": "202509051417568825210000",
    //               "createTimestamp": 1757081877000,
    //               "updateTimestamp": 1757081877000,
    //               "provinceId": "202509051417568035210000",
    //               "regionalismCode": "110000",
    //               "localizedFullName": "北京市",
    //               "fullName": "北京市",
    //               "fullNameZhCn": "北京市",
    //               "province": {
    //                 "flagList": [],
    //                 "id": "202509051417568035210000",
    //                 "createTimestamp": 1757081877000,
    //                 "updateTimestamp": 1757081877000,
    //                 "countryId": "202509051417566675210000",
    //                 "regionalismCode": "110000",
    //                 "localizedFullName": "北京市",
    //                 "fullName": "北京市",
    //                 "fullNameZhCn": "北京市",
    //                 "country": {
    //                   "flagList": [],
    //                   "id": "202509051417566675210000",
    //                   "createTimestamp": 1757081877000,
    //                   "updateTimestamp": 1757081877000,
    //                   "localizedFullName": "中华人民共和国",
    //                   "fullName": "中华人民共和国",
    //                   "fullNameZhCn": "中华人民共和国"
    //                 }
    //               }
    //             }
    //           },
    //           "firstBusinessLicensePicFile": null,
    //           "secondBusinessLicensePicFile": null,
    //           "thirdBusinessLicensePicFile": null,
    //           "userPartyType": "RETAILER",
    //           "userPartyTypeDescription": "零售商",
    //           "merchantPartyType": "RETAILER",
    //           "merchantPartyTypeDescription": "零售商",
    //           "businessPartyTypeDescription": "零售商",
    //           "businessPartyType": "RETAILER",
    //           "warrantyConfigRefTargetTypeDescription": "零售商",
    //           "warrantyConfigRefTargetType": "RETAILER",
    //           "warehouseOwningPartyTypeDescription": "零售商",
    //           "warehouseOwningPartyType": "RETAILER",
    //           "warehouseInventoryOutboundTargetType": "RETAILER",
    //           "warehouseInventoryOutboundTargetTypeDescription": "零售商",
    //           "productRefTargetTypeDescription": "零售商",
    //           "productRefTargetType": "RETAILER",
    //           "serviceStoreOwningPartyType": "RETAILER",
    //           "serviceStoreOwningPartyTypeDescription": "零售商",
    //           "serviceRefTargetTypeDescription": "零售商",
    //           "serviceRefTargetType": "RETAILER",
    //           "sellerPartyTypeDescription": "零售商",
    //           "sellerPartyType": "RETAILER",
    //           "buyerPartyTypeDescription": "零售商",
    //           "buyerPartyType": "RETAILER"
    //         },
    //         {
    //           "flagList": [],
    //           "id": "202604240758249120340000",
    //           "createTimestamp": 1777017505000,
    //           "updateTimestamp": 1777019889000,
    //           "directSuperiorDistributorId": "202603230837030730530000",
    //           "fullName": "重庆中升之宝汽车销售服务有限公司",
    //           "approvalStatus": "APPROVED",
    //           "approvalStatusDescription": "已审核",
    //           "districtId": "202509051420385295210000",
    //           "address": "重庆市九龙坡区谢家湾民主四村（九滨路）",
    //           "longitude": 106.520547,
    //           "latitude": 29.530106,
    //           "businessLicenseType": "BUSINESS_LICENSE",
    //           "businessLicenseTypeDescription": "营业执照",
    //           "businessLicenseNumber": "9150010755408447XX",
    //           "firstBusinessLicensePicFileId": "202604240749030210340000",
    //           "secondBusinessLicensePicFileId": null,
    //           "thirdBusinessLicensePicFileId": null,
    //           "directSuperiorDistributor": {
    //             "flagList": [],
    //             "id": "202603230837030730530000",
    //             "createTimestamp": 1774255023000,
    //             "updateTimestamp": 1774255731000,
    //             "type": "DISTRIBUTOR",
    //             "typeDescription": "分销商",
    //             "level": 1,
    //             "directSuperiorDistributorId": null,
    //             "fullName": "兰考县翰峰信息科技有限公司",
    //             "approvalStatus": "APPROVED",
    //             "approvalStatusDescription": "已审核",
    //             "districtId": "202509051419452105210000",
    //             "address": "经济技术开发区兰考县科技路南50米兰考同乐居（跨境）电商家居产业园469号",
    //             "longitude": 0.0,
    //             "latitude": 0.0,
    //             "businessLicenseType": "BUSINESS_LICENSE",
    //             "businessLicenseTypeDescription": "营业执照",
    //             "businessLicenseNumber": "91410225MA9LHPBQ66",
    //             "firstBusinessLicensePicFileId": null,
    //             "secondBusinessLicensePicFileId": null,
    //             "thirdBusinessLicensePicFileId": null,
    //             "directSuperiorDistributor": null,
    //             "district": {
    //               "flagList": [],
    //               "id": "202509051419452105210000",
    //               "createTimestamp": 1757081985000,
    //               "updateTimestamp": 1757081985000,
    //               "cityId": "202509051419445765210000",
    //               "regionalismCode": "410225",
    //               "localizedFullName": "兰考县",
    //               "fullName": "兰考县",
    //               "fullNameZhCn": "兰考县",
    //               "city": {
    //                 "flagList": [],
    //                 "id": "202509051419445765210000",
    //                 "createTimestamp": 1757081985000,
    //                 "updateTimestamp": 1757081985000,
    //                 "provinceId": "202509051419435825210000",
    //                 "regionalismCode": "410200",
    //                 "localizedFullName": "开封市",
    //                 "fullName": "开封市",
    //                 "fullNameZhCn": "开封市",
    //                 "province": {
    //                   "flagList": [],
    //                   "id": "202509051419435825210000",
    //                   "createTimestamp": 1757081984000,
    //                   "updateTimestamp": 1757081984000,
    //                   "countryId": "202509051417566675210000",
    //                   "regionalismCode": "410000",
    //                   "localizedFullName": "河南省",
    //                   "fullName": "河南省",
    //                   "fullNameZhCn": "河南省",
    //                   "country": {
    //                     "flagList": [],
    //                     "id": "202509051417566675210000",
    //                     "createTimestamp": 1757081877000,
    //                     "updateTimestamp": 1757081877000,
    //                     "localizedFullName": "中华人民共和国",
    //                     "fullName": "中华人民共和国",
    //                     "fullNameZhCn": "中华人民共和国"
    //                   }
    //                 }
    //               }
    //             },
    //             "firstBusinessLicensePicFile": null,
    //             "secondBusinessLicensePicFile": null,
    //             "thirdBusinessLicensePicFile": null,
    //             "userPartyType": "DISTRIBUTOR",
    //             "userPartyTypeDescription": "分销商",
    //             "merchantPartyType": "DISTRIBUTOR",
    //             "merchantPartyTypeDescription": "分销商",
    //             "businessPartyTypeDescription": "分销商",
    //             "businessPartyType": "DISTRIBUTOR",
    //             "warrantyConfigRefTargetTypeDescription": "分销商",
    //             "warrantyConfigRefTargetType": "DISTRIBUTOR",
    //             "warehouseOwningPartyTypeDescription": "分销商",
    //             "warehouseOwningPartyType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceTypeDescription": "分销商",
    //             "warehouseInventoryOutboundTargetType": "DISTRIBUTOR",
    //             "warehouseInventoryOutboundTargetTypeDescription": "分销商",
    //             "productRefTargetTypeDescription": "分销商",
    //             "productRefTargetType": "DISTRIBUTOR",
    //             "sellerPartyTypeDescription": "分销商",
    //             "sellerPartyType": "DISTRIBUTOR",
    //             "buyerPartyTypeDescription": "分销商",
    //             "buyerPartyType": "DISTRIBUTOR"
    //           },
    //           "district": {
    //             "flagList": [],
    //             "id": "202509051420385295210000",
    //             "createTimestamp": 1757082039000,
    //             "updateTimestamp": 1757082039000,
    //             "cityId": "202509051420380265210000",
    //             "regionalismCode": "500107",
    //             "localizedFullName": "九龙坡区",
    //             "fullName": "九龙坡区",
    //             "fullNameZhCn": "九龙坡区",
    //             "city": {
    //               "flagList": [],
    //               "id": "202509051420380265210000",
    //               "createTimestamp": 1757082038000,
    //               "updateTimestamp": 1757082038000,
    //               "provinceId": "202509051420379565210000",
    //               "regionalismCode": "500000",
    //               "localizedFullName": "重庆市",
    //               "fullName": "重庆市",
    //               "fullNameZhCn": "重庆市",
    //               "province": {
    //                 "flagList": [],
    //                 "id": "202509051420379565210000",
    //                 "createTimestamp": 1757082038000,
    //                 "updateTimestamp": 1757082038000,
    //                 "countryId": "202509051417566675210000",
    //                 "regionalismCode": "500000",
    //                 "localizedFullName": "重庆市",
    //                 "fullName": "重庆市",
    //                 "fullNameZhCn": "重庆市",
    //                 "country": {
    //                   "flagList": [],
    //                   "id": "202509051417566675210000",
    //                   "createTimestamp": 1757081877000,
    //                   "updateTimestamp": 1757081877000,
    //                   "localizedFullName": "中华人民共和国",
    //                   "fullName": "中华人民共和国",
    //                   "fullNameZhCn": "中华人民共和国"
    //                 }
    //               }
    //             }
    //           },
    //           "firstBusinessLicensePicFile": {
    //             "flagList": [],
    //             "id": "202604240749030210340000",
    //             "createTimestamp": 1777016943000,
    //             "updateTimestamp": 1777016943000,
    //             "format": "JPEG",
    //             "formatDescription": "JPEG",
    //             "width": 810,
    //             "height": 1440,
    //             "ossBucket": "goldenkiwiprod",
    //             "ossPath": "nbiz/retailer/2026/04/24/07/810_1440_f5f1054692d684a43067da29e2bad88e.jpeg",
    //             "url": "https://img.youbanban.com/oss/download/nbiz/retailer/2026/04/24/07/810_1440_f5f1054692d684a43067da29e2bad88e.jpeg",
    //             "fileTypeDescription": "图片",
    //             "fileType": "PIC"
    //           },
    //           "secondBusinessLicensePicFile": null,
    //           "thirdBusinessLicensePicFile": null,
    //           "userPartyType": "RETAILER",
    //           "userPartyTypeDescription": "零售商",
    //           "merchantPartyType": "RETAILER",
    //           "merchantPartyTypeDescription": "零售商",
    //           "businessPartyTypeDescription": "零售商",
    //           "businessPartyType": "RETAILER",
    //           "warrantyConfigRefTargetTypeDescription": "零售商",
    //           "warrantyConfigRefTargetType": "RETAILER",
    //           "warehouseOwningPartyTypeDescription": "零售商",
    //           "warehouseOwningPartyType": "RETAILER",
    //           "warehouseInventoryOutboundTargetType": "RETAILER",
    //           "warehouseInventoryOutboundTargetTypeDescription": "零售商",
    //           "productRefTargetTypeDescription": "零售商",
    //           "productRefTargetType": "RETAILER",
    //           "serviceStoreOwningPartyType": "RETAILER",
    //           "serviceStoreOwningPartyTypeDescription": "零售商",
    //           "serviceRefTargetTypeDescription": "零售商",
    //           "serviceRefTargetType": "RETAILER",
    //           "sellerPartyTypeDescription": "零售商",
    //           "sellerPartyType": "RETAILER",
    //           "buyerPartyTypeDescription": "零售商",
    //           "buyerPartyType": "RETAILER"
    //         },
    //         {
    //           "flagList": [],
    //           "id": "202604280729287070340000",
    //           "createTimestamp": 1777361369000,
    //           "updateTimestamp": 1777366802000,
    //           "directSuperiorDistributorId": "202603230837030730530000",
    //           "fullName": "大连中升丰田汽车销售服务有限公司",
    //           "approvalStatus": "APPROVED",
    //           "approvalStatusDescription": "已审核",
    //           "districtId": "202509051418317075210000",
    //           "address": "华北路759号",
    //           "longitude": 121.61141,
    //           "latitude": 39.01548,
    //           "businessLicenseType": "BUSINESS_LICENSE",
    //           "businessLicenseTypeDescription": "营业执照",
    //           "businessLicenseNumber": "91210200604867721Y",
    //           "firstBusinessLicensePicFileId": "202604280728506760340000",
    //           "secondBusinessLicensePicFileId": null,
    //           "thirdBusinessLicensePicFileId": null,
    //           "directSuperiorDistributor": {
    //             "flagList": [],
    //             "id": "202603230837030730530000",
    //             "createTimestamp": 1774255023000,
    //             "updateTimestamp": 1774255731000,
    //             "type": "DISTRIBUTOR",
    //             "typeDescription": "分销商",
    //             "level": 1,
    //             "directSuperiorDistributorId": null,
    //             "fullName": "兰考县翰峰信息科技有限公司",
    //             "approvalStatus": "APPROVED",
    //             "approvalStatusDescription": "已审核",
    //             "districtId": "202509051419452105210000",
    //             "address": "经济技术开发区兰考县科技路南50米兰考同乐居（跨境）电商家居产业园469号",
    //             "longitude": 0.0,
    //             "latitude": 0.0,
    //             "businessLicenseType": "BUSINESS_LICENSE",
    //             "businessLicenseTypeDescription": "营业执照",
    //             "businessLicenseNumber": "91410225MA9LHPBQ66",
    //             "firstBusinessLicensePicFileId": null,
    //             "secondBusinessLicensePicFileId": null,
    //             "thirdBusinessLicensePicFileId": null,
    //             "directSuperiorDistributor": null,
    //             "district": {
    //               "flagList": [],
    //               "id": "202509051419452105210000",
    //               "createTimestamp": 1757081985000,
    //               "updateTimestamp": 1757081985000,
    //               "cityId": "202509051419445765210000",
    //               "regionalismCode": "410225",
    //               "localizedFullName": "兰考县",
    //               "fullName": "兰考县",
    //               "fullNameZhCn": "兰考县",
    //               "city": {
    //                 "flagList": [],
    //                 "id": "202509051419445765210000",
    //                 "createTimestamp": 1757081985000,
    //                 "updateTimestamp": 1757081985000,
    //                 "provinceId": "202509051419435825210000",
    //                 "regionalismCode": "410200",
    //                 "localizedFullName": "开封市",
    //                 "fullName": "开封市",
    //                 "fullNameZhCn": "开封市",
    //                 "province": {
    //                   "flagList": [],
    //                   "id": "202509051419435825210000",
    //                   "createTimestamp": 1757081984000,
    //                   "updateTimestamp": 1757081984000,
    //                   "countryId": "202509051417566675210000",
    //                   "regionalismCode": "410000",
    //                   "localizedFullName": "河南省",
    //                   "fullName": "河南省",
    //                   "fullNameZhCn": "河南省",
    //                   "country": {
    //                     "flagList": [],
    //                     "id": "202509051417566675210000",
    //                     "createTimestamp": 1757081877000,
    //                     "updateTimestamp": 1757081877000,
    //                     "localizedFullName": "中华人民共和国",
    //                     "fullName": "中华人民共和国",
    //                     "fullNameZhCn": "中华人民共和国"
    //                   }
    //                 }
    //               }
    //             },
    //             "firstBusinessLicensePicFile": null,
    //             "secondBusinessLicensePicFile": null,
    //             "thirdBusinessLicensePicFile": null,
    //             "userPartyType": "DISTRIBUTOR",
    //             "userPartyTypeDescription": "分销商",
    //             "merchantPartyType": "DISTRIBUTOR",
    //             "merchantPartyTypeDescription": "分销商",
    //             "businessPartyTypeDescription": "分销商",
    //             "businessPartyType": "DISTRIBUTOR",
    //             "warrantyConfigRefTargetTypeDescription": "分销商",
    //             "warrantyConfigRefTargetType": "DISTRIBUTOR",
    //             "warehouseOwningPartyTypeDescription": "分销商",
    //             "warehouseOwningPartyType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceTypeDescription": "分销商",
    //             "warehouseInventoryOutboundTargetType": "DISTRIBUTOR",
    //             "warehouseInventoryOutboundTargetTypeDescription": "分销商",
    //             "productRefTargetTypeDescription": "分销商",
    //             "productRefTargetType": "DISTRIBUTOR",
    //             "sellerPartyTypeDescription": "分销商",
    //             "sellerPartyType": "DISTRIBUTOR",
    //             "buyerPartyTypeDescription": "分销商",
    //             "buyerPartyType": "DISTRIBUTOR"
    //           },
    //           "district": {
    //             "flagList": [],
    //             "id": "202509051418317075210000",
    //             "createTimestamp": 1757081912000,
    //             "updateTimestamp": 1757081912000,
    //             "cityId": "202509051418314265210000",
    //             "regionalismCode": "210211",
    //             "localizedFullName": "甘井子区",
    //             "fullName": "甘井子区",
    //             "fullNameZhCn": "甘井子区",
    //             "city": {
    //               "flagList": [],
    //               "id": "202509051418314265210000",
    //               "createTimestamp": 1757081911000,
    //               "updateTimestamp": 1757081911000,
    //               "provinceId": "202509051418303305210000",
    //               "regionalismCode": "210200",
    //               "localizedFullName": "大连市",
    //               "fullName": "大连市",
    //               "fullNameZhCn": "大连市",
    //               "province": {
    //                 "flagList": [],
    //                 "id": "202509051418303305210000",
    //                 "createTimestamp": 1757081910000,
    //                 "updateTimestamp": 1757081910000,
    //                 "countryId": "202509051417566675210000",
    //                 "regionalismCode": "210000",
    //                 "localizedFullName": "辽宁省",
    //                 "fullName": "辽宁省",
    //                 "fullNameZhCn": "辽宁省",
    //                 "country": {
    //                   "flagList": [],
    //                   "id": "202509051417566675210000",
    //                   "createTimestamp": 1757081877000,
    //                   "updateTimestamp": 1757081877000,
    //                   "localizedFullName": "中华人民共和国",
    //                   "fullName": "中华人民共和国",
    //                   "fullNameZhCn": "中华人民共和国"
    //                 }
    //               }
    //             }
    //           },
    //           "firstBusinessLicensePicFile": {
    //             "flagList": [],
    //             "id": "202604280728506760340000",
    //             "createTimestamp": 1777361331000,
    //             "updateTimestamp": 1777361331000,
    //             "format": "JPEG",
    //             "formatDescription": "JPEG",
    //             "width": 1275,
    //             "height": 905,
    //             "ossBucket": "goldenkiwiprod",
    //             "ossPath": "nbiz/retailer/2026/04/28/07/1275_905_a1e19a49ea70695c301af57b3d3e7cf4.jpeg",
    //             "url": "https://img.youbanban.com/oss/download/nbiz/retailer/2026/04/28/07/1275_905_a1e19a49ea70695c301af57b3d3e7cf4.jpeg",
    //             "fileTypeDescription": "图片",
    //             "fileType": "PIC"
    //           },
    //           "secondBusinessLicensePicFile": null,
    //           "thirdBusinessLicensePicFile": null,
    //           "userPartyType": "RETAILER",
    //           "userPartyTypeDescription": "零售商",
    //           "merchantPartyType": "RETAILER",
    //           "merchantPartyTypeDescription": "零售商",
    //           "businessPartyTypeDescription": "零售商",
    //           "businessPartyType": "RETAILER",
    //           "warrantyConfigRefTargetTypeDescription": "零售商",
    //           "warrantyConfigRefTargetType": "RETAILER",
    //           "warehouseOwningPartyTypeDescription": "零售商",
    //           "warehouseOwningPartyType": "RETAILER",
    //           "warehouseInventoryOutboundTargetType": "RETAILER",
    //           "warehouseInventoryOutboundTargetTypeDescription": "零售商",
    //           "productRefTargetTypeDescription": "零售商",
    //           "productRefTargetType": "RETAILER",
    //           "serviceStoreOwningPartyType": "RETAILER",
    //           "serviceStoreOwningPartyTypeDescription": "零售商",
    //           "serviceRefTargetTypeDescription": "零售商",
    //           "serviceRefTargetType": "RETAILER",
    //           "sellerPartyTypeDescription": "零售商",
    //           "sellerPartyType": "RETAILER",
    //           "buyerPartyTypeDescription": "零售商",
    //           "buyerPartyType": "RETAILER"
    //         },
    //         {
    //           "flagList": [],
    //           "id": "202604280834345900340000",
    //           "createTimestamp": 1777365275000,
    //           "updateTimestamp": 1777366802000,
    //           "directSuperiorDistributorId": "202603230837030730530000",
    //           "fullName": "大连新盛荣奥迪汽车销售服务有限公司",
    //           "approvalStatus": "APPROVED",
    //           "approvalStatusDescription": "已审核",
    //           "districtId": "202509051418316375210000",
    //           "address": "太原街357-2号",
    //           "longitude": 121.5897,
    //           "latitude": 38.89282,
    //           "businessLicenseType": "BUSINESS_LICENSE",
    //           "businessLicenseTypeDescription": "营业执照",
    //           "businessLicenseNumber": "912200760768330",
    //           "firstBusinessLicensePicFileId": null,
    //           "secondBusinessLicensePicFileId": null,
    //           "thirdBusinessLicensePicFileId": null,
    //           "directSuperiorDistributor": {
    //             "flagList": [],
    //             "id": "202603230837030730530000",
    //             "createTimestamp": 1774255023000,
    //             "updateTimestamp": 1774255731000,
    //             "type": "DISTRIBUTOR",
    //             "typeDescription": "分销商",
    //             "level": 1,
    //             "directSuperiorDistributorId": null,
    //             "fullName": "兰考县翰峰信息科技有限公司",
    //             "approvalStatus": "APPROVED",
    //             "approvalStatusDescription": "已审核",
    //             "districtId": "202509051419452105210000",
    //             "address": "经济技术开发区兰考县科技路南50米兰考同乐居（跨境）电商家居产业园469号",
    //             "longitude": 0.0,
    //             "latitude": 0.0,
    //             "businessLicenseType": "BUSINESS_LICENSE",
    //             "businessLicenseTypeDescription": "营业执照",
    //             "businessLicenseNumber": "91410225MA9LHPBQ66",
    //             "firstBusinessLicensePicFileId": null,
    //             "secondBusinessLicensePicFileId": null,
    //             "thirdBusinessLicensePicFileId": null,
    //             "directSuperiorDistributor": null,
    //             "district": {
    //               "flagList": [],
    //               "id": "202509051419452105210000",
    //               "createTimestamp": 1757081985000,
    //               "updateTimestamp": 1757081985000,
    //               "cityId": "202509051419445765210000",
    //               "regionalismCode": "410225",
    //               "localizedFullName": "兰考县",
    //               "fullName": "兰考县",
    //               "fullNameZhCn": "兰考县",
    //               "city": {
    //                 "flagList": [],
    //                 "id": "202509051419445765210000",
    //                 "createTimestamp": 1757081985000,
    //                 "updateTimestamp": 1757081985000,
    //                 "provinceId": "202509051419435825210000",
    //                 "regionalismCode": "410200",
    //                 "localizedFullName": "开封市",
    //                 "fullName": "开封市",
    //                 "fullNameZhCn": "开封市",
    //                 "province": {
    //                   "flagList": [],
    //                   "id": "202509051419435825210000",
    //                   "createTimestamp": 1757081984000,
    //                   "updateTimestamp": 1757081984000,
    //                   "countryId": "202509051417566675210000",
    //                   "regionalismCode": "410000",
    //                   "localizedFullName": "河南省",
    //                   "fullName": "河南省",
    //                   "fullNameZhCn": "河南省",
    //                   "country": {
    //                     "flagList": [],
    //                     "id": "202509051417566675210000",
    //                     "createTimestamp": 1757081877000,
    //                     "updateTimestamp": 1757081877000,
    //                     "localizedFullName": "中华人民共和国",
    //                     "fullName": "中华人民共和国",
    //                     "fullNameZhCn": "中华人民共和国"
    //                   }
    //                 }
    //               }
    //             },
    //             "firstBusinessLicensePicFile": null,
    //             "secondBusinessLicensePicFile": null,
    //             "thirdBusinessLicensePicFile": null,
    //             "userPartyType": "DISTRIBUTOR",
    //             "userPartyTypeDescription": "分销商",
    //             "merchantPartyType": "DISTRIBUTOR",
    //             "merchantPartyTypeDescription": "分销商",
    //             "businessPartyTypeDescription": "分销商",
    //             "businessPartyType": "DISTRIBUTOR",
    //             "warrantyConfigRefTargetTypeDescription": "分销商",
    //             "warrantyConfigRefTargetType": "DISTRIBUTOR",
    //             "warehouseOwningPartyTypeDescription": "分销商",
    //             "warehouseOwningPartyType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceTypeDescription": "分销商",
    //             "warehouseInventoryOutboundTargetType": "DISTRIBUTOR",
    //             "warehouseInventoryOutboundTargetTypeDescription": "分销商",
    //             "productRefTargetTypeDescription": "分销商",
    //             "productRefTargetType": "DISTRIBUTOR",
    //             "sellerPartyTypeDescription": "分销商",
    //             "sellerPartyType": "DISTRIBUTOR",
    //             "buyerPartyTypeDescription": "分销商",
    //             "buyerPartyType": "DISTRIBUTOR"
    //           },
    //           "district": {
    //             "flagList": [],
    //             "id": "202509051418316375210000",
    //             "createTimestamp": 1757081912000,
    //             "updateTimestamp": 1757081912000,
    //             "cityId": "202509051418314265210000",
    //             "regionalismCode": "210204",
    //             "localizedFullName": "沙河口区",
    //             "fullName": "沙河口区",
    //             "fullNameZhCn": "沙河口区",
    //             "city": {
    //               "flagList": [],
    //               "id": "202509051418314265210000",
    //               "createTimestamp": 1757081911000,
    //               "updateTimestamp": 1757081911000,
    //               "provinceId": "202509051418303305210000",
    //               "regionalismCode": "210200",
    //               "localizedFullName": "大连市",
    //               "fullName": "大连市",
    //               "fullNameZhCn": "大连市",
    //               "province": {
    //                 "flagList": [],
    //                 "id": "202509051418303305210000",
    //                 "createTimestamp": 1757081910000,
    //                 "updateTimestamp": 1757081910000,
    //                 "countryId": "202509051417566675210000",
    //                 "regionalismCode": "210000",
    //                 "localizedFullName": "辽宁省",
    //                 "fullName": "辽宁省",
    //                 "fullNameZhCn": "辽宁省",
    //                 "country": {
    //                   "flagList": [],
    //                   "id": "202509051417566675210000",
    //                   "createTimestamp": 1757081877000,
    //                   "updateTimestamp": 1757081877000,
    //                   "localizedFullName": "中华人民共和国",
    //                   "fullName": "中华人民共和国",
    //                   "fullNameZhCn": "中华人民共和国"
    //                 }
    //               }
    //             }
    //           },
    //           "firstBusinessLicensePicFile": null,
    //           "secondBusinessLicensePicFile": null,
    //           "thirdBusinessLicensePicFile": null,
    //           "userPartyType": "RETAILER",
    //           "userPartyTypeDescription": "零售商",
    //           "merchantPartyType": "RETAILER",
    //           "merchantPartyTypeDescription": "零售商",
    //           "businessPartyTypeDescription": "零售商",
    //           "businessPartyType": "RETAILER",
    //           "warrantyConfigRefTargetTypeDescription": "零售商",
    //           "warrantyConfigRefTargetType": "RETAILER",
    //           "warehouseOwningPartyTypeDescription": "零售商",
    //           "warehouseOwningPartyType": "RETAILER",
    //           "warehouseInventoryOutboundTargetType": "RETAILER",
    //           "warehouseInventoryOutboundTargetTypeDescription": "零售商",
    //           "productRefTargetTypeDescription": "零售商",
    //           "productRefTargetType": "RETAILER",
    //           "serviceStoreOwningPartyType": "RETAILER",
    //           "serviceStoreOwningPartyTypeDescription": "零售商",
    //           "serviceRefTargetTypeDescription": "零售商",
    //           "serviceRefTargetType": "RETAILER",
    //           "sellerPartyTypeDescription": "零售商",
    //           "sellerPartyType": "RETAILER",
    //           "buyerPartyTypeDescription": "零售商",
    //           "buyerPartyType": "RETAILER"
    //         },
    //         {
    //           "flagList": [],
    //           "id": "202604290122129060340000",
    //           "createTimestamp": 1777425733000,
    //           "updateTimestamp": 1778139316000,
    //           "directSuperiorDistributorId": "202603230837030730530000",
    //           "fullName": "大连新盛荣丰田汽车销售服务有限公司",
    //           "approvalStatus": "APPROVED",
    //           "approvalStatusDescription": "已审核",
    //           "districtId": "202509051418318475210000",
    //           "address": "五一路999号",
    //           "longitude": 121.7584,
    //           "latitude": 39.07172,
    //           "businessLicenseType": "BUSINESS_LICENSE",
    //           "businessLicenseTypeDescription": "营业执照",
    //           "businessLicenseNumber": "91210213751589779Q",
    //           "firstBusinessLicensePicFileId": null,
    //           "secondBusinessLicensePicFileId": null,
    //           "thirdBusinessLicensePicFileId": null,
    //           "directSuperiorDistributor": {
    //             "flagList": [],
    //             "id": "202603230837030730530000",
    //             "createTimestamp": 1774255023000,
    //             "updateTimestamp": 1774255731000,
    //             "type": "DISTRIBUTOR",
    //             "typeDescription": "分销商",
    //             "level": 1,
    //             "directSuperiorDistributorId": null,
    //             "fullName": "兰考县翰峰信息科技有限公司",
    //             "approvalStatus": "APPROVED",
    //             "approvalStatusDescription": "已审核",
    //             "districtId": "202509051419452105210000",
    //             "address": "经济技术开发区兰考县科技路南50米兰考同乐居（跨境）电商家居产业园469号",
    //             "longitude": 0.0,
    //             "latitude": 0.0,
    //             "businessLicenseType": "BUSINESS_LICENSE",
    //             "businessLicenseTypeDescription": "营业执照",
    //             "businessLicenseNumber": "91410225MA9LHPBQ66",
    //             "firstBusinessLicensePicFileId": null,
    //             "secondBusinessLicensePicFileId": null,
    //             "thirdBusinessLicensePicFileId": null,
    //             "directSuperiorDistributor": null,
    //             "district": {
    //               "flagList": [],
    //               "id": "202509051419452105210000",
    //               "createTimestamp": 1757081985000,
    //               "updateTimestamp": 1757081985000,
    //               "cityId": "202509051419445765210000",
    //               "regionalismCode": "410225",
    //               "localizedFullName": "兰考县",
    //               "fullName": "兰考县",
    //               "fullNameZhCn": "兰考县",
    //               "city": {
    //                 "flagList": [],
    //                 "id": "202509051419445765210000",
    //                 "createTimestamp": 1757081985000,
    //                 "updateTimestamp": 1757081985000,
    //                 "provinceId": "202509051419435825210000",
    //                 "regionalismCode": "410200",
    //                 "localizedFullName": "开封市",
    //                 "fullName": "开封市",
    //                 "fullNameZhCn": "开封市",
    //                 "province": {
    //                   "flagList": [],
    //                   "id": "202509051419435825210000",
    //                   "createTimestamp": 1757081984000,
    //                   "updateTimestamp": 1757081984000,
    //                   "countryId": "202509051417566675210000",
    //                   "regionalismCode": "410000",
    //                   "localizedFullName": "河南省",
    //                   "fullName": "河南省",
    //                   "fullNameZhCn": "河南省",
    //                   "country": {
    //                     "flagList": [],
    //                     "id": "202509051417566675210000",
    //                     "createTimestamp": 1757081877000,
    //                     "updateTimestamp": 1757081877000,
    //                     "localizedFullName": "中华人民共和国",
    //                     "fullName": "中华人民共和国",
    //                     "fullNameZhCn": "中华人民共和国"
    //                   }
    //                 }
    //               }
    //             },
    //             "firstBusinessLicensePicFile": null,
    //             "secondBusinessLicensePicFile": null,
    //             "thirdBusinessLicensePicFile": null,
    //             "userPartyType": "DISTRIBUTOR",
    //             "userPartyTypeDescription": "分销商",
    //             "merchantPartyType": "DISTRIBUTOR",
    //             "merchantPartyTypeDescription": "分销商",
    //             "businessPartyTypeDescription": "分销商",
    //             "businessPartyType": "DISTRIBUTOR",
    //             "warrantyConfigRefTargetTypeDescription": "分销商",
    //             "warrantyConfigRefTargetType": "DISTRIBUTOR",
    //             "warehouseOwningPartyTypeDescription": "分销商",
    //             "warehouseOwningPartyType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceTypeDescription": "分销商",
    //             "warehouseInventoryOutboundTargetType": "DISTRIBUTOR",
    //             "warehouseInventoryOutboundTargetTypeDescription": "分销商",
    //             "productRefTargetTypeDescription": "分销商",
    //             "productRefTargetType": "DISTRIBUTOR",
    //             "sellerPartyTypeDescription": "分销商",
    //             "sellerPartyType": "DISTRIBUTOR",
    //             "buyerPartyTypeDescription": "分销商",
    //             "buyerPartyType": "DISTRIBUTOR"
    //           },
    //           "district": {
    //             "flagList": [],
    //             "id": "202509051418318475210000",
    //             "createTimestamp": 1757081912000,
    //             "updateTimestamp": 1757081912000,
    //             "cityId": "202509051418314265210000",
    //             "regionalismCode": "210213",
    //             "localizedFullName": "金州区",
    //             "fullName": "金州区",
    //             "fullNameZhCn": "金州区",
    //             "city": {
    //               "flagList": [],
    //               "id": "202509051418314265210000",
    //               "createTimestamp": 1757081911000,
    //               "updateTimestamp": 1757081911000,
    //               "provinceId": "202509051418303305210000",
    //               "regionalismCode": "210200",
    //               "localizedFullName": "大连市",
    //               "fullName": "大连市",
    //               "fullNameZhCn": "大连市",
    //               "province": {
    //                 "flagList": [],
    //                 "id": "202509051418303305210000",
    //                 "createTimestamp": 1757081910000,
    //                 "updateTimestamp": 1757081910000,
    //                 "countryId": "202509051417566675210000",
    //                 "regionalismCode": "210000",
    //                 "localizedFullName": "辽宁省",
    //                 "fullName": "辽宁省",
    //                 "fullNameZhCn": "辽宁省",
    //                 "country": {
    //                   "flagList": [],
    //                   "id": "202509051417566675210000",
    //                   "createTimestamp": 1757081877000,
    //                   "updateTimestamp": 1757081877000,
    //                   "localizedFullName": "中华人民共和国",
    //                   "fullName": "中华人民共和国",
    //                   "fullNameZhCn": "中华人民共和国"
    //                 }
    //               }
    //             }
    //           },
    //           "firstBusinessLicensePicFile": null,
    //           "secondBusinessLicensePicFile": null,
    //           "thirdBusinessLicensePicFile": null,
    //           "userPartyType": "RETAILER",
    //           "userPartyTypeDescription": "零售商",
    //           "merchantPartyType": "RETAILER",
    //           "merchantPartyTypeDescription": "零售商",
    //           "businessPartyTypeDescription": "零售商",
    //           "businessPartyType": "RETAILER",
    //           "warrantyConfigRefTargetTypeDescription": "零售商",
    //           "warrantyConfigRefTargetType": "RETAILER",
    //           "warehouseOwningPartyTypeDescription": "零售商",
    //           "warehouseOwningPartyType": "RETAILER",
    //           "warehouseInventoryOutboundTargetType": "RETAILER",
    //           "warehouseInventoryOutboundTargetTypeDescription": "零售商",
    //           "productRefTargetTypeDescription": "零售商",
    //           "productRefTargetType": "RETAILER",
    //           "serviceStoreOwningPartyType": "RETAILER",
    //           "serviceStoreOwningPartyTypeDescription": "零售商",
    //           "serviceRefTargetTypeDescription": "零售商",
    //           "serviceRefTargetType": "RETAILER",
    //           "sellerPartyTypeDescription": "零售商",
    //           "sellerPartyType": "RETAILER",
    //           "buyerPartyTypeDescription": "零售商",
    //           "buyerPartyType": "RETAILER"
    //         },
    //         {
    //           "flagList": [],
    //           "id": "202604300637182420340000",
    //           "createTimestamp": 1777531038000,
    //           "updateTimestamp": 1778139317000,
    //           "directSuperiorDistributorId": "202603230837030730530000",
    //           "fullName": "大连虹港中升雷克萨斯汽车销售服务有限公司",
    //           "approvalStatus": "APPROVED",
    //           "approvalStatusDescription": "已审核",
    //           "districtId": "202509051418317075210000",
    //           "address": "大连虹港路560号雷克萨斯",
    //           "longitude": 121.5172,
    //           "latitude": 38.96061,
    //           "businessLicenseType": "BUSINESS_LICENSE",
    //           "businessLicenseTypeDescription": "营业执照",
    //           "businessLicenseNumber": "91210231550640299E",
    //           "firstBusinessLicensePicFileId": "202604300631210030340000",
    //           "secondBusinessLicensePicFileId": null,
    //           "thirdBusinessLicensePicFileId": null,
    //           "directSuperiorDistributor": {
    //             "flagList": [],
    //             "id": "202603230837030730530000",
    //             "createTimestamp": 1774255023000,
    //             "updateTimestamp": 1774255731000,
    //             "type": "DISTRIBUTOR",
    //             "typeDescription": "分销商",
    //             "level": 1,
    //             "directSuperiorDistributorId": null,
    //             "fullName": "兰考县翰峰信息科技有限公司",
    //             "approvalStatus": "APPROVED",
    //             "approvalStatusDescription": "已审核",
    //             "districtId": "202509051419452105210000",
    //             "address": "经济技术开发区兰考县科技路南50米兰考同乐居（跨境）电商家居产业园469号",
    //             "longitude": 0.0,
    //             "latitude": 0.0,
    //             "businessLicenseType": "BUSINESS_LICENSE",
    //             "businessLicenseTypeDescription": "营业执照",
    //             "businessLicenseNumber": "91410225MA9LHPBQ66",
    //             "firstBusinessLicensePicFileId": null,
    //             "secondBusinessLicensePicFileId": null,
    //             "thirdBusinessLicensePicFileId": null,
    //             "directSuperiorDistributor": null,
    //             "district": {
    //               "flagList": [],
    //               "id": "202509051419452105210000",
    //               "createTimestamp": 1757081985000,
    //               "updateTimestamp": 1757081985000,
    //               "cityId": "202509051419445765210000",
    //               "regionalismCode": "410225",
    //               "localizedFullName": "兰考县",
    //               "fullName": "兰考县",
    //               "fullNameZhCn": "兰考县",
    //               "city": {
    //                 "flagList": [],
    //                 "id": "202509051419445765210000",
    //                 "createTimestamp": 1757081985000,
    //                 "updateTimestamp": 1757081985000,
    //                 "provinceId": "202509051419435825210000",
    //                 "regionalismCode": "410200",
    //                 "localizedFullName": "开封市",
    //                 "fullName": "开封市",
    //                 "fullNameZhCn": "开封市",
    //                 "province": {
    //                   "flagList": [],
    //                   "id": "202509051419435825210000",
    //                   "createTimestamp": 1757081984000,
    //                   "updateTimestamp": 1757081984000,
    //                   "countryId": "202509051417566675210000",
    //                   "regionalismCode": "410000",
    //                   "localizedFullName": "河南省",
    //                   "fullName": "河南省",
    //                   "fullNameZhCn": "河南省",
    //                   "country": {
    //                     "flagList": [],
    //                     "id": "202509051417566675210000",
    //                     "createTimestamp": 1757081877000,
    //                     "updateTimestamp": 1757081877000,
    //                     "localizedFullName": "中华人民共和国",
    //                     "fullName": "中华人民共和国",
    //                     "fullNameZhCn": "中华人民共和国"
    //                   }
    //                 }
    //               }
    //             },
    //             "firstBusinessLicensePicFile": null,
    //             "secondBusinessLicensePicFile": null,
    //             "thirdBusinessLicensePicFile": null,
    //             "userPartyType": "DISTRIBUTOR",
    //             "userPartyTypeDescription": "分销商",
    //             "merchantPartyType": "DISTRIBUTOR",
    //             "merchantPartyTypeDescription": "分销商",
    //             "businessPartyTypeDescription": "分销商",
    //             "businessPartyType": "DISTRIBUTOR",
    //             "warrantyConfigRefTargetTypeDescription": "分销商",
    //             "warrantyConfigRefTargetType": "DISTRIBUTOR",
    //             "warehouseOwningPartyTypeDescription": "分销商",
    //             "warehouseOwningPartyType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceTypeDescription": "分销商",
    //             "warehouseInventoryOutboundTargetType": "DISTRIBUTOR",
    //             "warehouseInventoryOutboundTargetTypeDescription": "分销商",
    //             "productRefTargetTypeDescription": "分销商",
    //             "productRefTargetType": "DISTRIBUTOR",
    //             "sellerPartyTypeDescription": "分销商",
    //             "sellerPartyType": "DISTRIBUTOR",
    //             "buyerPartyTypeDescription": "分销商",
    //             "buyerPartyType": "DISTRIBUTOR"
    //           },
    //           "district": {
    //             "flagList": [],
    //             "id": "202509051418317075210000",
    //             "createTimestamp": 1757081912000,
    //             "updateTimestamp": 1757081912000,
    //             "cityId": "202509051418314265210000",
    //             "regionalismCode": "210211",
    //             "localizedFullName": "甘井子区",
    //             "fullName": "甘井子区",
    //             "fullNameZhCn": "甘井子区",
    //             "city": {
    //               "flagList": [],
    //               "id": "202509051418314265210000",
    //               "createTimestamp": 1757081911000,
    //               "updateTimestamp": 1757081911000,
    //               "provinceId": "202509051418303305210000",
    //               "regionalismCode": "210200",
    //               "localizedFullName": "大连市",
    //               "fullName": "大连市",
    //               "fullNameZhCn": "大连市",
    //               "province": {
    //                 "flagList": [],
    //                 "id": "202509051418303305210000",
    //                 "createTimestamp": 1757081910000,
    //                 "updateTimestamp": 1757081910000,
    //                 "countryId": "202509051417566675210000",
    //                 "regionalismCode": "210000",
    //                 "localizedFullName": "辽宁省",
    //                 "fullName": "辽宁省",
    //                 "fullNameZhCn": "辽宁省",
    //                 "country": {
    //                   "flagList": [],
    //                   "id": "202509051417566675210000",
    //                   "createTimestamp": 1757081877000,
    //                   "updateTimestamp": 1757081877000,
    //                   "localizedFullName": "中华人民共和国",
    //                   "fullName": "中华人民共和国",
    //                   "fullNameZhCn": "中华人民共和国"
    //                 }
    //               }
    //             }
    //           },
    //           "firstBusinessLicensePicFile": {
    //             "flagList": [],
    //             "id": "202604300631210030340000",
    //             "createTimestamp": 1777530681000,
    //             "updateTimestamp": 1777530681000,
    //             "format": "JPEG",
    //             "formatDescription": "JPEG",
    //             "width": 1772,
    //             "height": 1268,
    //             "ossBucket": "goldenkiwiprod",
    //             "ossPath": "nbiz/retailer/2026/04/30/06/1772_1268_f4ed6bf3ea9383a94720cef0e4ebddb0.jpeg",
    //             "url": "https://img.youbanban.com/oss/download/nbiz/retailer/2026/04/30/06/1772_1268_f4ed6bf3ea9383a94720cef0e4ebddb0.jpeg",
    //             "fileTypeDescription": "图片",
    //             "fileType": "PIC"
    //           },
    //           "secondBusinessLicensePicFile": null,
    //           "thirdBusinessLicensePicFile": null,
    //           "userPartyType": "RETAILER",
    //           "userPartyTypeDescription": "零售商",
    //           "merchantPartyType": "RETAILER",
    //           "merchantPartyTypeDescription": "零售商",
    //           "businessPartyTypeDescription": "零售商",
    //           "businessPartyType": "RETAILER",
    //           "warrantyConfigRefTargetTypeDescription": "零售商",
    //           "warrantyConfigRefTargetType": "RETAILER",
    //           "warehouseOwningPartyTypeDescription": "零售商",
    //           "warehouseOwningPartyType": "RETAILER",
    //           "warehouseInventoryOutboundTargetType": "RETAILER",
    //           "warehouseInventoryOutboundTargetTypeDescription": "零售商",
    //           "productRefTargetTypeDescription": "零售商",
    //           "productRefTargetType": "RETAILER",
    //           "serviceStoreOwningPartyType": "RETAILER",
    //           "serviceStoreOwningPartyTypeDescription": "零售商",
    //           "serviceRefTargetTypeDescription": "零售商",
    //           "serviceRefTargetType": "RETAILER",
    //           "sellerPartyTypeDescription": "零售商",
    //           "sellerPartyType": "RETAILER",
    //           "buyerPartyTypeDescription": "零售商",
    //           "buyerPartyType": "RETAILER"
    //         },
    //         {
    //           "flagList": [],
    //           "id": "202605060537397480340000",
    //           "createTimestamp": 1778045860000,
    //           "updateTimestamp": 1778139318000,
    //           "directSuperiorDistributorId": "202603230837030730530000",
    //           "fullName": "天津中升沃茂汽车销售有限公司",
    //           "approvalStatus": "APPROVED",
    //           "approvalStatusDescription": "已审核",
    //           "districtId": "202509051417584625210000",
    //           "address": "解放南路489号",
    //           "longitude": 117.22817,
    //           "latitude": 39.06281,
    //           "businessLicenseType": "BUSINESS_LICENSE",
    //           "businessLicenseTypeDescription": "营业执照",
    //           "businessLicenseNumber": "911201035661221620",
    //           "firstBusinessLicensePicFileId": null,
    //           "secondBusinessLicensePicFileId": null,
    //           "thirdBusinessLicensePicFileId": null,
    //           "directSuperiorDistributor": {
    //             "flagList": [],
    //             "id": "202603230837030730530000",
    //             "createTimestamp": 1774255023000,
    //             "updateTimestamp": 1774255731000,
    //             "type": "DISTRIBUTOR",
    //             "typeDescription": "分销商",
    //             "level": 1,
    //             "directSuperiorDistributorId": null,
    //             "fullName": "兰考县翰峰信息科技有限公司",
    //             "approvalStatus": "APPROVED",
    //             "approvalStatusDescription": "已审核",
    //             "districtId": "202509051419452105210000",
    //             "address": "经济技术开发区兰考县科技路南50米兰考同乐居（跨境）电商家居产业园469号",
    //             "longitude": 0.0,
    //             "latitude": 0.0,
    //             "businessLicenseType": "BUSINESS_LICENSE",
    //             "businessLicenseTypeDescription": "营业执照",
    //             "businessLicenseNumber": "91410225MA9LHPBQ66",
    //             "firstBusinessLicensePicFileId": null,
    //             "secondBusinessLicensePicFileId": null,
    //             "thirdBusinessLicensePicFileId": null,
    //             "directSuperiorDistributor": null,
    //             "district": {
    //               "flagList": [],
    //               "id": "202509051419452105210000",
    //               "createTimestamp": 1757081985000,
    //               "updateTimestamp": 1757081985000,
    //               "cityId": "202509051419445765210000",
    //               "regionalismCode": "410225",
    //               "localizedFullName": "兰考县",
    //               "fullName": "兰考县",
    //               "fullNameZhCn": "兰考县",
    //               "city": {
    //                 "flagList": [],
    //                 "id": "202509051419445765210000",
    //                 "createTimestamp": 1757081985000,
    //                 "updateTimestamp": 1757081985000,
    //                 "provinceId": "202509051419435825210000",
    //                 "regionalismCode": "410200",
    //                 "localizedFullName": "开封市",
    //                 "fullName": "开封市",
    //                 "fullNameZhCn": "开封市",
    //                 "province": {
    //                   "flagList": [],
    //                   "id": "202509051419435825210000",
    //                   "createTimestamp": 1757081984000,
    //                   "updateTimestamp": 1757081984000,
    //                   "countryId": "202509051417566675210000",
    //                   "regionalismCode": "410000",
    //                   "localizedFullName": "河南省",
    //                   "fullName": "河南省",
    //                   "fullNameZhCn": "河南省",
    //                   "country": {
    //                     "flagList": [],
    //                     "id": "202509051417566675210000",
    //                     "createTimestamp": 1757081877000,
    //                     "updateTimestamp": 1757081877000,
    //                     "localizedFullName": "中华人民共和国",
    //                     "fullName": "中华人民共和国",
    //                     "fullNameZhCn": "中华人民共和国"
    //                   }
    //                 }
    //               }
    //             },
    //             "firstBusinessLicensePicFile": null,
    //             "secondBusinessLicensePicFile": null,
    //             "thirdBusinessLicensePicFile": null,
    //             "userPartyType": "DISTRIBUTOR",
    //             "userPartyTypeDescription": "分销商",
    //             "merchantPartyType": "DISTRIBUTOR",
    //             "merchantPartyTypeDescription": "分销商",
    //             "businessPartyTypeDescription": "分销商",
    //             "businessPartyType": "DISTRIBUTOR",
    //             "warrantyConfigRefTargetTypeDescription": "分销商",
    //             "warrantyConfigRefTargetType": "DISTRIBUTOR",
    //             "warehouseOwningPartyTypeDescription": "分销商",
    //             "warehouseOwningPartyType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceTypeDescription": "分销商",
    //             "warehouseInventoryOutboundTargetType": "DISTRIBUTOR",
    //             "warehouseInventoryOutboundTargetTypeDescription": "分销商",
    //             "productRefTargetTypeDescription": "分销商",
    //             "productRefTargetType": "DISTRIBUTOR",
    //             "sellerPartyTypeDescription": "分销商",
    //             "sellerPartyType": "DISTRIBUTOR",
    //             "buyerPartyTypeDescription": "分销商",
    //             "buyerPartyType": "DISTRIBUTOR"
    //           },
    //           "district": {
    //             "flagList": [],
    //             "id": "202509051417584625210000",
    //             "createTimestamp": 1757081878000,
    //             "updateTimestamp": 1757081878000,
    //             "cityId": "202509051417582375210000",
    //             "regionalismCode": "120103",
    //             "localizedFullName": "河西区",
    //             "fullName": "河西区",
    //             "fullNameZhCn": "河西区",
    //             "city": {
    //               "flagList": [],
    //               "id": "202509051417582375210000",
    //               "createTimestamp": 1757081878000,
    //               "updateTimestamp": 1757081878000,
    //               "provinceId": "202509051417581655210000",
    //               "regionalismCode": "120000",
    //               "localizedFullName": "天津市",
    //               "fullName": "天津市",
    //               "fullNameZhCn": "天津市",
    //               "province": {
    //                 "flagList": [],
    //                 "id": "202509051417581655210000",
    //                 "createTimestamp": 1757081878000,
    //                 "updateTimestamp": 1757081878000,
    //                 "countryId": "202509051417566675210000",
    //                 "regionalismCode": "120000",
    //                 "localizedFullName": "天津市",
    //                 "fullName": "天津市",
    //                 "fullNameZhCn": "天津市",
    //                 "country": {
    //                   "flagList": [],
    //                   "id": "202509051417566675210000",
    //                   "createTimestamp": 1757081877000,
    //                   "updateTimestamp": 1757081877000,
    //                   "localizedFullName": "中华人民共和国",
    //                   "fullName": "中华人民共和国",
    //                   "fullNameZhCn": "中华人民共和国"
    //                 }
    //               }
    //             }
    //           },
    //           "firstBusinessLicensePicFile": null,
    //           "secondBusinessLicensePicFile": null,
    //           "thirdBusinessLicensePicFile": null,
    //           "userPartyType": "RETAILER",
    //           "userPartyTypeDescription": "零售商",
    //           "merchantPartyType": "RETAILER",
    //           "merchantPartyTypeDescription": "零售商",
    //           "businessPartyTypeDescription": "零售商",
    //           "businessPartyType": "RETAILER",
    //           "warrantyConfigRefTargetTypeDescription": "零售商",
    //           "warrantyConfigRefTargetType": "RETAILER",
    //           "warehouseOwningPartyTypeDescription": "零售商",
    //           "warehouseOwningPartyType": "RETAILER",
    //           "warehouseInventoryOutboundTargetType": "RETAILER",
    //           "warehouseInventoryOutboundTargetTypeDescription": "零售商",
    //           "productRefTargetTypeDescription": "零售商",
    //           "productRefTargetType": "RETAILER",
    //           "serviceStoreOwningPartyType": "RETAILER",
    //           "serviceStoreOwningPartyTypeDescription": "零售商",
    //           "serviceRefTargetTypeDescription": "零售商",
    //           "serviceRefTargetType": "RETAILER",
    //           "sellerPartyTypeDescription": "零售商",
    //           "sellerPartyType": "RETAILER",
    //           "buyerPartyTypeDescription": "零售商",
    //           "buyerPartyType": "RETAILER"
    //         },
    //         {
    //           "flagList": [],
    //           "id": "202605070539157430340000",
    //           "createTimestamp": 1778132356000,
    //           "updateTimestamp": 1778139318000,
    //           "directSuperiorDistributorId": "202603230837030730530000",
    //           "fullName": "大连中升汇迪汽车销售服务有限公司",
    //           "approvalStatus": "APPROVED",
    //           "approvalStatusDescription": "已审核",
    //           "districtId": "202509051418317075210000",
    //           "address": "华北路761号",
    //           "longitude": 121.61263,
    //           "latitude": 39.01582,
    //           "businessLicenseType": "BUSINESS_LICENSE",
    //           "businessLicenseTypeDescription": "营业执照",
    //           "businessLicenseNumber": "91210200716916583E",
    //           "firstBusinessLicensePicFileId": null,
    //           "secondBusinessLicensePicFileId": null,
    //           "thirdBusinessLicensePicFileId": null,
    //           "directSuperiorDistributor": {
    //             "flagList": [],
    //             "id": "202603230837030730530000",
    //             "createTimestamp": 1774255023000,
    //             "updateTimestamp": 1774255731000,
    //             "type": "DISTRIBUTOR",
    //             "typeDescription": "分销商",
    //             "level": 1,
    //             "directSuperiorDistributorId": null,
    //             "fullName": "兰考县翰峰信息科技有限公司",
    //             "approvalStatus": "APPROVED",
    //             "approvalStatusDescription": "已审核",
    //             "districtId": "202509051419452105210000",
    //             "address": "经济技术开发区兰考县科技路南50米兰考同乐居（跨境）电商家居产业园469号",
    //             "longitude": 0.0,
    //             "latitude": 0.0,
    //             "businessLicenseType": "BUSINESS_LICENSE",
    //             "businessLicenseTypeDescription": "营业执照",
    //             "businessLicenseNumber": "91410225MA9LHPBQ66",
    //             "firstBusinessLicensePicFileId": null,
    //             "secondBusinessLicensePicFileId": null,
    //             "thirdBusinessLicensePicFileId": null,
    //             "directSuperiorDistributor": null,
    //             "district": {
    //               "flagList": [],
    //               "id": "202509051419452105210000",
    //               "createTimestamp": 1757081985000,
    //               "updateTimestamp": 1757081985000,
    //               "cityId": "202509051419445765210000",
    //               "regionalismCode": "410225",
    //               "localizedFullName": "兰考县",
    //               "fullName": "兰考县",
    //               "fullNameZhCn": "兰考县",
    //               "city": {
    //                 "flagList": [],
    //                 "id": "202509051419445765210000",
    //                 "createTimestamp": 1757081985000,
    //                 "updateTimestamp": 1757081985000,
    //                 "provinceId": "202509051419435825210000",
    //                 "regionalismCode": "410200",
    //                 "localizedFullName": "开封市",
    //                 "fullName": "开封市",
    //                 "fullNameZhCn": "开封市",
    //                 "province": {
    //                   "flagList": [],
    //                   "id": "202509051419435825210000",
    //                   "createTimestamp": 1757081984000,
    //                   "updateTimestamp": 1757081984000,
    //                   "countryId": "202509051417566675210000",
    //                   "regionalismCode": "410000",
    //                   "localizedFullName": "河南省",
    //                   "fullName": "河南省",
    //                   "fullNameZhCn": "河南省",
    //                   "country": {
    //                     "flagList": [],
    //                     "id": "202509051417566675210000",
    //                     "createTimestamp": 1757081877000,
    //                     "updateTimestamp": 1757081877000,
    //                     "localizedFullName": "中华人民共和国",
    //                     "fullName": "中华人民共和国",
    //                     "fullNameZhCn": "中华人民共和国"
    //                   }
    //                 }
    //               }
    //             },
    //             "firstBusinessLicensePicFile": null,
    //             "secondBusinessLicensePicFile": null,
    //             "thirdBusinessLicensePicFile": null,
    //             "userPartyType": "DISTRIBUTOR",
    //             "userPartyTypeDescription": "分销商",
    //             "merchantPartyType": "DISTRIBUTOR",
    //             "merchantPartyTypeDescription": "分销商",
    //             "businessPartyTypeDescription": "分销商",
    //             "businessPartyType": "DISTRIBUTOR",
    //             "warrantyConfigRefTargetTypeDescription": "分销商",
    //             "warrantyConfigRefTargetType": "DISTRIBUTOR",
    //             "warehouseOwningPartyTypeDescription": "分销商",
    //             "warehouseOwningPartyType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceTypeDescription": "分销商",
    //             "warehouseInventoryOutboundTargetType": "DISTRIBUTOR",
    //             "warehouseInventoryOutboundTargetTypeDescription": "分销商",
    //             "productRefTargetTypeDescription": "分销商",
    //             "productRefTargetType": "DISTRIBUTOR",
    //             "sellerPartyTypeDescription": "分销商",
    //             "sellerPartyType": "DISTRIBUTOR",
    //             "buyerPartyTypeDescription": "分销商",
    //             "buyerPartyType": "DISTRIBUTOR"
    //           },
    //           "district": {
    //             "flagList": [],
    //             "id": "202509051418317075210000",
    //             "createTimestamp": 1757081912000,
    //             "updateTimestamp": 1757081912000,
    //             "cityId": "202509051418314265210000",
    //             "regionalismCode": "210211",
    //             "localizedFullName": "甘井子区",
    //             "fullName": "甘井子区",
    //             "fullNameZhCn": "甘井子区",
    //             "city": {
    //               "flagList": [],
    //               "id": "202509051418314265210000",
    //               "createTimestamp": 1757081911000,
    //               "updateTimestamp": 1757081911000,
    //               "provinceId": "202509051418303305210000",
    //               "regionalismCode": "210200",
    //               "localizedFullName": "大连市",
    //               "fullName": "大连市",
    //               "fullNameZhCn": "大连市",
    //               "province": {
    //                 "flagList": [],
    //                 "id": "202509051418303305210000",
    //                 "createTimestamp": 1757081910000,
    //                 "updateTimestamp": 1757081910000,
    //                 "countryId": "202509051417566675210000",
    //                 "regionalismCode": "210000",
    //                 "localizedFullName": "辽宁省",
    //                 "fullName": "辽宁省",
    //                 "fullNameZhCn": "辽宁省",
    //                 "country": {
    //                   "flagList": [],
    //                   "id": "202509051417566675210000",
    //                   "createTimestamp": 1757081877000,
    //                   "updateTimestamp": 1757081877000,
    //                   "localizedFullName": "中华人民共和国",
    //                   "fullName": "中华人民共和国",
    //                   "fullNameZhCn": "中华人民共和国"
    //                 }
    //               }
    //             }
    //           },
    //           "firstBusinessLicensePicFile": null,
    //           "secondBusinessLicensePicFile": null,
    //           "thirdBusinessLicensePicFile": null,
    //           "userPartyType": "RETAILER",
    //           "userPartyTypeDescription": "零售商",
    //           "merchantPartyType": "RETAILER",
    //           "merchantPartyTypeDescription": "零售商",
    //           "businessPartyTypeDescription": "零售商",
    //           "businessPartyType": "RETAILER",
    //           "warrantyConfigRefTargetTypeDescription": "零售商",
    //           "warrantyConfigRefTargetType": "RETAILER",
    //           "warehouseOwningPartyTypeDescription": "零售商",
    //           "warehouseOwningPartyType": "RETAILER",
    //           "warehouseInventoryOutboundTargetType": "RETAILER",
    //           "warehouseInventoryOutboundTargetTypeDescription": "零售商",
    //           "productRefTargetTypeDescription": "零售商",
    //           "productRefTargetType": "RETAILER",
    //           "serviceStoreOwningPartyType": "RETAILER",
    //           "serviceStoreOwningPartyTypeDescription": "零售商",
    //           "serviceRefTargetTypeDescription": "零售商",
    //           "serviceRefTargetType": "RETAILER",
    //           "sellerPartyTypeDescription": "零售商",
    //           "sellerPartyType": "RETAILER",
    //           "buyerPartyTypeDescription": "零售商",
    //           "buyerPartyType": "RETAILER"
    //         },
    //         {
    //           "flagList": [],
    //           "id": "202605080247332003540000",
    //           "createTimestamp": 1778208453000,
    //           "updateTimestamp": 1778210475000,
    //           "directSuperiorDistributorId": "202509230730592586830000",
    //           "fullName": "云南省澄江市名车汇汽车美容服务中心",
    //           "approvalStatus": "APPROVED",
    //           "approvalStatusDescription": "已审核",
    //           "districtId": "202509051421054885210000",
    //           "address": "仙湖路东65号（青少年活动中心旁）",
    //           "longitude": 102.9086,
    //           "latitude": 24.66596,
    //           "businessLicenseType": "BUSINESS_LICENSE",
    //           "businessLicenseTypeDescription": "营业执照",
    //           "businessLicenseNumber": "92530481MAC9YQF339",
    //           "firstBusinessLicensePicFileId": null,
    //           "secondBusinessLicensePicFileId": null,
    //           "thirdBusinessLicensePicFileId": null,
    //           "directSuperiorDistributor": {
    //             "flagList": [],
    //             "id": "202509230730592586830000",
    //             "createTimestamp": 1758612659000,
    //             "updateTimestamp": 1758612694000,
    //             "type": "DISTRIBUTOR",
    //             "typeDescription": "分销商",
    //             "level": 1,
    //             "directSuperiorDistributorId": null,
    //             "fullName": "驰捷车多管（云南）企业管理有限公司",
    //             "approvalStatus": "APPROVED",
    //             "approvalStatusDescription": "已审核",
    //             "districtId": "202509051421033585210000",
    //             "address": "云南省昆明市西山区西坝路639号",
    //             "longitude": 0.0,
    //             "latitude": 0.0,
    //             "businessLicenseType": "BUSINESS_LICENSE",
    //             "businessLicenseTypeDescription": "营业执照",
    //             "businessLicenseNumber": "91310118560192303A",
    //             "firstBusinessLicensePicFileId": null,
    //             "secondBusinessLicensePicFileId": null,
    //             "thirdBusinessLicensePicFileId": null,
    //             "directSuperiorDistributor": null,
    //             "district": {
    //               "flagList": [],
    //               "id": "202509051421033585210000",
    //               "createTimestamp": 1757082063000,
    //               "updateTimestamp": 1757082063000,
    //               "cityId": "202509051421030835210000",
    //               "regionalismCode": "530112",
    //               "localizedFullName": "西山区",
    //               "fullName": "西山区",
    //               "fullNameZhCn": "西山区",
    //               "city": {
    //                 "flagList": [],
    //                 "id": "202509051421030835210000",
    //                 "createTimestamp": 1757082063000,
    //                 "updateTimestamp": 1757082063000,
    //                 "provinceId": "202509051421030075210000",
    //                 "regionalismCode": "530100",
    //                 "localizedFullName": "昆明市",
    //                 "fullName": "昆明市",
    //                 "fullNameZhCn": "昆明市",
    //                 "province": {
    //                   "flagList": [],
    //                   "id": "202509051421030075210000",
    //                   "createTimestamp": 1757082063000,
    //                   "updateTimestamp": 1757082063000,
    //                   "countryId": "202509051417566675210000",
    //                   "regionalismCode": "530000",
    //                   "localizedFullName": "云南省",
    //                   "fullName": "云南省",
    //                   "fullNameZhCn": "云南省",
    //                   "country": {
    //                     "flagList": [],
    //                     "id": "202509051417566675210000",
    //                     "createTimestamp": 1757081877000,
    //                     "updateTimestamp": 1757081877000,
    //                     "localizedFullName": "中华人民共和国",
    //                     "fullName": "中华人民共和国",
    //                     "fullNameZhCn": "中华人民共和国"
    //                   }
    //                 }
    //               }
    //             },
    //             "firstBusinessLicensePicFile": null,
    //             "secondBusinessLicensePicFile": null,
    //             "thirdBusinessLicensePicFile": null,
    //             "userPartyType": "DISTRIBUTOR",
    //             "userPartyTypeDescription": "分销商",
    //             "merchantPartyType": "DISTRIBUTOR",
    //             "merchantPartyTypeDescription": "分销商",
    //             "businessPartyTypeDescription": "分销商",
    //             "businessPartyType": "DISTRIBUTOR",
    //             "warrantyConfigRefTargetTypeDescription": "分销商",
    //             "warrantyConfigRefTargetType": "DISTRIBUTOR",
    //             "warehouseOwningPartyTypeDescription": "分销商",
    //             "warehouseOwningPartyType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceTypeDescription": "分销商",
    //             "warehouseInventoryOutboundTargetType": "DISTRIBUTOR",
    //             "warehouseInventoryOutboundTargetTypeDescription": "分销商",
    //             "productRefTargetTypeDescription": "分销商",
    //             "productRefTargetType": "DISTRIBUTOR",
    //             "sellerPartyTypeDescription": "分销商",
    //             "sellerPartyType": "DISTRIBUTOR",
    //             "buyerPartyTypeDescription": "分销商",
    //             "buyerPartyType": "DISTRIBUTOR"
    //           },
    //           "district": {
    //             "flagList": [],
    //             "id": "202509051421054885210000",
    //             "createTimestamp": 1757082065000,
    //             "updateTimestamp": 1757082065000,
    //             "cityId": "202509051421048425210000",
    //             "regionalismCode": "530481",
    //             "localizedFullName": "澄江市",
    //             "fullName": "澄江市",
    //             "fullNameZhCn": "澄江市",
    //             "city": {
    //               "flagList": [],
    //               "id": "202509051421048425210000",
    //               "createTimestamp": 1757082065000,
    //               "updateTimestamp": 1757082065000,
    //               "provinceId": "202509051421030075210000",
    //               "regionalismCode": "530400",
    //               "localizedFullName": "玉溪市",
    //               "fullName": "玉溪市",
    //               "fullNameZhCn": "玉溪市",
    //               "province": {
    //                 "flagList": [],
    //                 "id": "202509051421030075210000",
    //                 "createTimestamp": 1757082063000,
    //                 "updateTimestamp": 1757082063000,
    //                 "countryId": "202509051417566675210000",
    //                 "regionalismCode": "530000",
    //                 "localizedFullName": "云南省",
    //                 "fullName": "云南省",
    //                 "fullNameZhCn": "云南省",
    //                 "country": {
    //                   "flagList": [],
    //                   "id": "202509051417566675210000",
    //                   "createTimestamp": 1757081877000,
    //                   "updateTimestamp": 1757081877000,
    //                   "localizedFullName": "中华人民共和国",
    //                   "fullName": "中华人民共和国",
    //                   "fullNameZhCn": "中华人民共和国"
    //                 }
    //               }
    //             }
    //           },
    //           "firstBusinessLicensePicFile": null,
    //           "secondBusinessLicensePicFile": null,
    //           "thirdBusinessLicensePicFile": null,
    //           "userPartyType": "RETAILER",
    //           "userPartyTypeDescription": "零售商",
    //           "merchantPartyType": "RETAILER",
    //           "merchantPartyTypeDescription": "零售商",
    //           "businessPartyTypeDescription": "零售商",
    //           "businessPartyType": "RETAILER",
    //           "warrantyConfigRefTargetTypeDescription": "零售商",
    //           "warrantyConfigRefTargetType": "RETAILER",
    //           "warehouseOwningPartyTypeDescription": "零售商",
    //           "warehouseOwningPartyType": "RETAILER",
    //           "warehouseInventoryOutboundTargetType": "RETAILER",
    //           "warehouseInventoryOutboundTargetTypeDescription": "零售商",
    //           "productRefTargetTypeDescription": "零售商",
    //           "productRefTargetType": "RETAILER",
    //           "serviceStoreOwningPartyType": "RETAILER",
    //           "serviceStoreOwningPartyTypeDescription": "零售商",
    //           "serviceRefTargetTypeDescription": "零售商",
    //           "serviceRefTargetType": "RETAILER",
    //           "sellerPartyTypeDescription": "零售商",
    //           "sellerPartyType": "RETAILER",
    //           "buyerPartyTypeDescription": "零售商",
    //           "buyerPartyType": "RETAILER"
    //         },
    //         {
    //           "flagList": [],
    //           "id": "202605090534070895050000",
    //           "createTimestamp": 1778304847000,
    //           "updateTimestamp": 1778311426000,
    //           "directSuperiorDistributorId": "202603230837030730530000",
    //           "fullName": "青岛中升汇迪汽车销售服务有限公司",
    //           "approvalStatus": "APPROVED",
    //           "approvalStatusDescription": "已审核",
    //           "districtId": "202509051419340205210000",
    //           "address": "海尔路170号",
    //           "longitude": 120.44325,
    //           "latitude": 36.1116,
    //           "businessLicenseType": "BUSINESS_LICENSE",
    //           "businessLicenseTypeDescription": "营业执照",
    //           "businessLicenseNumber": "91370212MAER1JEK78",
    //           "firstBusinessLicensePicFileId": "202605090533317555050000",
    //           "secondBusinessLicensePicFileId": null,
    //           "thirdBusinessLicensePicFileId": null,
    //           "directSuperiorDistributor": {
    //             "flagList": [],
    //             "id": "202603230837030730530000",
    //             "createTimestamp": 1774255023000,
    //             "updateTimestamp": 1774255731000,
    //             "type": "DISTRIBUTOR",
    //             "typeDescription": "分销商",
    //             "level": 1,
    //             "directSuperiorDistributorId": null,
    //             "fullName": "兰考县翰峰信息科技有限公司",
    //             "approvalStatus": "APPROVED",
    //             "approvalStatusDescription": "已审核",
    //             "districtId": "202509051419452105210000",
    //             "address": "经济技术开发区兰考县科技路南50米兰考同乐居（跨境）电商家居产业园469号",
    //             "longitude": 0.0,
    //             "latitude": 0.0,
    //             "businessLicenseType": "BUSINESS_LICENSE",
    //             "businessLicenseTypeDescription": "营业执照",
    //             "businessLicenseNumber": "91410225MA9LHPBQ66",
    //             "firstBusinessLicensePicFileId": null,
    //             "secondBusinessLicensePicFileId": null,
    //             "thirdBusinessLicensePicFileId": null,
    //             "directSuperiorDistributor": null,
    //             "district": {
    //               "flagList": [],
    //               "id": "202509051419452105210000",
    //               "createTimestamp": 1757081985000,
    //               "updateTimestamp": 1757081985000,
    //               "cityId": "202509051419445765210000",
    //               "regionalismCode": "410225",
    //               "localizedFullName": "兰考县",
    //               "fullName": "兰考县",
    //               "fullNameZhCn": "兰考县",
    //               "city": {
    //                 "flagList": [],
    //                 "id": "202509051419445765210000",
    //                 "createTimestamp": 1757081985000,
    //                 "updateTimestamp": 1757081985000,
    //                 "provinceId": "202509051419435825210000",
    //                 "regionalismCode": "410200",
    //                 "localizedFullName": "开封市",
    //                 "fullName": "开封市",
    //                 "fullNameZhCn": "开封市",
    //                 "province": {
    //                   "flagList": [],
    //                   "id": "202509051419435825210000",
    //                   "createTimestamp": 1757081984000,
    //                   "updateTimestamp": 1757081984000,
    //                   "countryId": "202509051417566675210000",
    //                   "regionalismCode": "410000",
    //                   "localizedFullName": "河南省",
    //                   "fullName": "河南省",
    //                   "fullNameZhCn": "河南省",
    //                   "country": {
    //                     "flagList": [],
    //                     "id": "202509051417566675210000",
    //                     "createTimestamp": 1757081877000,
    //                     "updateTimestamp": 1757081877000,
    //                     "localizedFullName": "中华人民共和国",
    //                     "fullName": "中华人民共和国",
    //                     "fullNameZhCn": "中华人民共和国"
    //                   }
    //                 }
    //               }
    //             },
    //             "firstBusinessLicensePicFile": null,
    //             "secondBusinessLicensePicFile": null,
    //             "thirdBusinessLicensePicFile": null,
    //             "userPartyType": "DISTRIBUTOR",
    //             "userPartyTypeDescription": "分销商",
    //             "merchantPartyType": "DISTRIBUTOR",
    //             "merchantPartyTypeDescription": "分销商",
    //             "businessPartyTypeDescription": "分销商",
    //             "businessPartyType": "DISTRIBUTOR",
    //             "warrantyConfigRefTargetTypeDescription": "分销商",
    //             "warrantyConfigRefTargetType": "DISTRIBUTOR",
    //             "warehouseOwningPartyTypeDescription": "分销商",
    //             "warehouseOwningPartyType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceTypeDescription": "分销商",
    //             "warehouseInventoryOutboundTargetType": "DISTRIBUTOR",
    //             "warehouseInventoryOutboundTargetTypeDescription": "分销商",
    //             "productRefTargetTypeDescription": "分销商",
    //             "productRefTargetType": "DISTRIBUTOR",
    //             "sellerPartyTypeDescription": "分销商",
    //             "sellerPartyType": "DISTRIBUTOR",
    //             "buyerPartyTypeDescription": "分销商",
    //             "buyerPartyType": "DISTRIBUTOR"
    //           },
    //           "district": {
    //             "flagList": [],
    //             "id": "202509051419340205210000",
    //             "createTimestamp": 1757081974000,
    //             "updateTimestamp": 1757081974000,
    //             "cityId": "202509051419337405210000",
    //             "regionalismCode": "370212",
    //             "localizedFullName": "崂山区",
    //             "fullName": "崂山区",
    //             "fullNameZhCn": "崂山区",
    //             "city": {
    //               "flagList": [],
    //               "id": "202509051419337405210000",
    //               "createTimestamp": 1757081974000,
    //               "updateTimestamp": 1757081974000,
    //               "provinceId": "202509051419327565210000",
    //               "regionalismCode": "370200",
    //               "localizedFullName": "青岛市",
    //               "fullName": "青岛市",
    //               "fullNameZhCn": "青岛市",
    //               "province": {
    //                 "flagList": [],
    //                 "id": "202509051419327565210000",
    //                 "createTimestamp": 1757081973000,
    //                 "updateTimestamp": 1757081973000,
    //                 "countryId": "202509051417566675210000",
    //                 "regionalismCode": "370000",
    //                 "localizedFullName": "山东省",
    //                 "fullName": "山东省",
    //                 "fullNameZhCn": "山东省",
    //                 "country": {
    //                   "flagList": [],
    //                   "id": "202509051417566675210000",
    //                   "createTimestamp": 1757081877000,
    //                   "updateTimestamp": 1757081877000,
    //                   "localizedFullName": "中华人民共和国",
    //                   "fullName": "中华人民共和国",
    //                   "fullNameZhCn": "中华人民共和国"
    //                 }
    //               }
    //             }
    //           },
    //           "firstBusinessLicensePicFile": {
    //             "flagList": [],
    //             "id": "202605090533317555050000",
    //             "createTimestamp": 1778304812000,
    //             "updateTimestamp": 1778304812000,
    //             "format": "JPEG",
    //             "formatDescription": "JPEG",
    //             "width": 5712,
    //             "height": 4284,
    //             "ossBucket": "goldenkiwiprod",
    //             "ossPath": "nbiz/retailer/2026/05/09/05/5712_4284_4acd6f578cf68878e3d2bb72d6369602.jpeg",
    //             "url": "https://img.youbanban.com/oss/download/nbiz/retailer/2026/05/09/05/5712_4284_4acd6f578cf68878e3d2bb72d6369602.jpeg",
    //             "fileTypeDescription": "图片",
    //             "fileType": "PIC"
    //           },
    //           "secondBusinessLicensePicFile": null,
    //           "thirdBusinessLicensePicFile": null,
    //           "userPartyType": "RETAILER",
    //           "userPartyTypeDescription": "零售商",
    //           "merchantPartyType": "RETAILER",
    //           "merchantPartyTypeDescription": "零售商",
    //           "businessPartyTypeDescription": "零售商",
    //           "businessPartyType": "RETAILER",
    //           "warrantyConfigRefTargetTypeDescription": "零售商",
    //           "warrantyConfigRefTargetType": "RETAILER",
    //           "warehouseOwningPartyTypeDescription": "零售商",
    //           "warehouseOwningPartyType": "RETAILER",
    //           "warehouseInventoryOutboundTargetType": "RETAILER",
    //           "warehouseInventoryOutboundTargetTypeDescription": "零售商",
    //           "productRefTargetTypeDescription": "零售商",
    //           "productRefTargetType": "RETAILER",
    //           "serviceStoreOwningPartyType": "RETAILER",
    //           "serviceStoreOwningPartyTypeDescription": "零售商",
    //           "serviceRefTargetTypeDescription": "零售商",
    //           "serviceRefTargetType": "RETAILER",
    //           "sellerPartyTypeDescription": "零售商",
    //           "sellerPartyType": "RETAILER",
    //           "buyerPartyTypeDescription": "零售商",
    //           "buyerPartyType": "RETAILER"
    //         },
    //         {
    //           "flagList": [],
    //           "id": "202605110501481805050000",
    //           "createTimestamp": 1778475708000,
    //           "updateTimestamp": 1778555370000,
    //           "directSuperiorDistributorId": "202603230837030730530000",
    //           "fullName": "大连中升凌志汽车销售服务有限公司",
    //           "approvalStatus": "APPROVED",
    //           "approvalStatusDescription": "已审核",
    //           "districtId": "202509051418317075210000",
    //           "address": "华北路500号",
    //           "longitude": 121.5886,
    //           "latitude": 39.00833,
    //           "businessLicenseType": "BUSINESS_LICENSE",
    //           "businessLicenseTypeDescription": "营业执照",
    //           "businessLicenseNumber": "91210200760786296X",
    //           "firstBusinessLicensePicFileId": null,
    //           "secondBusinessLicensePicFileId": null,
    //           "thirdBusinessLicensePicFileId": null,
    //           "directSuperiorDistributor": {
    //             "flagList": [],
    //             "id": "202603230837030730530000",
    //             "createTimestamp": 1774255023000,
    //             "updateTimestamp": 1774255731000,
    //             "type": "DISTRIBUTOR",
    //             "typeDescription": "分销商",
    //             "level": 1,
    //             "directSuperiorDistributorId": null,
    //             "fullName": "兰考县翰峰信息科技有限公司",
    //             "approvalStatus": "APPROVED",
    //             "approvalStatusDescription": "已审核",
    //             "districtId": "202509051419452105210000",
    //             "address": "经济技术开发区兰考县科技路南50米兰考同乐居（跨境）电商家居产业园469号",
    //             "longitude": 0.0,
    //             "latitude": 0.0,
    //             "businessLicenseType": "BUSINESS_LICENSE",
    //             "businessLicenseTypeDescription": "营业执照",
    //             "businessLicenseNumber": "91410225MA9LHPBQ66",
    //             "firstBusinessLicensePicFileId": null,
    //             "secondBusinessLicensePicFileId": null,
    //             "thirdBusinessLicensePicFileId": null,
    //             "directSuperiorDistributor": null,
    //             "district": {
    //               "flagList": [],
    //               "id": "202509051419452105210000",
    //               "createTimestamp": 1757081985000,
    //               "updateTimestamp": 1757081985000,
    //               "cityId": "202509051419445765210000",
    //               "regionalismCode": "410225",
    //               "localizedFullName": "兰考县",
    //               "fullName": "兰考县",
    //               "fullNameZhCn": "兰考县",
    //               "city": {
    //                 "flagList": [],
    //                 "id": "202509051419445765210000",
    //                 "createTimestamp": 1757081985000,
    //                 "updateTimestamp": 1757081985000,
    //                 "provinceId": "202509051419435825210000",
    //                 "regionalismCode": "410200",
    //                 "localizedFullName": "开封市",
    //                 "fullName": "开封市",
    //                 "fullNameZhCn": "开封市",
    //                 "province": {
    //                   "flagList": [],
    //                   "id": "202509051419435825210000",
    //                   "createTimestamp": 1757081984000,
    //                   "updateTimestamp": 1757081984000,
    //                   "countryId": "202509051417566675210000",
    //                   "regionalismCode": "410000",
    //                   "localizedFullName": "河南省",
    //                   "fullName": "河南省",
    //                   "fullNameZhCn": "河南省",
    //                   "country": {
    //                     "flagList": [],
    //                     "id": "202509051417566675210000",
    //                     "createTimestamp": 1757081877000,
    //                     "updateTimestamp": 1757081877000,
    //                     "localizedFullName": "中华人民共和国",
    //                     "fullName": "中华人民共和国",
    //                     "fullNameZhCn": "中华人民共和国"
    //                   }
    //                 }
    //               }
    //             },
    //             "firstBusinessLicensePicFile": null,
    //             "secondBusinessLicensePicFile": null,
    //             "thirdBusinessLicensePicFile": null,
    //             "userPartyType": "DISTRIBUTOR",
    //             "userPartyTypeDescription": "分销商",
    //             "merchantPartyType": "DISTRIBUTOR",
    //             "merchantPartyTypeDescription": "分销商",
    //             "businessPartyTypeDescription": "分销商",
    //             "businessPartyType": "DISTRIBUTOR",
    //             "warrantyConfigRefTargetTypeDescription": "分销商",
    //             "warrantyConfigRefTargetType": "DISTRIBUTOR",
    //             "warehouseOwningPartyTypeDescription": "分销商",
    //             "warehouseOwningPartyType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceTypeDescription": "分销商",
    //             "warehouseInventoryOutboundTargetType": "DISTRIBUTOR",
    //             "warehouseInventoryOutboundTargetTypeDescription": "分销商",
    //             "productRefTargetTypeDescription": "分销商",
    //             "productRefTargetType": "DISTRIBUTOR",
    //             "sellerPartyTypeDescription": "分销商",
    //             "sellerPartyType": "DISTRIBUTOR",
    //             "buyerPartyTypeDescription": "分销商",
    //             "buyerPartyType": "DISTRIBUTOR"
    //           },
    //           "district": {
    //             "flagList": [],
    //             "id": "202509051418317075210000",
    //             "createTimestamp": 1757081912000,
    //             "updateTimestamp": 1757081912000,
    //             "cityId": "202509051418314265210000",
    //             "regionalismCode": "210211",
    //             "localizedFullName": "甘井子区",
    //             "fullName": "甘井子区",
    //             "fullNameZhCn": "甘井子区",
    //             "city": {
    //               "flagList": [],
    //               "id": "202509051418314265210000",
    //               "createTimestamp": 1757081911000,
    //               "updateTimestamp": 1757081911000,
    //               "provinceId": "202509051418303305210000",
    //               "regionalismCode": "210200",
    //               "localizedFullName": "大连市",
    //               "fullName": "大连市",
    //               "fullNameZhCn": "大连市",
    //               "province": {
    //                 "flagList": [],
    //                 "id": "202509051418303305210000",
    //                 "createTimestamp": 1757081910000,
    //                 "updateTimestamp": 1757081910000,
    //                 "countryId": "202509051417566675210000",
    //                 "regionalismCode": "210000",
    //                 "localizedFullName": "辽宁省",
    //                 "fullName": "辽宁省",
    //                 "fullNameZhCn": "辽宁省",
    //                 "country": {
    //                   "flagList": [],
    //                   "id": "202509051417566675210000",
    //                   "createTimestamp": 1757081877000,
    //                   "updateTimestamp": 1757081877000,
    //                   "localizedFullName": "中华人民共和国",
    //                   "fullName": "中华人民共和国",
    //                   "fullNameZhCn": "中华人民共和国"
    //                 }
    //               }
    //             }
    //           },
    //           "firstBusinessLicensePicFile": null,
    //           "secondBusinessLicensePicFile": null,
    //           "thirdBusinessLicensePicFile": null,
    //           "userPartyType": "RETAILER",
    //           "userPartyTypeDescription": "零售商",
    //           "merchantPartyType": "RETAILER",
    //           "merchantPartyTypeDescription": "零售商",
    //           "businessPartyTypeDescription": "零售商",
    //           "businessPartyType": "RETAILER",
    //           "warrantyConfigRefTargetTypeDescription": "零售商",
    //           "warrantyConfigRefTargetType": "RETAILER",
    //           "warehouseOwningPartyTypeDescription": "零售商",
    //           "warehouseOwningPartyType": "RETAILER",
    //           "warehouseInventoryOutboundTargetType": "RETAILER",
    //           "warehouseInventoryOutboundTargetTypeDescription": "零售商",
    //           "productRefTargetTypeDescription": "零售商",
    //           "productRefTargetType": "RETAILER",
    //           "serviceStoreOwningPartyType": "RETAILER",
    //           "serviceStoreOwningPartyTypeDescription": "零售商",
    //           "serviceRefTargetTypeDescription": "零售商",
    //           "serviceRefTargetType": "RETAILER",
    //           "sellerPartyTypeDescription": "零售商",
    //           "sellerPartyType": "RETAILER",
    //           "buyerPartyTypeDescription": "零售商",
    //           "buyerPartyType": "RETAILER"
    //         },
    //         {
    //           "flagList": [],
    //           "id": "202605120225171365050000",
    //           "createTimestamp": 1778552717000,
    //           "updateTimestamp": 1778555370000,
    //           "directSuperiorDistributorId": "202603230837030730530000",
    //           "fullName": "郑州中升之宝汽车销售服务有限公司",
    //           "approvalStatus": "APPROVED",
    //           "approvalStatusDescription": "已审核",
    //           "districtId": "202509051419437255210000",
    //           "address": "郑州市中原区西三环与中原路交叉口东南角",
    //           "longitude": 113.580078,
    //           "latitude": 34.747925,
    //           "businessLicenseType": "BUSINESS_LICENSE",
    //           "businessLicenseTypeDescription": "营业执照",
    //           "businessLicenseNumber": "91410100MA3X9J716B",
    //           "firstBusinessLicensePicFileId": null,
    //           "secondBusinessLicensePicFileId": null,
    //           "thirdBusinessLicensePicFileId": null,
    //           "directSuperiorDistributor": {
    //             "flagList": [],
    //             "id": "202603230837030730530000",
    //             "createTimestamp": 1774255023000,
    //             "updateTimestamp": 1774255731000,
    //             "type": "DISTRIBUTOR",
    //             "typeDescription": "分销商",
    //             "level": 1,
    //             "directSuperiorDistributorId": null,
    //             "fullName": "兰考县翰峰信息科技有限公司",
    //             "approvalStatus": "APPROVED",
    //             "approvalStatusDescription": "已审核",
    //             "districtId": "202509051419452105210000",
    //             "address": "经济技术开发区兰考县科技路南50米兰考同乐居（跨境）电商家居产业园469号",
    //             "longitude": 0.0,
    //             "latitude": 0.0,
    //             "businessLicenseType": "BUSINESS_LICENSE",
    //             "businessLicenseTypeDescription": "营业执照",
    //             "businessLicenseNumber": "91410225MA9LHPBQ66",
    //             "firstBusinessLicensePicFileId": null,
    //             "secondBusinessLicensePicFileId": null,
    //             "thirdBusinessLicensePicFileId": null,
    //             "directSuperiorDistributor": null,
    //             "district": {
    //               "flagList": [],
    //               "id": "202509051419452105210000",
    //               "createTimestamp": 1757081985000,
    //               "updateTimestamp": 1757081985000,
    //               "cityId": "202509051419445765210000",
    //               "regionalismCode": "410225",
    //               "localizedFullName": "兰考县",
    //               "fullName": "兰考县",
    //               "fullNameZhCn": "兰考县",
    //               "city": {
    //                 "flagList": [],
    //                 "id": "202509051419445765210000",
    //                 "createTimestamp": 1757081985000,
    //                 "updateTimestamp": 1757081985000,
    //                 "provinceId": "202509051419435825210000",
    //                 "regionalismCode": "410200",
    //                 "localizedFullName": "开封市",
    //                 "fullName": "开封市",
    //                 "fullNameZhCn": "开封市",
    //                 "province": {
    //                   "flagList": [],
    //                   "id": "202509051419435825210000",
    //                   "createTimestamp": 1757081984000,
    //                   "updateTimestamp": 1757081984000,
    //                   "countryId": "202509051417566675210000",
    //                   "regionalismCode": "410000",
    //                   "localizedFullName": "河南省",
    //                   "fullName": "河南省",
    //                   "fullNameZhCn": "河南省",
    //                   "country": {
    //                     "flagList": [],
    //                     "id": "202509051417566675210000",
    //                     "createTimestamp": 1757081877000,
    //                     "updateTimestamp": 1757081877000,
    //                     "localizedFullName": "中华人民共和国",
    //                     "fullName": "中华人民共和国",
    //                     "fullNameZhCn": "中华人民共和国"
    //                   }
    //                 }
    //               }
    //             },
    //             "firstBusinessLicensePicFile": null,
    //             "secondBusinessLicensePicFile": null,
    //             "thirdBusinessLicensePicFile": null,
    //             "userPartyType": "DISTRIBUTOR",
    //             "userPartyTypeDescription": "分销商",
    //             "merchantPartyType": "DISTRIBUTOR",
    //             "merchantPartyTypeDescription": "分销商",
    //             "businessPartyTypeDescription": "分销商",
    //             "businessPartyType": "DISTRIBUTOR",
    //             "warrantyConfigRefTargetTypeDescription": "分销商",
    //             "warrantyConfigRefTargetType": "DISTRIBUTOR",
    //             "warehouseOwningPartyTypeDescription": "分销商",
    //             "warehouseOwningPartyType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceTypeDescription": "分销商",
    //             "warehouseInventoryOutboundTargetType": "DISTRIBUTOR",
    //             "warehouseInventoryOutboundTargetTypeDescription": "分销商",
    //             "productRefTargetTypeDescription": "分销商",
    //             "productRefTargetType": "DISTRIBUTOR",
    //             "sellerPartyTypeDescription": "分销商",
    //             "sellerPartyType": "DISTRIBUTOR",
    //             "buyerPartyTypeDescription": "分销商",
    //             "buyerPartyType": "DISTRIBUTOR"
    //           },
    //           "district": {
    //             "flagList": [],
    //             "id": "202509051419437255210000",
    //             "createTimestamp": 1757081984000,
    //             "updateTimestamp": 1757081984000,
    //             "cityId": "202509051419436515210000",
    //             "regionalismCode": "410102",
    //             "localizedFullName": "中原区",
    //             "fullName": "中原区",
    //             "fullNameZhCn": "中原区",
    //             "city": {
    //               "flagList": [],
    //               "id": "202509051419436515210000",
    //               "createTimestamp": 1757081984000,
    //               "updateTimestamp": 1757081984000,
    //               "provinceId": "202509051419435825210000",
    //               "regionalismCode": "410100",
    //               "localizedFullName": "郑州市",
    //               "fullName": "郑州市",
    //               "fullNameZhCn": "郑州市",
    //               "province": {
    //                 "flagList": [],
    //                 "id": "202509051419435825210000",
    //                 "createTimestamp": 1757081984000,
    //                 "updateTimestamp": 1757081984000,
    //                 "countryId": "202509051417566675210000",
    //                 "regionalismCode": "410000",
    //                 "localizedFullName": "河南省",
    //                 "fullName": "河南省",
    //                 "fullNameZhCn": "河南省",
    //                 "country": {
    //                   "flagList": [],
    //                   "id": "202509051417566675210000",
    //                   "createTimestamp": 1757081877000,
    //                   "updateTimestamp": 1757081877000,
    //                   "localizedFullName": "中华人民共和国",
    //                   "fullName": "中华人民共和国",
    //                   "fullNameZhCn": "中华人民共和国"
    //                 }
    //               }
    //             }
    //           },
    //           "firstBusinessLicensePicFile": null,
    //           "secondBusinessLicensePicFile": null,
    //           "thirdBusinessLicensePicFile": null,
    //           "userPartyType": "RETAILER",
    //           "userPartyTypeDescription": "零售商",
    //           "merchantPartyType": "RETAILER",
    //           "merchantPartyTypeDescription": "零售商",
    //           "businessPartyTypeDescription": "零售商",
    //           "businessPartyType": "RETAILER",
    //           "warrantyConfigRefTargetTypeDescription": "零售商",
    //           "warrantyConfigRefTargetType": "RETAILER",
    //           "warehouseOwningPartyTypeDescription": "零售商",
    //           "warehouseOwningPartyType": "RETAILER",
    //           "warehouseInventoryOutboundTargetType": "RETAILER",
    //           "warehouseInventoryOutboundTargetTypeDescription": "零售商",
    //           "productRefTargetTypeDescription": "零售商",
    //           "productRefTargetType": "RETAILER",
    //           "serviceStoreOwningPartyType": "RETAILER",
    //           "serviceStoreOwningPartyTypeDescription": "零售商",
    //           "serviceRefTargetTypeDescription": "零售商",
    //           "serviceRefTargetType": "RETAILER",
    //           "sellerPartyTypeDescription": "零售商",
    //           "sellerPartyType": "RETAILER",
    //           "buyerPartyTypeDescription": "零售商",
    //           "buyerPartyType": "RETAILER"
    //         },
    //         {
    //           "flagList": [],
    //           "id": "202605120243082685050000",
    //           "createTimestamp": 1778553788000,
    //           "updateTimestamp": 1778555371000,
    //           "directSuperiorDistributorId": "202603230837030730530000",
    //           "fullName": "新疆中升汇迪汽车销售服务有限公司",
    //           "approvalStatus": "APPROVED",
    //           "approvalStatusDescription": "已审核",
    //           "districtId": "202509051421416925210000",
    //           "address": "新疆乌鲁木齐市水磨沟区红光山路1888号",
    //           "longitude": 87.61757,
    //           "latitude": 43.87362,
    //           "businessLicenseType": "BUSINESS_LICENSE",
    //           "businessLicenseTypeDescription": "营业执照",
    //           "businessLicenseNumber": "91650105MAEWPKLP0L",
    //           "firstBusinessLicensePicFileId": null,
    //           "secondBusinessLicensePicFileId": null,
    //           "thirdBusinessLicensePicFileId": null,
    //           "directSuperiorDistributor": {
    //             "flagList": [],
    //             "id": "202603230837030730530000",
    //             "createTimestamp": 1774255023000,
    //             "updateTimestamp": 1774255731000,
    //             "type": "DISTRIBUTOR",
    //             "typeDescription": "分销商",
    //             "level": 1,
    //             "directSuperiorDistributorId": null,
    //             "fullName": "兰考县翰峰信息科技有限公司",
    //             "approvalStatus": "APPROVED",
    //             "approvalStatusDescription": "已审核",
    //             "districtId": "202509051419452105210000",
    //             "address": "经济技术开发区兰考县科技路南50米兰考同乐居（跨境）电商家居产业园469号",
    //             "longitude": 0.0,
    //             "latitude": 0.0,
    //             "businessLicenseType": "BUSINESS_LICENSE",
    //             "businessLicenseTypeDescription": "营业执照",
    //             "businessLicenseNumber": "91410225MA9LHPBQ66",
    //             "firstBusinessLicensePicFileId": null,
    //             "secondBusinessLicensePicFileId": null,
    //             "thirdBusinessLicensePicFileId": null,
    //             "directSuperiorDistributor": null,
    //             "district": {
    //               "flagList": [],
    //               "id": "202509051419452105210000",
    //               "createTimestamp": 1757081985000,
    //               "updateTimestamp": 1757081985000,
    //               "cityId": "202509051419445765210000",
    //               "regionalismCode": "410225",
    //               "localizedFullName": "兰考县",
    //               "fullName": "兰考县",
    //               "fullNameZhCn": "兰考县",
    //               "city": {
    //                 "flagList": [],
    //                 "id": "202509051419445765210000",
    //                 "createTimestamp": 1757081985000,
    //                 "updateTimestamp": 1757081985000,
    //                 "provinceId": "202509051419435825210000",
    //                 "regionalismCode": "410200",
    //                 "localizedFullName": "开封市",
    //                 "fullName": "开封市",
    //                 "fullNameZhCn": "开封市",
    //                 "province": {
    //                   "flagList": [],
    //                   "id": "202509051419435825210000",
    //                   "createTimestamp": 1757081984000,
    //                   "updateTimestamp": 1757081984000,
    //                   "countryId": "202509051417566675210000",
    //                   "regionalismCode": "410000",
    //                   "localizedFullName": "河南省",
    //                   "fullName": "河南省",
    //                   "fullNameZhCn": "河南省",
    //                   "country": {
    //                     "flagList": [],
    //                     "id": "202509051417566675210000",
    //                     "createTimestamp": 1757081877000,
    //                     "updateTimestamp": 1757081877000,
    //                     "localizedFullName": "中华人民共和国",
    //                     "fullName": "中华人民共和国",
    //                     "fullNameZhCn": "中华人民共和国"
    //                   }
    //                 }
    //               }
    //             },
    //             "firstBusinessLicensePicFile": null,
    //             "secondBusinessLicensePicFile": null,
    //             "thirdBusinessLicensePicFile": null,
    //             "userPartyType": "DISTRIBUTOR",
    //             "userPartyTypeDescription": "分销商",
    //             "merchantPartyType": "DISTRIBUTOR",
    //             "merchantPartyTypeDescription": "分销商",
    //             "businessPartyTypeDescription": "分销商",
    //             "businessPartyType": "DISTRIBUTOR",
    //             "warrantyConfigRefTargetTypeDescription": "分销商",
    //             "warrantyConfigRefTargetType": "DISTRIBUTOR",
    //             "warehouseOwningPartyTypeDescription": "分销商",
    //             "warehouseOwningPartyType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceTypeDescription": "分销商",
    //             "warehouseInventoryOutboundTargetType": "DISTRIBUTOR",
    //             "warehouseInventoryOutboundTargetTypeDescription": "分销商",
    //             "productRefTargetTypeDescription": "分销商",
    //             "productRefTargetType": "DISTRIBUTOR",
    //             "sellerPartyTypeDescription": "分销商",
    //             "sellerPartyType": "DISTRIBUTOR",
    //             "buyerPartyTypeDescription": "分销商",
    //             "buyerPartyType": "DISTRIBUTOR"
    //           },
    //           "district": {
    //             "flagList": [],
    //             "id": "202509051421416925210000",
    //             "createTimestamp": 1757082102000,
    //             "updateTimestamp": 1757082102000,
    //             "cityId": "202509051421413975210000",
    //             "regionalismCode": "650105",
    //             "localizedFullName": "水磨沟区",
    //             "fullName": "水磨沟区",
    //             "fullNameZhCn": "水磨沟区",
    //             "city": {
    //               "flagList": [],
    //               "id": "202509051421413975210000",
    //               "createTimestamp": 1757082101000,
    //               "updateTimestamp": 1757082101000,
    //               "provinceId": "202509051421413275210000",
    //               "regionalismCode": "650100",
    //               "localizedFullName": "乌鲁木齐市",
    //               "fullName": "乌鲁木齐市",
    //               "fullNameZhCn": "乌鲁木齐市",
    //               "province": {
    //                 "flagList": [],
    //                 "id": "202509051421413275210000",
    //                 "createTimestamp": 1757082101000,
    //                 "updateTimestamp": 1757082101000,
    //                 "countryId": "202509051417566675210000",
    //                 "regionalismCode": "650000",
    //                 "localizedFullName": "新疆维吾尔自治区",
    //                 "fullName": "新疆维吾尔自治区",
    //                 "fullNameZhCn": "新疆维吾尔自治区",
    //                 "country": {
    //                   "flagList": [],
    //                   "id": "202509051417566675210000",
    //                   "createTimestamp": 1757081877000,
    //                   "updateTimestamp": 1757081877000,
    //                   "localizedFullName": "中华人民共和国",
    //                   "fullName": "中华人民共和国",
    //                   "fullNameZhCn": "中华人民共和国"
    //                 }
    //               }
    //             }
    //           },
    //           "firstBusinessLicensePicFile": null,
    //           "secondBusinessLicensePicFile": null,
    //           "thirdBusinessLicensePicFile": null,
    //           "userPartyType": "RETAILER",
    //           "userPartyTypeDescription": "零售商",
    //           "merchantPartyType": "RETAILER",
    //           "merchantPartyTypeDescription": "零售商",
    //           "businessPartyTypeDescription": "零售商",
    //           "businessPartyType": "RETAILER",
    //           "warrantyConfigRefTargetTypeDescription": "零售商",
    //           "warrantyConfigRefTargetType": "RETAILER",
    //           "warehouseOwningPartyTypeDescription": "零售商",
    //           "warehouseOwningPartyType": "RETAILER",
    //           "warehouseInventoryOutboundTargetType": "RETAILER",
    //           "warehouseInventoryOutboundTargetTypeDescription": "零售商",
    //           "productRefTargetTypeDescription": "零售商",
    //           "productRefTargetType": "RETAILER",
    //           "serviceStoreOwningPartyType": "RETAILER",
    //           "serviceStoreOwningPartyTypeDescription": "零售商",
    //           "serviceRefTargetTypeDescription": "零售商",
    //           "serviceRefTargetType": "RETAILER",
    //           "sellerPartyTypeDescription": "零售商",
    //           "sellerPartyType": "RETAILER",
    //           "buyerPartyTypeDescription": "零售商",
    //           "buyerPartyType": "RETAILER"
    //         },
    //         {
    //           "flagList": [],
    //           "id": "202605120422275645050000",
    //           "createTimestamp": 1778559748000,
    //           "updateTimestamp": 1779089001000,
    //           "directSuperiorDistributorId": "202603230837030730530000",
    //           "fullName": "新郑市中升汇宝汽车销售服务有限公司",
    //           "approvalStatus": "APPROVED",
    //           "approvalStatusDescription": "已审核",
    //           "districtId": "202509051419444365210000",
    //           "address": "河南省新郑市龙湖镇郑新快速路与泰山路交叉口向南200米路西宝马4S店",
    //           "longitude": 113.713961,
    //           "latitude": 34.590184,
    //           "businessLicenseType": "BUSINESS_LICENSE",
    //           "businessLicenseTypeDescription": "营业执照",
    //           "businessLicenseNumber": "91410184317372232X",
    //           "firstBusinessLicensePicFileId": null,
    //           "secondBusinessLicensePicFileId": null,
    //           "thirdBusinessLicensePicFileId": null,
    //           "directSuperiorDistributor": {
    //             "flagList": [],
    //             "id": "202603230837030730530000",
    //             "createTimestamp": 1774255023000,
    //             "updateTimestamp": 1774255731000,
    //             "type": "DISTRIBUTOR",
    //             "typeDescription": "分销商",
    //             "level": 1,
    //             "directSuperiorDistributorId": null,
    //             "fullName": "兰考县翰峰信息科技有限公司",
    //             "approvalStatus": "APPROVED",
    //             "approvalStatusDescription": "已审核",
    //             "districtId": "202509051419452105210000",
    //             "address": "经济技术开发区兰考县科技路南50米兰考同乐居（跨境）电商家居产业园469号",
    //             "longitude": 0.0,
    //             "latitude": 0.0,
    //             "businessLicenseType": "BUSINESS_LICENSE",
    //             "businessLicenseTypeDescription": "营业执照",
    //             "businessLicenseNumber": "91410225MA9LHPBQ66",
    //             "firstBusinessLicensePicFileId": null,
    //             "secondBusinessLicensePicFileId": null,
    //             "thirdBusinessLicensePicFileId": null,
    //             "directSuperiorDistributor": null,
    //             "district": {
    //               "flagList": [],
    //               "id": "202509051419452105210000",
    //               "createTimestamp": 1757081985000,
    //               "updateTimestamp": 1757081985000,
    //               "cityId": "202509051419445765210000",
    //               "regionalismCode": "410225",
    //               "localizedFullName": "兰考县",
    //               "fullName": "兰考县",
    //               "fullNameZhCn": "兰考县",
    //               "city": {
    //                 "flagList": [],
    //                 "id": "202509051419445765210000",
    //                 "createTimestamp": 1757081985000,
    //                 "updateTimestamp": 1757081985000,
    //                 "provinceId": "202509051419435825210000",
    //                 "regionalismCode": "410200",
    //                 "localizedFullName": "开封市",
    //                 "fullName": "开封市",
    //                 "fullNameZhCn": "开封市",
    //                 "province": {
    //                   "flagList": [],
    //                   "id": "202509051419435825210000",
    //                   "createTimestamp": 1757081984000,
    //                   "updateTimestamp": 1757081984000,
    //                   "countryId": "202509051417566675210000",
    //                   "regionalismCode": "410000",
    //                   "localizedFullName": "河南省",
    //                   "fullName": "河南省",
    //                   "fullNameZhCn": "河南省",
    //                   "country": {
    //                     "flagList": [],
    //                     "id": "202509051417566675210000",
    //                     "createTimestamp": 1757081877000,
    //                     "updateTimestamp": 1757081877000,
    //                     "localizedFullName": "中华人民共和国",
    //                     "fullName": "中华人民共和国",
    //                     "fullNameZhCn": "中华人民共和国"
    //                   }
    //                 }
    //               }
    //             },
    //             "firstBusinessLicensePicFile": null,
    //             "secondBusinessLicensePicFile": null,
    //             "thirdBusinessLicensePicFile": null,
    //             "userPartyType": "DISTRIBUTOR",
    //             "userPartyTypeDescription": "分销商",
    //             "merchantPartyType": "DISTRIBUTOR",
    //             "merchantPartyTypeDescription": "分销商",
    //             "businessPartyTypeDescription": "分销商",
    //             "businessPartyType": "DISTRIBUTOR",
    //             "warrantyConfigRefTargetTypeDescription": "分销商",
    //             "warrantyConfigRefTargetType": "DISTRIBUTOR",
    //             "warehouseOwningPartyTypeDescription": "分销商",
    //             "warehouseOwningPartyType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceTypeDescription": "分销商",
    //             "warehouseInventoryOutboundTargetType": "DISTRIBUTOR",
    //             "warehouseInventoryOutboundTargetTypeDescription": "分销商",
    //             "productRefTargetTypeDescription": "分销商",
    //             "productRefTargetType": "DISTRIBUTOR",
    //             "sellerPartyTypeDescription": "分销商",
    //             "sellerPartyType": "DISTRIBUTOR",
    //             "buyerPartyTypeDescription": "分销商",
    //             "buyerPartyType": "DISTRIBUTOR"
    //           },
    //           "district": {
    //             "flagList": [],
    //             "id": "202509051419444365210000",
    //             "createTimestamp": 1757081984000,
    //             "updateTimestamp": 1757081984000,
    //             "cityId": "202509051419436515210000",
    //             "regionalismCode": "410184",
    //             "localizedFullName": "新郑市",
    //             "fullName": "新郑市",
    //             "fullNameZhCn": "新郑市",
    //             "city": {
    //               "flagList": [],
    //               "id": "202509051419436515210000",
    //               "createTimestamp": 1757081984000,
    //               "updateTimestamp": 1757081984000,
    //               "provinceId": "202509051419435825210000",
    //               "regionalismCode": "410100",
    //               "localizedFullName": "郑州市",
    //               "fullName": "郑州市",
    //               "fullNameZhCn": "郑州市",
    //               "province": {
    //                 "flagList": [],
    //                 "id": "202509051419435825210000",
    //                 "createTimestamp": 1757081984000,
    //                 "updateTimestamp": 1757081984000,
    //                 "countryId": "202509051417566675210000",
    //                 "regionalismCode": "410000",
    //                 "localizedFullName": "河南省",
    //                 "fullName": "河南省",
    //                 "fullNameZhCn": "河南省",
    //                 "country": {
    //                   "flagList": [],
    //                   "id": "202509051417566675210000",
    //                   "createTimestamp": 1757081877000,
    //                   "updateTimestamp": 1757081877000,
    //                   "localizedFullName": "中华人民共和国",
    //                   "fullName": "中华人民共和国",
    //                   "fullNameZhCn": "中华人民共和国"
    //                 }
    //               }
    //             }
    //           },
    //           "firstBusinessLicensePicFile": null,
    //           "secondBusinessLicensePicFile": null,
    //           "thirdBusinessLicensePicFile": null,
    //           "userPartyType": "RETAILER",
    //           "userPartyTypeDescription": "零售商",
    //           "merchantPartyType": "RETAILER",
    //           "merchantPartyTypeDescription": "零售商",
    //           "businessPartyTypeDescription": "零售商",
    //           "businessPartyType": "RETAILER",
    //           "warrantyConfigRefTargetTypeDescription": "零售商",
    //           "warrantyConfigRefTargetType": "RETAILER",
    //           "warehouseOwningPartyTypeDescription": "零售商",
    //           "warehouseOwningPartyType": "RETAILER",
    //           "warehouseInventoryOutboundTargetType": "RETAILER",
    //           "warehouseInventoryOutboundTargetTypeDescription": "零售商",
    //           "productRefTargetTypeDescription": "零售商",
    //           "productRefTargetType": "RETAILER",
    //           "serviceStoreOwningPartyType": "RETAILER",
    //           "serviceStoreOwningPartyTypeDescription": "零售商",
    //           "serviceRefTargetTypeDescription": "零售商",
    //           "serviceRefTargetType": "RETAILER",
    //           "sellerPartyTypeDescription": "零售商",
    //           "sellerPartyType": "RETAILER",
    //           "buyerPartyTypeDescription": "零售商",
    //           "buyerPartyType": "RETAILER"
    //         },
    //         {
    //           "flagList": [],
    //           "id": "202605120428489205050000",
    //           "createTimestamp": 1778560129000,
    //           "updateTimestamp": 1779089002000,
    //           "directSuperiorDistributorId": "202603230837030730530000",
    //           "fullName": "郑州中升裕迪汽车销售服务有限公司",
    //           "approvalStatus": "APPROVED",
    //           "approvalStatusDescription": "已审核",
    //           "districtId": "202509051419441455210000",
    //           "address": "郑州市中牟县白沙镇郑开大道与京港澳高速交汇处东3公里贾鲁河桥南800米路西",
    //           "longitude": 113.8443,
    //           "latitude": 34.77385,
    //           "businessLicenseType": "BUSINESS_LICENSE",
    //           "businessLicenseTypeDescription": "营业执照",
    //           "businessLicenseNumber": "91410100MA44J4MW4N",
    //           "firstBusinessLicensePicFileId": null,
    //           "secondBusinessLicensePicFileId": null,
    //           "thirdBusinessLicensePicFileId": null,
    //           "directSuperiorDistributor": {
    //             "flagList": [],
    //             "id": "202603230837030730530000",
    //             "createTimestamp": 1774255023000,
    //             "updateTimestamp": 1774255731000,
    //             "type": "DISTRIBUTOR",
    //             "typeDescription": "分销商",
    //             "level": 1,
    //             "directSuperiorDistributorId": null,
    //             "fullName": "兰考县翰峰信息科技有限公司",
    //             "approvalStatus": "APPROVED",
    //             "approvalStatusDescription": "已审核",
    //             "districtId": "202509051419452105210000",
    //             "address": "经济技术开发区兰考县科技路南50米兰考同乐居（跨境）电商家居产业园469号",
    //             "longitude": 0.0,
    //             "latitude": 0.0,
    //             "businessLicenseType": "BUSINESS_LICENSE",
    //             "businessLicenseTypeDescription": "营业执照",
    //             "businessLicenseNumber": "91410225MA9LHPBQ66",
    //             "firstBusinessLicensePicFileId": null,
    //             "secondBusinessLicensePicFileId": null,
    //             "thirdBusinessLicensePicFileId": null,
    //             "directSuperiorDistributor": null,
    //             "district": {
    //               "flagList": [],
    //               "id": "202509051419452105210000",
    //               "createTimestamp": 1757081985000,
    //               "updateTimestamp": 1757081985000,
    //               "cityId": "202509051419445765210000",
    //               "regionalismCode": "410225",
    //               "localizedFullName": "兰考县",
    //               "fullName": "兰考县",
    //               "fullNameZhCn": "兰考县",
    //               "city": {
    //                 "flagList": [],
    //                 "id": "202509051419445765210000",
    //                 "createTimestamp": 1757081985000,
    //                 "updateTimestamp": 1757081985000,
    //                 "provinceId": "202509051419435825210000",
    //                 "regionalismCode": "410200",
    //                 "localizedFullName": "开封市",
    //                 "fullName": "开封市",
    //                 "fullNameZhCn": "开封市",
    //                 "province": {
    //                   "flagList": [],
    //                   "id": "202509051419435825210000",
    //                   "createTimestamp": 1757081984000,
    //                   "updateTimestamp": 1757081984000,
    //                   "countryId": "202509051417566675210000",
    //                   "regionalismCode": "410000",
    //                   "localizedFullName": "河南省",
    //                   "fullName": "河南省",
    //                   "fullNameZhCn": "河南省",
    //                   "country": {
    //                     "flagList": [],
    //                     "id": "202509051417566675210000",
    //                     "createTimestamp": 1757081877000,
    //                     "updateTimestamp": 1757081877000,
    //                     "localizedFullName": "中华人民共和国",
    //                     "fullName": "中华人民共和国",
    //                     "fullNameZhCn": "中华人民共和国"
    //                   }
    //                 }
    //               }
    //             },
    //             "firstBusinessLicensePicFile": null,
    //             "secondBusinessLicensePicFile": null,
    //             "thirdBusinessLicensePicFile": null,
    //             "userPartyType": "DISTRIBUTOR",
    //             "userPartyTypeDescription": "分销商",
    //             "merchantPartyType": "DISTRIBUTOR",
    //             "merchantPartyTypeDescription": "分销商",
    //             "businessPartyTypeDescription": "分销商",
    //             "businessPartyType": "DISTRIBUTOR",
    //             "warrantyConfigRefTargetTypeDescription": "分销商",
    //             "warrantyConfigRefTargetType": "DISTRIBUTOR",
    //             "warehouseOwningPartyTypeDescription": "分销商",
    //             "warehouseOwningPartyType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceTypeDescription": "分销商",
    //             "warehouseInventoryOutboundTargetType": "DISTRIBUTOR",
    //             "warehouseInventoryOutboundTargetTypeDescription": "分销商",
    //             "productRefTargetTypeDescription": "分销商",
    //             "productRefTargetType": "DISTRIBUTOR",
    //             "sellerPartyTypeDescription": "分销商",
    //             "sellerPartyType": "DISTRIBUTOR",
    //             "buyerPartyTypeDescription": "分销商",
    //             "buyerPartyType": "DISTRIBUTOR"
    //           },
    //           "district": {
    //             "flagList": [],
    //             "id": "202509051419441455210000",
    //             "createTimestamp": 1757081984000,
    //             "updateTimestamp": 1757081984000,
    //             "cityId": "202509051419436515210000",
    //             "regionalismCode": "410122",
    //             "localizedFullName": "中牟县",
    //             "fullName": "中牟县",
    //             "fullNameZhCn": "中牟县",
    //             "city": {
    //               "flagList": [],
    //               "id": "202509051419436515210000",
    //               "createTimestamp": 1757081984000,
    //               "updateTimestamp": 1757081984000,
    //               "provinceId": "202509051419435825210000",
    //               "regionalismCode": "410100",
    //               "localizedFullName": "郑州市",
    //               "fullName": "郑州市",
    //               "fullNameZhCn": "郑州市",
    //               "province": {
    //                 "flagList": [],
    //                 "id": "202509051419435825210000",
    //                 "createTimestamp": 1757081984000,
    //                 "updateTimestamp": 1757081984000,
    //                 "countryId": "202509051417566675210000",
    //                 "regionalismCode": "410000",
    //                 "localizedFullName": "河南省",
    //                 "fullName": "河南省",
    //                 "fullNameZhCn": "河南省",
    //                 "country": {
    //                   "flagList": [],
    //                   "id": "202509051417566675210000",
    //                   "createTimestamp": 1757081877000,
    //                   "updateTimestamp": 1757081877000,
    //                   "localizedFullName": "中华人民共和国",
    //                   "fullName": "中华人民共和国",
    //                   "fullNameZhCn": "中华人民共和国"
    //                 }
    //               }
    //             }
    //           },
    //           "firstBusinessLicensePicFile": null,
    //           "secondBusinessLicensePicFile": null,
    //           "thirdBusinessLicensePicFile": null,
    //           "userPartyType": "RETAILER",
    //           "userPartyTypeDescription": "零售商",
    //           "merchantPartyType": "RETAILER",
    //           "merchantPartyTypeDescription": "零售商",
    //           "businessPartyTypeDescription": "零售商",
    //           "businessPartyType": "RETAILER",
    //           "warrantyConfigRefTargetTypeDescription": "零售商",
    //           "warrantyConfigRefTargetType": "RETAILER",
    //           "warehouseOwningPartyTypeDescription": "零售商",
    //           "warehouseOwningPartyType": "RETAILER",
    //           "warehouseInventoryOutboundTargetType": "RETAILER",
    //           "warehouseInventoryOutboundTargetTypeDescription": "零售商",
    //           "productRefTargetTypeDescription": "零售商",
    //           "productRefTargetType": "RETAILER",
    //           "serviceStoreOwningPartyType": "RETAILER",
    //           "serviceStoreOwningPartyTypeDescription": "零售商",
    //           "serviceRefTargetTypeDescription": "零售商",
    //           "serviceRefTargetType": "RETAILER",
    //           "sellerPartyTypeDescription": "零售商",
    //           "sellerPartyType": "RETAILER",
    //           "buyerPartyTypeDescription": "零售商",
    //           "buyerPartyType": "RETAILER"
    //         },
    //         {
    //           "flagList": [],
    //           "id": "202605120505202205050000",
    //           "createTimestamp": 1778562320000,
    //           "updateTimestamp": 1779089002000,
    //           "directSuperiorDistributorId": "202603230837030730530000",
    //           "fullName": "洛阳中升汇宝汽车销售服务有限公司",
    //           "approvalStatus": "APPROVED",
    //           "approvalStatusDescription": "已审核",
    //           "districtId": "202509051419455645210000",
    //           "address": "河南省洛阳市涧西区建设路152号院内西区101号",
    //           "longitude": 112.39812,
    //           "latitude": 34.6655,
    //           "businessLicenseType": "BUSINESS_LICENSE",
    //           "businessLicenseTypeDescription": "营业执照",
    //           "businessLicenseNumber": "91410300MA3X6KFU0B",
    //           "firstBusinessLicensePicFileId": null,
    //           "secondBusinessLicensePicFileId": null,
    //           "thirdBusinessLicensePicFileId": null,
    //           "directSuperiorDistributor": {
    //             "flagList": [],
    //             "id": "202603230837030730530000",
    //             "createTimestamp": 1774255023000,
    //             "updateTimestamp": 1774255731000,
    //             "type": "DISTRIBUTOR",
    //             "typeDescription": "分销商",
    //             "level": 1,
    //             "directSuperiorDistributorId": null,
    //             "fullName": "兰考县翰峰信息科技有限公司",
    //             "approvalStatus": "APPROVED",
    //             "approvalStatusDescription": "已审核",
    //             "districtId": "202509051419452105210000",
    //             "address": "经济技术开发区兰考县科技路南50米兰考同乐居（跨境）电商家居产业园469号",
    //             "longitude": 0.0,
    //             "latitude": 0.0,
    //             "businessLicenseType": "BUSINESS_LICENSE",
    //             "businessLicenseTypeDescription": "营业执照",
    //             "businessLicenseNumber": "91410225MA9LHPBQ66",
    //             "firstBusinessLicensePicFileId": null,
    //             "secondBusinessLicensePicFileId": null,
    //             "thirdBusinessLicensePicFileId": null,
    //             "directSuperiorDistributor": null,
    //             "district": {
    //               "flagList": [],
    //               "id": "202509051419452105210000",
    //               "createTimestamp": 1757081985000,
    //               "updateTimestamp": 1757081985000,
    //               "cityId": "202509051419445765210000",
    //               "regionalismCode": "410225",
    //               "localizedFullName": "兰考县",
    //               "fullName": "兰考县",
    //               "fullNameZhCn": "兰考县",
    //               "city": {
    //                 "flagList": [],
    //                 "id": "202509051419445765210000",
    //                 "createTimestamp": 1757081985000,
    //                 "updateTimestamp": 1757081985000,
    //                 "provinceId": "202509051419435825210000",
    //                 "regionalismCode": "410200",
    //                 "localizedFullName": "开封市",
    //                 "fullName": "开封市",
    //                 "fullNameZhCn": "开封市",
    //                 "province": {
    //                   "flagList": [],
    //                   "id": "202509051419435825210000",
    //                   "createTimestamp": 1757081984000,
    //                   "updateTimestamp": 1757081984000,
    //                   "countryId": "202509051417566675210000",
    //                   "regionalismCode": "410000",
    //                   "localizedFullName": "河南省",
    //                   "fullName": "河南省",
    //                   "fullNameZhCn": "河南省",
    //                   "country": {
    //                     "flagList": [],
    //                     "id": "202509051417566675210000",
    //                     "createTimestamp": 1757081877000,
    //                     "updateTimestamp": 1757081877000,
    //                     "localizedFullName": "中华人民共和国",
    //                     "fullName": "中华人民共和国",
    //                     "fullNameZhCn": "中华人民共和国"
    //                   }
    //                 }
    //               }
    //             },
    //             "firstBusinessLicensePicFile": null,
    //             "secondBusinessLicensePicFile": null,
    //             "thirdBusinessLicensePicFile": null,
    //             "userPartyType": "DISTRIBUTOR",
    //             "userPartyTypeDescription": "分销商",
    //             "merchantPartyType": "DISTRIBUTOR",
    //             "merchantPartyTypeDescription": "分销商",
    //             "businessPartyTypeDescription": "分销商",
    //             "businessPartyType": "DISTRIBUTOR",
    //             "warrantyConfigRefTargetTypeDescription": "分销商",
    //             "warrantyConfigRefTargetType": "DISTRIBUTOR",
    //             "warehouseOwningPartyTypeDescription": "分销商",
    //             "warehouseOwningPartyType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceTypeDescription": "分销商",
    //             "warehouseInventoryOutboundTargetType": "DISTRIBUTOR",
    //             "warehouseInventoryOutboundTargetTypeDescription": "分销商",
    //             "productRefTargetTypeDescription": "分销商",
    //             "productRefTargetType": "DISTRIBUTOR",
    //             "sellerPartyTypeDescription": "分销商",
    //             "sellerPartyType": "DISTRIBUTOR",
    //             "buyerPartyTypeDescription": "分销商",
    //             "buyerPartyType": "DISTRIBUTOR"
    //           },
    //           "district": {
    //             "flagList": [],
    //             "id": "202509051419455645210000",
    //             "createTimestamp": 1757081986000,
    //             "updateTimestamp": 1757081986000,
    //             "cityId": "202509051419452825210000",
    //             "regionalismCode": "410305",
    //             "localizedFullName": "涧西区",
    //             "fullName": "涧西区",
    //             "fullNameZhCn": "涧西区",
    //             "city": {
    //               "flagList": [],
    //               "id": "202509051419452825210000",
    //               "createTimestamp": 1757081985000,
    //               "updateTimestamp": 1757081985000,
    //               "provinceId": "202509051419435825210000",
    //               "regionalismCode": "410300",
    //               "localizedFullName": "洛阳市",
    //               "fullName": "洛阳市",
    //               "fullNameZhCn": "洛阳市",
    //               "province": {
    //                 "flagList": [],
    //                 "id": "202509051419435825210000",
    //                 "createTimestamp": 1757081984000,
    //                 "updateTimestamp": 1757081984000,
    //                 "countryId": "202509051417566675210000",
    //                 "regionalismCode": "410000",
    //                 "localizedFullName": "河南省",
    //                 "fullName": "河南省",
    //                 "fullNameZhCn": "河南省",
    //                 "country": {
    //                   "flagList": [],
    //                   "id": "202509051417566675210000",
    //                   "createTimestamp": 1757081877000,
    //                   "updateTimestamp": 1757081877000,
    //                   "localizedFullName": "中华人民共和国",
    //                   "fullName": "中华人民共和国",
    //                   "fullNameZhCn": "中华人民共和国"
    //                 }
    //               }
    //             }
    //           },
    //           "firstBusinessLicensePicFile": null,
    //           "secondBusinessLicensePicFile": null,
    //           "thirdBusinessLicensePicFile": null,
    //           "userPartyType": "RETAILER",
    //           "userPartyTypeDescription": "零售商",
    //           "merchantPartyType": "RETAILER",
    //           "merchantPartyTypeDescription": "零售商",
    //           "businessPartyTypeDescription": "零售商",
    //           "businessPartyType": "RETAILER",
    //           "warrantyConfigRefTargetTypeDescription": "零售商",
    //           "warrantyConfigRefTargetType": "RETAILER",
    //           "warehouseOwningPartyTypeDescription": "零售商",
    //           "warehouseOwningPartyType": "RETAILER",
    //           "warehouseInventoryOutboundTargetType": "RETAILER",
    //           "warehouseInventoryOutboundTargetTypeDescription": "零售商",
    //           "productRefTargetTypeDescription": "零售商",
    //           "productRefTargetType": "RETAILER",
    //           "serviceStoreOwningPartyType": "RETAILER",
    //           "serviceStoreOwningPartyTypeDescription": "零售商",
    //           "serviceRefTargetTypeDescription": "零售商",
    //           "serviceRefTargetType": "RETAILER",
    //           "sellerPartyTypeDescription": "零售商",
    //           "sellerPartyType": "RETAILER",
    //           "buyerPartyTypeDescription": "零售商",
    //           "buyerPartyType": "RETAILER"
    //         },
    //         {
    //           "flagList": [],
    //           "id": "202605120508346185050000",
    //           "createTimestamp": 1778562515000,
    //           "updateTimestamp": 1779089003000,
    //           "directSuperiorDistributorId": "202603230837030730530000",
    //           "fullName": "郑州中升汇迪汽车销售服务有限公司",
    //           "approvalStatus": "APPROVED",
    //           "approvalStatusDescription": "已审核",
    //           "districtId": "202509051419440755210000",
    //           "address": "郑州市惠济区银通路东开元路南（河南汽贸中心院内）",
    //           "longitude": 113.6649,
    //           "latitude": 34.86544,
    //           "businessLicenseType": "BUSINESS_LICENSE",
    //           "businessLicenseTypeDescription": "营业执照",
    //           "businessLicenseNumber": "91410100551613241G",
    //           "firstBusinessLicensePicFileId": null,
    //           "secondBusinessLicensePicFileId": null,
    //           "thirdBusinessLicensePicFileId": null,
    //           "directSuperiorDistributor": {
    //             "flagList": [],
    //             "id": "202603230837030730530000",
    //             "createTimestamp": 1774255023000,
    //             "updateTimestamp": 1774255731000,
    //             "type": "DISTRIBUTOR",
    //             "typeDescription": "分销商",
    //             "level": 1,
    //             "directSuperiorDistributorId": null,
    //             "fullName": "兰考县翰峰信息科技有限公司",
    //             "approvalStatus": "APPROVED",
    //             "approvalStatusDescription": "已审核",
    //             "districtId": "202509051419452105210000",
    //             "address": "经济技术开发区兰考县科技路南50米兰考同乐居（跨境）电商家居产业园469号",
    //             "longitude": 0.0,
    //             "latitude": 0.0,
    //             "businessLicenseType": "BUSINESS_LICENSE",
    //             "businessLicenseTypeDescription": "营业执照",
    //             "businessLicenseNumber": "91410225MA9LHPBQ66",
    //             "firstBusinessLicensePicFileId": null,
    //             "secondBusinessLicensePicFileId": null,
    //             "thirdBusinessLicensePicFileId": null,
    //             "directSuperiorDistributor": null,
    //             "district": {
    //               "flagList": [],
    //               "id": "202509051419452105210000",
    //               "createTimestamp": 1757081985000,
    //               "updateTimestamp": 1757081985000,
    //               "cityId": "202509051419445765210000",
    //               "regionalismCode": "410225",
    //               "localizedFullName": "兰考县",
    //               "fullName": "兰考县",
    //               "fullNameZhCn": "兰考县",
    //               "city": {
    //                 "flagList": [],
    //                 "id": "202509051419445765210000",
    //                 "createTimestamp": 1757081985000,
    //                 "updateTimestamp": 1757081985000,
    //                 "provinceId": "202509051419435825210000",
    //                 "regionalismCode": "410200",
    //                 "localizedFullName": "开封市",
    //                 "fullName": "开封市",
    //                 "fullNameZhCn": "开封市",
    //                 "province": {
    //                   "flagList": [],
    //                   "id": "202509051419435825210000",
    //                   "createTimestamp": 1757081984000,
    //                   "updateTimestamp": 1757081984000,
    //                   "countryId": "202509051417566675210000",
    //                   "regionalismCode": "410000",
    //                   "localizedFullName": "河南省",
    //                   "fullName": "河南省",
    //                   "fullNameZhCn": "河南省",
    //                   "country": {
    //                     "flagList": [],
    //                     "id": "202509051417566675210000",
    //                     "createTimestamp": 1757081877000,
    //                     "updateTimestamp": 1757081877000,
    //                     "localizedFullName": "中华人民共和国",
    //                     "fullName": "中华人民共和国",
    //                     "fullNameZhCn": "中华人民共和国"
    //                   }
    //                 }
    //               }
    //             },
    //             "firstBusinessLicensePicFile": null,
    //             "secondBusinessLicensePicFile": null,
    //             "thirdBusinessLicensePicFile": null,
    //             "userPartyType": "DISTRIBUTOR",
    //             "userPartyTypeDescription": "分销商",
    //             "merchantPartyType": "DISTRIBUTOR",
    //             "merchantPartyTypeDescription": "分销商",
    //             "businessPartyTypeDescription": "分销商",
    //             "businessPartyType": "DISTRIBUTOR",
    //             "warrantyConfigRefTargetTypeDescription": "分销商",
    //             "warrantyConfigRefTargetType": "DISTRIBUTOR",
    //             "warehouseOwningPartyTypeDescription": "分销商",
    //             "warehouseOwningPartyType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceTypeDescription": "分销商",
    //             "warehouseInventoryOutboundTargetType": "DISTRIBUTOR",
    //             "warehouseInventoryOutboundTargetTypeDescription": "分销商",
    //             "productRefTargetTypeDescription": "分销商",
    //             "productRefTargetType": "DISTRIBUTOR",
    //             "sellerPartyTypeDescription": "分销商",
    //             "sellerPartyType": "DISTRIBUTOR",
    //             "buyerPartyTypeDescription": "分销商",
    //             "buyerPartyType": "DISTRIBUTOR"
    //           },
    //           "district": {
    //             "flagList": [],
    //             "id": "202509051419440755210000",
    //             "createTimestamp": 1757081984000,
    //             "updateTimestamp": 1757081984000,
    //             "cityId": "202509051419436515210000",
    //             "regionalismCode": "410108",
    //             "localizedFullName": "惠济区",
    //             "fullName": "惠济区",
    //             "fullNameZhCn": "惠济区",
    //             "city": {
    //               "flagList": [],
    //               "id": "202509051419436515210000",
    //               "createTimestamp": 1757081984000,
    //               "updateTimestamp": 1757081984000,
    //               "provinceId": "202509051419435825210000",
    //               "regionalismCode": "410100",
    //               "localizedFullName": "郑州市",
    //               "fullName": "郑州市",
    //               "fullNameZhCn": "郑州市",
    //               "province": {
    //                 "flagList": [],
    //                 "id": "202509051419435825210000",
    //                 "createTimestamp": 1757081984000,
    //                 "updateTimestamp": 1757081984000,
    //                 "countryId": "202509051417566675210000",
    //                 "regionalismCode": "410000",
    //                 "localizedFullName": "河南省",
    //                 "fullName": "河南省",
    //                 "fullNameZhCn": "河南省",
    //                 "country": {
    //                   "flagList": [],
    //                   "id": "202509051417566675210000",
    //                   "createTimestamp": 1757081877000,
    //                   "updateTimestamp": 1757081877000,
    //                   "localizedFullName": "中华人民共和国",
    //                   "fullName": "中华人民共和国",
    //                   "fullNameZhCn": "中华人民共和国"
    //                 }
    //               }
    //             }
    //           },
    //           "firstBusinessLicensePicFile": null,
    //           "secondBusinessLicensePicFile": null,
    //           "thirdBusinessLicensePicFile": null,
    //           "userPartyType": "RETAILER",
    //           "userPartyTypeDescription": "零售商",
    //           "merchantPartyType": "RETAILER",
    //           "merchantPartyTypeDescription": "零售商",
    //           "businessPartyTypeDescription": "零售商",
    //           "businessPartyType": "RETAILER",
    //           "warrantyConfigRefTargetTypeDescription": "零售商",
    //           "warrantyConfigRefTargetType": "RETAILER",
    //           "warehouseOwningPartyTypeDescription": "零售商",
    //           "warehouseOwningPartyType": "RETAILER",
    //           "warehouseInventoryOutboundTargetType": "RETAILER",
    //           "warehouseInventoryOutboundTargetTypeDescription": "零售商",
    //           "productRefTargetTypeDescription": "零售商",
    //           "productRefTargetType": "RETAILER",
    //           "serviceStoreOwningPartyType": "RETAILER",
    //           "serviceStoreOwningPartyTypeDescription": "零售商",
    //           "serviceRefTargetTypeDescription": "零售商",
    //           "serviceRefTargetType": "RETAILER",
    //           "sellerPartyTypeDescription": "零售商",
    //           "sellerPartyType": "RETAILER",
    //           "buyerPartyTypeDescription": "零售商",
    //           "buyerPartyType": "RETAILER"
    //         },
    //         {
    //           "flagList": [],
    //           "id": "202605180721184715050000",
    //           "createTimestamp": 1779088878000,
    //           "updateTimestamp": 1779089003000,
    //           "directSuperiorDistributorId": "202603230837030730530000",
    //           "fullName": "大连中升汽车销售服务有限公司",
    //           "approvalStatus": "APPROVED",
    //           "approvalStatusDescription": "已审核",
    //           "districtId": "202509051418317075210000",
    //           "address": "辛寨子街道虹港路560号",
    //           "longitude": 121.5172,
    //           "latitude": 38.96061,
    //           "businessLicenseType": "BUSINESS_LICENSE",
    //           "businessLicenseTypeDescription": "营业执照",
    //           "businessLicenseNumber": "91210200782480183B",
    //           "firstBusinessLicensePicFileId": null,
    //           "secondBusinessLicensePicFileId": null,
    //           "thirdBusinessLicensePicFileId": null,
    //           "directSuperiorDistributor": {
    //             "flagList": [],
    //             "id": "202603230837030730530000",
    //             "createTimestamp": 1774255023000,
    //             "updateTimestamp": 1774255731000,
    //             "type": "DISTRIBUTOR",
    //             "typeDescription": "分销商",
    //             "level": 1,
    //             "directSuperiorDistributorId": null,
    //             "fullName": "兰考县翰峰信息科技有限公司",
    //             "approvalStatus": "APPROVED",
    //             "approvalStatusDescription": "已审核",
    //             "districtId": "202509051419452105210000",
    //             "address": "经济技术开发区兰考县科技路南50米兰考同乐居（跨境）电商家居产业园469号",
    //             "longitude": 0.0,
    //             "latitude": 0.0,
    //             "businessLicenseType": "BUSINESS_LICENSE",
    //             "businessLicenseTypeDescription": "营业执照",
    //             "businessLicenseNumber": "91410225MA9LHPBQ66",
    //             "firstBusinessLicensePicFileId": null,
    //             "secondBusinessLicensePicFileId": null,
    //             "thirdBusinessLicensePicFileId": null,
    //             "directSuperiorDistributor": null,
    //             "district": {
    //               "flagList": [],
    //               "id": "202509051419452105210000",
    //               "createTimestamp": 1757081985000,
    //               "updateTimestamp": 1757081985000,
    //               "cityId": "202509051419445765210000",
    //               "regionalismCode": "410225",
    //               "localizedFullName": "兰考县",
    //               "fullName": "兰考县",
    //               "fullNameZhCn": "兰考县",
    //               "city": {
    //                 "flagList": [],
    //                 "id": "202509051419445765210000",
    //                 "createTimestamp": 1757081985000,
    //                 "updateTimestamp": 1757081985000,
    //                 "provinceId": "202509051419435825210000",
    //                 "regionalismCode": "410200",
    //                 "localizedFullName": "开封市",
    //                 "fullName": "开封市",
    //                 "fullNameZhCn": "开封市",
    //                 "province": {
    //                   "flagList": [],
    //                   "id": "202509051419435825210000",
    //                   "createTimestamp": 1757081984000,
    //                   "updateTimestamp": 1757081984000,
    //                   "countryId": "202509051417566675210000",
    //                   "regionalismCode": "410000",
    //                   "localizedFullName": "河南省",
    //                   "fullName": "河南省",
    //                   "fullNameZhCn": "河南省",
    //                   "country": {
    //                     "flagList": [],
    //                     "id": "202509051417566675210000",
    //                     "createTimestamp": 1757081877000,
    //                     "updateTimestamp": 1757081877000,
    //                     "localizedFullName": "中华人民共和国",
    //                     "fullName": "中华人民共和国",
    //                     "fullNameZhCn": "中华人民共和国"
    //                   }
    //                 }
    //               }
    //             },
    //             "firstBusinessLicensePicFile": null,
    //             "secondBusinessLicensePicFile": null,
    //             "thirdBusinessLicensePicFile": null,
    //             "userPartyType": "DISTRIBUTOR",
    //             "userPartyTypeDescription": "分销商",
    //             "merchantPartyType": "DISTRIBUTOR",
    //             "merchantPartyTypeDescription": "分销商",
    //             "businessPartyTypeDescription": "分销商",
    //             "businessPartyType": "DISTRIBUTOR",
    //             "warrantyConfigRefTargetTypeDescription": "分销商",
    //             "warrantyConfigRefTargetType": "DISTRIBUTOR",
    //             "warehouseOwningPartyTypeDescription": "分销商",
    //             "warehouseOwningPartyType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceTypeDescription": "分销商",
    //             "warehouseInventoryOutboundTargetType": "DISTRIBUTOR",
    //             "warehouseInventoryOutboundTargetTypeDescription": "分销商",
    //             "productRefTargetTypeDescription": "分销商",
    //             "productRefTargetType": "DISTRIBUTOR",
    //             "sellerPartyTypeDescription": "分销商",
    //             "sellerPartyType": "DISTRIBUTOR",
    //             "buyerPartyTypeDescription": "分销商",
    //             "buyerPartyType": "DISTRIBUTOR"
    //           },
    //           "district": {
    //             "flagList": [],
    //             "id": "202509051418317075210000",
    //             "createTimestamp": 1757081912000,
    //             "updateTimestamp": 1757081912000,
    //             "cityId": "202509051418314265210000",
    //             "regionalismCode": "210211",
    //             "localizedFullName": "甘井子区",
    //             "fullName": "甘井子区",
    //             "fullNameZhCn": "甘井子区",
    //             "city": {
    //               "flagList": [],
    //               "id": "202509051418314265210000",
    //               "createTimestamp": 1757081911000,
    //               "updateTimestamp": 1757081911000,
    //               "provinceId": "202509051418303305210000",
    //               "regionalismCode": "210200",
    //               "localizedFullName": "大连市",
    //               "fullName": "大连市",
    //               "fullNameZhCn": "大连市",
    //               "province": {
    //                 "flagList": [],
    //                 "id": "202509051418303305210000",
    //                 "createTimestamp": 1757081910000,
    //                 "updateTimestamp": 1757081910000,
    //                 "countryId": "202509051417566675210000",
    //                 "regionalismCode": "210000",
    //                 "localizedFullName": "辽宁省",
    //                 "fullName": "辽宁省",
    //                 "fullNameZhCn": "辽宁省",
    //                 "country": {
    //                   "flagList": [],
    //                   "id": "202509051417566675210000",
    //                   "createTimestamp": 1757081877000,
    //                   "updateTimestamp": 1757081877000,
    //                   "localizedFullName": "中华人民共和国",
    //                   "fullName": "中华人民共和国",
    //                   "fullNameZhCn": "中华人民共和国"
    //                 }
    //               }
    //             }
    //           },
    //           "firstBusinessLicensePicFile": null,
    //           "secondBusinessLicensePicFile": null,
    //           "thirdBusinessLicensePicFile": null,
    //           "userPartyType": "RETAILER",
    //           "userPartyTypeDescription": "零售商",
    //           "merchantPartyType": "RETAILER",
    //           "merchantPartyTypeDescription": "零售商",
    //           "businessPartyTypeDescription": "零售商",
    //           "businessPartyType": "RETAILER",
    //           "warrantyConfigRefTargetTypeDescription": "零售商",
    //           "warrantyConfigRefTargetType": "RETAILER",
    //           "warehouseOwningPartyTypeDescription": "零售商",
    //           "warehouseOwningPartyType": "RETAILER",
    //           "warehouseInventoryOutboundTargetType": "RETAILER",
    //           "warehouseInventoryOutboundTargetTypeDescription": "零售商",
    //           "productRefTargetTypeDescription": "零售商",
    //           "productRefTargetType": "RETAILER",
    //           "serviceStoreOwningPartyType": "RETAILER",
    //           "serviceStoreOwningPartyTypeDescription": "零售商",
    //           "serviceRefTargetTypeDescription": "零售商",
    //           "serviceRefTargetType": "RETAILER",
    //           "sellerPartyTypeDescription": "零售商",
    //           "sellerPartyType": "RETAILER",
    //           "buyerPartyTypeDescription": "零售商",
    //           "buyerPartyType": "RETAILER"
    //         },
    //         {
    //           "flagList": [],
    //           "id": "202605190102355195050000",
    //           "createTimestamp": 1779152556000,
    //           "updateTimestamp": 1779364437000,
    //           "directSuperiorDistributorId": "202603230837030730530000",
    //           "fullName": "大连中升庆通汽车销售服务有限公司",
    //           "approvalStatus": "APPROVED",
    //           "approvalStatusDescription": "已审核",
    //           "districtId": "202509051418318475210000",
    //           "address": "辽宁省大连保税区填海区中港北二路18号",
    //           "longitude": 121.8361,
    //           "latitude": 39.04122,
    //           "businessLicenseType": "BUSINESS_LICENSE",
    //           "businessLicenseTypeDescription": "营业执照",
    //           "businessLicenseNumber": "912102425655029992",
    //           "firstBusinessLicensePicFileId": null,
    //           "secondBusinessLicensePicFileId": null,
    //           "thirdBusinessLicensePicFileId": null,
    //           "directSuperiorDistributor": {
    //             "flagList": [],
    //             "id": "202603230837030730530000",
    //             "createTimestamp": 1774255023000,
    //             "updateTimestamp": 1774255731000,
    //             "type": "DISTRIBUTOR",
    //             "typeDescription": "分销商",
    //             "level": 1,
    //             "directSuperiorDistributorId": null,
    //             "fullName": "兰考县翰峰信息科技有限公司",
    //             "approvalStatus": "APPROVED",
    //             "approvalStatusDescription": "已审核",
    //             "districtId": "202509051419452105210000",
    //             "address": "经济技术开发区兰考县科技路南50米兰考同乐居（跨境）电商家居产业园469号",
    //             "longitude": 0.0,
    //             "latitude": 0.0,
    //             "businessLicenseType": "BUSINESS_LICENSE",
    //             "businessLicenseTypeDescription": "营业执照",
    //             "businessLicenseNumber": "91410225MA9LHPBQ66",
    //             "firstBusinessLicensePicFileId": null,
    //             "secondBusinessLicensePicFileId": null,
    //             "thirdBusinessLicensePicFileId": null,
    //             "directSuperiorDistributor": null,
    //             "district": {
    //               "flagList": [],
    //               "id": "202509051419452105210000",
    //               "createTimestamp": 1757081985000,
    //               "updateTimestamp": 1757081985000,
    //               "cityId": "202509051419445765210000",
    //               "regionalismCode": "410225",
    //               "localizedFullName": "兰考县",
    //               "fullName": "兰考县",
    //               "fullNameZhCn": "兰考县",
    //               "city": {
    //                 "flagList": [],
    //                 "id": "202509051419445765210000",
    //                 "createTimestamp": 1757081985000,
    //                 "updateTimestamp": 1757081985000,
    //                 "provinceId": "202509051419435825210000",
    //                 "regionalismCode": "410200",
    //                 "localizedFullName": "开封市",
    //                 "fullName": "开封市",
    //                 "fullNameZhCn": "开封市",
    //                 "province": {
    //                   "flagList": [],
    //                   "id": "202509051419435825210000",
    //                   "createTimestamp": 1757081984000,
    //                   "updateTimestamp": 1757081984000,
    //                   "countryId": "202509051417566675210000",
    //                   "regionalismCode": "410000",
    //                   "localizedFullName": "河南省",
    //                   "fullName": "河南省",
    //                   "fullNameZhCn": "河南省",
    //                   "country": {
    //                     "flagList": [],
    //                     "id": "202509051417566675210000",
    //                     "createTimestamp": 1757081877000,
    //                     "updateTimestamp": 1757081877000,
    //                     "localizedFullName": "中华人民共和国",
    //                     "fullName": "中华人民共和国",
    //                     "fullNameZhCn": "中华人民共和国"
    //                   }
    //                 }
    //               }
    //             },
    //             "firstBusinessLicensePicFile": null,
    //             "secondBusinessLicensePicFile": null,
    //             "thirdBusinessLicensePicFile": null,
    //             "userPartyType": "DISTRIBUTOR",
    //             "userPartyTypeDescription": "分销商",
    //             "merchantPartyType": "DISTRIBUTOR",
    //             "merchantPartyTypeDescription": "分销商",
    //             "businessPartyTypeDescription": "分销商",
    //             "businessPartyType": "DISTRIBUTOR",
    //             "warrantyConfigRefTargetTypeDescription": "分销商",
    //             "warrantyConfigRefTargetType": "DISTRIBUTOR",
    //             "warehouseOwningPartyTypeDescription": "分销商",
    //             "warehouseOwningPartyType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceTypeDescription": "分销商",
    //             "warehouseInventoryOutboundTargetType": "DISTRIBUTOR",
    //             "warehouseInventoryOutboundTargetTypeDescription": "分销商",
    //             "productRefTargetTypeDescription": "分销商",
    //             "productRefTargetType": "DISTRIBUTOR",
    //             "sellerPartyTypeDescription": "分销商",
    //             "sellerPartyType": "DISTRIBUTOR",
    //             "buyerPartyTypeDescription": "分销商",
    //             "buyerPartyType": "DISTRIBUTOR"
    //           },
    //           "district": {
    //             "flagList": [],
    //             "id": "202509051418318475210000",
    //             "createTimestamp": 1757081912000,
    //             "updateTimestamp": 1757081912000,
    //             "cityId": "202509051418314265210000",
    //             "regionalismCode": "210213",
    //             "localizedFullName": "金州区",
    //             "fullName": "金州区",
    //             "fullNameZhCn": "金州区",
    //             "city": {
    //               "flagList": [],
    //               "id": "202509051418314265210000",
    //               "createTimestamp": 1757081911000,
    //               "updateTimestamp": 1757081911000,
    //               "provinceId": "202509051418303305210000",
    //               "regionalismCode": "210200",
    //               "localizedFullName": "大连市",
    //               "fullName": "大连市",
    //               "fullNameZhCn": "大连市",
    //               "province": {
    //                 "flagList": [],
    //                 "id": "202509051418303305210000",
    //                 "createTimestamp": 1757081910000,
    //                 "updateTimestamp": 1757081910000,
    //                 "countryId": "202509051417566675210000",
    //                 "regionalismCode": "210000",
    //                 "localizedFullName": "辽宁省",
    //                 "fullName": "辽宁省",
    //                 "fullNameZhCn": "辽宁省",
    //                 "country": {
    //                   "flagList": [],
    //                   "id": "202509051417566675210000",
    //                   "createTimestamp": 1757081877000,
    //                   "updateTimestamp": 1757081877000,
    //                   "localizedFullName": "中华人民共和国",
    //                   "fullName": "中华人民共和国",
    //                   "fullNameZhCn": "中华人民共和国"
    //                 }
    //               }
    //             }
    //           },
    //           "firstBusinessLicensePicFile": null,
    //           "secondBusinessLicensePicFile": null,
    //           "thirdBusinessLicensePicFile": null,
    //           "userPartyType": "RETAILER",
    //           "userPartyTypeDescription": "零售商",
    //           "merchantPartyType": "RETAILER",
    //           "merchantPartyTypeDescription": "零售商",
    //           "businessPartyTypeDescription": "零售商",
    //           "businessPartyType": "RETAILER",
    //           "warrantyConfigRefTargetTypeDescription": "零售商",
    //           "warrantyConfigRefTargetType": "RETAILER",
    //           "warehouseOwningPartyTypeDescription": "零售商",
    //           "warehouseOwningPartyType": "RETAILER",
    //           "warehouseInventoryOutboundTargetType": "RETAILER",
    //           "warehouseInventoryOutboundTargetTypeDescription": "零售商",
    //           "productRefTargetTypeDescription": "零售商",
    //           "productRefTargetType": "RETAILER",
    //           "serviceStoreOwningPartyType": "RETAILER",
    //           "serviceStoreOwningPartyTypeDescription": "零售商",
    //           "serviceRefTargetTypeDescription": "零售商",
    //           "serviceRefTargetType": "RETAILER",
    //           "sellerPartyTypeDescription": "零售商",
    //           "sellerPartyType": "RETAILER",
    //           "buyerPartyTypeDescription": "零售商",
    //           "buyerPartyType": "RETAILER"
    //         },
    //         {
    //           "flagList": [],
    //           "id": "202605190143092155050000",
    //           "createTimestamp": 1779154989000,
    //           "updateTimestamp": 1779364438000,
    //           "directSuperiorDistributorId": "202603230837030730530000",
    //           "fullName": "大连中升裕德丰田汽车销售服务有限公司",
    //           "approvalStatus": "APPROVED",
    //           "approvalStatusDescription": "已审核",
    //           "districtId": "202509051418317075210000",
    //           "address": "辽宁省大连市甘井子区山东路80-1号",
    //           "longitude": 121.57196,
    //           "latitude": 38.96224,
    //           "businessLicenseType": "BUSINESS_LICENSE",
    //           "businessLicenseTypeDescription": "营业执照",
    //           "businessLicenseNumber": "9121020075159230XB",
    //           "firstBusinessLicensePicFileId": "202605190142312855050000",
    //           "secondBusinessLicensePicFileId": null,
    //           "thirdBusinessLicensePicFileId": null,
    //           "directSuperiorDistributor": {
    //             "flagList": [],
    //             "id": "202603230837030730530000",
    //             "createTimestamp": 1774255023000,
    //             "updateTimestamp": 1774255731000,
    //             "type": "DISTRIBUTOR",
    //             "typeDescription": "分销商",
    //             "level": 1,
    //             "directSuperiorDistributorId": null,
    //             "fullName": "兰考县翰峰信息科技有限公司",
    //             "approvalStatus": "APPROVED",
    //             "approvalStatusDescription": "已审核",
    //             "districtId": "202509051419452105210000",
    //             "address": "经济技术开发区兰考县科技路南50米兰考同乐居（跨境）电商家居产业园469号",
    //             "longitude": 0.0,
    //             "latitude": 0.0,
    //             "businessLicenseType": "BUSINESS_LICENSE",
    //             "businessLicenseTypeDescription": "营业执照",
    //             "businessLicenseNumber": "91410225MA9LHPBQ66",
    //             "firstBusinessLicensePicFileId": null,
    //             "secondBusinessLicensePicFileId": null,
    //             "thirdBusinessLicensePicFileId": null,
    //             "directSuperiorDistributor": null,
    //             "district": {
    //               "flagList": [],
    //               "id": "202509051419452105210000",
    //               "createTimestamp": 1757081985000,
    //               "updateTimestamp": 1757081985000,
    //               "cityId": "202509051419445765210000",
    //               "regionalismCode": "410225",
    //               "localizedFullName": "兰考县",
    //               "fullName": "兰考县",
    //               "fullNameZhCn": "兰考县",
    //               "city": {
    //                 "flagList": [],
    //                 "id": "202509051419445765210000",
    //                 "createTimestamp": 1757081985000,
    //                 "updateTimestamp": 1757081985000,
    //                 "provinceId": "202509051419435825210000",
    //                 "regionalismCode": "410200",
    //                 "localizedFullName": "开封市",
    //                 "fullName": "开封市",
    //                 "fullNameZhCn": "开封市",
    //                 "province": {
    //                   "flagList": [],
    //                   "id": "202509051419435825210000",
    //                   "createTimestamp": 1757081984000,
    //                   "updateTimestamp": 1757081984000,
    //                   "countryId": "202509051417566675210000",
    //                   "regionalismCode": "410000",
    //                   "localizedFullName": "河南省",
    //                   "fullName": "河南省",
    //                   "fullNameZhCn": "河南省",
    //                   "country": {
    //                     "flagList": [],
    //                     "id": "202509051417566675210000",
    //                     "createTimestamp": 1757081877000,
    //                     "updateTimestamp": 1757081877000,
    //                     "localizedFullName": "中华人民共和国",
    //                     "fullName": "中华人民共和国",
    //                     "fullNameZhCn": "中华人民共和国"
    //                   }
    //                 }
    //               }
    //             },
    //             "firstBusinessLicensePicFile": null,
    //             "secondBusinessLicensePicFile": null,
    //             "thirdBusinessLicensePicFile": null,
    //             "userPartyType": "DISTRIBUTOR",
    //             "userPartyTypeDescription": "分销商",
    //             "merchantPartyType": "DISTRIBUTOR",
    //             "merchantPartyTypeDescription": "分销商",
    //             "businessPartyTypeDescription": "分销商",
    //             "businessPartyType": "DISTRIBUTOR",
    //             "warrantyConfigRefTargetTypeDescription": "分销商",
    //             "warrantyConfigRefTargetType": "DISTRIBUTOR",
    //             "warehouseOwningPartyTypeDescription": "分销商",
    //             "warehouseOwningPartyType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceTypeDescription": "分销商",
    //             "warehouseInventoryOutboundTargetType": "DISTRIBUTOR",
    //             "warehouseInventoryOutboundTargetTypeDescription": "分销商",
    //             "productRefTargetTypeDescription": "分销商",
    //             "productRefTargetType": "DISTRIBUTOR",
    //             "sellerPartyTypeDescription": "分销商",
    //             "sellerPartyType": "DISTRIBUTOR",
    //             "buyerPartyTypeDescription": "分销商",
    //             "buyerPartyType": "DISTRIBUTOR"
    //           },
    //           "district": {
    //             "flagList": [],
    //             "id": "202509051418317075210000",
    //             "createTimestamp": 1757081912000,
    //             "updateTimestamp": 1757081912000,
    //             "cityId": "202509051418314265210000",
    //             "regionalismCode": "210211",
    //             "localizedFullName": "甘井子区",
    //             "fullName": "甘井子区",
    //             "fullNameZhCn": "甘井子区",
    //             "city": {
    //               "flagList": [],
    //               "id": "202509051418314265210000",
    //               "createTimestamp": 1757081911000,
    //               "updateTimestamp": 1757081911000,
    //               "provinceId": "202509051418303305210000",
    //               "regionalismCode": "210200",
    //               "localizedFullName": "大连市",
    //               "fullName": "大连市",
    //               "fullNameZhCn": "大连市",
    //               "province": {
    //                 "flagList": [],
    //                 "id": "202509051418303305210000",
    //                 "createTimestamp": 1757081910000,
    //                 "updateTimestamp": 1757081910000,
    //                 "countryId": "202509051417566675210000",
    //                 "regionalismCode": "210000",
    //                 "localizedFullName": "辽宁省",
    //                 "fullName": "辽宁省",
    //                 "fullNameZhCn": "辽宁省",
    //                 "country": {
    //                   "flagList": [],
    //                   "id": "202509051417566675210000",
    //                   "createTimestamp": 1757081877000,
    //                   "updateTimestamp": 1757081877000,
    //                   "localizedFullName": "中华人民共和国",
    //                   "fullName": "中华人民共和国",
    //                   "fullNameZhCn": "中华人民共和国"
    //                 }
    //               }
    //             }
    //           },
    //           "firstBusinessLicensePicFile": {
    //             "flagList": [],
    //             "id": "202605190142312855050000",
    //             "createTimestamp": 1779154951000,
    //             "updateTimestamp": 1779154951000,
    //             "format": "JPEG",
    //             "formatDescription": "JPEG",
    //             "width": 1706,
    //             "height": 1279,
    //             "ossBucket": "goldenkiwiprod",
    //             "ossPath": "nbiz/retailer/2026/05/19/01/1706_1279_dd5a772a3ed19a3010738b9c31f4f5ab.jpeg",
    //             "url": "https://img.youbanban.com/oss/download/nbiz/retailer/2026/05/19/01/1706_1279_dd5a772a3ed19a3010738b9c31f4f5ab.jpeg",
    //             "fileTypeDescription": "图片",
    //             "fileType": "PIC"
    //           },
    //           "secondBusinessLicensePicFile": null,
    //           "thirdBusinessLicensePicFile": null,
    //           "userPartyType": "RETAILER",
    //           "userPartyTypeDescription": "零售商",
    //           "merchantPartyType": "RETAILER",
    //           "merchantPartyTypeDescription": "零售商",
    //           "businessPartyTypeDescription": "零售商",
    //           "businessPartyType": "RETAILER",
    //           "warrantyConfigRefTargetTypeDescription": "零售商",
    //           "warrantyConfigRefTargetType": "RETAILER",
    //           "warehouseOwningPartyTypeDescription": "零售商",
    //           "warehouseOwningPartyType": "RETAILER",
    //           "warehouseInventoryOutboundTargetType": "RETAILER",
    //           "warehouseInventoryOutboundTargetTypeDescription": "零售商",
    //           "productRefTargetTypeDescription": "零售商",
    //           "productRefTargetType": "RETAILER",
    //           "serviceStoreOwningPartyType": "RETAILER",
    //           "serviceStoreOwningPartyTypeDescription": "零售商",
    //           "serviceRefTargetTypeDescription": "零售商",
    //           "serviceRefTargetType": "RETAILER",
    //           "sellerPartyTypeDescription": "零售商",
    //           "sellerPartyType": "RETAILER",
    //           "buyerPartyTypeDescription": "零售商",
    //           "buyerPartyType": "RETAILER"
    //         },
    //         {
    //           "flagList": [],
    //           "id": "202605190525502420270000",
    //           "createTimestamp": 1779168350000,
    //           "updateTimestamp": 1779364439000,
    //           "directSuperiorDistributorId": "202603230837030730530000",
    //           "fullName": "大连中升之星汽车销售服务有限公司",
    //           "approvalStatus": "APPROVED",
    //           "approvalStatusDescription": "已审核",
    //           "districtId": "202509051418316375210000",
    //           "address": "河曲街20号",
    //           "longitude": 121.5578,
    //           "latitude": 38.90906,
    //           "businessLicenseType": "BUSINESS_LICENSE",
    //           "businessLicenseTypeDescription": "营业执照",
    //           "businessLicenseNumber": "91210200777261752N",
    //           "firstBusinessLicensePicFileId": "202605190524444160270000",
    //           "secondBusinessLicensePicFileId": null,
    //           "thirdBusinessLicensePicFileId": null,
    //           "directSuperiorDistributor": {
    //             "flagList": [],
    //             "id": "202603230837030730530000",
    //             "createTimestamp": 1774255023000,
    //             "updateTimestamp": 1774255731000,
    //             "type": "DISTRIBUTOR",
    //             "typeDescription": "分销商",
    //             "level": 1,
    //             "directSuperiorDistributorId": null,
    //             "fullName": "兰考县翰峰信息科技有限公司",
    //             "approvalStatus": "APPROVED",
    //             "approvalStatusDescription": "已审核",
    //             "districtId": "202509051419452105210000",
    //             "address": "经济技术开发区兰考县科技路南50米兰考同乐居（跨境）电商家居产业园469号",
    //             "longitude": 0.0,
    //             "latitude": 0.0,
    //             "businessLicenseType": "BUSINESS_LICENSE",
    //             "businessLicenseTypeDescription": "营业执照",
    //             "businessLicenseNumber": "91410225MA9LHPBQ66",
    //             "firstBusinessLicensePicFileId": null,
    //             "secondBusinessLicensePicFileId": null,
    //             "thirdBusinessLicensePicFileId": null,
    //             "directSuperiorDistributor": null,
    //             "district": {
    //               "flagList": [],
    //               "id": "202509051419452105210000",
    //               "createTimestamp": 1757081985000,
    //               "updateTimestamp": 1757081985000,
    //               "cityId": "202509051419445765210000",
    //               "regionalismCode": "410225",
    //               "localizedFullName": "兰考县",
    //               "fullName": "兰考县",
    //               "fullNameZhCn": "兰考县",
    //               "city": {
    //                 "flagList": [],
    //                 "id": "202509051419445765210000",
    //                 "createTimestamp": 1757081985000,
    //                 "updateTimestamp": 1757081985000,
    //                 "provinceId": "202509051419435825210000",
    //                 "regionalismCode": "410200",
    //                 "localizedFullName": "开封市",
    //                 "fullName": "开封市",
    //                 "fullNameZhCn": "开封市",
    //                 "province": {
    //                   "flagList": [],
    //                   "id": "202509051419435825210000",
    //                   "createTimestamp": 1757081984000,
    //                   "updateTimestamp": 1757081984000,
    //                   "countryId": "202509051417566675210000",
    //                   "regionalismCode": "410000",
    //                   "localizedFullName": "河南省",
    //                   "fullName": "河南省",
    //                   "fullNameZhCn": "河南省",
    //                   "country": {
    //                     "flagList": [],
    //                     "id": "202509051417566675210000",
    //                     "createTimestamp": 1757081877000,
    //                     "updateTimestamp": 1757081877000,
    //                     "localizedFullName": "中华人民共和国",
    //                     "fullName": "中华人民共和国",
    //                     "fullNameZhCn": "中华人民共和国"
    //                   }
    //                 }
    //               }
    //             },
    //             "firstBusinessLicensePicFile": null,
    //             "secondBusinessLicensePicFile": null,
    //             "thirdBusinessLicensePicFile": null,
    //             "userPartyType": "DISTRIBUTOR",
    //             "userPartyTypeDescription": "分销商",
    //             "merchantPartyType": "DISTRIBUTOR",
    //             "merchantPartyTypeDescription": "分销商",
    //             "businessPartyTypeDescription": "分销商",
    //             "businessPartyType": "DISTRIBUTOR",
    //             "warrantyConfigRefTargetTypeDescription": "分销商",
    //             "warrantyConfigRefTargetType": "DISTRIBUTOR",
    //             "warehouseOwningPartyTypeDescription": "分销商",
    //             "warehouseOwningPartyType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceTypeDescription": "分销商",
    //             "warehouseInventoryOutboundTargetType": "DISTRIBUTOR",
    //             "warehouseInventoryOutboundTargetTypeDescription": "分销商",
    //             "productRefTargetTypeDescription": "分销商",
    //             "productRefTargetType": "DISTRIBUTOR",
    //             "sellerPartyTypeDescription": "分销商",
    //             "sellerPartyType": "DISTRIBUTOR",
    //             "buyerPartyTypeDescription": "分销商",
    //             "buyerPartyType": "DISTRIBUTOR"
    //           },
    //           "district": {
    //             "flagList": [],
    //             "id": "202509051418316375210000",
    //             "createTimestamp": 1757081912000,
    //             "updateTimestamp": 1757081912000,
    //             "cityId": "202509051418314265210000",
    //             "regionalismCode": "210204",
    //             "localizedFullName": "沙河口区",
    //             "fullName": "沙河口区",
    //             "fullNameZhCn": "沙河口区",
    //             "city": {
    //               "flagList": [],
    //               "id": "202509051418314265210000",
    //               "createTimestamp": 1757081911000,
    //               "updateTimestamp": 1757081911000,
    //               "provinceId": "202509051418303305210000",
    //               "regionalismCode": "210200",
    //               "localizedFullName": "大连市",
    //               "fullName": "大连市",
    //               "fullNameZhCn": "大连市",
    //               "province": {
    //                 "flagList": [],
    //                 "id": "202509051418303305210000",
    //                 "createTimestamp": 1757081910000,
    //                 "updateTimestamp": 1757081910000,
    //                 "countryId": "202509051417566675210000",
    //                 "regionalismCode": "210000",
    //                 "localizedFullName": "辽宁省",
    //                 "fullName": "辽宁省",
    //                 "fullNameZhCn": "辽宁省",
    //                 "country": {
    //                   "flagList": [],
    //                   "id": "202509051417566675210000",
    //                   "createTimestamp": 1757081877000,
    //                   "updateTimestamp": 1757081877000,
    //                   "localizedFullName": "中华人民共和国",
    //                   "fullName": "中华人民共和国",
    //                   "fullNameZhCn": "中华人民共和国"
    //                 }
    //               }
    //             }
    //           },
    //           "firstBusinessLicensePicFile": {
    //             "flagList": [],
    //             "id": "202605190524444160270000",
    //             "createTimestamp": 1779168284000,
    //             "updateTimestamp": 1779168284000,
    //             "format": "JPEG",
    //             "formatDescription": "JPEG",
    //             "width": 4032,
    //             "height": 3024,
    //             "ossBucket": "goldenkiwiprod",
    //             "ossPath": "nbiz/retailer/2026/05/19/05/4032_3024_4828c338cb207ee84b47e78b07147604.jpeg",
    //             "url": "https://img.youbanban.com/oss/download/nbiz/retailer/2026/05/19/05/4032_3024_4828c338cb207ee84b47e78b07147604.jpeg",
    //             "fileTypeDescription": "图片",
    //             "fileType": "PIC"
    //           },
    //           "secondBusinessLicensePicFile": null,
    //           "thirdBusinessLicensePicFile": null,
    //           "userPartyType": "RETAILER",
    //           "userPartyTypeDescription": "零售商",
    //           "merchantPartyType": "RETAILER",
    //           "merchantPartyTypeDescription": "零售商",
    //           "businessPartyTypeDescription": "零售商",
    //           "businessPartyType": "RETAILER",
    //           "warrantyConfigRefTargetTypeDescription": "零售商",
    //           "warrantyConfigRefTargetType": "RETAILER",
    //           "warehouseOwningPartyTypeDescription": "零售商",
    //           "warehouseOwningPartyType": "RETAILER",
    //           "warehouseInventoryOutboundTargetType": "RETAILER",
    //           "warehouseInventoryOutboundTargetTypeDescription": "零售商",
    //           "productRefTargetTypeDescription": "零售商",
    //           "productRefTargetType": "RETAILER",
    //           "serviceStoreOwningPartyType": "RETAILER",
    //           "serviceStoreOwningPartyTypeDescription": "零售商",
    //           "serviceRefTargetTypeDescription": "零售商",
    //           "serviceRefTargetType": "RETAILER",
    //           "sellerPartyTypeDescription": "零售商",
    //           "sellerPartyType": "RETAILER",
    //           "buyerPartyTypeDescription": "零售商",
    //           "buyerPartyType": "RETAILER"
    //         },
    //         {
    //           "flagList": [],
    //           "id": "202605220811185244120000",
    //           "createTimestamp": 1779437479000,
    //           "updateTimestamp": 1779437749000,
    //           "directSuperiorDistributorId": "202509230730592586830000",
    //           "fullName": "弥渡酷车族汽车装饰美容中心",
    //           "approvalStatus": "APPROVED",
    //           "approvalStatusDescription": "已审核",
    //           "districtId": "202509051421117395210000",
    //           "address": "弥城镇毗江路123号",
    //           "longitude": 100.478668,
    //           "latitude": 25.350413,
    //           "businessLicenseType": "BUSINESS_LICENSE",
    //           "businessLicenseTypeDescription": "营业执照",
    //           "businessLicenseNumber": "92532925MA6MB4LK65",
    //           "firstBusinessLicensePicFileId": null,
    //           "secondBusinessLicensePicFileId": null,
    //           "thirdBusinessLicensePicFileId": null,
    //           "directSuperiorDistributor": {
    //             "flagList": [],
    //             "id": "202509230730592586830000",
    //             "createTimestamp": 1758612659000,
    //             "updateTimestamp": 1758612694000,
    //             "type": "DISTRIBUTOR",
    //             "typeDescription": "分销商",
    //             "level": 1,
    //             "directSuperiorDistributorId": null,
    //             "fullName": "驰捷车多管（云南）企业管理有限公司",
    //             "approvalStatus": "APPROVED",
    //             "approvalStatusDescription": "已审核",
    //             "districtId": "202509051421033585210000",
    //             "address": "云南省昆明市西山区西坝路639号",
    //             "longitude": 0.0,
    //             "latitude": 0.0,
    //             "businessLicenseType": "BUSINESS_LICENSE",
    //             "businessLicenseTypeDescription": "营业执照",
    //             "businessLicenseNumber": "91310118560192303A",
    //             "firstBusinessLicensePicFileId": null,
    //             "secondBusinessLicensePicFileId": null,
    //             "thirdBusinessLicensePicFileId": null,
    //             "directSuperiorDistributor": null,
    //             "district": {
    //               "flagList": [],
    //               "id": "202509051421033585210000",
    //               "createTimestamp": 1757082063000,
    //               "updateTimestamp": 1757082063000,
    //               "cityId": "202509051421030835210000",
    //               "regionalismCode": "530112",
    //               "localizedFullName": "西山区",
    //               "fullName": "西山区",
    //               "fullNameZhCn": "西山区",
    //               "city": {
    //                 "flagList": [],
    //                 "id": "202509051421030835210000",
    //                 "createTimestamp": 1757082063000,
    //                 "updateTimestamp": 1757082063000,
    //                 "provinceId": "202509051421030075210000",
    //                 "regionalismCode": "530100",
    //                 "localizedFullName": "昆明市",
    //                 "fullName": "昆明市",
    //                 "fullNameZhCn": "昆明市",
    //                 "province": {
    //                   "flagList": [],
    //                   "id": "202509051421030075210000",
    //                   "createTimestamp": 1757082063000,
    //                   "updateTimestamp": 1757082063000,
    //                   "countryId": "202509051417566675210000",
    //                   "regionalismCode": "530000",
    //                   "localizedFullName": "云南省",
    //                   "fullName": "云南省",
    //                   "fullNameZhCn": "云南省",
    //                   "country": {
    //                     "flagList": [],
    //                     "id": "202509051417566675210000",
    //                     "createTimestamp": 1757081877000,
    //                     "updateTimestamp": 1757081877000,
    //                     "localizedFullName": "中华人民共和国",
    //                     "fullName": "中华人民共和国",
    //                     "fullNameZhCn": "中华人民共和国"
    //                   }
    //                 }
    //               }
    //             },
    //             "firstBusinessLicensePicFile": null,
    //             "secondBusinessLicensePicFile": null,
    //             "thirdBusinessLicensePicFile": null,
    //             "userPartyType": "DISTRIBUTOR",
    //             "userPartyTypeDescription": "分销商",
    //             "merchantPartyType": "DISTRIBUTOR",
    //             "merchantPartyTypeDescription": "分销商",
    //             "businessPartyTypeDescription": "分销商",
    //             "businessPartyType": "DISTRIBUTOR",
    //             "warrantyConfigRefTargetTypeDescription": "分销商",
    //             "warrantyConfigRefTargetType": "DISTRIBUTOR",
    //             "warehouseOwningPartyTypeDescription": "分销商",
    //             "warehouseOwningPartyType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceTypeDescription": "分销商",
    //             "warehouseInventoryOutboundTargetType": "DISTRIBUTOR",
    //             "warehouseInventoryOutboundTargetTypeDescription": "分销商",
    //             "productRefTargetTypeDescription": "分销商",
    //             "productRefTargetType": "DISTRIBUTOR",
    //             "sellerPartyTypeDescription": "分销商",
    //             "sellerPartyType": "DISTRIBUTOR",
    //             "buyerPartyTypeDescription": "分销商",
    //             "buyerPartyType": "DISTRIBUTOR"
    //           },
    //           "district": {
    //             "flagList": [],
    //             "id": "202509051421117395210000",
    //             "createTimestamp": 1757082072000,
    //             "updateTimestamp": 1757082072000,
    //             "cityId": "202509051421113855210000",
    //             "regionalismCode": "532925",
    //             "localizedFullName": "弥渡县",
    //             "fullName": "弥渡县",
    //             "fullNameZhCn": "弥渡县",
    //             "city": {
    //               "flagList": [],
    //               "id": "202509051421113855210000",
    //               "createTimestamp": 1757082071000,
    //               "updateTimestamp": 1757082071000,
    //               "provinceId": "202509051421030075210000",
    //               "regionalismCode": "532900",
    //               "localizedFullName": "大理白族自治州",
    //               "fullName": "大理白族自治州",
    //               "fullNameZhCn": "大理白族自治州",
    //               "province": {
    //                 "flagList": [],
    //                 "id": "202509051421030075210000",
    //                 "createTimestamp": 1757082063000,
    //                 "updateTimestamp": 1757082063000,
    //                 "countryId": "202509051417566675210000",
    //                 "regionalismCode": "530000",
    //                 "localizedFullName": "云南省",
    //                 "fullName": "云南省",
    //                 "fullNameZhCn": "云南省",
    //                 "country": {
    //                   "flagList": [],
    //                   "id": "202509051417566675210000",
    //                   "createTimestamp": 1757081877000,
    //                   "updateTimestamp": 1757081877000,
    //                   "localizedFullName": "中华人民共和国",
    //                   "fullName": "中华人民共和国",
    //                   "fullNameZhCn": "中华人民共和国"
    //                 }
    //               }
    //             }
    //           },
    //           "firstBusinessLicensePicFile": null,
    //           "secondBusinessLicensePicFile": null,
    //           "thirdBusinessLicensePicFile": null,
    //           "userPartyType": "RETAILER",
    //           "userPartyTypeDescription": "零售商",
    //           "merchantPartyType": "RETAILER",
    //           "merchantPartyTypeDescription": "零售商",
    //           "businessPartyTypeDescription": "零售商",
    //           "businessPartyType": "RETAILER",
    //           "warrantyConfigRefTargetTypeDescription": "零售商",
    //           "warrantyConfigRefTargetType": "RETAILER",
    //           "warehouseOwningPartyTypeDescription": "零售商",
    //           "warehouseOwningPartyType": "RETAILER",
    //           "warehouseInventoryOutboundTargetType": "RETAILER",
    //           "warehouseInventoryOutboundTargetTypeDescription": "零售商",
    //           "productRefTargetTypeDescription": "零售商",
    //           "productRefTargetType": "RETAILER",
    //           "serviceStoreOwningPartyType": "RETAILER",
    //           "serviceStoreOwningPartyTypeDescription": "零售商",
    //           "serviceRefTargetTypeDescription": "零售商",
    //           "serviceRefTargetType": "RETAILER",
    //           "sellerPartyTypeDescription": "零售商",
    //           "sellerPartyType": "RETAILER",
    //           "buyerPartyTypeDescription": "零售商",
    //           "buyerPartyType": "RETAILER"
    //         },
    //         {
    //           "flagList": [],
    //           "id": "202605220813203054120000",
    //           "createTimestamp": 1779437600000,
    //           "updateTimestamp": 1779437750000,
    //           "directSuperiorDistributorId": "202509230730592586830000",
    //           "fullName": "曲靖护航美车精护工坊",
    //           "approvalStatus": "APPROVED",
    //           "approvalStatusDescription": "已审核",
    //           "districtId": "202509051421042005210000",
    //           "address": "寥廓街道莫家新村66-20负一层B02、B04",
    //           "longitude": 103.7899,
    //           "latitude": 25.50264,
    //           "businessLicenseType": "BUSINESS_LICENSE",
    //           "businessLicenseTypeDescription": "营业执照",
    //           "businessLicenseNumber": "92530300MAKD2FDL1M",
    //           "firstBusinessLicensePicFileId": null,
    //           "secondBusinessLicensePicFileId": null,
    //           "thirdBusinessLicensePicFileId": null,
    //           "directSuperiorDistributor": {
    //             "flagList": [],
    //             "id": "202509230730592586830000",
    //             "createTimestamp": 1758612659000,
    //             "updateTimestamp": 1758612694000,
    //             "type": "DISTRIBUTOR",
    //             "typeDescription": "分销商",
    //             "level": 1,
    //             "directSuperiorDistributorId": null,
    //             "fullName": "驰捷车多管（云南）企业管理有限公司",
    //             "approvalStatus": "APPROVED",
    //             "approvalStatusDescription": "已审核",
    //             "districtId": "202509051421033585210000",
    //             "address": "云南省昆明市西山区西坝路639号",
    //             "longitude": 0.0,
    //             "latitude": 0.0,
    //             "businessLicenseType": "BUSINESS_LICENSE",
    //             "businessLicenseTypeDescription": "营业执照",
    //             "businessLicenseNumber": "91310118560192303A",
    //             "firstBusinessLicensePicFileId": null,
    //             "secondBusinessLicensePicFileId": null,
    //             "thirdBusinessLicensePicFileId": null,
    //             "directSuperiorDistributor": null,
    //             "district": {
    //               "flagList": [],
    //               "id": "202509051421033585210000",
    //               "createTimestamp": 1757082063000,
    //               "updateTimestamp": 1757082063000,
    //               "cityId": "202509051421030835210000",
    //               "regionalismCode": "530112",
    //               "localizedFullName": "西山区",
    //               "fullName": "西山区",
    //               "fullNameZhCn": "西山区",
    //               "city": {
    //                 "flagList": [],
    //                 "id": "202509051421030835210000",
    //                 "createTimestamp": 1757082063000,
    //                 "updateTimestamp": 1757082063000,
    //                 "provinceId": "202509051421030075210000",
    //                 "regionalismCode": "530100",
    //                 "localizedFullName": "昆明市",
    //                 "fullName": "昆明市",
    //                 "fullNameZhCn": "昆明市",
    //                 "province": {
    //                   "flagList": [],
    //                   "id": "202509051421030075210000",
    //                   "createTimestamp": 1757082063000,
    //                   "updateTimestamp": 1757082063000,
    //                   "countryId": "202509051417566675210000",
    //                   "regionalismCode": "530000",
    //                   "localizedFullName": "云南省",
    //                   "fullName": "云南省",
    //                   "fullNameZhCn": "云南省",
    //                   "country": {
    //                     "flagList": [],
    //                     "id": "202509051417566675210000",
    //                     "createTimestamp": 1757081877000,
    //                     "updateTimestamp": 1757081877000,
    //                     "localizedFullName": "中华人民共和国",
    //                     "fullName": "中华人民共和国",
    //                     "fullNameZhCn": "中华人民共和国"
    //                   }
    //                 }
    //               }
    //             },
    //             "firstBusinessLicensePicFile": null,
    //             "secondBusinessLicensePicFile": null,
    //             "thirdBusinessLicensePicFile": null,
    //             "userPartyType": "DISTRIBUTOR",
    //             "userPartyTypeDescription": "分销商",
    //             "merchantPartyType": "DISTRIBUTOR",
    //             "merchantPartyTypeDescription": "分销商",
    //             "businessPartyTypeDescription": "分销商",
    //             "businessPartyType": "DISTRIBUTOR",
    //             "warrantyConfigRefTargetTypeDescription": "分销商",
    //             "warrantyConfigRefTargetType": "DISTRIBUTOR",
    //             "warehouseOwningPartyTypeDescription": "分销商",
    //             "warehouseOwningPartyType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceTypeDescription": "分销商",
    //             "warehouseInventoryOutboundTargetType": "DISTRIBUTOR",
    //             "warehouseInventoryOutboundTargetTypeDescription": "分销商",
    //             "productRefTargetTypeDescription": "分销商",
    //             "productRefTargetType": "DISTRIBUTOR",
    //             "sellerPartyTypeDescription": "分销商",
    //             "sellerPartyType": "DISTRIBUTOR",
    //             "buyerPartyTypeDescription": "分销商",
    //             "buyerPartyType": "DISTRIBUTOR"
    //           },
    //           "district": {
    //             "flagList": [],
    //             "id": "202509051421042005210000",
    //             "createTimestamp": 1757082064000,
    //             "updateTimestamp": 1757082064000,
    //             "cityId": "202509051421041325210000",
    //             "regionalismCode": "530302",
    //             "localizedFullName": "麒麟区",
    //             "fullName": "麒麟区",
    //             "fullNameZhCn": "麒麟区",
    //             "city": {
    //               "flagList": [],
    //               "id": "202509051421041325210000",
    //               "createTimestamp": 1757082064000,
    //               "updateTimestamp": 1757082064000,
    //               "provinceId": "202509051421030075210000",
    //               "regionalismCode": "530300",
    //               "localizedFullName": "曲靖市",
    //               "fullName": "曲靖市",
    //               "fullNameZhCn": "曲靖市",
    //               "province": {
    //                 "flagList": [],
    //                 "id": "202509051421030075210000",
    //                 "createTimestamp": 1757082063000,
    //                 "updateTimestamp": 1757082063000,
    //                 "countryId": "202509051417566675210000",
    //                 "regionalismCode": "530000",
    //                 "localizedFullName": "云南省",
    //                 "fullName": "云南省",
    //                 "fullNameZhCn": "云南省",
    //                 "country": {
    //                   "flagList": [],
    //                   "id": "202509051417566675210000",
    //                   "createTimestamp": 1757081877000,
    //                   "updateTimestamp": 1757081877000,
    //                   "localizedFullName": "中华人民共和国",
    //                   "fullName": "中华人民共和国",
    //                   "fullNameZhCn": "中华人民共和国"
    //                 }
    //               }
    //             }
    //           },
    //           "firstBusinessLicensePicFile": null,
    //           "secondBusinessLicensePicFile": null,
    //           "thirdBusinessLicensePicFile": null,
    //           "userPartyType": "RETAILER",
    //           "userPartyTypeDescription": "零售商",
    //           "merchantPartyType": "RETAILER",
    //           "merchantPartyTypeDescription": "零售商",
    //           "businessPartyTypeDescription": "零售商",
    //           "businessPartyType": "RETAILER",
    //           "warrantyConfigRefTargetTypeDescription": "零售商",
    //           "warrantyConfigRefTargetType": "RETAILER",
    //           "warehouseOwningPartyTypeDescription": "零售商",
    //           "warehouseOwningPartyType": "RETAILER",
    //           "warehouseInventoryOutboundTargetType": "RETAILER",
    //           "warehouseInventoryOutboundTargetTypeDescription": "零售商",
    //           "productRefTargetTypeDescription": "零售商",
    //           "productRefTargetType": "RETAILER",
    //           "serviceStoreOwningPartyType": "RETAILER",
    //           "serviceStoreOwningPartyTypeDescription": "零售商",
    //           "serviceRefTargetTypeDescription": "零售商",
    //           "serviceRefTargetType": "RETAILER",
    //           "sellerPartyTypeDescription": "零售商",
    //           "sellerPartyType": "RETAILER",
    //           "buyerPartyTypeDescription": "零售商",
    //           "buyerPartyType": "RETAILER"
    //         },
    //         {
    //           "flagList": [],
    //           "id": "202605280147116945340000",
    //           "createTimestamp": 1779932832000,
    //           "updateTimestamp": 1780480083000,
    //           "directSuperiorDistributorId": "202603230837030730530000",
    //           "fullName": "青岛中升沃盛",
    //           "approvalStatus": "APPROVED",
    //           "approvalStatusDescription": "已审核",
    //           "districtId": "202509051419341605210000",
    //           "address": "黑龙江中路300号沃尔沃",
    //           "longitude": 120.41995,
    //           "latitude": 36.2507,
    //           "businessLicenseType": "BUSINESS_LICENSE",
    //           "businessLicenseTypeDescription": "营业执照",
    //           "businessLicenseNumber": "91370214MABXK5PU4C",
    //           "firstBusinessLicensePicFileId": null,
    //           "secondBusinessLicensePicFileId": null,
    //           "thirdBusinessLicensePicFileId": null,
    //           "directSuperiorDistributor": {
    //             "flagList": [],
    //             "id": "202603230837030730530000",
    //             "createTimestamp": 1774255023000,
    //             "updateTimestamp": 1774255731000,
    //             "type": "DISTRIBUTOR",
    //             "typeDescription": "分销商",
    //             "level": 1,
    //             "directSuperiorDistributorId": null,
    //             "fullName": "兰考县翰峰信息科技有限公司",
    //             "approvalStatus": "APPROVED",
    //             "approvalStatusDescription": "已审核",
    //             "districtId": "202509051419452105210000",
    //             "address": "经济技术开发区兰考县科技路南50米兰考同乐居（跨境）电商家居产业园469号",
    //             "longitude": 0.0,
    //             "latitude": 0.0,
    //             "businessLicenseType": "BUSINESS_LICENSE",
    //             "businessLicenseTypeDescription": "营业执照",
    //             "businessLicenseNumber": "91410225MA9LHPBQ66",
    //             "firstBusinessLicensePicFileId": null,
    //             "secondBusinessLicensePicFileId": null,
    //             "thirdBusinessLicensePicFileId": null,
    //             "directSuperiorDistributor": null,
    //             "district": {
    //               "flagList": [],
    //               "id": "202509051419452105210000",
    //               "createTimestamp": 1757081985000,
    //               "updateTimestamp": 1757081985000,
    //               "cityId": "202509051419445765210000",
    //               "regionalismCode": "410225",
    //               "localizedFullName": "兰考县",
    //               "fullName": "兰考县",
    //               "fullNameZhCn": "兰考县",
    //               "city": {
    //                 "flagList": [],
    //                 "id": "202509051419445765210000",
    //                 "createTimestamp": 1757081985000,
    //                 "updateTimestamp": 1757081985000,
    //                 "provinceId": "202509051419435825210000",
    //                 "regionalismCode": "410200",
    //                 "localizedFullName": "开封市",
    //                 "fullName": "开封市",
    //                 "fullNameZhCn": "开封市",
    //                 "province": {
    //                   "flagList": [],
    //                   "id": "202509051419435825210000",
    //                   "createTimestamp": 1757081984000,
    //                   "updateTimestamp": 1757081984000,
    //                   "countryId": "202509051417566675210000",
    //                   "regionalismCode": "410000",
    //                   "localizedFullName": "河南省",
    //                   "fullName": "河南省",
    //                   "fullNameZhCn": "河南省",
    //                   "country": {
    //                     "flagList": [],
    //                     "id": "202509051417566675210000",
    //                     "createTimestamp": 1757081877000,
    //                     "updateTimestamp": 1757081877000,
    //                     "localizedFullName": "中华人民共和国",
    //                     "fullName": "中华人民共和国",
    //                     "fullNameZhCn": "中华人民共和国"
    //                   }
    //                 }
    //               }
    //             },
    //             "firstBusinessLicensePicFile": null,
    //             "secondBusinessLicensePicFile": null,
    //             "thirdBusinessLicensePicFile": null,
    //             "userPartyType": "DISTRIBUTOR",
    //             "userPartyTypeDescription": "分销商",
    //             "merchantPartyType": "DISTRIBUTOR",
    //             "merchantPartyTypeDescription": "分销商",
    //             "businessPartyTypeDescription": "分销商",
    //             "businessPartyType": "DISTRIBUTOR",
    //             "warrantyConfigRefTargetTypeDescription": "分销商",
    //             "warrantyConfigRefTargetType": "DISTRIBUTOR",
    //             "warehouseOwningPartyTypeDescription": "分销商",
    //             "warehouseOwningPartyType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceTypeDescription": "分销商",
    //             "warehouseInventoryOutboundTargetType": "DISTRIBUTOR",
    //             "warehouseInventoryOutboundTargetTypeDescription": "分销商",
    //             "productRefTargetTypeDescription": "分销商",
    //             "productRefTargetType": "DISTRIBUTOR",
    //             "sellerPartyTypeDescription": "分销商",
    //             "sellerPartyType": "DISTRIBUTOR",
    //             "buyerPartyTypeDescription": "分销商",
    //             "buyerPartyType": "DISTRIBUTOR"
    //           },
    //           "district": {
    //             "flagList": [],
    //             "id": "202509051419341605210000",
    //             "createTimestamp": 1757081974000,
    //             "updateTimestamp": 1757081974000,
    //             "cityId": "202509051419337405210000",
    //             "regionalismCode": "370214",
    //             "localizedFullName": "城阳区",
    //             "fullName": "城阳区",
    //             "fullNameZhCn": "城阳区",
    //             "city": {
    //               "flagList": [],
    //               "id": "202509051419337405210000",
    //               "createTimestamp": 1757081974000,
    //               "updateTimestamp": 1757081974000,
    //               "provinceId": "202509051419327565210000",
    //               "regionalismCode": "370200",
    //               "localizedFullName": "青岛市",
    //               "fullName": "青岛市",
    //               "fullNameZhCn": "青岛市",
    //               "province": {
    //                 "flagList": [],
    //                 "id": "202509051419327565210000",
    //                 "createTimestamp": 1757081973000,
    //                 "updateTimestamp": 1757081973000,
    //                 "countryId": "202509051417566675210000",
    //                 "regionalismCode": "370000",
    //                 "localizedFullName": "山东省",
    //                 "fullName": "山东省",
    //                 "fullNameZhCn": "山东省",
    //                 "country": {
    //                   "flagList": [],
    //                   "id": "202509051417566675210000",
    //                   "createTimestamp": 1757081877000,
    //                   "updateTimestamp": 1757081877000,
    //                   "localizedFullName": "中华人民共和国",
    //                   "fullName": "中华人民共和国",
    //                   "fullNameZhCn": "中华人民共和国"
    //                 }
    //               }
    //             }
    //           },
    //           "firstBusinessLicensePicFile": null,
    //           "secondBusinessLicensePicFile": null,
    //           "thirdBusinessLicensePicFile": null,
    //           "userPartyType": "RETAILER",
    //           "userPartyTypeDescription": "零售商",
    //           "merchantPartyType": "RETAILER",
    //           "merchantPartyTypeDescription": "零售商",
    //           "businessPartyTypeDescription": "零售商",
    //           "businessPartyType": "RETAILER",
    //           "warrantyConfigRefTargetTypeDescription": "零售商",
    //           "warrantyConfigRefTargetType": "RETAILER",
    //           "warehouseOwningPartyTypeDescription": "零售商",
    //           "warehouseOwningPartyType": "RETAILER",
    //           "warehouseInventoryOutboundTargetType": "RETAILER",
    //           "warehouseInventoryOutboundTargetTypeDescription": "零售商",
    //           "productRefTargetTypeDescription": "零售商",
    //           "productRefTargetType": "RETAILER",
    //           "serviceStoreOwningPartyType": "RETAILER",
    //           "serviceStoreOwningPartyTypeDescription": "零售商",
    //           "serviceRefTargetTypeDescription": "零售商",
    //           "serviceRefTargetType": "RETAILER",
    //           "sellerPartyTypeDescription": "零售商",
    //           "sellerPartyType": "RETAILER",
    //           "buyerPartyTypeDescription": "零售商",
    //           "buyerPartyType": "RETAILER"
    //         },
    //         {
    //           "flagList": [],
    //           "id": "202605312335144015340000",
    //           "createTimestamp": 1780270514000,
    //           "updateTimestamp": 1780480083000,
    //           "directSuperiorDistributorId": "202603230837030730530000",
    //           "fullName": "福建中升之宝汽车销售服务有限公司",
    //           "approvalStatus": "APPROVED",
    //           "approvalStatusDescription": "已审核",
    //           "districtId": "202509051419184315210000",
    //           "address": "建新镇金洲北路2号",
    //           "longitude": 119.25148,
    //           "latitude": 26.06312,
    //           "businessLicenseType": "BUSINESS_LICENSE",
    //           "businessLicenseTypeDescription": "营业执照",
    //           "businessLicenseNumber": "913500005509847297",
    //           "firstBusinessLicensePicFileId": null,
    //           "secondBusinessLicensePicFileId": null,
    //           "thirdBusinessLicensePicFileId": null,
    //           "directSuperiorDistributor": {
    //             "flagList": [],
    //             "id": "202603230837030730530000",
    //             "createTimestamp": 1774255023000,
    //             "updateTimestamp": 1774255731000,
    //             "type": "DISTRIBUTOR",
    //             "typeDescription": "分销商",
    //             "level": 1,
    //             "directSuperiorDistributorId": null,
    //             "fullName": "兰考县翰峰信息科技有限公司",
    //             "approvalStatus": "APPROVED",
    //             "approvalStatusDescription": "已审核",
    //             "districtId": "202509051419452105210000",
    //             "address": "经济技术开发区兰考县科技路南50米兰考同乐居（跨境）电商家居产业园469号",
    //             "longitude": 0.0,
    //             "latitude": 0.0,
    //             "businessLicenseType": "BUSINESS_LICENSE",
    //             "businessLicenseTypeDescription": "营业执照",
    //             "businessLicenseNumber": "91410225MA9LHPBQ66",
    //             "firstBusinessLicensePicFileId": null,
    //             "secondBusinessLicensePicFileId": null,
    //             "thirdBusinessLicensePicFileId": null,
    //             "directSuperiorDistributor": null,
    //             "district": {
    //               "flagList": [],
    //               "id": "202509051419452105210000",
    //               "createTimestamp": 1757081985000,
    //               "updateTimestamp": 1757081985000,
    //               "cityId": "202509051419445765210000",
    //               "regionalismCode": "410225",
    //               "localizedFullName": "兰考县",
    //               "fullName": "兰考县",
    //               "fullNameZhCn": "兰考县",
    //               "city": {
    //                 "flagList": [],
    //                 "id": "202509051419445765210000",
    //                 "createTimestamp": 1757081985000,
    //                 "updateTimestamp": 1757081985000,
    //                 "provinceId": "202509051419435825210000",
    //                 "regionalismCode": "410200",
    //                 "localizedFullName": "开封市",
    //                 "fullName": "开封市",
    //                 "fullNameZhCn": "开封市",
    //                 "province": {
    //                   "flagList": [],
    //                   "id": "202509051419435825210000",
    //                   "createTimestamp": 1757081984000,
    //                   "updateTimestamp": 1757081984000,
    //                   "countryId": "202509051417566675210000",
    //                   "regionalismCode": "410000",
    //                   "localizedFullName": "河南省",
    //                   "fullName": "河南省",
    //                   "fullNameZhCn": "河南省",
    //                   "country": {
    //                     "flagList": [],
    //                     "id": "202509051417566675210000",
    //                     "createTimestamp": 1757081877000,
    //                     "updateTimestamp": 1757081877000,
    //                     "localizedFullName": "中华人民共和国",
    //                     "fullName": "中华人民共和国",
    //                     "fullNameZhCn": "中华人民共和国"
    //                   }
    //                 }
    //               }
    //             },
    //             "firstBusinessLicensePicFile": null,
    //             "secondBusinessLicensePicFile": null,
    //             "thirdBusinessLicensePicFile": null,
    //             "userPartyType": "DISTRIBUTOR",
    //             "userPartyTypeDescription": "分销商",
    //             "merchantPartyType": "DISTRIBUTOR",
    //             "merchantPartyTypeDescription": "分销商",
    //             "businessPartyTypeDescription": "分销商",
    //             "businessPartyType": "DISTRIBUTOR",
    //             "warrantyConfigRefTargetTypeDescription": "分销商",
    //             "warrantyConfigRefTargetType": "DISTRIBUTOR",
    //             "warehouseOwningPartyTypeDescription": "分销商",
    //             "warehouseOwningPartyType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceTypeDescription": "分销商",
    //             "warehouseInventoryOutboundTargetType": "DISTRIBUTOR",
    //             "warehouseInventoryOutboundTargetTypeDescription": "分销商",
    //             "productRefTargetTypeDescription": "分销商",
    //             "productRefTargetType": "DISTRIBUTOR",
    //             "sellerPartyTypeDescription": "分销商",
    //             "sellerPartyType": "DISTRIBUTOR",
    //             "buyerPartyTypeDescription": "分销商",
    //             "buyerPartyType": "DISTRIBUTOR"
    //           },
    //           "district": {
    //             "flagList": [],
    //             "id": "202509051419184315210000",
    //             "createTimestamp": 1757081958000,
    //             "updateTimestamp": 1757081958000,
    //             "cityId": "202509051419182235210000",
    //             "regionalismCode": "350104",
    //             "localizedFullName": "仓山区",
    //             "fullName": "仓山区",
    //             "fullNameZhCn": "仓山区",
    //             "city": {
    //               "flagList": [],
    //               "id": "202509051419182235210000",
    //               "createTimestamp": 1757081958000,
    //               "updateTimestamp": 1757081958000,
    //               "provinceId": "202509051419181545210000",
    //               "regionalismCode": "350100",
    //               "localizedFullName": "福州市",
    //               "fullName": "福州市",
    //               "fullNameZhCn": "福州市",
    //               "province": {
    //                 "flagList": [],
    //                 "id": "202509051419181545210000",
    //                 "createTimestamp": 1757081958000,
    //                 "updateTimestamp": 1757081958000,
    //                 "countryId": "202509051417566675210000",
    //                 "regionalismCode": "350000",
    //                 "localizedFullName": "福建省",
    //                 "fullName": "福建省",
    //                 "fullNameZhCn": "福建省",
    //                 "country": {
    //                   "flagList": [],
    //                   "id": "202509051417566675210000",
    //                   "createTimestamp": 1757081877000,
    //                   "updateTimestamp": 1757081877000,
    //                   "localizedFullName": "中华人民共和国",
    //                   "fullName": "中华人民共和国",
    //                   "fullNameZhCn": "中华人民共和国"
    //                 }
    //               }
    //             }
    //           },
    //           "firstBusinessLicensePicFile": null,
    //           "secondBusinessLicensePicFile": null,
    //           "thirdBusinessLicensePicFile": null,
    //           "userPartyType": "RETAILER",
    //           "userPartyTypeDescription": "零售商",
    //           "merchantPartyType": "RETAILER",
    //           "merchantPartyTypeDescription": "零售商",
    //           "businessPartyTypeDescription": "零售商",
    //           "businessPartyType": "RETAILER",
    //           "warrantyConfigRefTargetTypeDescription": "零售商",
    //           "warrantyConfigRefTargetType": "RETAILER",
    //           "warehouseOwningPartyTypeDescription": "零售商",
    //           "warehouseOwningPartyType": "RETAILER",
    //           "warehouseInventoryOutboundTargetType": "RETAILER",
    //           "warehouseInventoryOutboundTargetTypeDescription": "零售商",
    //           "productRefTargetTypeDescription": "零售商",
    //           "productRefTargetType": "RETAILER",
    //           "serviceStoreOwningPartyType": "RETAILER",
    //           "serviceStoreOwningPartyTypeDescription": "零售商",
    //           "serviceRefTargetTypeDescription": "零售商",
    //           "serviceRefTargetType": "RETAILER",
    //           "sellerPartyTypeDescription": "零售商",
    //           "sellerPartyType": "RETAILER",
    //           "buyerPartyTypeDescription": "零售商",
    //           "buyerPartyType": "RETAILER"
    //         },
    //         {
    //           "flagList": [],
    //           "id": "202606010347546665340000",
    //           "createTimestamp": 1780285675000,
    //           "updateTimestamp": 1780480084000,
    //           "directSuperiorDistributorId": "202603230837030730530000",
    //           "fullName": "大理中升汽车销售服务有限公司",
    //           "approvalStatus": "APPROVED",
    //           "approvalStatusDescription": "已审核",
    //           "districtId": "202509051421114555210000",
    //           "address": "云南大理州大理市 巍山路与风盛路交界处中升滇源路店广汽丰田",
    //           "longitude": 100.26124,
    //           "latitude": 25.59702,
    //           "businessLicenseType": "BUSINESS_LICENSE",
    //           "businessLicenseTypeDescription": "营业执照",
    //           "businessLicenseNumber": "91532900574659525",
    //           "firstBusinessLicensePicFileId": null,
    //           "secondBusinessLicensePicFileId": null,
    //           "thirdBusinessLicensePicFileId": null,
    //           "directSuperiorDistributor": {
    //             "flagList": [],
    //             "id": "202603230837030730530000",
    //             "createTimestamp": 1774255023000,
    //             "updateTimestamp": 1774255731000,
    //             "type": "DISTRIBUTOR",
    //             "typeDescription": "分销商",
    //             "level": 1,
    //             "directSuperiorDistributorId": null,
    //             "fullName": "兰考县翰峰信息科技有限公司",
    //             "approvalStatus": "APPROVED",
    //             "approvalStatusDescription": "已审核",
    //             "districtId": "202509051419452105210000",
    //             "address": "经济技术开发区兰考县科技路南50米兰考同乐居（跨境）电商家居产业园469号",
    //             "longitude": 0.0,
    //             "latitude": 0.0,
    //             "businessLicenseType": "BUSINESS_LICENSE",
    //             "businessLicenseTypeDescription": "营业执照",
    //             "businessLicenseNumber": "91410225MA9LHPBQ66",
    //             "firstBusinessLicensePicFileId": null,
    //             "secondBusinessLicensePicFileId": null,
    //             "thirdBusinessLicensePicFileId": null,
    //             "directSuperiorDistributor": null,
    //             "district": {
    //               "flagList": [],
    //               "id": "202509051419452105210000",
    //               "createTimestamp": 1757081985000,
    //               "updateTimestamp": 1757081985000,
    //               "cityId": "202509051419445765210000",
    //               "regionalismCode": "410225",
    //               "localizedFullName": "兰考县",
    //               "fullName": "兰考县",
    //               "fullNameZhCn": "兰考县",
    //               "city": {
    //                 "flagList": [],
    //                 "id": "202509051419445765210000",
    //                 "createTimestamp": 1757081985000,
    //                 "updateTimestamp": 1757081985000,
    //                 "provinceId": "202509051419435825210000",
    //                 "regionalismCode": "410200",
    //                 "localizedFullName": "开封市",
    //                 "fullName": "开封市",
    //                 "fullNameZhCn": "开封市",
    //                 "province": {
    //                   "flagList": [],
    //                   "id": "202509051419435825210000",
    //                   "createTimestamp": 1757081984000,
    //                   "updateTimestamp": 1757081984000,
    //                   "countryId": "202509051417566675210000",
    //                   "regionalismCode": "410000",
    //                   "localizedFullName": "河南省",
    //                   "fullName": "河南省",
    //                   "fullNameZhCn": "河南省",
    //                   "country": {
    //                     "flagList": [],
    //                     "id": "202509051417566675210000",
    //                     "createTimestamp": 1757081877000,
    //                     "updateTimestamp": 1757081877000,
    //                     "localizedFullName": "中华人民共和国",
    //                     "fullName": "中华人民共和国",
    //                     "fullNameZhCn": "中华人民共和国"
    //                   }
    //                 }
    //               }
    //             },
    //             "firstBusinessLicensePicFile": null,
    //             "secondBusinessLicensePicFile": null,
    //             "thirdBusinessLicensePicFile": null,
    //             "userPartyType": "DISTRIBUTOR",
    //             "userPartyTypeDescription": "分销商",
    //             "merchantPartyType": "DISTRIBUTOR",
    //             "merchantPartyTypeDescription": "分销商",
    //             "businessPartyTypeDescription": "分销商",
    //             "businessPartyType": "DISTRIBUTOR",
    //             "warrantyConfigRefTargetTypeDescription": "分销商",
    //             "warrantyConfigRefTargetType": "DISTRIBUTOR",
    //             "warehouseOwningPartyTypeDescription": "分销商",
    //             "warehouseOwningPartyType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceTypeDescription": "分销商",
    //             "warehouseInventoryOutboundTargetType": "DISTRIBUTOR",
    //             "warehouseInventoryOutboundTargetTypeDescription": "分销商",
    //             "productRefTargetTypeDescription": "分销商",
    //             "productRefTargetType": "DISTRIBUTOR",
    //             "sellerPartyTypeDescription": "分销商",
    //             "sellerPartyType": "DISTRIBUTOR",
    //             "buyerPartyTypeDescription": "分销商",
    //             "buyerPartyType": "DISTRIBUTOR"
    //           },
    //           "district": {
    //             "flagList": [],
    //             "id": "202509051421114555210000",
    //             "createTimestamp": 1757082071000,
    //             "updateTimestamp": 1757082071000,
    //             "cityId": "202509051421113855210000",
    //             "regionalismCode": "532901",
    //             "localizedFullName": "大理市",
    //             "fullName": "大理市",
    //             "fullNameZhCn": "大理市",
    //             "city": {
    //               "flagList": [],
    //               "id": "202509051421113855210000",
    //               "createTimestamp": 1757082071000,
    //               "updateTimestamp": 1757082071000,
    //               "provinceId": "202509051421030075210000",
    //               "regionalismCode": "532900",
    //               "localizedFullName": "大理白族自治州",
    //               "fullName": "大理白族自治州",
    //               "fullNameZhCn": "大理白族自治州",
    //               "province": {
    //                 "flagList": [],
    //                 "id": "202509051421030075210000",
    //                 "createTimestamp": 1757082063000,
    //                 "updateTimestamp": 1757082063000,
    //                 "countryId": "202509051417566675210000",
    //                 "regionalismCode": "530000",
    //                 "localizedFullName": "云南省",
    //                 "fullName": "云南省",
    //                 "fullNameZhCn": "云南省",
    //                 "country": {
    //                   "flagList": [],
    //                   "id": "202509051417566675210000",
    //                   "createTimestamp": 1757081877000,
    //                   "updateTimestamp": 1757081877000,
    //                   "localizedFullName": "中华人民共和国",
    //                   "fullName": "中华人民共和国",
    //                   "fullNameZhCn": "中华人民共和国"
    //                 }
    //               }
    //             }
    //           },
    //           "firstBusinessLicensePicFile": null,
    //           "secondBusinessLicensePicFile": null,
    //           "thirdBusinessLicensePicFile": null,
    //           "userPartyType": "RETAILER",
    //           "userPartyTypeDescription": "零售商",
    //           "merchantPartyType": "RETAILER",
    //           "merchantPartyTypeDescription": "零售商",
    //           "businessPartyTypeDescription": "零售商",
    //           "businessPartyType": "RETAILER",
    //           "warrantyConfigRefTargetTypeDescription": "零售商",
    //           "warrantyConfigRefTargetType": "RETAILER",
    //           "warehouseOwningPartyTypeDescription": "零售商",
    //           "warehouseOwningPartyType": "RETAILER",
    //           "warehouseInventoryOutboundTargetType": "RETAILER",
    //           "warehouseInventoryOutboundTargetTypeDescription": "零售商",
    //           "productRefTargetTypeDescription": "零售商",
    //           "productRefTargetType": "RETAILER",
    //           "serviceStoreOwningPartyType": "RETAILER",
    //           "serviceStoreOwningPartyTypeDescription": "零售商",
    //           "serviceRefTargetTypeDescription": "零售商",
    //           "serviceRefTargetType": "RETAILER",
    //           "sellerPartyTypeDescription": "零售商",
    //           "sellerPartyType": "RETAILER",
    //           "buyerPartyTypeDescription": "零售商",
    //           "buyerPartyType": "RETAILER"
    //         },
    //         {
    //           "flagList": [],
    //           "id": "202606030530431165340000",
    //           "createTimestamp": 1780464643000,
    //           "updateTimestamp": 1780480084000,
    //           "directSuperiorDistributorId": "202603230837030730530000",
    //           "fullName": "长沙中升之宝汽车销售服务有限公司",
    //           "approvalStatus": "APPROVED",
    //           "approvalStatusDescription": "已审核",
    //           "districtId": "202509051420066055210000",
    //           "address": "麓谷汽车世界",
    //           "longitude": 112.8548,
    //           "latitude": 28.209,
    //           "businessLicenseType": "BUSINESS_LICENSE",
    //           "businessLicenseTypeDescription": "营业执照",
    //           "businessLicenseNumber": "91430104MA4LP2MC3N",
    //           "firstBusinessLicensePicFileId": null,
    //           "secondBusinessLicensePicFileId": null,
    //           "thirdBusinessLicensePicFileId": null,
    //           "directSuperiorDistributor": {
    //             "flagList": [],
    //             "id": "202603230837030730530000",
    //             "createTimestamp": 1774255023000,
    //             "updateTimestamp": 1774255731000,
    //             "type": "DISTRIBUTOR",
    //             "typeDescription": "分销商",
    //             "level": 1,
    //             "directSuperiorDistributorId": null,
    //             "fullName": "兰考县翰峰信息科技有限公司",
    //             "approvalStatus": "APPROVED",
    //             "approvalStatusDescription": "已审核",
    //             "districtId": "202509051419452105210000",
    //             "address": "经济技术开发区兰考县科技路南50米兰考同乐居（跨境）电商家居产业园469号",
    //             "longitude": 0.0,
    //             "latitude": 0.0,
    //             "businessLicenseType": "BUSINESS_LICENSE",
    //             "businessLicenseTypeDescription": "营业执照",
    //             "businessLicenseNumber": "91410225MA9LHPBQ66",
    //             "firstBusinessLicensePicFileId": null,
    //             "secondBusinessLicensePicFileId": null,
    //             "thirdBusinessLicensePicFileId": null,
    //             "directSuperiorDistributor": null,
    //             "district": {
    //               "flagList": [],
    //               "id": "202509051419452105210000",
    //               "createTimestamp": 1757081985000,
    //               "updateTimestamp": 1757081985000,
    //               "cityId": "202509051419445765210000",
    //               "regionalismCode": "410225",
    //               "localizedFullName": "兰考县",
    //               "fullName": "兰考县",
    //               "fullNameZhCn": "兰考县",
    //               "city": {
    //                 "flagList": [],
    //                 "id": "202509051419445765210000",
    //                 "createTimestamp": 1757081985000,
    //                 "updateTimestamp": 1757081985000,
    //                 "provinceId": "202509051419435825210000",
    //                 "regionalismCode": "410200",
    //                 "localizedFullName": "开封市",
    //                 "fullName": "开封市",
    //                 "fullNameZhCn": "开封市",
    //                 "province": {
    //                   "flagList": [],
    //                   "id": "202509051419435825210000",
    //                   "createTimestamp": 1757081984000,
    //                   "updateTimestamp": 1757081984000,
    //                   "countryId": "202509051417566675210000",
    //                   "regionalismCode": "410000",
    //                   "localizedFullName": "河南省",
    //                   "fullName": "河南省",
    //                   "fullNameZhCn": "河南省",
    //                   "country": {
    //                     "flagList": [],
    //                     "id": "202509051417566675210000",
    //                     "createTimestamp": 1757081877000,
    //                     "updateTimestamp": 1757081877000,
    //                     "localizedFullName": "中华人民共和国",
    //                     "fullName": "中华人民共和国",
    //                     "fullNameZhCn": "中华人民共和国"
    //                   }
    //                 }
    //               }
    //             },
    //             "firstBusinessLicensePicFile": null,
    //             "secondBusinessLicensePicFile": null,
    //             "thirdBusinessLicensePicFile": null,
    //             "userPartyType": "DISTRIBUTOR",
    //             "userPartyTypeDescription": "分销商",
    //             "merchantPartyType": "DISTRIBUTOR",
    //             "merchantPartyTypeDescription": "分销商",
    //             "businessPartyTypeDescription": "分销商",
    //             "businessPartyType": "DISTRIBUTOR",
    //             "warrantyConfigRefTargetTypeDescription": "分销商",
    //             "warrantyConfigRefTargetType": "DISTRIBUTOR",
    //             "warehouseOwningPartyTypeDescription": "分销商",
    //             "warehouseOwningPartyType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceTypeDescription": "分销商",
    //             "warehouseInventoryOutboundTargetType": "DISTRIBUTOR",
    //             "warehouseInventoryOutboundTargetTypeDescription": "分销商",
    //             "productRefTargetTypeDescription": "分销商",
    //             "productRefTargetType": "DISTRIBUTOR",
    //             "sellerPartyTypeDescription": "分销商",
    //             "sellerPartyType": "DISTRIBUTOR",
    //             "buyerPartyTypeDescription": "分销商",
    //             "buyerPartyType": "DISTRIBUTOR"
    //           },
    //           "district": {
    //             "flagList": [],
    //             "id": "202509051420066055210000",
    //             "createTimestamp": 1757082007000,
    //             "updateTimestamp": 1757082007000,
    //             "cityId": "202509051420063755210000",
    //             "regionalismCode": "430104",
    //             "localizedFullName": "岳麓区",
    //             "fullName": "岳麓区",
    //             "fullNameZhCn": "岳麓区",
    //             "city": {
    //               "flagList": [],
    //               "id": "202509051420063755210000",
    //               "createTimestamp": 1757082006000,
    //               "updateTimestamp": 1757082006000,
    //               "provinceId": "202509051420063025210000",
    //               "regionalismCode": "430100",
    //               "localizedFullName": "长沙市",
    //               "fullName": "长沙市",
    //               "fullNameZhCn": "长沙市",
    //               "province": {
    //                 "flagList": [],
    //                 "id": "202509051420063025210000",
    //                 "createTimestamp": 1757082006000,
    //                 "updateTimestamp": 1757082006000,
    //                 "countryId": "202509051417566675210000",
    //                 "regionalismCode": "430000",
    //                 "localizedFullName": "湖南省",
    //                 "fullName": "湖南省",
    //                 "fullNameZhCn": "湖南省",
    //                 "country": {
    //                   "flagList": [],
    //                   "id": "202509051417566675210000",
    //                   "createTimestamp": 1757081877000,
    //                   "updateTimestamp": 1757081877000,
    //                   "localizedFullName": "中华人民共和国",
    //                   "fullName": "中华人民共和国",
    //                   "fullNameZhCn": "中华人民共和国"
    //                 }
    //               }
    //             }
    //           },
    //           "firstBusinessLicensePicFile": null,
    //           "secondBusinessLicensePicFile": null,
    //           "thirdBusinessLicensePicFile": null,
    //           "userPartyType": "RETAILER",
    //           "userPartyTypeDescription": "零售商",
    //           "merchantPartyType": "RETAILER",
    //           "merchantPartyTypeDescription": "零售商",
    //           "businessPartyTypeDescription": "零售商",
    //           "businessPartyType": "RETAILER",
    //           "warrantyConfigRefTargetTypeDescription": "零售商",
    //           "warrantyConfigRefTargetType": "RETAILER",
    //           "warehouseOwningPartyTypeDescription": "零售商",
    //           "warehouseOwningPartyType": "RETAILER",
    //           "warehouseInventoryOutboundTargetType": "RETAILER",
    //           "warehouseInventoryOutboundTargetTypeDescription": "零售商",
    //           "productRefTargetTypeDescription": "零售商",
    //           "productRefTargetType": "RETAILER",
    //           "serviceStoreOwningPartyType": "RETAILER",
    //           "serviceStoreOwningPartyTypeDescription": "零售商",
    //           "serviceRefTargetTypeDescription": "零售商",
    //           "serviceRefTargetType": "RETAILER",
    //           "sellerPartyTypeDescription": "零售商",
    //           "sellerPartyType": "RETAILER",
    //           "buyerPartyTypeDescription": "零售商",
    //           "buyerPartyType": "RETAILER"
    //         },
    //         {
    //           "flagList": [],
    //           "id": "202606041353178055340000",
    //           "createTimestamp": 1780581198000,
    //           "updateTimestamp": 1780886052000,
    //           "directSuperiorDistributorId": "202603230837030730530000",
    //           "fullName": "郑州中升沃茂汽车销售服务有限公司",
    //           "approvalStatus": "APPROVED",
    //           "approvalStatusDescription": "已审核",
    //           "districtId": "202509051419440755210000",
    //           "address": "花园路与开元路交叉口向南50米路西",
    //           "longitude": 113.665283,
    //           "latitude": 34.867126,
    //           "businessLicenseType": "BUSINESS_LICENSE",
    //           "businessLicenseTypeDescription": "营业执照",
    //           "businessLicenseNumber": "91410108MAE0FAUA4H",
    //           "firstBusinessLicensePicFileId": null,
    //           "secondBusinessLicensePicFileId": null,
    //           "thirdBusinessLicensePicFileId": null,
    //           "directSuperiorDistributor": {
    //             "flagList": [],
    //             "id": "202603230837030730530000",
    //             "createTimestamp": 1774255023000,
    //             "updateTimestamp": 1774255731000,
    //             "type": "DISTRIBUTOR",
    //             "typeDescription": "分销商",
    //             "level": 1,
    //             "directSuperiorDistributorId": null,
    //             "fullName": "兰考县翰峰信息科技有限公司",
    //             "approvalStatus": "APPROVED",
    //             "approvalStatusDescription": "已审核",
    //             "districtId": "202509051419452105210000",
    //             "address": "经济技术开发区兰考县科技路南50米兰考同乐居（跨境）电商家居产业园469号",
    //             "longitude": 0.0,
    //             "latitude": 0.0,
    //             "businessLicenseType": "BUSINESS_LICENSE",
    //             "businessLicenseTypeDescription": "营业执照",
    //             "businessLicenseNumber": "91410225MA9LHPBQ66",
    //             "firstBusinessLicensePicFileId": null,
    //             "secondBusinessLicensePicFileId": null,
    //             "thirdBusinessLicensePicFileId": null,
    //             "directSuperiorDistributor": null,
    //             "district": {
    //               "flagList": [],
    //               "id": "202509051419452105210000",
    //               "createTimestamp": 1757081985000,
    //               "updateTimestamp": 1757081985000,
    //               "cityId": "202509051419445765210000",
    //               "regionalismCode": "410225",
    //               "localizedFullName": "兰考县",
    //               "fullName": "兰考县",
    //               "fullNameZhCn": "兰考县",
    //               "city": {
    //                 "flagList": [],
    //                 "id": "202509051419445765210000",
    //                 "createTimestamp": 1757081985000,
    //                 "updateTimestamp": 1757081985000,
    //                 "provinceId": "202509051419435825210000",
    //                 "regionalismCode": "410200",
    //                 "localizedFullName": "开封市",
    //                 "fullName": "开封市",
    //                 "fullNameZhCn": "开封市",
    //                 "province": {
    //                   "flagList": [],
    //                   "id": "202509051419435825210000",
    //                   "createTimestamp": 1757081984000,
    //                   "updateTimestamp": 1757081984000,
    //                   "countryId": "202509051417566675210000",
    //                   "regionalismCode": "410000",
    //                   "localizedFullName": "河南省",
    //                   "fullName": "河南省",
    //                   "fullNameZhCn": "河南省",
    //                   "country": {
    //                     "flagList": [],
    //                     "id": "202509051417566675210000",
    //                     "createTimestamp": 1757081877000,
    //                     "updateTimestamp": 1757081877000,
    //                     "localizedFullName": "中华人民共和国",
    //                     "fullName": "中华人民共和国",
    //                     "fullNameZhCn": "中华人民共和国"
    //                   }
    //                 }
    //               }
    //             },
    //             "firstBusinessLicensePicFile": null,
    //             "secondBusinessLicensePicFile": null,
    //             "thirdBusinessLicensePicFile": null,
    //             "userPartyType": "DISTRIBUTOR",
    //             "userPartyTypeDescription": "分销商",
    //             "merchantPartyType": "DISTRIBUTOR",
    //             "merchantPartyTypeDescription": "分销商",
    //             "businessPartyTypeDescription": "分销商",
    //             "businessPartyType": "DISTRIBUTOR",
    //             "warrantyConfigRefTargetTypeDescription": "分销商",
    //             "warrantyConfigRefTargetType": "DISTRIBUTOR",
    //             "warehouseOwningPartyTypeDescription": "分销商",
    //             "warehouseOwningPartyType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceTypeDescription": "分销商",
    //             "warehouseInventoryOutboundTargetType": "DISTRIBUTOR",
    //             "warehouseInventoryOutboundTargetTypeDescription": "分销商",
    //             "productRefTargetTypeDescription": "分销商",
    //             "productRefTargetType": "DISTRIBUTOR",
    //             "sellerPartyTypeDescription": "分销商",
    //             "sellerPartyType": "DISTRIBUTOR",
    //             "buyerPartyTypeDescription": "分销商",
    //             "buyerPartyType": "DISTRIBUTOR"
    //           },
    //           "district": {
    //             "flagList": [],
    //             "id": "202509051419440755210000",
    //             "createTimestamp": 1757081984000,
    //             "updateTimestamp": 1757081984000,
    //             "cityId": "202509051419436515210000",
    //             "regionalismCode": "410108",
    //             "localizedFullName": "惠济区",
    //             "fullName": "惠济区",
    //             "fullNameZhCn": "惠济区",
    //             "city": {
    //               "flagList": [],
    //               "id": "202509051419436515210000",
    //               "createTimestamp": 1757081984000,
    //               "updateTimestamp": 1757081984000,
    //               "provinceId": "202509051419435825210000",
    //               "regionalismCode": "410100",
    //               "localizedFullName": "郑州市",
    //               "fullName": "郑州市",
    //               "fullNameZhCn": "郑州市",
    //               "province": {
    //                 "flagList": [],
    //                 "id": "202509051419435825210000",
    //                 "createTimestamp": 1757081984000,
    //                 "updateTimestamp": 1757081984000,
    //                 "countryId": "202509051417566675210000",
    //                 "regionalismCode": "410000",
    //                 "localizedFullName": "河南省",
    //                 "fullName": "河南省",
    //                 "fullNameZhCn": "河南省",
    //                 "country": {
    //                   "flagList": [],
    //                   "id": "202509051417566675210000",
    //                   "createTimestamp": 1757081877000,
    //                   "updateTimestamp": 1757081877000,
    //                   "localizedFullName": "中华人民共和国",
    //                   "fullName": "中华人民共和国",
    //                   "fullNameZhCn": "中华人民共和国"
    //                 }
    //               }
    //             }
    //           },
    //           "firstBusinessLicensePicFile": null,
    //           "secondBusinessLicensePicFile": null,
    //           "thirdBusinessLicensePicFile": null,
    //           "userPartyType": "RETAILER",
    //           "userPartyTypeDescription": "零售商",
    //           "merchantPartyType": "RETAILER",
    //           "merchantPartyTypeDescription": "零售商",
    //           "businessPartyTypeDescription": "零售商",
    //           "businessPartyType": "RETAILER",
    //           "warrantyConfigRefTargetTypeDescription": "零售商",
    //           "warrantyConfigRefTargetType": "RETAILER",
    //           "warehouseOwningPartyTypeDescription": "零售商",
    //           "warehouseOwningPartyType": "RETAILER",
    //           "warehouseInventoryOutboundTargetType": "RETAILER",
    //           "warehouseInventoryOutboundTargetTypeDescription": "零售商",
    //           "productRefTargetTypeDescription": "零售商",
    //           "productRefTargetType": "RETAILER",
    //           "serviceStoreOwningPartyType": "RETAILER",
    //           "serviceStoreOwningPartyTypeDescription": "零售商",
    //           "serviceRefTargetTypeDescription": "零售商",
    //           "serviceRefTargetType": "RETAILER",
    //           "sellerPartyTypeDescription": "零售商",
    //           "sellerPartyType": "RETAILER",
    //           "buyerPartyTypeDescription": "零售商",
    //           "buyerPartyType": "RETAILER"
    //         },
    //         {
    //           "flagList": [],
    //           "id": "202606070313101135340000",
    //           "createTimestamp": 1780801990000,
    //           "updateTimestamp": 1780886053000,
    //           "directSuperiorDistributorId": "202603230837030730530000",
    //           "fullName": "沈阳中升汽车销售服务有限公司",
    //           "approvalStatus": "APPROVED",
    //           "approvalStatusDescription": "已审核",
    //           "districtId": "202509051418307745210000",
    //           "address": "沈辽西路183号",
    //           "longitude": 123.21843,
    //           "latitude": 41.72245,
    //           "businessLicenseType": "BUSINESS_LICENSE",
    //           "businessLicenseTypeDescription": "营业执照",
    //           "businessLicenseNumber": "91210106578380097E",
    //           "firstBusinessLicensePicFileId": null,
    //           "secondBusinessLicensePicFileId": null,
    //           "thirdBusinessLicensePicFileId": null,
    //           "directSuperiorDistributor": {
    //             "flagList": [],
    //             "id": "202603230837030730530000",
    //             "createTimestamp": 1774255023000,
    //             "updateTimestamp": 1774255731000,
    //             "type": "DISTRIBUTOR",
    //             "typeDescription": "分销商",
    //             "level": 1,
    //             "directSuperiorDistributorId": null,
    //             "fullName": "兰考县翰峰信息科技有限公司",
    //             "approvalStatus": "APPROVED",
    //             "approvalStatusDescription": "已审核",
    //             "districtId": "202509051419452105210000",
    //             "address": "经济技术开发区兰考县科技路南50米兰考同乐居（跨境）电商家居产业园469号",
    //             "longitude": 0.0,
    //             "latitude": 0.0,
    //             "businessLicenseType": "BUSINESS_LICENSE",
    //             "businessLicenseTypeDescription": "营业执照",
    //             "businessLicenseNumber": "91410225MA9LHPBQ66",
    //             "firstBusinessLicensePicFileId": null,
    //             "secondBusinessLicensePicFileId": null,
    //             "thirdBusinessLicensePicFileId": null,
    //             "directSuperiorDistributor": null,
    //             "district": {
    //               "flagList": [],
    //               "id": "202509051419452105210000",
    //               "createTimestamp": 1757081985000,
    //               "updateTimestamp": 1757081985000,
    //               "cityId": "202509051419445765210000",
    //               "regionalismCode": "410225",
    //               "localizedFullName": "兰考县",
    //               "fullName": "兰考县",
    //               "fullNameZhCn": "兰考县",
    //               "city": {
    //                 "flagList": [],
    //                 "id": "202509051419445765210000",
    //                 "createTimestamp": 1757081985000,
    //                 "updateTimestamp": 1757081985000,
    //                 "provinceId": "202509051419435825210000",
    //                 "regionalismCode": "410200",
    //                 "localizedFullName": "开封市",
    //                 "fullName": "开封市",
    //                 "fullNameZhCn": "开封市",
    //                 "province": {
    //                   "flagList": [],
    //                   "id": "202509051419435825210000",
    //                   "createTimestamp": 1757081984000,
    //                   "updateTimestamp": 1757081984000,
    //                   "countryId": "202509051417566675210000",
    //                   "regionalismCode": "410000",
    //                   "localizedFullName": "河南省",
    //                   "fullName": "河南省",
    //                   "fullNameZhCn": "河南省",
    //                   "country": {
    //                     "flagList": [],
    //                     "id": "202509051417566675210000",
    //                     "createTimestamp": 1757081877000,
    //                     "updateTimestamp": 1757081877000,
    //                     "localizedFullName": "中华人民共和国",
    //                     "fullName": "中华人民共和国",
    //                     "fullNameZhCn": "中华人民共和国"
    //                   }
    //                 }
    //               }
    //             },
    //             "firstBusinessLicensePicFile": null,
    //             "secondBusinessLicensePicFile": null,
    //             "thirdBusinessLicensePicFile": null,
    //             "userPartyType": "DISTRIBUTOR",
    //             "userPartyTypeDescription": "分销商",
    //             "merchantPartyType": "DISTRIBUTOR",
    //             "merchantPartyTypeDescription": "分销商",
    //             "businessPartyTypeDescription": "分销商",
    //             "businessPartyType": "DISTRIBUTOR",
    //             "warrantyConfigRefTargetTypeDescription": "分销商",
    //             "warrantyConfigRefTargetType": "DISTRIBUTOR",
    //             "warehouseOwningPartyTypeDescription": "分销商",
    //             "warehouseOwningPartyType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceTypeDescription": "分销商",
    //             "warehouseInventoryOutboundTargetType": "DISTRIBUTOR",
    //             "warehouseInventoryOutboundTargetTypeDescription": "分销商",
    //             "productRefTargetTypeDescription": "分销商",
    //             "productRefTargetType": "DISTRIBUTOR",
    //             "sellerPartyTypeDescription": "分销商",
    //             "sellerPartyType": "DISTRIBUTOR",
    //             "buyerPartyTypeDescription": "分销商",
    //             "buyerPartyType": "DISTRIBUTOR"
    //           },
    //           "district": {
    //             "flagList": [],
    //             "id": "202509051418307745210000",
    //             "createTimestamp": 1757081911000,
    //             "updateTimestamp": 1757081911000,
    //             "cityId": "202509051418304105210000",
    //             "regionalismCode": "210106",
    //             "localizedFullName": "铁西区",
    //             "fullName": "铁西区",
    //             "fullNameZhCn": "铁西区",
    //             "city": {
    //               "flagList": [],
    //               "id": "202509051418304105210000",
    //               "createTimestamp": 1757081910000,
    //               "updateTimestamp": 1757081910000,
    //               "provinceId": "202509051418303305210000",
    //               "regionalismCode": "210100",
    //               "localizedFullName": "沈阳市",
    //               "fullName": "沈阳市",
    //               "fullNameZhCn": "沈阳市",
    //               "province": {
    //                 "flagList": [],
    //                 "id": "202509051418303305210000",
    //                 "createTimestamp": 1757081910000,
    //                 "updateTimestamp": 1757081910000,
    //                 "countryId": "202509051417566675210000",
    //                 "regionalismCode": "210000",
    //                 "localizedFullName": "辽宁省",
    //                 "fullName": "辽宁省",
    //                 "fullNameZhCn": "辽宁省",
    //                 "country": {
    //                   "flagList": [],
    //                   "id": "202509051417566675210000",
    //                   "createTimestamp": 1757081877000,
    //                   "updateTimestamp": 1757081877000,
    //                   "localizedFullName": "中华人民共和国",
    //                   "fullName": "中华人民共和国",
    //                   "fullNameZhCn": "中华人民共和国"
    //                 }
    //               }
    //             }
    //           },
    //           "firstBusinessLicensePicFile": null,
    //           "secondBusinessLicensePicFile": null,
    //           "thirdBusinessLicensePicFile": null,
    //           "userPartyType": "RETAILER",
    //           "userPartyTypeDescription": "零售商",
    //           "merchantPartyType": "RETAILER",
    //           "merchantPartyTypeDescription": "零售商",
    //           "businessPartyTypeDescription": "零售商",
    //           "businessPartyType": "RETAILER",
    //           "warrantyConfigRefTargetTypeDescription": "零售商",
    //           "warrantyConfigRefTargetType": "RETAILER",
    //           "warehouseOwningPartyTypeDescription": "零售商",
    //           "warehouseOwningPartyType": "RETAILER",
    //           "warehouseInventoryOutboundTargetType": "RETAILER",
    //           "warehouseInventoryOutboundTargetTypeDescription": "零售商",
    //           "productRefTargetTypeDescription": "零售商",
    //           "productRefTargetType": "RETAILER",
    //           "serviceStoreOwningPartyType": "RETAILER",
    //           "serviceStoreOwningPartyTypeDescription": "零售商",
    //           "serviceRefTargetTypeDescription": "零售商",
    //           "serviceRefTargetType": "RETAILER",
    //           "sellerPartyTypeDescription": "零售商",
    //           "sellerPartyType": "RETAILER",
    //           "buyerPartyTypeDescription": "零售商",
    //           "buyerPartyType": "RETAILER"
    //         },
    //         {
    //           "flagList": [],
    //           "id": "202606080250242945340000",
    //           "createTimestamp": 1780887024000,
    //           "updateTimestamp": 1780887240000,
    //           "directSuperiorDistributorId": "202509220505318268640000",
    //           "fullName": "嘉实多汽车服务尊享店（上海星河湾店）",
    //           "approvalStatus": "APPROVED",
    //           "approvalStatusDescription": "已审核",
    //           "districtId": "202509051418538535210000",
    //           "address": "区都庄路4174号",
    //           "longitude": 121.41317,
    //           "latitude": 31.08289,
    //           "businessLicenseType": "BUSINESS_LICENSE",
    //           "businessLicenseTypeDescription": "营业执照",
    //           "businessLicenseNumber": "****",
    //           "firstBusinessLicensePicFileId": null,
    //           "secondBusinessLicensePicFileId": null,
    //           "thirdBusinessLicensePicFileId": null,
    //           "directSuperiorDistributor": {
    //             "flagList": [],
    //             "id": "202509220505318268640000",
    //             "createTimestamp": 1758517532000,
    //             "updateTimestamp": 1758517637000,
    //             "type": "DISTRIBUTOR",
    //             "typeDescription": "分销商",
    //             "level": 1,
    //             "directSuperiorDistributorId": null,
    //             "fullName": "嘉实多（上海）汽车技术服务有限公司",
    //             "approvalStatus": "APPROVED",
    //             "approvalStatusDescription": "已审核",
    //             "districtId": "202509051418534255210000",
    //             "address": "上海市徐汇区冠生园路393号4幢1层125室",
    //             "longitude": 0.0,
    //             "latitude": 0.0,
    //             "businessLicenseType": "BUSINESS_LICENSE",
    //             "businessLicenseTypeDescription": "营业执照",
    //             "businessLicenseNumber": "****",
    //             "firstBusinessLicensePicFileId": null,
    //             "secondBusinessLicensePicFileId": null,
    //             "thirdBusinessLicensePicFileId": null,
    //             "directSuperiorDistributor": null,
    //             "district": {
    //               "flagList": [],
    //               "id": "202509051418534255210000",
    //               "createTimestamp": 1757081933000,
    //               "updateTimestamp": 1757081933000,
    //               "cityId": "202509051418532875210000",
    //               "regionalismCode": "310104",
    //               "localizedFullName": "徐汇区",
    //               "fullName": "徐汇区",
    //               "fullNameZhCn": "徐汇区",
    //               "city": {
    //                 "flagList": [],
    //                 "id": "202509051418532875210000",
    //                 "createTimestamp": 1757081933000,
    //                 "updateTimestamp": 1757081933000,
    //                 "provinceId": "202509051418532155210000",
    //                 "regionalismCode": "310000",
    //                 "localizedFullName": "上海市",
    //                 "fullName": "上海市",
    //                 "fullNameZhCn": "上海市",
    //                 "province": {
    //                   "flagList": [],
    //                   "id": "202509051418532155210000",
    //                   "createTimestamp": 1757081933000,
    //                   "updateTimestamp": 1757081933000,
    //                   "countryId": "202509051417566675210000",
    //                   "regionalismCode": "310000",
    //                   "localizedFullName": "上海市",
    //                   "fullName": "上海市",
    //                   "fullNameZhCn": "上海市",
    //                   "country": {
    //                     "flagList": [],
    //                     "id": "202509051417566675210000",
    //                     "createTimestamp": 1757081877000,
    //                     "updateTimestamp": 1757081877000,
    //                     "localizedFullName": "中华人民共和国",
    //                     "fullName": "中华人民共和国",
    //                     "fullNameZhCn": "中华人民共和国"
    //                   }
    //                 }
    //               }
    //             },
    //             "firstBusinessLicensePicFile": null,
    //             "secondBusinessLicensePicFile": null,
    //             "thirdBusinessLicensePicFile": null,
    //             "userPartyType": "DISTRIBUTOR",
    //             "userPartyTypeDescription": "分销商",
    //             "merchantPartyType": "DISTRIBUTOR",
    //             "merchantPartyTypeDescription": "分销商",
    //             "businessPartyTypeDescription": "分销商",
    //             "businessPartyType": "DISTRIBUTOR",
    //             "warrantyConfigRefTargetTypeDescription": "分销商",
    //             "warrantyConfigRefTargetType": "DISTRIBUTOR",
    //             "warehouseOwningPartyTypeDescription": "分销商",
    //             "warehouseOwningPartyType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceTypeDescription": "分销商",
    //             "warehouseInventoryOutboundTargetType": "DISTRIBUTOR",
    //             "warehouseInventoryOutboundTargetTypeDescription": "分销商",
    //             "productRefTargetTypeDescription": "分销商",
    //             "productRefTargetType": "DISTRIBUTOR",
    //             "sellerPartyTypeDescription": "分销商",
    //             "sellerPartyType": "DISTRIBUTOR",
    //             "buyerPartyTypeDescription": "分销商",
    //             "buyerPartyType": "DISTRIBUTOR"
    //           },
    //           "district": {
    //             "flagList": [],
    //             "id": "202509051418538535210000",
    //             "createTimestamp": 1757081934000,
    //             "updateTimestamp": 1757081934000,
    //             "cityId": "202509051418532875210000",
    //             "regionalismCode": "310112",
    //             "localizedFullName": "闵行区",
    //             "fullName": "闵行区",
    //             "fullNameZhCn": "闵行区",
    //             "city": {
    //               "flagList": [],
    //               "id": "202509051418532875210000",
    //               "createTimestamp": 1757081933000,
    //               "updateTimestamp": 1757081933000,
    //               "provinceId": "202509051418532155210000",
    //               "regionalismCode": "310000",
    //               "localizedFullName": "上海市",
    //               "fullName": "上海市",
    //               "fullNameZhCn": "上海市",
    //               "province": {
    //                 "flagList": [],
    //                 "id": "202509051418532155210000",
    //                 "createTimestamp": 1757081933000,
    //                 "updateTimestamp": 1757081933000,
    //                 "countryId": "202509051417566675210000",
    //                 "regionalismCode": "310000",
    //                 "localizedFullName": "上海市",
    //                 "fullName": "上海市",
    //                 "fullNameZhCn": "上海市",
    //                 "country": {
    //                   "flagList": [],
    //                   "id": "202509051417566675210000",
    //                   "createTimestamp": 1757081877000,
    //                   "updateTimestamp": 1757081877000,
    //                   "localizedFullName": "中华人民共和国",
    //                   "fullName": "中华人民共和国",
    //                   "fullNameZhCn": "中华人民共和国"
    //                 }
    //               }
    //             }
    //           },
    //           "firstBusinessLicensePicFile": null,
    //           "secondBusinessLicensePicFile": null,
    //           "thirdBusinessLicensePicFile": null,
    //           "userPartyType": "RETAILER",
    //           "userPartyTypeDescription": "零售商",
    //           "merchantPartyType": "RETAILER",
    //           "merchantPartyTypeDescription": "零售商",
    //           "businessPartyTypeDescription": "零售商",
    //           "businessPartyType": "RETAILER",
    //           "warrantyConfigRefTargetTypeDescription": "零售商",
    //           "warrantyConfigRefTargetType": "RETAILER",
    //           "warehouseOwningPartyTypeDescription": "零售商",
    //           "warehouseOwningPartyType": "RETAILER",
    //           "warehouseInventoryOutboundTargetType": "RETAILER",
    //           "warehouseInventoryOutboundTargetTypeDescription": "零售商",
    //           "productRefTargetTypeDescription": "零售商",
    //           "productRefTargetType": "RETAILER",
    //           "serviceStoreOwningPartyType": "RETAILER",
    //           "serviceStoreOwningPartyTypeDescription": "零售商",
    //           "serviceRefTargetTypeDescription": "零售商",
    //           "serviceRefTargetType": "RETAILER",
    //           "sellerPartyTypeDescription": "零售商",
    //           "sellerPartyType": "RETAILER",
    //           "buyerPartyTypeDescription": "零售商",
    //           "buyerPartyType": "RETAILER"
    //         },
    //         {
    //           "flagList": [],
    //           "id": "202606080322011895340000",
    //           "createTimestamp": 1780888921000,
    //           "updateTimestamp": 1780889068000,
    //           "directSuperiorDistributorId": "202606071156106955340000",
    //           "fullName": "沈阳市铁西区爱车联汽车洗车店",
    //           "approvalStatus": "APPROVED",
    //           "approvalStatusDescription": "已审核",
    //           "districtId": "202509051418307745210000",
    //           "address": "兴工北街95-4号（6门）",
    //           "longitude": 123.38058,
    //           "latitude": 41.79041,
    //           "businessLicenseType": "BUSINESS_LICENSE",
    //           "businessLicenseTypeDescription": "营业执照",
    //           "businessLicenseNumber": "92210106MA116YX80X",
    //           "firstBusinessLicensePicFileId": null,
    //           "secondBusinessLicensePicFileId": null,
    //           "thirdBusinessLicensePicFileId": null,
    //           "directSuperiorDistributor": {
    //             "flagList": [],
    //             "id": "202606071156106955340000",
    //             "createTimestamp": 1780833371000,
    //             "updateTimestamp": 1780888713000,
    //             "type": "DISTRIBUTOR",
    //             "typeDescription": "分销商",
    //             "level": 1,
    //             "directSuperiorDistributorId": null,
    //             "fullName": "沈阳因泰汽车配件有限公司",
    //             "approvalStatus": "APPROVED",
    //             "approvalStatusDescription": "已审核",
    //             "districtId": "202509051418310635210000",
    //             "address": "白山区5-1号(1-2-3)",
    //             "longitude": 0.0,
    //             "latitude": 0.0,
    //             "businessLicenseType": "BUSINESS_LICENSE",
    //             "businessLicenseTypeDescription": "营业执照",
    //             "businessLicenseNumber": "91210114MA0UB91U04",
    //             "firstBusinessLicensePicFileId": "202606071155027665340000",
    //             "secondBusinessLicensePicFileId": null,
    //             "thirdBusinessLicensePicFileId": null,
    //             "directSuperiorDistributor": null,
    //             "district": {
    //               "flagList": [],
    //               "id": "202509051418310635210000",
    //               "createTimestamp": 1757081911000,
    //               "updateTimestamp": 1757081911000,
    //               "cityId": "202509051418304105210000",
    //               "regionalismCode": "210114",
    //               "localizedFullName": "于洪区",
    //               "fullName": "于洪区",
    //               "fullNameZhCn": "于洪区",
    //               "city": {
    //                 "flagList": [],
    //                 "id": "202509051418304105210000",
    //                 "createTimestamp": 1757081910000,
    //                 "updateTimestamp": 1757081910000,
    //                 "provinceId": "202509051418303305210000",
    //                 "regionalismCode": "210100",
    //                 "localizedFullName": "沈阳市",
    //                 "fullName": "沈阳市",
    //                 "fullNameZhCn": "沈阳市",
    //                 "province": {
    //                   "flagList": [],
    //                   "id": "202509051418303305210000",
    //                   "createTimestamp": 1757081910000,
    //                   "updateTimestamp": 1757081910000,
    //                   "countryId": "202509051417566675210000",
    //                   "regionalismCode": "210000",
    //                   "localizedFullName": "辽宁省",
    //                   "fullName": "辽宁省",
    //                   "fullNameZhCn": "辽宁省",
    //                   "country": {
    //                     "flagList": [],
    //                     "id": "202509051417566675210000",
    //                     "createTimestamp": 1757081877000,
    //                     "updateTimestamp": 1757081877000,
    //                     "localizedFullName": "中华人民共和国",
    //                     "fullName": "中华人民共和国",
    //                     "fullNameZhCn": "中华人民共和国"
    //                   }
    //                 }
    //               }
    //             },
    //             "firstBusinessLicensePicFile": {
    //               "flagList": [],
    //               "id": "202606071155027665340000",
    //               "createTimestamp": 1780833303000,
    //               "updateTimestamp": 1780833303000,
    //               "format": "JPEG",
    //               "formatDescription": "JPEG",
    //               "width": 3072,
    //               "height": 4096,
    //               "ossBucket": "goldenkiwiprod",
    //               "ossPath": "nbiz/distributor/2026/06/07/11/3072_4096_eec8640dd9b63ed629678a56b52d54fe.jpeg",
    //               "url": "https://img.youbanban.com/oss/download/nbiz/distributor/2026/06/07/11/3072_4096_eec8640dd9b63ed629678a56b52d54fe.jpeg",
    //               "fileTypeDescription": "图片",
    //               "fileType": "PIC"
    //             },
    //             "secondBusinessLicensePicFile": null,
    //             "thirdBusinessLicensePicFile": null,
    //             "userPartyType": "DISTRIBUTOR",
    //             "userPartyTypeDescription": "分销商",
    //             "merchantPartyType": "DISTRIBUTOR",
    //             "merchantPartyTypeDescription": "分销商",
    //             "businessPartyTypeDescription": "分销商",
    //             "businessPartyType": "DISTRIBUTOR",
    //             "warrantyConfigRefTargetTypeDescription": "分销商",
    //             "warrantyConfigRefTargetType": "DISTRIBUTOR",
    //             "warehouseOwningPartyTypeDescription": "分销商",
    //             "warehouseOwningPartyType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceTypeDescription": "分销商",
    //             "warehouseInventoryOutboundTargetType": "DISTRIBUTOR",
    //             "warehouseInventoryOutboundTargetTypeDescription": "分销商",
    //             "productRefTargetTypeDescription": "分销商",
    //             "productRefTargetType": "DISTRIBUTOR",
    //             "sellerPartyTypeDescription": "分销商",
    //             "sellerPartyType": "DISTRIBUTOR",
    //             "buyerPartyTypeDescription": "分销商",
    //             "buyerPartyType": "DISTRIBUTOR"
    //           },
    //           "district": {
    //             "flagList": [],
    //             "id": "202509051418307745210000",
    //             "createTimestamp": 1757081911000,
    //             "updateTimestamp": 1757081911000,
    //             "cityId": "202509051418304105210000",
    //             "regionalismCode": "210106",
    //             "localizedFullName": "铁西区",
    //             "fullName": "铁西区",
    //             "fullNameZhCn": "铁西区",
    //             "city": {
    //               "flagList": [],
    //               "id": "202509051418304105210000",
    //               "createTimestamp": 1757081910000,
    //               "updateTimestamp": 1757081910000,
    //               "provinceId": "202509051418303305210000",
    //               "regionalismCode": "210100",
    //               "localizedFullName": "沈阳市",
    //               "fullName": "沈阳市",
    //               "fullNameZhCn": "沈阳市",
    //               "province": {
    //                 "flagList": [],
    //                 "id": "202509051418303305210000",
    //                 "createTimestamp": 1757081910000,
    //                 "updateTimestamp": 1757081910000,
    //                 "countryId": "202509051417566675210000",
    //                 "regionalismCode": "210000",
    //                 "localizedFullName": "辽宁省",
    //                 "fullName": "辽宁省",
    //                 "fullNameZhCn": "辽宁省",
    //                 "country": {
    //                   "flagList": [],
    //                   "id": "202509051417566675210000",
    //                   "createTimestamp": 1757081877000,
    //                   "updateTimestamp": 1757081877000,
    //                   "localizedFullName": "中华人民共和国",
    //                   "fullName": "中华人民共和国",
    //                   "fullNameZhCn": "中华人民共和国"
    //                 }
    //               }
    //             }
    //           },
    //           "firstBusinessLicensePicFile": null,
    //           "secondBusinessLicensePicFile": null,
    //           "thirdBusinessLicensePicFile": null,
    //           "userPartyType": "RETAILER",
    //           "userPartyTypeDescription": "零售商",
    //           "merchantPartyType": "RETAILER",
    //           "merchantPartyTypeDescription": "零售商",
    //           "businessPartyTypeDescription": "零售商",
    //           "businessPartyType": "RETAILER",
    //           "warrantyConfigRefTargetTypeDescription": "零售商",
    //           "warrantyConfigRefTargetType": "RETAILER",
    //           "warehouseOwningPartyTypeDescription": "零售商",
    //           "warehouseOwningPartyType": "RETAILER",
    //           "warehouseInventoryOutboundTargetType": "RETAILER",
    //           "warehouseInventoryOutboundTargetTypeDescription": "零售商",
    //           "productRefTargetTypeDescription": "零售商",
    //           "productRefTargetType": "RETAILER",
    //           "serviceStoreOwningPartyType": "RETAILER",
    //           "serviceStoreOwningPartyTypeDescription": "零售商",
    //           "serviceRefTargetTypeDescription": "零售商",
    //           "serviceRefTargetType": "RETAILER",
    //           "sellerPartyTypeDescription": "零售商",
    //           "sellerPartyType": "RETAILER",
    //           "buyerPartyTypeDescription": "零售商",
    //           "buyerPartyType": "RETAILER"
    //         },
    //         {
    //           "flagList": [],
    //           "id": "202606080601577065340000",
    //           "createTimestamp": 1780898518000,
    //           "updateTimestamp": 1780989645000,
    //           "directSuperiorDistributorId": "202603230837030730530000",
    //           "fullName": "营口中升汽车销售服务有限公司",
    //           "approvalStatus": "APPROVED",
    //           "approvalStatusDescription": "已审核",
    //           "districtId": "202509051418352065210000",
    //           "address": "营口市老边区新营路南9号",
    //           "longitude": 122.28,
    //           "latitude": 40.66049,
    //           "businessLicenseType": "BUSINESS_LICENSE",
    //           "businessLicenseTypeDescription": "营业执照",
    //           "businessLicenseNumber": "91210811692674830J",
    //           "firstBusinessLicensePicFileId": null,
    //           "secondBusinessLicensePicFileId": null,
    //           "thirdBusinessLicensePicFileId": null,
    //           "directSuperiorDistributor": {
    //             "flagList": [],
    //             "id": "202603230837030730530000",
    //             "createTimestamp": 1774255023000,
    //             "updateTimestamp": 1774255731000,
    //             "type": "DISTRIBUTOR",
    //             "typeDescription": "分销商",
    //             "level": 1,
    //             "directSuperiorDistributorId": null,
    //             "fullName": "兰考县翰峰信息科技有限公司",
    //             "approvalStatus": "APPROVED",
    //             "approvalStatusDescription": "已审核",
    //             "districtId": "202509051419452105210000",
    //             "address": "经济技术开发区兰考县科技路南50米兰考同乐居（跨境）电商家居产业园469号",
    //             "longitude": 0.0,
    //             "latitude": 0.0,
    //             "businessLicenseType": "BUSINESS_LICENSE",
    //             "businessLicenseTypeDescription": "营业执照",
    //             "businessLicenseNumber": "91410225MA9LHPBQ66",
    //             "firstBusinessLicensePicFileId": null,
    //             "secondBusinessLicensePicFileId": null,
    //             "thirdBusinessLicensePicFileId": null,
    //             "directSuperiorDistributor": null,
    //             "district": {
    //               "flagList": [],
    //               "id": "202509051419452105210000",
    //               "createTimestamp": 1757081985000,
    //               "updateTimestamp": 1757081985000,
    //               "cityId": "202509051419445765210000",
    //               "regionalismCode": "410225",
    //               "localizedFullName": "兰考县",
    //               "fullName": "兰考县",
    //               "fullNameZhCn": "兰考县",
    //               "city": {
    //                 "flagList": [],
    //                 "id": "202509051419445765210000",
    //                 "createTimestamp": 1757081985000,
    //                 "updateTimestamp": 1757081985000,
    //                 "provinceId": "202509051419435825210000",
    //                 "regionalismCode": "410200",
    //                 "localizedFullName": "开封市",
    //                 "fullName": "开封市",
    //                 "fullNameZhCn": "开封市",
    //                 "province": {
    //                   "flagList": [],
    //                   "id": "202509051419435825210000",
    //                   "createTimestamp": 1757081984000,
    //                   "updateTimestamp": 1757081984000,
    //                   "countryId": "202509051417566675210000",
    //                   "regionalismCode": "410000",
    //                   "localizedFullName": "河南省",
    //                   "fullName": "河南省",
    //                   "fullNameZhCn": "河南省",
    //                   "country": {
    //                     "flagList": [],
    //                     "id": "202509051417566675210000",
    //                     "createTimestamp": 1757081877000,
    //                     "updateTimestamp": 1757081877000,
    //                     "localizedFullName": "中华人民共和国",
    //                     "fullName": "中华人民共和国",
    //                     "fullNameZhCn": "中华人民共和国"
    //                   }
    //                 }
    //               }
    //             },
    //             "firstBusinessLicensePicFile": null,
    //             "secondBusinessLicensePicFile": null,
    //             "thirdBusinessLicensePicFile": null,
    //             "userPartyType": "DISTRIBUTOR",
    //             "userPartyTypeDescription": "分销商",
    //             "merchantPartyType": "DISTRIBUTOR",
    //             "merchantPartyTypeDescription": "分销商",
    //             "businessPartyTypeDescription": "分销商",
    //             "businessPartyType": "DISTRIBUTOR",
    //             "warrantyConfigRefTargetTypeDescription": "分销商",
    //             "warrantyConfigRefTargetType": "DISTRIBUTOR",
    //             "warehouseOwningPartyTypeDescription": "分销商",
    //             "warehouseOwningPartyType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceTypeDescription": "分销商",
    //             "warehouseInventoryOutboundTargetType": "DISTRIBUTOR",
    //             "warehouseInventoryOutboundTargetTypeDescription": "分销商",
    //             "productRefTargetTypeDescription": "分销商",
    //             "productRefTargetType": "DISTRIBUTOR",
    //             "sellerPartyTypeDescription": "分销商",
    //             "sellerPartyType": "DISTRIBUTOR",
    //             "buyerPartyTypeDescription": "分销商",
    //             "buyerPartyType": "DISTRIBUTOR"
    //           },
    //           "district": {
    //             "flagList": [],
    //             "id": "202509051418352065210000",
    //             "createTimestamp": 1757081915000,
    //             "updateTimestamp": 1757081915000,
    //             "cityId": "202509051418349255210000",
    //             "regionalismCode": "210811",
    //             "localizedFullName": "老边区",
    //             "fullName": "老边区",
    //             "fullNameZhCn": "老边区",
    //             "city": {
    //               "flagList": [],
    //               "id": "202509051418349255210000",
    //               "createTimestamp": 1757081915000,
    //               "updateTimestamp": 1757081915000,
    //               "provinceId": "202509051418303305210000",
    //               "regionalismCode": "210800",
    //               "localizedFullName": "营口市",
    //               "fullName": "营口市",
    //               "fullNameZhCn": "营口市",
    //               "province": {
    //                 "flagList": [],
    //                 "id": "202509051418303305210000",
    //                 "createTimestamp": 1757081910000,
    //                 "updateTimestamp": 1757081910000,
    //                 "countryId": "202509051417566675210000",
    //                 "regionalismCode": "210000",
    //                 "localizedFullName": "辽宁省",
    //                 "fullName": "辽宁省",
    //                 "fullNameZhCn": "辽宁省",
    //                 "country": {
    //                   "flagList": [],
    //                   "id": "202509051417566675210000",
    //                   "createTimestamp": 1757081877000,
    //                   "updateTimestamp": 1757081877000,
    //                   "localizedFullName": "中华人民共和国",
    //                   "fullName": "中华人民共和国",
    //                   "fullNameZhCn": "中华人民共和国"
    //                 }
    //               }
    //             }
    //           },
    //           "firstBusinessLicensePicFile": null,
    //           "secondBusinessLicensePicFile": null,
    //           "thirdBusinessLicensePicFile": null,
    //           "userPartyType": "RETAILER",
    //           "userPartyTypeDescription": "零售商",
    //           "merchantPartyType": "RETAILER",
    //           "merchantPartyTypeDescription": "零售商",
    //           "businessPartyTypeDescription": "零售商",
    //           "businessPartyType": "RETAILER",
    //           "warrantyConfigRefTargetTypeDescription": "零售商",
    //           "warrantyConfigRefTargetType": "RETAILER",
    //           "warehouseOwningPartyTypeDescription": "零售商",
    //           "warehouseOwningPartyType": "RETAILER",
    //           "warehouseInventoryOutboundTargetType": "RETAILER",
    //           "warehouseInventoryOutboundTargetTypeDescription": "零售商",
    //           "productRefTargetTypeDescription": "零售商",
    //           "productRefTargetType": "RETAILER",
    //           "serviceStoreOwningPartyType": "RETAILER",
    //           "serviceStoreOwningPartyTypeDescription": "零售商",
    //           "serviceRefTargetTypeDescription": "零售商",
    //           "serviceRefTargetType": "RETAILER",
    //           "sellerPartyTypeDescription": "零售商",
    //           "sellerPartyType": "RETAILER",
    //           "buyerPartyTypeDescription": "零售商",
    //           "buyerPartyType": "RETAILER"
    //         },
    //         {
    //           "flagList": [],
    //           "id": "202606090456020536230000",
    //           "createTimestamp": 1780980962000,
    //           "updateTimestamp": 1780989645000,
    //           "directSuperiorDistributorId": "202603230837030730530000",
    //           "fullName": "南京中升恒岳汽车销售服务有限公司",
    //           "approvalStatus": "APPROVED",
    //           "approvalStatusDescription": "已审核",
    //           "districtId": "202509051418549205210000",
    //           "address": "浦珠北路42号",
    //           "longitude": 118.714458,
    //           "latitude": 32.122511,
    //           "businessLicenseType": "BUSINESS_LICENSE",
    //           "businessLicenseTypeDescription": "营业执照",
    //           "businessLicenseNumber": "9132019156285338XL",
    //           "firstBusinessLicensePicFileId": null,
    //           "secondBusinessLicensePicFileId": null,
    //           "thirdBusinessLicensePicFileId": null,
    //           "directSuperiorDistributor": {
    //             "flagList": [],
    //             "id": "202603230837030730530000",
    //             "createTimestamp": 1774255023000,
    //             "updateTimestamp": 1774255731000,
    //             "type": "DISTRIBUTOR",
    //             "typeDescription": "分销商",
    //             "level": 1,
    //             "directSuperiorDistributorId": null,
    //             "fullName": "兰考县翰峰信息科技有限公司",
    //             "approvalStatus": "APPROVED",
    //             "approvalStatusDescription": "已审核",
    //             "districtId": "202509051419452105210000",
    //             "address": "经济技术开发区兰考县科技路南50米兰考同乐居（跨境）电商家居产业园469号",
    //             "longitude": 0.0,
    //             "latitude": 0.0,
    //             "businessLicenseType": "BUSINESS_LICENSE",
    //             "businessLicenseTypeDescription": "营业执照",
    //             "businessLicenseNumber": "91410225MA9LHPBQ66",
    //             "firstBusinessLicensePicFileId": null,
    //             "secondBusinessLicensePicFileId": null,
    //             "thirdBusinessLicensePicFileId": null,
    //             "directSuperiorDistributor": null,
    //             "district": {
    //               "flagList": [],
    //               "id": "202509051419452105210000",
    //               "createTimestamp": 1757081985000,
    //               "updateTimestamp": 1757081985000,
    //               "cityId": "202509051419445765210000",
    //               "regionalismCode": "410225",
    //               "localizedFullName": "兰考县",
    //               "fullName": "兰考县",
    //               "fullNameZhCn": "兰考县",
    //               "city": {
    //                 "flagList": [],
    //                 "id": "202509051419445765210000",
    //                 "createTimestamp": 1757081985000,
    //                 "updateTimestamp": 1757081985000,
    //                 "provinceId": "202509051419435825210000",
    //                 "regionalismCode": "410200",
    //                 "localizedFullName": "开封市",
    //                 "fullName": "开封市",
    //                 "fullNameZhCn": "开封市",
    //                 "province": {
    //                   "flagList": [],
    //                   "id": "202509051419435825210000",
    //                   "createTimestamp": 1757081984000,
    //                   "updateTimestamp": 1757081984000,
    //                   "countryId": "202509051417566675210000",
    //                   "regionalismCode": "410000",
    //                   "localizedFullName": "河南省",
    //                   "fullName": "河南省",
    //                   "fullNameZhCn": "河南省",
    //                   "country": {
    //                     "flagList": [],
    //                     "id": "202509051417566675210000",
    //                     "createTimestamp": 1757081877000,
    //                     "updateTimestamp": 1757081877000,
    //                     "localizedFullName": "中华人民共和国",
    //                     "fullName": "中华人民共和国",
    //                     "fullNameZhCn": "中华人民共和国"
    //                   }
    //                 }
    //               }
    //             },
    //             "firstBusinessLicensePicFile": null,
    //             "secondBusinessLicensePicFile": null,
    //             "thirdBusinessLicensePicFile": null,
    //             "userPartyType": "DISTRIBUTOR",
    //             "userPartyTypeDescription": "分销商",
    //             "merchantPartyType": "DISTRIBUTOR",
    //             "merchantPartyTypeDescription": "分销商",
    //             "businessPartyTypeDescription": "分销商",
    //             "businessPartyType": "DISTRIBUTOR",
    //             "warrantyConfigRefTargetTypeDescription": "分销商",
    //             "warrantyConfigRefTargetType": "DISTRIBUTOR",
    //             "warehouseOwningPartyTypeDescription": "分销商",
    //             "warehouseOwningPartyType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceType": "DISTRIBUTOR",
    //             "warehouseInventoryInboundSourceTypeDescription": "分销商",
    //             "warehouseInventoryOutboundTargetType": "DISTRIBUTOR",
    //             "warehouseInventoryOutboundTargetTypeDescription": "分销商",
    //             "productRefTargetTypeDescription": "分销商",
    //             "productRefTargetType": "DISTRIBUTOR",
    //             "sellerPartyTypeDescription": "分销商",
    //             "sellerPartyType": "DISTRIBUTOR",
    //             "buyerPartyTypeDescription": "分销商",
    //             "buyerPartyType": "DISTRIBUTOR"
    //           },
    //           "district": {
    //             "flagList": [],
    //             "id": "202509051418549205210000",
    //             "createTimestamp": 1757081935000,
    //             "updateTimestamp": 1757081935000,
    //             "cityId": "202509051418545735210000",
    //             "regionalismCode": "320111",
    //             "localizedFullName": "浦口区",
    //             "fullName": "浦口区",
    //             "fullNameZhCn": "浦口区",
    //             "city": {
    //               "flagList": [],
    //               "id": "202509051418545735210000",
    //               "createTimestamp": 1757081935000,
    //               "updateTimestamp": 1757081935000,
    //               "provinceId": "202509051418545015210000",
    //               "regionalismCode": "320100",
    //               "localizedFullName": "南京市",
    //               "fullName": "南京市",
    //               "fullNameZhCn": "南京市",
    //               "province": {
    //                 "flagList": [],
    //                 "id": "202509051418545015210000",
    //                 "createTimestamp": 1757081935000,
    //                 "updateTimestamp": 1757081935000,
    //                 "countryId": "202509051417566675210000",
    //                 "regionalismCode": "320000",
    //                 "localizedFullName": "江苏省",
    //                 "fullName": "江苏省",
    //                 "fullNameZhCn": "江苏省",
    //                 "country": {
    //                   "flagList": [],
    //                   "id": "202509051417566675210000",
    //                   "createTimestamp": 1757081877000,
    //                   "updateTimestamp": 1757081877000,
    //                   "localizedFullName": "中华人民共和国",
    //                   "fullName": "中华人民共和国",
    //                   "fullNameZhCn": "中华人民共和国"
    //                 }
    //               }
    //             }
    //           },
    //           "firstBusinessLicensePicFile": null,
    //           "secondBusinessLicensePicFile": null,
    //           "thirdBusinessLicensePicFile": null,
    //           "userPartyType": "RETAILER",
    //           "userPartyTypeDescription": "零售商",
    //           "merchantPartyType": "RETAILER",
    //           "merchantPartyTypeDescription": "零售商",
    //           "businessPartyTypeDescription": "零售商",
    //           "businessPartyType": "RETAILER",
    //           "warrantyConfigRefTargetTypeDescription": "零售商",
    //           "warrantyConfigRefTargetType": "RETAILER",
    //           "warehouseOwningPartyTypeDescription": "零售商",
    //           "warehouseOwningPartyType": "RETAILER",
    //           "warehouseInventoryOutboundTargetType": "RETAILER",
    //           "warehouseInventoryOutboundTargetTypeDescription": "零售商",
    //           "productRefTargetTypeDescription": "零售商",
    //           "productRefTargetType": "RETAILER",
    //           "serviceStoreOwningPartyType": "RETAILER",
    //           "serviceStoreOwningPartyTypeDescription": "零售商",
    //           "serviceRefTargetTypeDescription": "零售商",
    //           "serviceRefTargetType": "RETAILER",
    //           "sellerPartyTypeDescription": "零售商",
    //           "sellerPartyType": "RETAILER",
    //           "buyerPartyTypeDescription": "零售商",
    //           "buyerPartyType": "RETAILER"
    //         }
    //       ],
    //       "sortPropertyList": [
    //         "createTimestamp",
    //         "distanceMeters",
    //         "id",
    //         "updateTimestamp"
    //       ]
    //     },
    //     "traceId": "647NFAGP9U5OC9D4WH84A95Z",
    //     "path": "/nbiz/open/retailer/query-list",
    //     "requestTimestamp": 1781073641982,
    //     "responseTimestamp": 1781073642014
    //   };
    // }
   
    if (res?.error != null) return [];

    // const list = Array.isArray(res?.data?.list)
    //   ? res.data.list
    //   : Array.isArray(res?.data)
    //     ? res.data
    //     : [];

    cachedRegionRetailers.clear();
    cachedAllRetailers = templist.map((item, index) =>
      normalizeDealerItem({ ...item, __listKey: getDealerItemKey(item, index) })
    );
    return cachedAllRetailers;
  })();

  try {
    return await fetchAllPromise;
  } finally {
    fetchAllPromise = null;
  }
};

// 一次拉取全部门店，再按国家（非省份）过滤
export const fetchAllRetailersForRegion = async (
  region,
  { forceRefresh = false, silent = false } = {}
) => {
  const allDealers = await fetchAllRetailers({ forceRefresh, silent });
  return filterDealersByRegion(allDealers, region);
};
